import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import api, { uploadFile } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import careerRoadmapBanner from '../../../assets/learning/career-roadmap-banner.png';
import progressCareerGrowth from '../../../assets/learning/progress-career-growth.png';
import pythonLearningHero from '../../../assets/learning/python-learning-hero.png';
import uiUxDesignSpotlight from '../../../assets/learning/ui-ux-design-spotlight.png';
import { isCollegeStudentExperience } from '../../../utils/studentExperience';
import LinkedQuizAttempt from '../../../components/training/LinkedQuizAttempt';
import './OnDemandCoursesPage.css';

const defaultAccessRule = () => ({
  category: '',
  allowAllCourses: true,
  courseIds: [],
  selectionLimit: '',
  discountType: 'none',
  discountValue: '',
});

const defaultPlan = () => ({
  name: '',
  code: '',
  description: '',
  isActive: true,
  isDefaultBasic: false,
  price: '',
  salePrice: '',
  durationDays: 30,
  validFrom: '',
  validTo: '',
  expiryDate: '',
  quotas: {
    anyCourseLimit: '',
    liveCourseLimit: '',
    onDemandCourseLimit: '',
    unlimitedOnDemand: false,
  },
  rules: [defaultAccessRule()],
});

const defaultCoupon = () => ({
  code: '',
  title: '',
  description: '',
  packageIds: [],
  discountType: 'percent',
  discountValue: '',
  maxUses: '',
  maxUsesPerUser: 1,
  isActive: true,
  validFrom: '',
  validTo: '',
});

const defaultPromotion = () => ({
  name: '',
  description: '',
  isActive: true,
  startsAt: '',
  endsAt: '',
  categories: [],
  courseIds: [],
  deliveryModes: [],
  liveSession: {
    sessionDate: '',
    sessionTime: '',
    meetingUrl: '',
    meetingNote: '',
  },
  audienceRoleKeys: [],
});
const defaultPartnerCollege = () => ({
  name: '',
  code: '',
  uniqueAccessCode: '',
  description: '',
  websiteUrl: '',
  contactNumber: '',
  supportMessage: '',
  logoUrl: '',
  qrCodeUrl: '',
  autoApproveEnabled: true,
  acceptedYears: '',
});
const defaultLeadCaptureState = () => ({
  show: false,
  kind: 'course',
  title: '',
  subtitle: '',
  course: null,
});

const VIDEO_COMPLETION_PERCENT = 80;
const VIDEO_MINIMUM_WATCH_SECONDS = 40;
const isTrackedVideoLesson = (lesson) => ['uploaded_video', 'promo_video'].includes(String(lesson?.lessonType || ''))
  && Boolean(lesson?.uploadedVideoUrl);

const TrackedLessonVideo = ({ lesson, className = '', style, onProgress }) => {
  const trackingRef = useRef({ watchedSeconds: 0, lastCurrentTime: null, lastSavedSeconds: 0 });

  useEffect(() => {
    const watchedSeconds = Math.max(0, Number(lesson?.progress?.watchedSeconds || 0));
    trackingRef.current = { watchedSeconds, lastCurrentTime: null, lastSavedSeconds: watchedSeconds };
    onProgress?.(
      lesson?._id,
      Math.max(0, Number(lesson?.progress?.watchedPercent || 0)),
      watchedSeconds,
      Math.max(0, Number(lesson?.durationSeconds || 0))
    );
  }, [lesson?._id, lesson?.durationSeconds, lesson?.progress?.watchedPercent, lesson?.progress?.watchedSeconds, onProgress]);

  const saveProgress = useCallback(async (video) => {
    if (!lesson?._id || !video?.duration || !Number.isFinite(video.duration)) return;
    const watchedSeconds = Math.min(video.duration, trackingRef.current.watchedSeconds);
    const watchedPercent = Math.min(100, Math.round((watchedSeconds / video.duration) * 100));
    trackingRef.current.lastSavedSeconds = watchedSeconds;
    onProgress?.(lesson?._id, watchedPercent, watchedSeconds, video.duration);
    try {
      await api.post(`/training/on-demand/lessons/${lesson._id}/progress`, {
        completed: false,
        watchedSeconds: Math.round(watchedSeconds),
        watchedPercent,
        lastPositionSeconds: Math.round(video.currentTime || 0),
        videoDurationSeconds: Math.round(video.duration || 0),
      });
    } catch (_) {
      // A later playback heartbeat retries progress without interrupting the lesson.
    }
  }, [lesson?._id, onProgress]);

  const handleTimeUpdate = (event) => {
    const video = event.currentTarget;
    const previousTime = trackingRef.current.lastCurrentTime;
    const currentTime = Number(video.currentTime || 0);
    if (previousTime !== null) {
      const elapsed = currentTime - previousTime;
      if (elapsed > 0 && elapsed <= 2.5) trackingRef.current.watchedSeconds += elapsed;
    }
    trackingRef.current.lastCurrentTime = currentTime;
    if (video.duration && Number.isFinite(video.duration)) {
      const percent = Math.min(100, Math.round((trackingRef.current.watchedSeconds / video.duration) * 100));
      onProgress?.(lesson?._id, percent, trackingRef.current.watchedSeconds, video.duration);
      if (trackingRef.current.watchedSeconds - trackingRef.current.lastSavedSeconds >= 8) saveProgress(video);
    }
  };

  return (
    <video
      controls
      className={className}
      style={style}
      src={lesson?.uploadedVideoUrl}
      poster={lesson?.thumbnailUrl || undefined}
      onPlay={(event) => { trackingRef.current.lastCurrentTime = Number(event.currentTarget.currentTime || 0); }}
      onTimeUpdate={handleTimeUpdate}
      onPause={(event) => saveProgress(event.currentTarget)}
      onEnded={(event) => saveProgress(event.currentTarget)}
    />
  );
};

const COLLEGE_EXPERIENCE_TABS = new Set([
  'dashboard',
  'progress',
  'for-you',
  'recommended',
  'my-subscription',
  'subscription-details',
  'saved-courses',
  'billing',
  'downloads',
  'share-programs',
  'help-center',
  'advisor-contact',
  'career-quiz',
  'checkout',
]);

const CAREER_QUIZ_QUESTIONS = [
  {
    id: 'goal',
    title: 'What would you most like to achieve?',
    subtitle: 'Choose the outcome that matters most right now.',
    options: [
      { value: 'development', label: 'Build websites and applications', icon: 'bi-code-slash', terms: ['development', 'full stack', 'web', 'python', 'java'] },
      { value: 'data', label: 'Understand data and business trends', icon: 'bi-bar-chart', terms: ['data', 'analytics', 'excel', 'sql', 'tableau', 'power bi'] },
      { value: 'ai', label: 'Create AI and automation solutions', icon: 'bi-cpu', terms: ['artificial intelligence', 'machine learning', 'ai', 'ml', 'automation'] },
      { value: 'design', label: 'Design useful digital experiences', icon: 'bi-bezier2', terms: ['design', 'ui', 'ux', 'creative'] },
      { value: 'business', label: 'Grow business and leadership skills', icon: 'bi-briefcase', terms: ['business', 'management', 'marketing', 'leadership'] },
    ],
  },
  {
    id: 'experience',
    title: 'Where are you in your learning journey?',
    subtitle: 'This helps us prioritize the right starting level.',
    options: [
      { value: 'beginner', label: 'I am just getting started', icon: 'bi-sunrise', terms: ['beginner', 'fundamentals', 'introduction', 'basic'] },
      { value: 'intermediate', label: 'I know the basics and want practice', icon: 'bi-graph-up-arrow', terms: ['intermediate', 'project', 'practical'] },
      { value: 'advanced', label: 'I want career-focused specialization', icon: 'bi-stars', terms: ['advanced', 'professional', 'career', 'mastery'] },
    ],
  },
  {
    id: 'format',
    title: 'How do you prefer to learn?',
    subtitle: 'Select the format that fits your schedule.',
    options: [
      { value: 'on_demand', label: 'Learn at my own pace', icon: 'bi-play-circle', terms: ['on demand', 'self paced', 'recorded'] },
      { value: 'live', label: 'Join live guided sessions', icon: 'bi-camera-video', terms: ['live', 'online', 'mentor', 'instructor'] },
      { value: 'project', label: 'Learn through projects and practice', icon: 'bi-kanban', terms: ['project', 'internship', 'practical', 'hands-on'] },
    ],
  },
];
const defaultLeadForm = (user = {}) => ({
  name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
  phone: user?.phone || '',
  email: user?.email || '',
  collegeName: '',
  area: '',
  address: '',
  notes: '',
  membersCount: 1,
});

const defaultSection = () => ({
  title: '',
  description: '',
  isPublished: true,
});

const defaultLesson = () => ({
  sectionId: '',
  title: '',
  summary: '',
  lessonType: 'uploaded_video',
  durationSeconds: '',
  thumbnailUrl: '',
  uploadedVideoUrl: '',
  embeddedVideoUrl: '',
  imageUrl: '',
  htmlContent: '',
  resourceUrl: '',
  isPublished: true,
  requiresLessonIds: [],
  quizConfig: {
    passPercent: 60,
    questions: [{ question: '', options: ['', ''], correctOptionIndex: 0 }],
  },
});

const defaultQuizQuestion = () => ({ question: '', options: ['', ''], correctOptionIndex: 0 });
const SHOW_LMS_MIGRATION_TOOLS = false;

const fmtMoney = (value) => `Rs ${Number(value || 0).toLocaleString('en-IN')}`;
const fmtDate = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleDateString('en-IN');
};
const fmtDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};
const toTitleWords = (value) => String(value || '')
  .replace(/[_-]+/g, ' ')
  .trim()
  .replace(/\s+/g, ' ')
  .split(' ')
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');
const getInitials = (...values) => values
  .join(' ')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part.charAt(0).toUpperCase())
  .join('') || 'TS';
const withAuthToken = (url) => {
  if (!url) return '';
  const raw = String(url || '').trim();
  if (!raw || /^(https?:|data:|blob:)/i.test(raw)) return raw;
  const token = localStorage.getItem('token');
  if (!token) return raw;
  if (/[?&]t=/.test(raw)) return raw;
  return raw.includes('?') ? `${raw}&t=${encodeURIComponent(token)}` : `${raw}?t=${encodeURIComponent(token)}`;
};
const rewriteProtectedUploadUrls = (html) => String(html || '').replace(
  /(src|href)=["']([^"']+)["']/gi,
  (match, attr, url) => `${attr}="${withAuthToken(url)}"`
);
const normalizeMediaUrl = (value) => withAuthToken(String(value || '').trim());
const normalizeCourseMedia = (course = {}, fallbackCourse = null) => {
  if (!course && !fallbackCourse) return course;
  const merged = { ...(fallbackCourse || {}), ...(course || {}) };
  const thumbnailUrl = normalizeMediaUrl(merged.thumbnailUrl || fallbackCourse?.thumbnailUrl || course?.thumbnailUrl);
  const promoImageUrl = normalizeMediaUrl(merged.promoImageUrl || fallbackCourse?.promoImageUrl || course?.promoImageUrl);
  return {
    ...merged,
    thumbnailUrl: thumbnailUrl || promoImageUrl || '',
    promoImageUrl: promoImageUrl || thumbnailUrl || '',
  };
};
const normalizeLearningRow = (row = {}, catalogMap = new Map()) => {
  const courseId = String(row?.course?._id || row?.course || '');
  const fallbackCourse = courseId ? catalogMap.get(courseId) : null;
  return {
    ...row,
    course: normalizeCourseMedia(row?.course || {}, fallbackCourse),
  };
};
const addDaysToIsoDate = (startDate, days) => {
  if (!startDate) return '';
  const totalDays = Math.max(1, Number(days || 0));
  const dt = new Date(startDate);
  if (Number.isNaN(dt.getTime())) return '';
  dt.setDate(dt.getDate() + totalDays - 1);
  return dt.toISOString().slice(0, 10);
};
const formatPlanWindowLabel = (validFrom, validTo) => {
  const from = fmtDate(validFrom);
  const to = fmtDate(validTo);
  if (validFrom && validTo) return `${from} to ${to}`;
  if (validFrom) return `${from} onwards`;
  if (validTo) return `Until ${to}`;
  return 'Always available';
};
const generatePlanCode = (name) => {
  const cleaned = String(name || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((part) => part.slice(0, 4))
    .join('');
  return cleaned || 'PLAN';
};
const fmtDuration = (seconds) => {
  const total = Math.max(0, Number(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total % 3600) / 60);
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
};
const isWithinDateWindow = (start, end, now = new Date()) => {
  const current = now instanceof Date ? now : new Date(now);
  const startAt = start ? new Date(start) : null;
  const endAt = end ? new Date(end) : null;
  if (startAt && !Number.isNaN(startAt.getTime()) && startAt.getTime() > current.getTime()) return false;
  if (endAt && !Number.isNaN(endAt.getTime()) && endAt.getTime() < current.getTime()) return false;
  return true;
};
const studentPlanRuleMatchesCourse = (rule = {}, course = {}, at = new Date()) => {
  if (!course) return false;
  const current = at instanceof Date ? at : new Date(at);
  const startAt = rule.startDate ? new Date(rule.startDate) : null;
  const expiryAt = rule.expiryDate ? new Date(rule.expiryDate) : null;
  if (startAt && !Number.isNaN(startAt.getTime()) && startAt.getTime() > current.getTime()) return false;
  if (expiryAt && !Number.isNaN(expiryAt.getTime()) && expiryAt.getTime() < current.getTime()) return false;
  const category = normalizeCourseCategory(course.category);
  const categoryMatch = !Array.isArray(rule.categories) || !rule.categories.length
    || rule.categories.some((item) => normalizeCourseCategory(item) === category);
  const courseMatch = !Array.isArray(rule.courseIds) || !rule.courseIds.length
    || rule.courseIds.some((id) => String(id) === String(course._id));
  return categoryMatch && courseMatch;
};
const normalizeCourseCategory = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
const isOnDemandCategory = (value) => /^on[\s_-]?demand$/i.test(String(value || '').trim());
const isSelfServeCategory = (value) => /^(?:on[\s_-]?demand|free(?:[\s_-]?course)?|open[\s_-]?access)$/i.test(String(value || '').trim());
const inferCourseMode = (course = {}) => {
  if (isSelfServeCategory(course.category)) return 'on_demand';
  const legacyMode = String(course.deliveryMode || '').trim().toLowerCase();
  if (legacyMode === 'on_demand' || legacyMode === 'hybrid') return 'on_demand';
  return 'live';
};
const isSelfServeLearningCourse = (course = {}) => inferCourseMode(course) === 'on_demand';
const formatCourseCategoryLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'No category';
  return raw.split(/[\s_-]+/).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
};
const RELATED_LEARNING_CLUSTERS = [
  ['python', 'ai', 'artificial intelligence', 'machine learning', 'ml', 'data science', 'data analyst', 'analytics', 'deep learning'],
  ['sql', 'data analyst', 'analytics', 'power bi', 'excel', 'python', 'tableau', 'business intelligence'],
  ['frontend', 'react', 'javascript', 'typescript', 'ui', 'ux', 'full stack', 'web development'],
  ['backend', 'node', 'api', 'database', 'mongodb', 'sql', 'full stack', 'devops'],
  ['java', 'spring', 'backend', 'api', 'microservices', 'full stack'],
  ['devops', 'cloud', 'aws', 'azure', 'docker', 'kubernetes', 'ci/cd'],
  ['ui', 'ux', 'frontend', 'figma', 'product design', 'web design'],
];
const inferRelatedLearningTerms = (text = '') => {
  const hay = String(text || '').trim().toLowerCase();
  const terms = new Set();
  RELATED_LEARNING_CLUSTERS.forEach((cluster) => {
    if (cluster.some((item) => hay.includes(item))) {
      cluster.forEach((item) => terms.add(item));
    }
  });
  if (!terms.size && hay) {
    hay.split(/[^a-z0-9]+/i).filter(Boolean).forEach((item) => terms.add(item.toLowerCase()));
  }
  return Array.from(terms);
};
const orderStatusColor = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'paid': return 'success';
    case 'failed': return 'danger';
    case 'payment_pending': return 'warning';
    case 'created': return 'info';
    default: return 'secondary';
  }
};
const formatLessonTypeLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Lesson';
  return raw.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
};
const consistencyTone = (status) => ({
  strong: 'success',
  building: 'primary',
  started: 'warning',
  at_risk: 'danger',
  inactive: 'danger',
  new: 'secondary',
}[String(status || '').toLowerCase()] || 'secondary');
const isCompletedLearningRow = (row) => (
  Number(row?.learningSummary?.completionPercent || 0) >= 100
  || String(row?.status || '').trim().toLowerCase() === 'completed'
);
const getCoursePrice = (item = {}) => {
  const course = item?.course || item || {};
  const values = [course.salePrice, course.totalFees, course.price, course.fee]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value >= 0);
  return values.find((value) => value > 0) || 0;
};
const isFreeLearningCourse = (item = {}) => {
  const course = item?.course || item || {};
  const searchable = `${course.category || ''} ${course.pricingType || ''} ${course.accessType || ''}`.toLowerCase();
  const hasLearningAccess = Boolean(item?.learningSummary || item?.enrollment || course.enrollment || item?.access?.allowed || course.access?.allowed);
  const explicitlyFree = course.isFree === true || /(^|\s)(free|basic|open access)(\s|$)/.test(searchable);
  return hasLearningAccess || explicitlyFree || getCoursePrice(course) <= 0;
};
const getLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const hashCourseRotationValue = (value = '') => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};
const rotateCoursesDaily = (items = [], dateKey = getLocalDateKey()) => [...items].sort((left, right) => {
  const leftCourse = left?.course || left || {};
  const rightCourse = right?.course || right || {};
  const leftKey = `${dateKey}:${leftCourse._id || leftCourse.courseName || ''}`;
  const rightKey = `${dateKey}:${rightCourse._id || rightCourse.courseName || ''}`;
  const difference = hashCourseRotationValue(leftKey) - hashCourseRotationValue(rightKey);
  return difference || String(leftCourse._id || leftCourse.courseName || '').localeCompare(String(rightCourse._id || rightCourse.courseName || ''));
});
const EXPLORE_SPOTLIGHT_ROTATION_MS = 5 * 60 * 60 * 1000;
const getExploreSpotlightSlot = () => Math.floor(Date.now() / EXPLORE_SPOTLIGHT_ROTATION_MS);
const scrollCourseRail = (railRef, direction) => {
  const rail = railRef?.current;
  if (!rail) return;
  const firstCard = rail.firstElementChild;
  const distance = firstCard
    ? firstCard.getBoundingClientRect().width + 16
    : Math.max(240, rail.clientWidth * 0.8);
  rail.scrollBy({ left: direction * distance, behavior: 'smooth' });
};
const orderCoursesFreeFirst = (items = [], { paidLimit = 1, freeBeforePaid = 4, limit = 8 } = {}) => {
  const seen = new Set();
  const unique = items.filter((item) => {
    const course = item?.course || item || {};
    const id = String(course?._id || item?._id || '');
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  const free = rotateCoursesDaily(unique.filter(isFreeLearningCourse));
  const paid = rotateCoursesDaily(unique.filter((item) => !isFreeLearningCourse(item))).slice(0, paidLimit);
  const ordered = [
    ...free.slice(0, freeBeforePaid),
    ...paid,
    ...free.slice(freeBeforePaid),
  ];
  return ordered.slice(0, limit);
};
const hasPublishedCourseContent = (item = {}) => {
  const course = item?.course || item || {};
  return Number(
    course.totalLessons
    || course.lessonCount
    || course.publishedLessonCount
    || course.contentCount
    || item?.learningSummary?.totalLessons
    || 0
  ) > 0;
};
const orderCoursesContentFirst = (items = [], { paidLimit = 1, freeBeforePaid = 4, limit = 20 } = {}) => {
  const contentReady = items.filter(hasPublishedCourseContent);
  const contentPending = items.filter((item) => !hasPublishedCourseContent(item));
  const ready = orderCoursesFreeFirst(contentReady, { paidLimit, freeBeforePaid, limit });
  const paidUsed = ready.filter((item) => !isFreeLearningCourse(item)).length;
  const remaining = Math.max(0, limit - ready.length);
  const pending = orderCoursesFreeFirst(contentPending, {
    paidLimit: Math.max(0, paidLimit - paidUsed),
    freeBeforePaid,
    limit: remaining,
  });
  return [...ready, ...pending].slice(0, limit);
};
const getLearningFocusedCourseCta = (item = {}) => {
  const course = item?.course || item || {};
  if (item?.learningSummary || item?.enrollment || course.enrollment) return 'Start Learning';
  const mode = String(course.deliveryMode || course.mode || course.courseMode || course.category || '').toLowerCase();
  return /(live|hybrid|batch|cohort)/.test(mode) ? 'Enroll Now' : 'Start Your Career';
};
const getStudentCourseActionLabel = (item) => {
  if (!item) return 'Explore';
  const completionPercent = Number(item?.learningSummary?.completionPercent || 0);
  const inProgressLessons = Number(item?.learningSummary?.inProgressLessons || 0);
  const completedLessons = Number(item?.learningSummary?.completedLessons || 0);
  const pendingLessons = Number(item?.learningSummary?.pendingLessons || 0);
  const totalLessons = Number(item?.learningSummary?.totalLessons || 0);
  const hasActivity = inProgressLessons > 0
    || completedLessons > 0
    || completionPercent > 0
    || !!item?.learningSummary?.lastAccessedAt;
  const isCompleted = completionPercent >= 100 || String(item?.status || '').trim().toLowerCase() === 'completed';
  const isEnrolled = !!item?.enrollment || !!item?.course?.enrollment || !!item?.learningSummary || !!item?.course;
  if (isCompleted) return 'Open Content';
  if (isEnrolled && hasActivity) return 'Resume';
  if (isEnrolled && totalLessons > 0 && pendingLessons >= 0) return 'Start Learning';
  if (isEnrolled) return 'Start Now';
  if (item?.access?.allowed || item?.course?.access?.allowed) return 'Start Learning';
  return 'Explore';
};
const getLearningProgressState = (row = {}) => {
  const completion = Number(row?.learningSummary?.completionPercent || 0);
  const completedLessons = Number(row?.learningSummary?.completedLessons || 0);
  const inProgressLessons = Number(row?.learningSummary?.inProgressLessons || 0);
  const hasActivity = completion > 0
    || completedLessons > 0
    || inProgressLessons > 0
    || !!row?.learningSummary?.lastAccessedAt;
  if (completion >= 100 || String(row?.status || '').trim().toLowerCase() === 'completed') return 'completed';
  if (hasActivity) return 'in_progress';
  return 'not_started';
};
const hasRenderableLessonContent = (lesson = {}) => Boolean(
  lesson?.uploadedVideoUrl
  || lesson?.embeddedVideoUrl
  || lesson?.imageUrl
  || lesson?.htmlContent
  || lesson?.summary
  || lesson?.resourceUrl
  || lesson?.lessonType === 'linked_quiz'
  || (Array.isArray(lesson?.quizConfig?.questions) && lesson.quizConfig.questions.length)
);
const inferStudentLessonTypeFromItem = (item = {}) => {
  const itemType = String(item?.type || '').trim().toLowerCase();
  if (itemType === 'quiz' || item?.lmsLesson?.quizRef) return 'linked_quiz';
  const explicitType = String(item?.lmsLesson?.lessonType || '').trim();
  if (explicitType) return explicitType;
  if (itemType === 'video') return item?.lmsLesson?.embeddedVideoUrl ? 'embedded_video' : 'uploaded_video';
  if (
    itemType === 'html'
    || itemType === 'notion'
    || String(item?.htmlContent || '').trim()
    || (Array.isArray(item?.contentBlocks) && item.contentBlocks.length)
  ) return 'text';
  if (item?.lmsLesson?.promoVideoUrl) return 'promo_video';
  return 'resource';
};
const escapeStudentHtml = (value) => String(value || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const notionBlocksToStudentHtml = (blocks = []) => {
  if (!Array.isArray(blocks) || !blocks.length) return '';
  const html = [];
  let bulletItems = [];
  let numberedItems = [];
  let tableRows = [];

  const flushBulletItems = () => {
    if (!bulletItems.length) return;
    html.push(`<ul>${bulletItems.join('')}</ul>`);
    bulletItems = [];
  };
  const flushNumberedItems = () => {
    if (!numberedItems.length) return;
    html.push(`<ol>${numberedItems.join('')}</ol>`);
    numberedItems = [];
  };
  const flushTableRows = () => {
    if (!tableRows.length) return;
    html.push(`
      <div class="table-responsive mb-3">
        <table class="table table-bordered table-sm align-middle mb-0">
          <tbody>${tableRows.join('')}</tbody>
        </table>
      </div>
    `);
    tableRows = [];
  };
  const flushStructuredBlocks = () => {
    flushBulletItems();
    flushNumberedItems();
    flushTableRows();
  };

  blocks.forEach((block = {}) => {
    const type = String(block.type || '').trim();
    const text = escapeStudentHtml(block.text || '');
    if (type === 'bullet') {
      flushNumberedItems();
      flushTableRows();
      bulletItems.push(`<li>${text}</li>`);
      return;
    }
    if (type === 'numbered') {
      flushBulletItems();
      flushTableRows();
      numberedItems.push(`<li>${text}</li>`);
      return;
    }
    if (type === 'table_row') {
      flushBulletItems();
      flushNumberedItems();
      const cells = Array.isArray(block.cells) ? block.cells : [];
      tableRows.push(`<tr>${cells.map((cell) => `<td>${escapeStudentHtml(cell)}</td>`).join('')}</tr>`);
      return;
    }

    flushStructuredBlocks();

    switch (type) {
      case 'heading_1':
        html.push(`<h1>${text}</h1>`);
        break;
      case 'heading_2':
        html.push(`<h2>${text}</h2>`);
        break;
      case 'heading_3':
        html.push(`<h3>${text}</h3>`);
        break;
      case 'paragraph':
        html.push(`<p>${text}</p>`);
        break;
      case 'code':
        html.push(`<pre><code>${text}</code></pre>`);
        break;
      case 'quote':
        html.push(`<blockquote>${text}</blockquote>`);
        break;
      case 'callout':
        html.push(`<div class="alert alert-info py-2 px-3 mb-3">${text}</div>`);
        break;
      case 'divider':
        html.push('<hr/>');
        break;
      case 'todo':
        html.push(`<div class="form-check mb-2"><input type="checkbox" class="form-check-input" ${block.checked ? 'checked' : ''} disabled/><label class="form-check-label">${text}</label></div>`);
        break;
      case 'toggle':
        html.push(`<details class="mb-3"><summary class="fw-semibold">${text}</summary></details>`);
        break;
      case 'image':
        if (block.url) {
          html.push(`
            <figure class="mb-3">
              <img src="${withAuthToken(String(block.url))}" alt="${escapeStudentHtml(block.caption || '')}" style="max-width:100%;border-radius:12px;" />
              ${block.caption ? `<figcaption class="small text-muted mt-2">${escapeStudentHtml(block.caption)}</figcaption>` : ''}
            </figure>
          `);
        }
        break;
      default:
        html.push(`<p>${text}</p>`);
        break;
    }
  });

  flushStructuredBlocks();
  return html.join('\n');
};
const buildStudentHtmlContent = (item = {}) => {
  const directHtml = String(item?.htmlContent || '').trim();
  if (directHtml) return rewriteProtectedUploadUrls(directHtml);
  if (Array.isArray(item?.contentBlocks) && item.contentBlocks.length) {
    return rewriteProtectedUploadUrls(notionBlocksToStudentHtml(item.contentBlocks));
  }
  const referencedBlock = item?.contentBlock || item?.contentBlockId || null;
  const referencedBody = String(referencedBlock?.body || '').trim();
  if (referencedBody) return rewriteProtectedUploadUrls(referencedBody);
  const referencedNotionBlocks = Array.isArray(referencedBlock?.notionBlocks) ? referencedBlock.notionBlocks : [];
  if (referencedNotionBlocks.length) {
    return rewriteProtectedUploadUrls(notionBlocksToStudentHtml(referencedNotionBlocks));
  }
  return '';
};

const countRenderableOutlineLessons = (outline = []) => (
  (outline || []).reduce((sum, section) => (
    sum + (section.lessons || []).filter((lesson) => hasRenderableLessonContent(lesson)).length
  ), 0)
);

const glassPanel = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(248,250,252,0.72) 100%)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  border: '1px solid rgba(226,232,240,0.9)',
  boxShadow: '0 24px 60px rgba(15, 23, 42, 0.10)',
};

const glassTile = {
  background: 'linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(248,250,252,0.76) 100%)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: '1px solid rgba(226,232,240,0.92)',
  boxShadow: '0 20px 44px rgba(15, 23, 42, 0.08)',
};

const softHeroGradient = 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 38%, #0ea5e9 72%, #22c55e 100%)';
const softGlassInset = {
  background: 'rgba(255,255,255,0.18)',
  border: '1px solid rgba(255,255,255,0.22)',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
};

const STUDENT_DASHBOARD_THEMES = [
  {
    key: 'blue',
    label: 'Blue',
    pageBg: 'linear-gradient(180deg, #eef6ff 0%, #f6f9ff 100%)',
    shellBg: 'linear-gradient(180deg, rgba(255,255,255,0.94) 0%, rgba(241,245,255,0.96) 100%)',
    shellBorder: 'rgba(96, 165, 250, 0.18)',
    heroBg: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 44%, #38bdf8 100%)',
    cardBg: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(239,246,255,0.9) 100%)',
    panelBg: 'rgba(255,255,255,0.94)',
    panelBorder: 'rgba(191,219,254,0.9)',
    title: '#0f172a',
    text: '#334155',
    muted: '#64748b',
    accent: '#4f46e5',
    accentSoft: 'rgba(79,70,229,0.1)',
  },
  {
    key: 'white',
    label: 'White',
    pageBg: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    shellBg: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)',
    shellBorder: 'rgba(100,116,139,0.18)',
    heroBg: 'linear-gradient(135deg, #0f172a 0%, #334155 48%, #64748b 100%)',
    cardBg: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.92) 100%)',
    panelBg: 'rgba(255,255,255,0.96)',
    panelBorder: 'rgba(226,232,240,0.95)',
    title: '#0f172a',
    text: '#334155',
    muted: '#64748b',
    accent: '#334155',
    accentSoft: 'rgba(51,65,85,0.08)',
  },
  {
    key: 'black',
    label: 'Black',
    pageBg: 'linear-gradient(180deg, #020617 0%, #0f172a 100%)',
    shellBg: 'linear-gradient(180deg, rgba(15,23,42,0.94) 0%, rgba(2,6,23,0.96) 100%)',
    shellBorder: 'rgba(71,85,105,0.32)',
    heroBg: 'linear-gradient(135deg, #020617 0%, #111827 48%, #1e293b 100%)',
    cardBg: 'linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.92) 100%)',
    panelBg: 'rgba(15,23,42,0.95)',
    panelBorder: 'rgba(71,85,105,0.38)',
    title: '#f8fafc',
    text: '#cbd5e1',
    muted: '#94a3b8',
    accent: '#60a5fa',
    accentSoft: 'rgba(96,165,250,0.16)',
  },
];

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const existing = document.querySelector('script[data-razorpay-checkout="1"]');
  if (existing) {
    existing.addEventListener('load', () => resolve(true), { once: true });
    existing.addEventListener('error', () => resolve(false), { once: true });
    return;
  }
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.async = true;
  script.dataset.razorpayCheckout = '1';
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

const ProgressBar = ({ value, color = '#2563eb', trackColor = '#e2e8f0', className = '' }) => (
  <div className={`progress ${className}`.trim()} style={{ height: 8, background: trackColor }}>
    <div
      className="progress-bar"
      role="progressbar"
      style={{ width: `${Math.max(0, Math.min(100, Number(value || 0)))}%`, background: color }}
      aria-valuenow={Math.max(0, Math.min(100, Number(value || 0)))}
      aria-valuemin="0"
      aria-valuemax="100"
    />
  </div>
);

const MiniBarList = ({ rows = [], color = '#2563eb', valueKey = 'value', labelKey = 'label', suffix = '' }) => {
  const maxValue = Math.max(1, ...rows.map((row) => Number(row?.[valueKey] || 0)));
  return (
    <div className="d-flex flex-column gap-2">
      {rows.map((row, index) => (
        <div key={`${row?.[labelKey] || 'row'}-${index}`}>
          <div className="d-flex justify-content-between small mb-1">
            <span className="text-truncate pe-2">{row?.[labelKey] || '-'}</span>
            <span className="fw-semibold">{Number(row?.[valueKey] || 0).toLocaleString('en-IN')}{suffix}</span>
          </div>
          <ProgressBar value={(Number(row?.[valueKey] || 0) / maxValue) * 100} color={color} />
        </div>
      ))}
    </div>
  );
};

const CardShell = ({ title, actions, children }) => (
  <div className="card border-0 h-100" style={{ ...glassPanel, borderRadius: 24, overflow: 'hidden' }}>
    <div className="card-header d-flex justify-content-between align-items-center" style={{ background: 'rgba(255,255,255,0.52)', borderBottom: '1px solid rgba(226,232,240,0.78)' }}>
      <div className="fw-semibold">{title}</div>
      {actions}
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const Pill = ({ children, color = 'secondary' }) => (
  <span className={`badge bg-${color}-subtle text-${color} border border-${color}-subtle`} style={{ borderRadius: 999, padding: '7px 10px', fontWeight: 600 }}>
    {children}
  </span>
);

const StudentMetricTile = ({ label, value, hint, tone = 'primary', icon }) => (
  <div className="border h-100" style={{ ...glassTile, borderRadius: 22, padding: 18 }}>
    <div className="d-flex justify-content-between align-items-start gap-3">
      <div>
        <div className="small text-muted mb-1">{label}</div>
        <div className="fw-semibold" style={{ fontSize: 26, lineHeight: 1.1 }}>{value}</div>
        {hint ? <div className="small text-muted mt-2">{hint}</div> : null}
      </div>
      {icon ? (
        <div
          className={`d-inline-flex align-items-center justify-content-center text-${tone}`}
          style={{ width: 46, height: 46, borderRadius: 16, background: 'rgba(255,255,255,0.75)', boxShadow: 'inset 0 0 0 1px rgba(226,232,240,0.9)', fontSize: 18 }}
        >
          <i className={`bi ${icon}`} />
        </div>
      ) : null}
    </div>
  </div>
);

const getCourseArtworkTheme = (title, category, accent) => {
  const value = `${title || ''} ${category || ''}`.toLowerCase();
  if (/python/.test(value)) return { icon: 'bi-filetype-py', label: 'Python', start: '#2563eb', middle: '#4f46e5', end: '#facc15' };
  if (/java/.test(value)) return { icon: 'bi-cup-hot-fill', label: 'Java', start: '#dc2626', middle: '#f97316', end: '#7c2d12' };
  if (/data|analytics|excel|tableau|power bi|sql/.test(value)) return { icon: 'bi-bar-chart-line-fill', label: 'Data Analytics', start: '#0891b2', middle: '#0f766e', end: '#164e63' };
  if (/mern|full.?stack|web|react|node/.test(value)) return { icon: 'bi-code-slash', label: 'Web Development', start: '#7c3aed', middle: '#4f46e5', end: '#1e3a8a' };
  if (/artificial intelligence|machine learning|ai\b|ml\b/.test(value)) return { icon: 'bi-cpu-fill', label: 'AI & ML', start: '#c026d3', middle: '#7c3aed', end: '#312e81' };
  if (/ui|ux|design|creative/.test(value)) return { icon: 'bi-bezier2', label: 'UI/UX Design', start: '#db2777', middle: '#9333ea', end: '#4338ca' };
  if (/cyber|security/.test(value)) return { icon: 'bi-shield-lock-fill', label: 'Cyber Security', start: '#10b981', middle: '#047857', end: '#064e3b' };
  if (/cloud|aws|azure/.test(value)) return { icon: 'bi-cloud-fill', label: 'Cloud Technology', start: '#0ea5e9', middle: '#2563eb', end: '#312e81' };
  return { icon: 'bi-journal-code', label: 'Featured Learning', start: accent || '#2563eb', middle: '#4f46e5', end: '#172554' };
};

const CourseArtwork = ({ title, category, imageUrl, accent = '#2563eb', height = 180, compact = false }) => {
  const theme = getCourseArtworkTheme(title, category, accent);
  return imageUrl ? (
    <img
      src={imageUrl}
      alt={title || 'Course'}
      className="w-100"
      style={{ height, objectFit: 'cover', objectPosition: 'center', borderRadius: compact ? 14 : 18, display: 'block' }}
    />
  ) : (
    <div
      className="w-100 text-white d-flex flex-column justify-content-end"
      style={{
        height,
        borderRadius: compact ? 14 : 18,
        padding: compact ? 16 : 22,
        background: `radial-gradient(circle at 18% 18%, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0) 24%), radial-gradient(circle at 84% 22%, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 20%), linear-gradient(135deg, ${theme.start} 0%, ${theme.middle} 48%, ${theme.end} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', right: compact ? -12 : -18, top: compact ? -10 : -16, width: compact ? 72 : 96, height: compact ? 72 : 96, borderRadius: 24, background: 'rgba(255,255,255,0.12)', transform: 'rotate(18deg)' }} />
      <div style={{ position: 'absolute', left: compact ? -8 : -10, bottom: compact ? 20 : 28, width: compact ? 58 : 76, height: compact ? 58 : 76, borderRadius: 999, border: '1px solid rgba(255,255,255,0.16)', opacity: 0.4 }} />
      <div className="d-flex align-items-center justify-content-between mb-2" style={{ position: 'relative', zIndex: 1 }}>
        <div className="small text-white-50">{theme.label}</div>
        <div className="d-inline-flex align-items-center justify-content-center" style={{ width: compact ? 42 : 50, height: compact ? 42 : 50, borderRadius: 13, background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.22)', boxShadow: '0 10px 24px rgba(15,23,42,.2)', fontSize: compact ? 20 : 24 }}>
          <i className={`bi ${theme.icon}`} />
        </div>
      </div>
      <div className="fw-semibold" style={{ position: 'relative', zIndex: 1, fontSize: compact ? 16 : 22, lineHeight: 1.2 }}>{title || 'Course'}</div>
    </div>
  );
};

const LessonEpisodeSummary = ({ lesson, course, isActive = false, isExpanded = null, sequenceNumber = 0, progressOverride = null }) => {
  const progress = lesson?.progress?.status === 'completed'
    ? 100
    : Math.max(0, Math.min(100, Number(progressOverride ?? lesson?.progress?.watchedPercent ?? 0)));
  const imageUrl = lesson?.thumbnailUrl || course?.thumbnailUrl || course?.promoImageUrl || '';
  const theme = getCourseArtworkTheme(`${course?.courseName || ''} ${lesson?.title || ''}`, course?.category, course?.courseColor);
  const locked = Boolean(lesson?.access?.isLocked);

  return (
    <span className={`student-episode-summary ${isActive ? 'is-active' : ''} ${locked ? 'is-locked' : ''}`.trim()}>
      <span className="student-episode-thumbnail">
        {imageUrl ? (
          <img src={imageUrl} alt="" />
        ) : (
          <span className="student-episode-thumbnail-fallback" style={{ '--episode-start': theme.start, '--episode-end': theme.end }}>
            <i className={`bi ${theme.icon}`} />
          </span>
        )}
        <span className="student-episode-play"><i className={`bi ${locked ? 'bi-lock-fill' : 'bi-play-fill'}`} /></span>
        {progress > 0 && <span className="student-episode-thumbnail-progress" style={{ width: `${progress}%` }} />}
      </span>
      <span className="student-episode-copy">
        <small>{sequenceNumber > 0 ? `Episode ${sequenceNumber}` : 'Course lesson'}{isActive ? ' | Now playing' : ''}</small>
        <strong>{lesson?.title || 'Untitled lesson'}</strong>
        <span>{formatLessonTypeLabel(lesson?.lessonType)}{lesson?.durationSeconds ? ` | ${fmtDuration(lesson.durationSeconds)}` : ''}</span>
      </span>
      <span className="student-episode-state">
        {lesson?.progress?.status === 'completed' && <span className="is-complete"><i className="bi bi-check-circle-fill" /> Completed</span>}
        {!locked && lesson?.progress?.status === 'in_progress' && <span><i className="bi bi-play-circle" /> {progress}%</span>}
        {!locked && !lesson?.progress?.status && <span><i className="bi bi-play-circle" /> Ready</span>}
        {locked && <span><i className="bi bi-lock-fill" /> Locked</span>}
        {isExpanded !== null && <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} student-episode-chevron`} />}
      </span>
    </span>
  );
};

const StudentSurface = ({ children, className = '', style = {}, bordered = true }) => (
  <div
    className={`${bordered ? 'border' : ''} ${className}`.trim()}
    style={{
      ...glassPanel,
      borderRadius: 28,
      ...style,
    }}
  >
    {children}
  </div>
);

const StudentAccountPageHeader = ({ icon, eyebrow = 'Student account', title, subtitle, onBack, action = null }) => (
  <div className="student-account-page-header">
    <button type="button" className="student-account-back" onClick={onBack} aria-label="Back to Subscription">
      <i className="bi bi-arrow-left" />
    </button>
    <span className="student-account-header-icon"><i className={`bi ${icon}`} /></span>
    <div className="student-account-header-copy">
      <small>{eyebrow}</small>
      <h1>{title}</h1>
      <p>{subtitle}</p>
    </div>
    {action ? <div className="student-account-header-action">{action}</div> : null}
  </div>
);

const DashboardRecommendedCarousel = ({ courses = [], learning = [], isMobile, onOpen, onSeeAll, surface = 'course-recommendations', onReach }) => {
  const railRef = useRef(null);
  const lastReportedPositionRef = useRef(0);
  const visibleCount = isMobile ? 1 : 3;
  const canScroll = courses.length > visibleCount;
  const reportVisibleReach = useCallback(() => {
    const rail = railRef.current;
    if (!rail || typeof onReach !== 'function') return;
    const cards = Array.from(rail.querySelectorAll('.dashboard-course-carousel-card'));
    if (!cards.length) return;
    const visibleRight = rail.scrollLeft + rail.clientWidth;
    let furthestPosition = 1;
    cards.forEach((card, index) => {
      const revealThreshold = Math.min(card.getBoundingClientRect().width * 0.25, 48);
      if (card.offsetLeft + revealThreshold <= visibleRight) furthestPosition = index + 1;
    });
    if (furthestPosition <= lastReportedPositionRef.current) return;
    lastReportedPositionRef.current = furthestPosition;
    onReach({ surface, maxVisiblePosition: furthestPosition, totalItems: cards.length });
  }, [onReach, surface]);
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || typeof onReach !== 'function') return undefined;
    let timer = null;
    const scheduleReport = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(reportVisibleReach, 300);
    };
    scheduleReport();
    rail.addEventListener('scroll', scheduleReport, { passive: true });
    window.addEventListener('resize', scheduleReport);
    return () => {
      window.clearTimeout(timer);
      rail.removeEventListener('scroll', scheduleReport);
      window.removeEventListener('resize', scheduleReport);
    };
  }, [courses.length, onReach, reportVisibleReach]);
  const scrollCards = (direction) => {
    const rail = railRef.current;
    const firstCard = rail?.querySelector('.dashboard-course-carousel-card');
    if (!rail || !firstCard) return;
    const styles = window.getComputedStyle(rail);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    rail.scrollBy({ left: direction * (firstCard.getBoundingClientRect().width + gap) * visibleCount, behavior: 'smooth' });
  };

  if (!courses.length) return null;

  return (
    <section className="dashboard-course-carousel mb-4" aria-label="Recommended courses">
      <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
        <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 18 }}>Recommended for you</div>
        <div className="d-flex align-items-center gap-1">
          {canScroll ? (
            <>
              <button type="button" className="btn dashboard-course-carousel-control" aria-label="Previous recommended courses" onClick={() => scrollCards(-1)}>
                <i className="bi bi-chevron-left" />
              </button>
              <button type="button" className="btn dashboard-course-carousel-control" aria-label="Next recommended courses" onClick={() => scrollCards(1)}>
                <i className="bi bi-chevron-right" />
              </button>
            </>
          ) : null}
          <button type="button" className="btn btn-link btn-sm p-0 ms-1 text-decoration-none" style={{ color: '#7c3aed' }} onClick={onSeeAll}>
            See all <i className="bi bi-chevron-right" />
          </button>
        </div>
      </div>
      <div ref={railRef} className="dashboard-course-carousel-rail">
        {courses.map((course, index) => {
          const progress = learning.find((row) => String(row.course?._id || row.course) === String(course._id))?.learningSummary?.completionPercent || 0;
          const isFree = isFreeLearningCourse(course);
          return (
            <button
              key={`dashboard-recommended-${course._id || index}`}
              type="button"
              className="dashboard-course-carousel-card overflow-hidden bg-white text-start p-0"
              onClick={() => onOpen(course)}
            >
              <div className="position-relative dashboard-course-carousel-artwork">
                <CourseArtwork title={course.courseName} category={course.category} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#6366f1'} height={isMobile ? 158 : 205} compact />
                <span className={`badge position-absolute ${isFree ? 'dashboard-course-free-badge' : ''}`} style={{ top: 10, right: 10, background: isFree ? 'linear-gradient(135deg, #fffbea, #fef3c7 55%, #fde68a)' : 'rgba(255,255,255,0.94)', color: isFree ? '#92400e' : index === 0 ? '#3730a3' : '#6d28d9', boxShadow: isFree ? '0 0 0 2px rgba(251,191,36,.12), 0 7px 16px rgba(146,64,14,.16)' : '0 5px 14px rgba(15,23,42,0.12)', padding: '6px 9px', borderRadius: 8, fontWeight: isFree ? 900 : 700 }}>
                  <i className={`bi ${isFree ? 'bi-unlock-fill' : index === 0 ? 'bi-star-fill' : 'bi-lightning-charge-fill'} me-1`} /><span>{isFree ? 'Free to learn' : 'Recommended'}</span>
                </span>
              </div>
              <div className="dashboard-course-carousel-body">
                <div className="fw-semibold dashboard-course-carousel-title">{course.courseName || 'Course'}</div>
                <div className="dashboard-course-carousel-category text-truncate">{course.level || course.category || 'Beginner'}</div>
                <div className="dashboard-course-carousel-meta">
                  <span><i className="bi bi-journal-text" />{Number(course.totalLessons || course.lessonCount || 0)} Lessons</span>
                  <span><i className="bi bi-graph-up-arrow" />{progress}% Progress</span>
                </div>
                <ProgressBar value={progress} color={index % 3 === 0 ? '#2563eb' : index % 3 === 1 ? '#8b5cf6' : '#14b8a6'} trackColor="#e5e7eb" />
                <span className="dashboard-course-carousel-cta">
                  {getLearningFocusedCourseCta(course)}
                  <i className="bi bi-arrow-right" />
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const DashboardContinueLearningCarousel = ({ rows = [], isMobile, onOpen, onSeeAll }) => {
  if (rows.length < 2) return null;
  return (
    <section className="dashboard-course-carousel mb-4" aria-label="Continue learning courses">
      <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
        <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 18 }}>Continue Learning</div>
        <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ color: '#7c3aed' }} onClick={onSeeAll}>
          See all <i className="bi bi-chevron-right" />
        </button>
      </div>
      <div className="dashboard-course-carousel-rail">
        {rows.slice(0, 3).map((row, index) => {
          const course = row.course || {};
          const progress = Math.max(0, Math.min(100, Number(row.learningSummary?.completionPercent || 0)));
          return (
            <button
              key={`dashboard-continue-${course._id || index}`}
              type="button"
              className="dashboard-course-carousel-card overflow-hidden bg-white text-start p-0"
              onClick={() => onOpen(row)}
            >
              <div className="dashboard-course-carousel-artwork">
                <CourseArtwork
                  title={course.courseName}
                  category={course.category}
                  imageUrl={course.thumbnailUrl || course.promoImageUrl}
                  accent={course.courseColor || '#4f46e5'}
                  height={isMobile ? 132 : 158}
                  compact
                />
              </div>
              <div className="dashboard-course-carousel-body">
                <div className="fw-semibold dashboard-course-carousel-title">{course.courseName || 'Course'}</div>
                <div className="dashboard-course-carousel-category text-truncate">
                  {row.currentModuleTitle || course.category || (inferCourseMode(course) === 'on_demand' ? 'On-demand learning' : 'Instructor-led learning')}
                </div>
                <div className="dashboard-course-carousel-meta">
                  <span><i className="bi bi-journal-text" />{Number(row.learningSummary?.totalLessons || course.totalLessons || 0)} Lessons</span>
                  <span><i className="bi bi-graph-up-arrow" />{progress}% Progress</span>
                </div>
                <ProgressBar value={progress} color={index % 3 === 0 ? '#2563eb' : index % 3 === 1 ? '#8b5cf6' : '#14b8a6'} trackColor="#e5e7eb" />
                <span className="dashboard-course-carousel-cta">
                  Continue Learning <i className="bi bi-arrow-right" />
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const StudentUpcomingLiveClasses = ({ courses = [], isMobile, onViewAll, onOpen, className = '' }) => (
  <section className={`student-upcoming-live-classes ${className}`.trim()}>
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="fw-semibold" style={{ color: '#0f172a', fontSize: isMobile ? 18 : 22 }}>Upcoming Live Classes for You</div>
      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ color: '#7c3aed' }} onClick={onViewAll}>
        View all <i className="bi bi-chevron-right" />
      </button>
    </div>
    <div className="row g-2">
      {courses.slice(0, 2).map((course, index) => (
        <div className={isMobile ? 'col-6' : 'col-12 col-xl-6'} key={`upcoming-live-${course._id || index}`}>
          <div className="h-100 d-flex flex-column bg-white border" style={{ padding: isMobile ? 10 : 16, borderRadius: 10, borderColor: '#edf2ff', boxShadow: '0 10px 22px rgba(15,23,42,0.05)', minWidth: 0 }}>
            <div className="d-flex justify-content-between align-items-start gap-1 mb-2">
              {course._learningEvent && course.imageUrl ? (
                <img src={course.imageUrl} alt="" className="flex-shrink-0" style={{ width: isMobile ? 46 : 58, height: isMobile ? 34 : 42, borderRadius: 8, objectFit: 'cover' }} />
              ) : (
                <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: isMobile ? 34 : 42, height: isMobile ? 34 : 42, borderRadius: 8, background: index % 2 === 0 ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' : 'linear-gradient(135deg, #10b981 0%, #16a34a 100%)', color: '#fff', fontSize: isMobile ? 16 : 19 }}>
                  <i className={`bi ${index % 2 === 0 ? 'bi-code-slash' : 'bi-cpu-fill'}`} />
                </div>
              )}
              <span className="badge" style={{ maxWidth: isMobile ? 86 : 110, whiteSpace: 'normal', textAlign: 'right', background: '#fff7ed', color: '#ea580c', borderRadius: 6, padding: '4px 5px', fontSize: isMobile ? 8 : 10, lineHeight: 1.15 }}>{index === 0 ? 'Seats filling fast' : 'Limited seats'}</span>
            </div>
            <div className="fw-semibold mb-2" style={{ color: '#0f172a', fontSize: isMobile ? 13 : 16, lineHeight: 1.2, minHeight: isMobile ? 31 : 38 }}>{course.courseName || 'Live Course'}</div>
            <div className="mb-1" style={{ color: '#64748b', fontSize: isMobile ? 10 : 12 }}><i className="bi bi-camera-video-fill me-1" />Live Online</div>
            <div className="mb-3" style={{ color: '#64748b', fontSize: isMobile ? 10 : 12 }}><i className="bi bi-clock me-1" />{course._learningEvent ? [course.eventDate ? new Date(course.eventDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '', course.startTime].filter(Boolean).join(' | ') : (course.durationDays ? `${course.durationDays} days` : 'Flexible duration')}</div>
            <button type="button" className={`btn btn-sm w-100 mt-auto ${index === 0 ? 'btn-primary' : 'btn-success'}`} style={{ borderRadius: 8, padding: '6px 8px', fontSize: isMobile ? 11 : 12 }} onClick={() => onOpen(course)}>
              {getLearningFocusedCourseCta(course)} <i className="bi bi-arrow-right ms-1" />
            </button>
          </div>
        </div>
      ))}
      {!courses.length && (
        <div className="col-12">
          <div className="border bg-white p-4 text-center small" style={{ borderRadius: 10, borderColor: '#edf2ff', color: '#64748b' }}>
            New live classes will be listed here when they are published.
          </div>
        </div>
      )}
    </div>
  </section>
);

const CourseDiscussionPanel = ({ discussions = [], message, onMessageChange, onSubmit, loading, submitting }) => (
  <section className="student-course-discussion-panel">
    <div className="student-course-discussion-heading">
      <span><i className="bi bi-chat-square-text-fill" /></span>
      <div>
        <strong>Ask your course doubt</strong>
        <small>Send your question here. Faculty or admin will reply in this thread.</small>
      </div>
    </div>
    <form onSubmit={onSubmit} className="student-course-discussion-form">
      <textarea
        className="form-control"
        rows={3}
        maxLength={2000}
        value={message}
        onChange={(event) => onMessageChange(event.target.value)}
        placeholder="Type your question about this lesson..."
        aria-label="Course question"
      />
      <div className="d-flex justify-content-between align-items-center gap-2">
        <small>{message.length}/2000</small>
        <button type="submit" className="btn btn-primary btn-sm" disabled={submitting || message.trim().length < 3}>
          {submitting ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-send me-1" />}
          Send Question
        </button>
      </div>
    </form>
    <div className="student-course-discussion-list">
      {loading ? <div className="text-center py-3"><span className="spinner-border spinner-border-sm text-primary" /></div> : null}
      {!loading && !discussions.length ? <div className="student-course-discussion-empty">No questions yet. Ask whenever you need help with this course.</div> : null}
      {!loading && discussions.map((discussion) => (
        <article key={discussion._id} className="student-course-discussion-thread">
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div>
              <small>{discussion.lessonTitle || 'Course question'}</small>
              <p>{discussion.message}</p>
            </div>
            <span className={`badge ${discussion.status === 'answered' ? 'text-bg-success' : 'text-bg-warning'}`}>
              {discussion.status === 'answered' ? 'Answered' : 'Awaiting reply'}
            </span>
          </div>
          {(discussion.replies || []).map((reply) => (
            <div key={reply._id} className="student-course-discussion-reply">
              <span><i className="bi bi-person-check-fill" /></span>
              <div>
                <strong>{[reply.author?.firstName, reply.author?.lastName].filter(Boolean).join(' ') || 'Faculty'}</strong>
                <p>{reply.message}</p>
              </div>
            </div>
          ))}
        </article>
      ))}
    </div>
  </section>
);

const StudentMembershipPlanCollection = ({ entries = [], isMobile, activeSubscription, primaryCategory, onCurrent, onExplore }) => {
  if (!entries.length) return null;
  return (
    <section className="student-membership-plan-collection" aria-label="Available membership plans">
      <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
        <div>
          <div className="fw-semibold" style={{ color: '#0f172a', fontSize: isMobile ? 16 : 20 }}>Affordable Programs for Future-Ready Skills</div>
          <div style={{ color: '#64748b', fontSize: isMobile ? 10 : 12 }}>Start free or choose guided learning built for your career goals.</div>
        </div>
        <span className="badge flex-shrink-0" style={{ borderRadius: 6, padding: '6px 8px', color: '#4f46e5', background: '#eef2ff', fontSize: isMobile ? 9 : 11 }}>
          {entries.length} {entries.length === 1 ? 'plan' : 'plans'}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: isMobile ? 8 : 12 }}>
        {entries.map(({ plan, isCurrent, categoryMatch }, index) => {
          const categories = Array.from(new Set((plan.rules || []).flatMap((rule) => rule?.categories || []).filter(Boolean)));
          const availabilityStatus = String(plan.availability?.status || 'available');
          const isPurchasable = plan.availability?.isPurchasable !== false;
          const isPaidPlan = Number(plan.salePrice || plan.price || 0) > 0;
          const durationLabel = Number(plan.durationDays || 0) > 0 ? `${plan.durationDays} days` : 'Flexible access';
          const accessLabel = plan.quotas?.unlimitedOnDemand
            ? 'Unlimited courses'
            : Number(plan.quotas?.anyCourseLimit || 0) > 0
              ? `${plan.quotas.anyCourseLimit} courses included`
              : categories.length
                ? `${categories.length} learning ${categories.length === 1 ? 'track' : 'tracks'}`
                : 'Curated course access';
          const accent = isCurrent ? '#4f46e5' : !isPurchasable ? '#64748b' : index % 2 ? '#059669' : '#7c3aed';
          const tagline = isCurrent
            ? (activeSubscription ? 'Your active membership, ready whenever you are.' : 'Your exclusive free start to build job-ready skills.')
            : categoryMatch && primaryCategory
              ? `A focused career path for ${formatCourseCategoryLabel(primaryCategory)} learners.`
              : isPaidPlan
                ? 'Advance faster with guided learning, projects, and career support.'
                : 'Build your foundation and discover your next learning goal.';
          const statusLabel = isCurrent
            ? 'Current plan'
            : availabilityStatus === 'coming_soon'
              ? 'Coming soon'
              : availabilityStatus === 'closed'
                ? 'Enrollment closed'
                : categoryMatch ? 'Recommended' : 'Available';
          return (
            <div key={`membership-collection-${plan._id || plan.name || index}`} className="d-flex flex-column border position-relative overflow-hidden" style={{ minWidth: 0, minHeight: isMobile ? 174 : 190, padding: isMobile ? '12px 10px 10px' : '16px 14px 14px', borderRadius: 10, borderColor: isCurrent ? '#a5b4fc' : '#e2e8f0', background: isCurrent ? 'linear-gradient(155deg, #eef2ff 0%, #ffffff 62%)' : '#fff', boxShadow: isCurrent ? '0 12px 26px rgba(79,70,229,0.12)' : '0 10px 22px rgba(15,23,42,0.05)' }}>
              <div style={{ position: 'absolute', inset: '0 0 auto 0', height: 3, background: accent }} />
              <div className="d-flex justify-content-between align-items-center gap-1 mb-2">
                <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: isMobile ? 32 : 38, height: isMobile ? 32 : 38, borderRadius: 8, color: accent, background: `${accent}14` }}><i className={`bi ${isCurrent ? 'bi-patch-check-fill' : 'bi-stars'}`} /></div>
                <span className="badge" style={{ borderRadius: 6, padding: '4px 6px', fontSize: isMobile ? 8 : 10, color: isCurrent ? '#3730a3' : !isPurchasable ? '#475569' : categoryMatch ? '#047857' : '#6d28d9', background: isCurrent ? '#e0e7ff' : !isPurchasable ? '#f1f5f9' : categoryMatch ? '#ecfdf5' : '#f5f3ff' }}>{statusLabel}</span>
              </div>
              <div className="fw-semibold" style={{ color: '#0f172a', fontSize: isMobile ? 14 : 17, lineHeight: 1.2 }}>{plan.name || plan.title || 'Membership Plan'}</div>
              <div className="mb-2" style={{ color: isCurrent ? '#4f46e5' : '#64748b', fontSize: isMobile ? 9 : 11, lineHeight: 1.3 }}>{tagline}</div>
              <div className="d-flex flex-wrap gap-1 mb-3" style={{ color: '#475569', fontSize: isMobile ? 9 : 11 }}>
                <span style={{ padding: '4px 5px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0' }}><i className="bi bi-calendar3 me-1" />{durationLabel}</span>
                <span style={{ padding: '4px 5px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0' }}><i className="bi bi-journal-check me-1" />{accessLabel}</span>
              </div>
              <button type="button" className={`btn btn-sm w-100 fw-semibold mt-auto ${isCurrent ? 'btn-outline-primary' : 'btn-primary'}`} style={{ borderRadius: 8, padding: isMobile ? '6px 7px' : '8px 10px', fontSize: isMobile ? 10 : 12 }} disabled={!isCurrent && !isPurchasable} onClick={() => (isCurrent ? onCurrent(plan) : onExplore(plan))}>
                {isCurrent ? (activeSubscription ? 'View Membership' : 'Explore Courses') : availabilityStatus === 'coming_soon' ? 'Opens Soon' : availabilityStatus === 'closed' ? 'Enrollment Closed' : 'Explore Plan'} <i className={`bi ${isPurchasable || isCurrent ? 'bi-arrow-right' : 'bi-lock'} ms-1`} />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const StudentSectionTitle = ({ eyebrow, title, subtitle, action }) => (
  <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
    <div>
      {eyebrow ? <div className="text-uppercase small fw-semibold text-muted mb-1" style={{ letterSpacing: '.06em' }}>{eyebrow}</div> : null}
      <div className="fw-semibold" style={{ fontSize: 24, lineHeight: 1.15 }}>{title}</div>
      {subtitle ? <div className="text-muted mt-1">{subtitle}</div> : null}
    </div>
    {action}
  </div>
);

const AdminSurface = ({ children, className = '', style = {} }) => (
  <div
    className={`border ${className}`.trim()}
    style={{
      ...glassPanel,
      borderRadius: 26,
      ...style,
    }}
  >
    {children}
  </div>
);

const AdminSectionTitle = ({ eyebrow, title, subtitle, action, dense = false }) => (
  <div className={`d-flex flex-wrap justify-content-between align-items-end gap-3 ${dense ? 'mb-2' : 'mb-3'}`}>
    <div>
      {eyebrow ? <div className="text-uppercase small fw-semibold text-muted mb-1" style={{ letterSpacing: '.06em' }}>{eyebrow}</div> : null}
      <div className="fw-semibold" style={{ fontSize: dense ? 20 : 24, lineHeight: 1.15 }}>{title}</div>
      {subtitle ? <div className="text-muted mt-1">{subtitle}</div> : null}
    </div>
    {action}
  </div>
);

const AdminMetricTile = ({ label, value, icon, accent = '#2563eb', hint }) => (
  <div className="border h-100" style={{ ...glassTile, borderRadius: 22, padding: 18 }}>
    <div className="d-flex justify-content-between align-items-start gap-3">
      <div>
        <div className="small text-muted mb-1">{label}</div>
        <div className="fw-semibold" style={{ fontSize: 26, lineHeight: 1.1 }}>{value}</div>
        {hint ? <div className="small text-muted mt-2">{hint}</div> : null}
      </div>
      {icon ? (
        <div style={{ width: 44, height: 44, borderRadius: 16, background: 'rgba(255,255,255,0.75)', boxShadow: 'inset 0 0 0 1px rgba(226,232,240,0.9)', color: accent }} className="d-inline-flex align-items-center justify-content-center">
          <i className={`bi ${icon}`} />
        </div>
      ) : null}
    </div>
  </div>
);

const AdminWorkspaceTile = ({ title, description, stats = [], icon, accent = '#2563eb', ctaLabel, onClick }) => (
  <AdminSurface className="h-100" style={{ padding: 20 }}>
    <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
      <div>
        <div className="fw-semibold" style={{ fontSize: 20, lineHeight: 1.2 }}>{title}</div>
        <div className="text-muted mt-2">{description}</div>
      </div>
      <div
        className="d-inline-flex align-items-center justify-content-center"
        style={{ width: 48, height: 48, borderRadius: 16, background: '#f8fafc', color: accent, fontSize: 20 }}
      >
        <i className={`bi ${icon}`} />
      </div>
    </div>
    <div className="d-flex flex-column gap-2 mb-3">
      {stats.map((row) => (
        <div key={row.label} className="d-flex justify-content-between align-items-center gap-3 small">
          <span className="text-muted">{row.label}</span>
          <span className="fw-semibold text-dark">{row.value}</span>
        </div>
      ))}
    </div>
    <button type="button" className="btn btn-outline-primary btn-sm" onClick={onClick}>
      {ctaLabel}
    </button>
  </AdminSurface>
);

const WorkspaceActionButton = ({ label, icon, onClick, tone = 'primary' }) => (
  <button type="button" className={`btn btn-outline-${tone} btn-sm`} onClick={onClick}>
    {icon ? <i className={`bi ${icon} me-1`} /> : null}
    {label}
  </button>
);

const migrationBadge = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'migrated':
      return { label: 'Migrated', tone: 'success' };
    case 'partial':
      return { label: 'Partial', tone: 'warning' };
    default:
      return { label: 'Legacy', tone: 'secondary' };
  }
};

const adminTabButtonStyle = (active) => ({
  borderRadius: 16,
  padding: '10px 16px',
  border: active ? '1px solid rgba(59,130,246,0.35)' : '1px solid rgba(226,232,240,0.72)',
  background: active ? 'linear-gradient(135deg, rgba(29,78,216,0.92) 0%, rgba(14,165,233,0.82) 100%)' : 'rgba(255,255,255,0.52)',
  color: active ? '#ffffff' : '#334155',
  fontWeight: 600,
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  boxShadow: active ? '0 18px 34px rgba(37, 99, 235, 0.20)' : '0 8px 22px rgba(15, 23, 42, 0.04)',
});

const OverlayModal = ({ show, title, onClose, children, footer, dialogClassName = '', contentClassName = '', bodyClassName = '' }) => (
  show ? (
    <div
      className="modal fade show d-block"
      style={{ background: 'rgba(15, 23, 42, 0.45)', zIndex: 1050 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className={`modal-dialog modal-dialog-centered modal-lg ${dialogClassName}`.trim()}>
        <div className={`modal-content border-0 shadow ${contentClassName}`.trim()}>
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className={`modal-body ${bodyClassName}`.trim()}>{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null
);

const buildAssessmentAnswerMap = (submission) => {
  const map = {};
  (submission?.answers || []).forEach((answer) => {
    if (!answer?.questionId) return;
    map[String(answer.questionId)] = answer.answer;
  });
  return map;
};

const StudentAssessmentReview = ({ assessment, submission }) => {
  if (!assessment || !submission) return null;
  const marks = Number(submission.marksObtained || 0);
  const totalMarks = Number(assessment.totalMarks || 0);
  const pct = Number(submission.percentage || 0);
  const passingMarks = Number(assessment.passingMarks || 0);
  const passed = submission.passed;
  const evaluated = !!submission.evaluatedAt || submission.status === 'evaluated' || submission.status === 'passed' || submission.status === 'failed';
  return (
    <div className="d-flex flex-column gap-3">
      <div className={`alert ${passed === true ? 'alert-success' : passed === false ? 'alert-danger' : evaluated ? 'alert-info' : 'alert-warning'} mb-0`}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <div className="fw-bold" style={{ fontSize: 28 }}>{marks} <small className="fs-6 fw-normal text-muted">/ {totalMarks}</small></div>
            <div className="small text-muted">marks obtained</div>
          </div>
          <div className="text-center">
            <div className="fw-bold" style={{ fontSize: 34, lineHeight: 1 }}>{pct}%</div>
            <div className="small text-muted">percentage</div>
          </div>
          <div className="text-end">
            <span className={`badge fs-6 ${evaluated ? (passed === true ? 'text-bg-success' : passed === false ? 'text-bg-danger' : 'text-bg-secondary') : 'text-bg-warning'}`}>
              {evaluated ? (passed === true ? 'Passed' : passed === false ? 'Failed' : 'Reviewed') : 'Pending review'}
            </span>
            <div className="small text-muted mt-1">passing: {passingMarks}/{totalMarks}</div>
          </div>
        </div>
      </div>

      {submission.feedback ? (
        <div className="border rounded-4 p-3 bg-light">
          <div className="small text-muted mb-1">Teacher feedback</div>
          <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{submission.feedback}</div>
        </div>
      ) : null}

      {(assessment.questions || []).length ? (
        <div className="border rounded-4 p-3 bg-white">
          <div className="fw-semibold mb-3">Questions and your answers</div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {(assessment.questions || []).map((question, index) => {
              const answerRow = (submission.answers || []).find((item) => String(item.questionId) === String(question._id));
              const studentAnswer = answerRow?.answer;
              const correctAnswers = Array.isArray(question.options) && question.options.length
                ? question.options.filter((option) => option.isCorrect).map((option) => option.text)
                : (Array.isArray(question.correctAnswers) ? question.correctAnswers : []).filter(Boolean);
              const displayAnswer = Array.isArray(studentAnswer)
                ? studentAnswer.join(', ')
                : studentAnswer != null && studentAnswer !== ''
                  ? String(studentAnswer)
                  : 'Not answered';
              return (
                <div key={`assessment-review-${question._id || index}`} className="border rounded-4 p-3 mb-3" style={{ borderColor: '#e2e8f0', background: '#f8fafc' }}>
                  <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                    <div className="fw-semibold small">Q{index + 1}. {question.question || 'Question'}</div>
                    <span className={`badge ${answerRow?.marksAwarded != null ? 'text-bg-primary' : 'text-bg-light'}`}>
                      {answerRow?.marksAwarded != null ? `${answerRow.marksAwarded}/${question.marks || 0}` : `-/${question.marks || 0}`}
                    </span>
                  </div>
                  <div className="small text-muted mb-1">Your answer</div>
                  <div className="small mb-2">{displayAnswer}</div>
                  {correctAnswers.length ? (
                    <>
                      <div className="small text-muted mb-1">Correct answer</div>
                      <div className="small text-success mb-2">{correctAnswers.join(', ')}</div>
                    </>
                  ) : null}
                  {question.expectedAnswer ? (
                    <>
                      <div className="small text-muted mb-1">Reference answer</div>
                      <div className="small mb-2">{question.expectedAnswer}</div>
                    </>
                  ) : null}
                  {answerRow?.feedback ? (
                    <>
                      <div className="small text-muted mb-1">Feedback</div>
                      <div className="small text-info-emphasis">{answerRow.feedback}</div>
                    </>
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className="small text-muted text-end">
            {submission.submittedAt ? `Submitted: ${fmtDateTime(submission.submittedAt)}` : ''}
            {submission.evaluatedAt ? ` | Evaluated: ${fmtDateTime(submission.evaluatedAt)}` : ''}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const StudentAssessmentDialog = ({ show, mode, assessment, onClose, onSubmitted, notify }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [answers, setAnswers] = useState({});
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!show || !assessment?._id) {
      setDetail(null);
      setAnswers({});
      setPage(0);
      setLoading(false);
      setSubmitting(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        if (mode === 'take') {
          await api.post(`/training/assessments/${assessment._id}/start`);
        }
        const response = await api.get(`/training/assessments/${assessment._id}`);
        if (cancelled) return;
        const payload = response.data?.data || response.data;
        setDetail(payload || null);
        const latestSubmission = Array.isArray(payload?.submissions) ? payload.submissions[0] : null;
        setAnswers(buildAssessmentAnswerMap(latestSubmission));
        setPage(0);
      } catch (err) {
        if (!cancelled) {
          notify(err.response?.data?.message || 'Could not load assessment details.', 'danger');
          onClose?.();
        }
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [assessment?._id, mode, notify, onClose, show]);

  const questions = Array.isArray(detail?.questions) ? detail.questions : [];
  const activeQuestion = questions[page] || null;
  const submission = Array.isArray(detail?.submissions) ? detail.submissions[0] : null;
  const isReviewMode = mode === 'review';

  const setAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (!detail?._id || submitting) return;
    const missingRequired = questions.some((question) => {
      const value = answers[String(question._id)];
      if (Array.isArray(value)) return value.length === 0;
      return value == null || value === '';
    });
    if (missingRequired) {
      notify('Answer all questions before submitting.', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        answers: questions.map((question, index) => ({
          questionId: question._id,
          questionIndex: index,
          answer: answers[String(question._id)],
        })),
      };
      await api.post(`/training/assessments/${detail._id}/submit`, payload);
      const refreshed = await api.get(`/training/assessments/${detail._id}`);
      const refreshedPayload = refreshed.data?.data || refreshed.data;
      setDetail(refreshedPayload || null);
      onSubmitted?.(detail._id, refreshedPayload);
      notify('Assessment submitted successfully.', 'success');
    } catch (err) {
      notify(err.response?.data?.message || 'Could not submit assessment.', 'danger');
    }
    setSubmitting(false);
  };

  const renderQuestionInput = (question) => {
    if (!question) return null;
    const questionId = String(question._id);
    const value = answers[questionId];
    if (question.type === 'mcq_multi') {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div className="d-flex flex-column gap-2">
          {(question.options || []).map((option, index) => (
            <label key={`${questionId}-opt-${index}`} className="border rounded-3 px-3 py-2 d-flex align-items-center gap-2" style={{ borderColor: '#dbeafe' }}>
              <input
                type="checkbox"
                checked={selected.includes(option.text)}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...selected, option.text]
                    : selected.filter((item) => item !== option.text);
                  setAnswer(questionId, next);
                }}
              />
              <span>{option.text}</span>
            </label>
          ))}
        </div>
      );
    }
    if (question.type === 'mcq_single' || question.type === 'mcq' || question.type === 'true_false') {
      return (
        <div className="d-flex flex-column gap-2">
          {(question.options || []).map((option, index) => (
            <label key={`${questionId}-opt-${index}`} className="border rounded-3 px-3 py-2 d-flex align-items-center gap-2" style={{ borderColor: '#dbeafe' }}>
              <input
                type="radio"
                name={`assessment-question-${questionId}`}
                checked={value === option.text}
                onChange={() => setAnswer(questionId, option.text)}
              />
              <span>{option.text}</span>
            </label>
          ))}
        </div>
      );
    }
    return (
      <textarea
        className="form-control"
        rows={6}
        value={value || ''}
        onChange={(e) => setAnswer(questionId, e.target.value)}
        placeholder="Type your answer here"
      />
    );
  };

  return (
    <OverlayModal
      show={show}
      title={isReviewMode ? (assessment?.title ? `${assessment.title} - Result` : 'Assessment Result') : (assessment?.title ? `${assessment.title} - Attempt` : 'Take Assessment')}
      onClose={onClose}
      footer={!isReviewMode && questions.length ? (
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Assessment'}
          </button>
        </>
      ) : undefined}
    >
      {loading ? (
        <div className="text-center py-4 text-muted">Loading assessment...</div>
      ) : !detail ? (
        <div className="text-muted">Assessment details are not available.</div>
      ) : isReviewMode ? (
        <StudentAssessmentReview assessment={detail} submission={submission} />
      ) : (
        <div className="d-flex flex-column gap-3">
          <div className="d-flex flex-wrap gap-2">
            <Pill color="primary">{questions.length} questions</Pill>
            <Pill color="info">{detail.totalMarks || 0} marks</Pill>
            <Pill color="warning">{detail.duration ? `${detail.duration} min` : 'No time limit'}</Pill>
          </div>
          {detail.instructions ? (
            <div className="border rounded-4 p-3 bg-light">
              <div className="small text-muted mb-1">Instructions</div>
              <div className="small" style={{ whiteSpace: 'pre-wrap' }}>{detail.instructions}</div>
            </div>
          ) : null}
          {questions.length ? (
            <div className="row g-3">
              <div className="col-12 col-lg-4">
                <div className="border rounded-4 p-3 h-100 bg-white" style={{ borderColor: '#dbeafe' }}>
                  <div className="fw-semibold mb-3">Question list</div>
                  <div className="d-flex flex-wrap gap-2">
                    {questions.map((question, index) => {
                      const value = answers[String(question._id)];
                      const answered = Array.isArray(value) ? value.length > 0 : value != null && value !== '';
                      return (
                        <button
                          key={`assessment-nav-${question._id}`}
                          type="button"
                          className={`btn btn-sm ${page === index ? 'btn-primary' : answered ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                          onClick={() => setPage(index)}
                        >
                          Q{index + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-8">
                {activeQuestion ? (
                  <div className="border rounded-4 p-3 bg-white" style={{ borderColor: '#dbeafe' }}>
                    <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                      <div>
                        <div className="small text-muted mb-1">Question {page + 1} of {questions.length}</div>
                        <div className="fw-semibold">{activeQuestion.question || 'Question'}</div>
                      </div>
                      <Pill color="info">{activeQuestion.marks || 0} marks</Pill>
                    </div>
                    {renderQuestionInput(activeQuestion)}
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setPage((prev) => Math.max(0, prev - 1))} disabled={page === 0}>
                        Previous
                      </button>
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setPage((prev) => Math.min(questions.length - 1, prev + 1))} disabled={page >= questions.length - 1}>
                        Next
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="text-muted">No questions are configured for this assessment yet.</div>
          )}
        </div>
      )}
    </OverlayModal>
  );
};

export default function OnDemandCoursesPage() {
  const { user, tenant, isSuperAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const roleKey = user?.roleKey || '';
  const canWrite = isSuperAdmin || ['admin', 'manager', 'team_lead'].includes(roleKey);
  const isStudent = roleKey === 'student';
  const isCollegePortalStudent = isStudent && isCollegeStudentExperience(user);
  const isCollegeAuthorized = isCollegePortalStudent;
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1440));
  const [tab, setTab] = useState(canWrite ? 'workspace' : 'dashboard');
  const requestedStudentRouteTab = new URLSearchParams(location.search).get('tab') || '';
  const requestedCollegeOnlyTab = !isCollegePortalStudent
    && COLLEGE_EXPERIENCE_TABS.has(requestedStudentRouteTab);
  const studentRouteTab = requestedCollegeOnlyTab
    ? 'my-courses'
    : (requestedStudentRouteTab || (isCollegePortalStudent ? 'dashboard' : 'my-courses'));
  const activeTab = canWrite ? tab : studentRouteTab;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [migratingCourseId, setMigratingCourseId] = useState('');
  const [migrationStatus, setMigrationStatus] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [courses, setCourses] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [partnerColleges, setPartnerColleges] = useState([]);
  const [pendingCollegeAuthorizations, setPendingCollegeAuthorizations] = useState([]);
  const [selectedCollegeAuthorizationId, setSelectedCollegeAuthorizationId] = useState('');
  const [collegeSummary, setCollegeSummary] = useState({ collegeCount: 0, pendingCount: 0, authorizedCount: 0, rejectedCount: 0, activeSubscriptions: 0 });
  const [collegeReviewNotes, setCollegeReviewNotes] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [learning, setLearning] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedPlanScopeId, setSelectedPlanScopeId] = useState('');
  const [learnState, setLearnState] = useState({ course: null, summary: null, outline: [] });
  const [courseWorkspaceTab, setCourseWorkspaceTab] = useState('content');
  const [studentCourseAssessments, setStudentCourseAssessments] = useState([]);
  const [studentCourseAssessmentsLoading, setStudentCourseAssessmentsLoading] = useState(false);
  const [studentAssessmentDialog, setStudentAssessmentDialog] = useState({ show: false, mode: 'review', assessment: null });
  const [showCourseWorkspaceModal, setShowCourseWorkspaceModal] = useState(false);
  const [enrolledCourseCarouselIndex, setEnrolledCourseCarouselIndex] = useState(0);
  const [forYouSpotlightIndex, setForYouSpotlightIndex] = useState(0);
  const [exploreSpotlightSlot, setExploreSpotlightSlot] = useState(getExploreSpotlightSlot);
  const [expandedExploreSections, setExpandedExploreSections] = useState({ trending: false, live: false });
  const [wishlistCourseIds, setWishlistCourseIds] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem('student_lms_wishlist') || '[]');
      return Array.isArray(raw) ? raw.map(String) : [];
    } catch (_) {
      return [];
    }
  });
  const [selectedLessonId, setSelectedLessonId] = useState('');
  const [videoWatchProgress, setVideoWatchProgress] = useState({});
  const [videoWatchSeconds, setVideoWatchSeconds] = useState({});
  const [videoDurations, setVideoDurations] = useState({});
  const [lessonDetailTab, setLessonDetailTab] = useState('overview');
  const [courseDiscussions, setCourseDiscussions] = useState([]);
  const [courseDiscussionMessage, setCourseDiscussionMessage] = useState('');
  const [courseDiscussionsLoading, setCourseDiscussionsLoading] = useState(false);
  const [courseDiscussionSubmitting, setCourseDiscussionSubmitting] = useState(false);
  const [programPreviewCourse, setProgramPreviewCourse] = useState(null);
  const [selectedOutlineTopicId, setSelectedOutlineTopicId] = useState('');
  const [expandedLearningModuleId, setExpandedLearningModuleId] = useState('');
  const [isLearningOutlineCollapsed, setIsLearningOutlineCollapsed] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [checkoutState, setCheckoutState] = useState({ plan: null, order: null, stage: 'review', couponCode: '', provider: 'test' });
  const [planForm, setPlanForm] = useState(defaultPlan());
  const [editingPlanId, setEditingPlanId] = useState('');
  const [showPlanEditor, setShowPlanEditor] = useState(false);
  const [couponForm, setCouponForm] = useState(defaultCoupon());
  const [editingCouponId, setEditingCouponId] = useState('');
  const [showCouponEditor, setShowCouponEditor] = useState(false);
  const [promotionForm, setPromotionForm] = useState(defaultPromotion());
  const [editingPromotionId, setEditingPromotionId] = useState('');
  const [showPromotionEditor, setShowPromotionEditor] = useState(false);
  const [partnerCollegeForm, setPartnerCollegeForm] = useState(defaultPartnerCollege());
  const [editingPartnerCollegeId, setEditingPartnerCollegeId] = useState('');
  const [showPartnerCollegeEditor, setShowPartnerCollegeEditor] = useState(false);
  const [partnerCollegeUpload, setPartnerCollegeUpload] = useState({ logo: null, qrCode: null });
  const [leadCaptureState, setLeadCaptureState] = useState(defaultLeadCaptureState());
  const [leadCaptureForm, setLeadCaptureForm] = useState(() => defaultLeadForm(user));
  const [careerQuizAnswers, setCareerQuizAnswers] = useState({});
  const [careerQuizStep, setCareerQuizStep] = useState(0);
  const [careerQuizComplete, setCareerQuizComplete] = useState(false);
  const [studentGoal, setStudentGoal] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('student_lms_goal') || '{"enabled":true,"minutes":60}');
    } catch (_) {
      return { enabled: true, minutes: 60 };
    }
  });
  const [planGridColumns] = useState(1);
  const [expandedPlanIds, setExpandedPlanIds] = useState([]);
  const [sectionForm, setSectionForm] = useState(defaultSection());
  const [editingSectionId, setEditingSectionId] = useState('');
  const [lessonForm, setLessonForm] = useState(defaultLesson());
  const [editingLessonId, setEditingLessonId] = useState('');
  const [lessonUploadState, setLessonUploadState] = useState({ thumbnail: false, video: false, image: false });
  const [batchPicker, setBatchPicker] = useState({ show: false, course: null, batches: [] });
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [studentJobs, setStudentJobs] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [expandedLessonPreview, setExpandedLessonPreview] = useState(null);
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const exploreTrendingRailRef = useRef(null);
  const exploreLiveRailRef = useRef(null);
  const exploreTrendingReachRef = useRef(0);
  const [analytics, setAnalytics] = useState({ overview: {}, orderStatus: [], topPlans: [], quotaScopeBreakdown: [], quotaPlanUsage: [], courseEngagement: [], trendDays: [], range: {} });
  const [carouselStrategy, setCarouselStrategy] = useState({ paidSlot: 5, sampleSize: 0, medianReach: 0, minimumSamples: 20, reachByPosition: [] });
  const [paymentProviders, setPaymentProviders] = useState({ defaultProvider: 'test', providers: [] });
  const [catalogFilters, setCatalogFilters] = useState({ search: '', category: '', access: 'all' });
  const [learningFilters, setLearningFilters] = useState({ search: '', status: 'all', sort: 'recent' });
  const [studioLessonFilters, setStudioLessonFilters] = useState({ search: '', release: 'all' });
  const [workspaceFilters, setWorkspaceFilters] = useState({ search: '', migration: 'all' });
  const [analyticsRange, setAnalyticsRange] = useState(() => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - 29 * 86400000).toISOString().slice(0, 10);
    return { dateFrom: start, dateTo: end };
  });
  const [orderFilters, setOrderFilters] = useState(() => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - 29 * 86400000).toISOString().slice(0, 10);
    return { status: '', provider: '', search: '', dateFrom: start, dateTo: end };
  });
  const [orderQueueMode, setOrderQueueMode] = useState('all');
  const [renewalFilters, setRenewalFilters] = useState({ status: '', search: '', days: 30 });
  const [renewalQueueMode, setRenewalQueueMode] = useState('all');
  const [renewalActionState, setRenewalActionState] = useState({
    show: false,
    subscription: null,
    action: 'extend',
    expiresAt: '',
    note: '',
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (authLoading || canWrite || isCollegePortalStudent || !requestedStudentRouteTab) return;
    if (COLLEGE_EXPERIENCE_TABS.has(requestedStudentRouteTab)) {
      navigate('/lms?tab=my-courses', { replace: true });
    }
  }, [authLoading, canWrite, isCollegePortalStudent, navigate, requestedStudentRouteTab]);

  useEffect(() => {
    if (authLoading || (!canWrite && !isCollegePortalStudent)) return undefined;
    let active = true;
    api.get('/training/memberships/carousel-strategy')
      .then((response) => {
        if (!active) return;
        const strategy = response.data?.data || {};
        setCarouselStrategy({
          paidSlot: Math.min(12, Math.max(5, Number(strategy.paidSlot || 5))),
          sampleSize: Number(strategy.sampleSize || 0),
          medianReach: Number(strategy.medianReach || 0),
          minimumSamples: Number(strategy.minimumSamples || 20),
          reachByPosition: Array.isArray(strategy.reachByPosition) ? strategy.reachByPosition : [],
        });
      })
      .catch(() => {});
    return () => { active = false; };
  }, [authLoading, canWrite, isCollegePortalStudent]);

  const isStudentMobile = viewportWidth < 768;
  const isStudentTablet = viewportWidth < 1200;
  const studentPagePadding = isStudentMobile ? 16 : (isStudentTablet ? 20 : 24);
  const studentHeroTitleSize = isStudentMobile ? 28 : (isStudentTablet ? 32 : 38);
  const studentSectionTitleSize = isStudentMobile ? 26 : (isStudentTablet ? 30 : 34);
  const studentStickyStyle = isStudentTablet ? { position: 'static' } : { position: 'sticky', top: 16 };
  const carouselFreeBeforePaid = Math.max(0, Number(carouselStrategy.paidSlot || 5) - 1);

  const notify = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
  }, []);

  const reportCourseCarouselReach = useCallback(({ surface, maxVisiblePosition, totalItems }) => {
    if (!surface || !maxVisiblePosition || !totalItems) return;
    api.post('/training/memberships/carousel-engagement', {
      surface,
      maxVisiblePosition,
      totalItems,
      dateKey: getLocalDateKey(),
    }).catch(() => {});
  }, []);

  const openStudentAssessmentDialog = useCallback((assessment, mode = 'review') => {
    setStudentAssessmentDialog({ show: true, mode, assessment: assessment || null });
  }, []);

  const closeStudentAssessmentDialog = useCallback(() => {
    setStudentAssessmentDialog({ show: false, mode: 'review', assessment: null });
  }, []);

  const syncStudentAssessmentSubmission = useCallback((assessmentId, detail) => {
    const mySubmission = Array.isArray(detail?.submissions) ? detail.submissions[0] || null : null;
    setStudentCourseAssessments((prev) => prev.map((row) => (
      String(row?._id) === String(assessmentId)
        ? {
            ...row,
            mySubmission: mySubmission ? {
              _id: mySubmission._id,
              status: mySubmission.status,
              marksObtained: mySubmission.marksObtained,
              percentage: mySubmission.percentage,
              passed: mySubmission.passed,
              submittedAt: mySubmission.submittedAt,
              evaluatedAt: mySubmission.evaluatedAt,
              feedback: mySubmission.feedback,
              answers: mySubmission.answers || [],
            } : row.mySubmission,
          }
        : row
    )));
  }, []);

  const resetOrderWindow = useCallback(() => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - 29 * 86400000).toISOString().slice(0, 10);
    return { status: '', provider: '', search: '', dateFrom: start, dateTo: end };
  }, []);

  const openOrdersQueue = useCallback((status = '') => {
    if (status === 'pending') {
      setOrderQueueMode('pending');
      setOrderFilters((prev) => ({ ...resetOrderWindow(), ...prev, status: '' }));
    } else if (status === 'failed') {
      setOrderQueueMode('failed');
      setOrderFilters((prev) => ({ ...resetOrderWindow(), ...prev, status: 'failed' }));
    } else {
      setOrderQueueMode('all');
      setOrderFilters((prev) => ({ ...resetOrderWindow(), ...prev, status }));
    }
    setTab('orders');
  }, [resetOrderWindow]);

  const openRenewalsQueue = useCallback((mode = 'all') => {
    if (mode === 'expired') {
      setRenewalQueueMode('expired');
      setRenewalFilters({ status: 'expired', search: '', days: 60 });
    } else if (mode === 'urgent') {
      setRenewalQueueMode('urgent');
      setRenewalFilters({ status: 'active', search: '', days: 7 });
    } else {
      setRenewalQueueMode('all');
      setRenewalFilters({ status: '', search: '', days: 30 });
    }
    setTab('orders');
  }, []);

  const openTab = useCallback((nextTab) => {
    if (canWrite) {
      setTab(nextTab);
      return;
    }
    const hasSubscriptionTab = learning.some((row) => inferCourseMode(row?.course || {}) === 'on_demand')
      || subscriptions.some((row) => ['active', 'trial', 'expired', 'cancelled'].includes(String(row.status || '').toLowerCase()));
    const resolvedTab = !hasSubscriptionTab && nextTab === 'my-subscription'
      ? 'my-courses'
      : nextTab;
    navigate({ pathname: location.pathname, search: `tab=${encodeURIComponent(resolvedTab)}` }, { replace: false });
  }, [canWrite, learning, location.pathname, navigate, subscriptions]);

  const openStudentCtaLink = useCallback((link) => {
    const href = String(link || '').trim();
    if (!href) {
      openTab('for-you');
      return;
    }
    if (/^https?:\/\//i.test(href)) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    if (href.startsWith('/')) {
      navigate(href);
      return;
    }
    openTab('for-you');
  }, [navigate, openTab]);
  const openLeadCapture = useCallback((kind, course, overrides = {}) => {
    const normalizedKind = String(kind || 'course').trim().toLowerCase();
    setLeadCaptureState({
      show: true,
      kind: normalizedKind,
      title: overrides.title || (
        normalizedKind === 'project'
          ? 'Join this project program'
          : normalizedKind === 'internship'
            ? 'Join this internship program'
            : normalizedKind === 'live'
              ? 'Enquire about this live class'
              : 'Enquire about this course'
      ),
      subtitle: overrides.subtitle || (course?.courseName || course?.title || ''),
      course: course || null,
    });
    setLeadCaptureForm((prev) => ({
      ...defaultLeadForm(user),
      name: prev?.name || defaultLeadForm(user).name,
      phone: prev?.phone || defaultLeadForm(user).phone,
      email: prev?.email || defaultLeadForm(user).email,
      collegeName: prev?.collegeName || '',
      area: prev?.area || '',
      address: prev?.address || '',
      notes: '',
      membersCount: 1,
    }));
  }, [user]);

  const closeLeadCapture = useCallback(() => {
    setLeadCaptureState(defaultLeadCaptureState());
    setLeadCaptureForm(defaultLeadForm(user));
  }, [user]);

  const submitLeadCapture = useCallback(async () => {
    const course = leadCaptureState.course;
    const kind = String(leadCaptureState.kind || 'course').trim().toLowerCase();
    if (!course?._id) {
      notify('Select a valid course first.', 'warning');
      return;
    }
    const learnerName = String(leadCaptureForm.name || '').trim();
    const learnerPhone = String(leadCaptureForm.phone || '').replace(/\D/g, '');
    if (learnerName.length < 2) {
      notify('Enter the student name.', 'warning');
      return;
    }
    if (learnerPhone.length < 10 || learnerPhone.length > 15) {
      notify('Enter a valid mobile number with 10 to 15 digits.', 'warning');
      return;
    }
    const email = String(leadCaptureForm.email || '').trim().toLowerCase();
    if (!['internship', 'project'].includes(kind) && !email) {
      notify('Email is required.', 'warning');
      return;
    }
    if (!['internship', 'project'].includes(kind) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      notify('Enter a valid email address.', 'warning');
      return;
    }
    if (!['internship', 'project'].includes(kind) && String(leadCaptureForm.collegeName || '').trim().length < 2) {
      notify('College name is required.', 'warning');
      return;
    }
    if (!['internship', 'project'].includes(kind) && String(leadCaptureForm.area || '').trim().length < 2) {
      notify('Area is required.', 'warning');
      return;
    }
    if (!['internship', 'project'].includes(kind) && String(leadCaptureForm.address || '').trim().length < 5) {
      notify('Enter a complete address.', 'warning');
      return;
    }
    setSaving(true);
    try {
      if (kind === 'internship' || kind === 'project') {
        await api.post('/training/on-demand/interest/program', {
          kind,
          courseId: course._id,
          studentName: leadCaptureForm.name,
          phoneNumber: leadCaptureForm.phone,
          collegeName: leadCaptureForm.collegeName,
          membersCount: leadCaptureForm.membersCount,
          notes: leadCaptureForm.notes,
        });
      } else {
        await api.post('/training/on-demand/interest/course', {
          courseId: course._id,
          name: leadCaptureForm.name,
          phone: leadCaptureForm.phone,
          email,
          collegeName: leadCaptureForm.collegeName,
          area: leadCaptureForm.area,
          address: leadCaptureForm.address,
          notes: leadCaptureForm.notes,
          interestType: kind === 'live' ? 'live_class' : 'lms_course',
        });
      }
      notify(kind === 'internship' || kind === 'project'
        ? 'Programme enquiry submitted. Our team will contact you shortly.'
        : `Your enquiry for ${course.courseName || 'this program'} was submitted successfully. Our team will contact you shortly.`);
      closeLeadCapture();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not submit your enquiry.', 'danger');
    }
    setSaving(false);
  }, [closeLeadCapture, leadCaptureForm, leadCaptureState, notify]);

  const isLeadProgram = ['internship', 'project'].includes(String(leadCaptureState.kind || ''));
  const isLeadLive = String(leadCaptureState.kind || '') === 'live';
  const leadCaptureSubmitLabel = saving
    ? 'Submitting...'
    : isLeadProgram
      ? `Submit ${String(leadCaptureState.kind || '') === 'project' ? 'Project' : 'Internship'} Request`
      : isLeadLive
        ? 'Submit Live Class Enquiry'
        : 'Submit Course Enquiry';

  useEffect(() => {
    if (canWrite) return;
    localStorage.setItem('student_lms_goal', JSON.stringify(studentGoal));
  }, [canWrite, studentGoal]);
  useEffect(() => {
    if (canWrite || !showCourseWorkspaceModal) return;
    setIsLearningOutlineCollapsed(isStudentMobile);
  }, [canWrite, isStudentMobile, showCourseWorkspaceModal]);

  useEffect(() => {
    const activePromoCount = (promotions || []).filter((item) => ['carousel_slide', 'promotion'].includes(String(item.type || ''))).length;
    if (canWrite || activePromoCount <= 1) return undefined;
    const id = window.setInterval(() => {
      setHeroSlideIndex((prev) => (prev + 1) % activePromoCount);
    }, 5000);
    return () => window.clearInterval(id);
  }, [canWrite, promotions]);

  useEffect(() => {
    const adminTabs = new Set(['workspace', 'plans', 'orders', 'coupons', 'analytics', 'college-access', 'college-review', 'learning']);
    const studentTabs = new Set([
      'dashboard',
      'recommended',
      'for-you',
      'my-courses',
      'progress',
      'my-subscription',
      'subscription-details',
      'saved-courses',
      'billing',
      'downloads',
      'share-programs',
      'help-center',
      'advisor-contact',
      'career-quiz',
      'checkout',
    ]);
    // Subscription eligibility depends on learning, plan, order, and subscription data.
    // Do not redirect while those collections are still being loaded.
    if (!canWrite && loading) return;
    const hasTrackedSubscription = subscriptions.some((row) => ['active', 'trial', 'expired', 'cancelled'].includes(String(row.status || '').toLowerCase()));
    const hasOnDemandLearning = learning.some((row) => inferCourseMode(row?.course || {}) === 'on_demand');
    const hasOfflineOrLiveLearning = learning.some((row) => inferCourseMode(row?.course || {}) !== 'on_demand');
    const hasCommerceHistory = hasTrackedSubscription || orders.length > 0;
    const isClearlyOfflineOnly = hasOfflineOrLiveLearning && !hasOnDemandLearning && !hasCommerceHistory;
    const hasSubscriptionTab = !isClearlyOfflineOnly
      && (hasOnDemandLearning || hasCommerceHistory || plans.some((plan) => plan.isActive !== false));
    if (canWrite) {
      if (!adminTabs.has(tab) || tab === 'studio' || tab === 'catalog') {
        setTab('workspace');
      }
      return;
    }
    if (!studentTabs.has(studentRouteTab) || studentRouteTab === 'workspace' || studentRouteTab === 'studio') {
      navigate({ pathname: location.pathname, search: 'tab=dashboard' }, { replace: true });
      return;
    }
    if (!hasSubscriptionTab && ['my-subscription', 'checkout'].includes(studentRouteTab)) {
      navigate({ pathname: location.pathname, search: 'tab=my-courses' }, { replace: true });
    }
  }, [canWrite, learning, loading, location.pathname, navigate, orders.length, plans, studentRouteTab, subscriptions, tab]);

  const studioCourses = useMemo(
    () => courses.filter((row) => ['on_demand', 'hybrid'].includes(String(row.deliveryMode || 'live'))),
    [courses]
  );
  const membershipCourses = useMemo(
    () => courses.filter((row) => String(row.status || 'active') !== 'inactive'),
    [courses]
  );
  const courseCategories = useMemo(
    () => Array.from(new Set(membershipCourses.map((row) => String(row.category || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [membershipCourses]
  );
  const catalogCategories = useMemo(
    () => Array.from(new Set(catalog.map((row) => String(row.category || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [catalog]
  );

  const activeLesson = useMemo(() => {
    for (const section of learnState.outline || []) {
      const lesson = (section.lessons || []).find((item) => String(item._id) === String(selectedLessonId));
      if (lesson) return lesson;
    }
    return null;
  }, [learnState.outline, selectedLessonId]);
  const handleVideoWatchProgress = useCallback((lessonId, percent, watchedSeconds = 0, durationSeconds = 0) => {
    if (!lessonId) return;
    const key = String(lessonId);
    setVideoWatchProgress((previous) => {
      const nextPercent = Math.max(Number(previous[key] || 0), Number(percent || 0));
      if (nextPercent === Number(previous[key] || 0)) return previous;
      return { ...previous, [key]: nextPercent };
    });
    setVideoWatchSeconds((previous) => {
      const nextSeconds = Math.max(Number(previous[key] || 0), Number(watchedSeconds || 0));
      if (nextSeconds === Number(previous[key] || 0)) return previous;
      return { ...previous, [key]: nextSeconds };
    });
    if (Number(durationSeconds) > 0) {
      setVideoDurations((previous) => (Number(previous[key] || 0) === Number(durationSeconds)
        ? previous
        : { ...previous, [key]: Number(durationSeconds) }));
    }
  }, []);
  const getLessonWatchPercent = useCallback((lesson) => Math.max(
    Number(lesson?.progress?.watchedPercent || 0),
    Number(videoWatchProgress[String(lesson?._id || '')] || 0)
  ), [videoWatchProgress]);
  const getLessonWatchedSeconds = useCallback((lesson) => Math.max(
    Number(lesson?.progress?.watchedSeconds || 0),
    Number(videoWatchSeconds[String(lesson?._id || '')] || 0)
  ), [videoWatchSeconds]);
  const getLessonRequiredWatchSeconds = useCallback((lesson) => {
    const duration = Math.max(
      Number(lesson?.durationSeconds || 0),
      Number(videoDurations[String(lesson?._id || '')] || 0)
    );
    return duration > 0 ? Math.min(VIDEO_MINIMUM_WATCH_SECONDS, duration) : 0;
  }, [videoDurations]);
  const isLessonWatchRequirementMet = useCallback((lesson) => {
    if (!isTrackedVideoLesson(lesson)) return true;
    return getLessonWatchPercent(lesson) >= VIDEO_COMPLETION_PERCENT
      && getLessonWatchedSeconds(lesson) >= getLessonRequiredWatchSeconds(lesson);
  }, [getLessonRequiredWatchSeconds, getLessonWatchPercent, getLessonWatchedSeconds]);
  const flattenedLearnLessons = useMemo(
    () => (learnState.outline || []).flatMap((section) => (section.lessons || []).map((lesson) => ({
      ...lesson,
      sectionTitle: section.title,
    }))),
    [learnState.outline]
  );
  const activeLessonIndex = useMemo(
    () => flattenedLearnLessons.findIndex((lesson) => String(lesson._id) === String(selectedLessonId)),
    [flattenedLearnLessons, selectedLessonId]
  );
  const previousLesson = activeLessonIndex > 0 ? flattenedLearnLessons[activeLessonIndex - 1] : null;
  const nextLesson = activeLessonIndex >= 0 ? flattenedLearnLessons[activeLessonIndex + 1] || null : null;
  const nextAvailableLesson = useMemo(
    () => flattenedLearnLessons.find((lesson) => !lesson.access?.isLocked && lesson.progress?.status !== 'completed') || null,
    [flattenedLearnLessons]
  );
  const workspacePrimaryLesson = useMemo(
    () => (activeLesson?.progress?.status !== 'completed' ? activeLesson : nextAvailableLesson) || activeLesson || null,
    [activeLesson, nextAvailableLesson]
  );
  const nextUnlockedLessonById = useMemo(() => {
    const map = new Map();
    flattenedLearnLessons.forEach((lesson, index) => {
      const immediateNextLesson = flattenedLearnLessons[index + 1] || null;
      map.set(
        String(lesson._id),
        immediateNextLesson && !immediateNextLesson.access?.isLocked ? immediateNextLesson : null
      );
    });
    return map;
  }, [flattenedLearnLessons]);
  const selectedLearningCourse = useMemo(() => {
    const learningRow = learning.find((row) => String(row.course?._id || row.course) === String(selectedCourseId));
    if (learningRow) {
      const fallbackCourse = catalog.find((row) => String(row._id) === String(selectedCourseId));
      const loadedCourseId = String(learnState.course?._id || learnState.course || '');
      const liveSummary = loadedCourseId === String(selectedCourseId) ? (learnState.summary || {}) : {};
      return {
        ...learningRow,
        course: normalizeCourseMedia(learningRow.course || {}, fallbackCourse),
        learningSummary: {
          ...(learningRow.learningSummary || {}),
          ...liveSummary,
        },
      };
    }
    const fallbackCourse = catalog.find((row) => String(row._id) === String(selectedCourseId));
    if (!fallbackCourse) return null;
    const hasAccess = !!fallbackCourse.enrollment || !!fallbackCourse.access?.allowed;
    if (!hasAccess) return null;
    return {
      course: fallbackCourse,
      status: fallbackCourse.enrollment?.status || '',
      learningSummary: {
        completionPercent: Number(learnState.summary?.completionPercent || 0),
        completedLessons: Number(learnState.summary?.completedLessons || 0),
        pendingLessons: Number(learnState.summary?.pendingLessons || 0),
        inProgressLessons: Number(learnState.summary?.inProgressLessons || 0),
        totalLessons: Number(learnState.summary?.totalLessons || 0),
        totalDurationSeconds: Number(learnState.summary?.totalDurationSeconds || 0),
        watchedSeconds: Number(learnState.summary?.watchedSeconds || 0),
        remainingDurationSeconds: Number(learnState.summary?.remainingDurationSeconds || 0),
        averageScore: Number(learnState.summary?.averageScore || 0),
        lastAccessedAt: learnState.summary?.lastAccessedAt || null,
        completedAt: learnState.summary?.completedAt || null,
        nextLessonId: learnState.summary?.nextLessonId || '',
        consistency: learnState.summary?.consistency || {},
      },
    };
  }, [catalog, learnState.course, learnState.summary, learning, selectedCourseId]);
  const selectedLearningMode = useMemo(
    () => inferCourseMode(selectedLearningCourse?.course || {}),
    [selectedLearningCourse]
  );
  const showSelectedCourseAssessments = !canWrite && !!selectedLearningCourse && selectedLearningMode !== 'on_demand';
  const studentModuleGroups = useMemo(() => {
    const groups = new Map();
    (learnState.outline || []).forEach((section, index) => {
      const key = String(section.moduleId || section._id || index);
      const title = String(section.moduleTitle || section.title || `Module ${index + 1}`).trim();
      if (!groups.has(key)) groups.set(key, { key, title, sections: [] });
      groups.get(key).sections.push(section);
    });
    return Array.from(groups.values());
  }, [learnState.outline]);
  const workspaceModuleOverview = useMemo(
    () => studentModuleGroups.map((group) => {
      const lessons = group.sections.flatMap((section) => section.lessons || []);
      const totalLessons = lessons.length;
      const completedLessons = lessons.filter((lesson) => lesson.progress?.status === 'completed').length;
      return {
        ...group,
        totalLessons,
        completedLessons,
        completionPercent: totalLessons ? Math.round((completedLessons / totalLessons) * 100) : 0,
      };
    }),
    [studentModuleGroups]
  );
  const workspaceModuleOverviewMap = useMemo(
    () => new Map(workspaceModuleOverview.map((group) => [String(group.key), group])),
    [workspaceModuleOverview]
  );
  const currentWorkspaceModule = useMemo(
    () => workspaceModuleOverview.find((group) => (
      (group.sections || []).some((section) => (
        (section.lessons || []).some((lesson) => String(lesson._id) === String(selectedLessonId))
      ))
    )) || null,
    [selectedLessonId, workspaceModuleOverview]
  );
  const selectedOutlineSection = useMemo(
    () => (learnState.outline || []).find((section) => String(section._id) === String(selectedOutlineTopicId)) || null,
    [learnState.outline, selectedOutlineTopicId]
  );
  const selectedCollegeAuthorization = useMemo(
    () => pendingCollegeAuthorizations.find((row) => String(row._id) === String(selectedCollegeAuthorizationId)) || pendingCollegeAuthorizations[0] || null,
    [pendingCollegeAuthorizations, selectedCollegeAuthorizationId]
  );
  useEffect(() => {
    if (!selectedOutlineSection || activeLesson) return;
    const fallbackLesson = (selectedOutlineSection.lessons || []).find((lesson) => !lesson.access?.isLocked)
      || (selectedOutlineSection.lessons || [])[0]
      || null;
    if (!fallbackLesson?._id) return;
    setSelectedLessonId(String(fallbackLesson._id));
  }, [activeLesson, selectedOutlineSection]);
  const workspaceAssessmentOverview = useMemo(() => {
    const submitted = studentCourseAssessments.filter((item) => item.mySubmission?.submittedAt);
    const pending = studentCourseAssessments.filter((item) => !item.mySubmission?.submittedAt);
    const firstPending = pending.find((item) => String(item.status || '').toLowerCase() === 'active') || pending[0] || null;
    const latestSubmitted = [...submitted]
      .sort((a, b) => {
        const aTime = a.mySubmission?.submittedAt ? new Date(a.mySubmission.submittedAt).getTime() : 0;
        const bTime = b.mySubmission?.submittedAt ? new Date(b.mySubmission.submittedAt).getTime() : 0;
        return bTime - aTime;
      })[0] || null;
    return {
      total: studentCourseAssessments.length,
      submittedCount: submitted.length,
      pendingCount: pending.length,
      firstPending,
      latestSubmitted,
    };
  }, [studentCourseAssessments]);
  const activeQuizQuestions = useMemo(
    () => Array.isArray(activeLesson?.quizConfig?.questions) ? activeLesson.quizConfig.questions : [],
    [activeLesson]
  );
  const studentLearningOverview = useMemo(() => {
    const now = new Date();
    const totalCourses = learning.length;
    const completedCourses = learning.filter((row) => isCompletedLearningRow(row)).length;
    const activeCourses = learning.filter((row) => (
      Number(row.learningSummary?.inProgressLessons || 0) > 0
      || (Number(row.learningSummary?.completionPercent || 0) > 0 && Number(row.learningSummary?.completionPercent || 0) < 100)
      || !!row.learningSummary?.lastAccessedAt
    )).length;
    const totalPendingLessons = learning.reduce((sum, row) => sum + Number(row.learningSummary?.pendingLessons || 0), 0);
    const avgCompletion = totalCourses
      ? Math.round(learning.reduce((sum, row) => sum + Number(row.learningSummary?.completionPercent || 0), 0) / totalCourses)
      : 0;
    const avgScoreRows = learning
      .map((row) => Number(row.learningSummary?.averageScore))
      .filter((value) => Number.isFinite(value));
    const averageScore = avgScoreRows.length
      ? Math.round((avgScoreRows.reduce((sum, value) => sum + value, 0) / avgScoreRows.length) * 100) / 100
      : null;
    const continueCourse = [...learning]
      .filter((row) => (
        Number(row.learningSummary?.completionPercent || 0) < 100
        && (
          Number(row.learningSummary?.completionPercent || 0) > 0
          || Number(row.learningSummary?.inProgressLessons || 0) > 0
          || Number(row.learningSummary?.completedLessons || 0) > 0
          || !!row.learningSummary?.lastAccessedAt
        )
      ))
      .sort((a, b) => {
        const aTime = a.learningSummary?.lastAccessedAt ? new Date(a.learningSummary.lastAccessedAt).getTime() : 0;
        const bTime = b.learningSummary?.lastAccessedAt ? new Date(b.learningSummary.lastAccessedAt).getTime() : 0;
        return bTime - aTime;
      })[0] || null;
    const watchedSecondsToday = learning.reduce((sum, row) => {
      const lastAccessedAt = row.learningSummary?.lastAccessedAt ? new Date(row.learningSummary.lastAccessedAt) : null;
      if (!lastAccessedAt || Number.isNaN(lastAccessedAt.getTime())) return sum;
      const sameDay = lastAccessedAt.getDate() === now.getDate()
        && lastAccessedAt.getMonth() === now.getMonth()
        && lastAccessedAt.getFullYear() === now.getFullYear();
      return sameDay ? sum + Number(row.learningSummary?.watchedSeconds || 0) : sum;
    }, 0);
    const todayActivityCount = learning.filter((row) => {
      const dateValue = row.learningSummary?.lastAccessedAt || row.learningSummary?.completedAt || null;
      if (!dateValue) return false;
      const dt = new Date(dateValue);
      if (Number.isNaN(dt.getTime())) return false;
      return dt.getDate() === now.getDate()
        && dt.getMonth() === now.getMonth()
        && dt.getFullYear() === now.getFullYear();
    }).length;
    return {
      totalCourses,
      completedCourses,
      activeCourses,
      totalPendingLessons,
      avgCompletion,
      averageScore,
      continueCourse,
      watchedSecondsToday,
      todayActivityCount,
    };
  }, [learning]);
  const studentPerformanceInsights = useMemo(() => {
    const now = new Date();
    const dayMs = 86400000;
    const recentCourses = learning.filter((row) => {
      const last = row.learningSummary?.lastAccessedAt ? new Date(row.learningSummary.lastAccessedAt) : null;
      return last && !Number.isNaN(last.getTime()) && ((now.getTime() - last.getTime()) / dayMs) <= 7;
    });
    const staleCourses = learning.filter((row) => {
      const last = row.learningSummary?.lastAccessedAt ? new Date(row.learningSummary.lastAccessedAt) : null;
      return !last || Number.isNaN(last.getTime()) || ((now.getTime() - last.getTime()) / dayMs) > 14;
    });
    const completedThisMonth = learning.filter((row) => {
      const dt = row.learningSummary?.completedAt ? new Date(row.learningSummary.completedAt) : null;
      return dt && !Number.isNaN(dt.getTime()) && dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
    }).length;
    const scoreBands = {
      strong: learning.filter((row) => Number(row.learningSummary?.averageScore || 0) >= 80).length,
      stable: learning.filter((row) => Number(row.learningSummary?.averageScore || 0) >= 60 && Number(row.learningSummary?.averageScore || 0) < 80).length,
      needsWork: learning.filter((row) => Number(row.learningSummary?.averageScore || 0) > 0 && Number(row.learningSummary?.averageScore || 0) < 60).length,
    };
    const recentActivityRows = [...learning]
      .filter((row) => row.learningSummary?.lastAccessedAt)
      .sort((a, b) => new Date(b.learningSummary.lastAccessedAt).getTime() - new Date(a.learningSummary.lastAccessedAt).getTime())
      .slice(0, 5)
      .map((row) => ({
        label: row.course?.courseName || 'Course',
        value: row.learningSummary?.completionPercent || 0,
        date: row.learningSummary?.lastAccessedAt,
      }));
    return {
      recentCourses: recentCourses.length,
      staleCourses: staleCourses.length,
      completedThisMonth,
      scoreBands,
      recentActivityRows,
      momentumLabel: recentCourses.length >= Math.max(1, Math.ceil(learning.length / 2))
        ? 'High'
        : recentCourses.length > 0 ? 'Moderate' : 'Low',
    };
  }, [learning]);
  const studentConsistencyOverview = useMemo(() => {
    const rows = learning.map((row) => row.learningSummary?.consistency || {});
    const totalCourses = learning.length || 0;
    const totalWatchSeconds = rows.reduce((sum, item) => sum + Number(item.watchedSecondsTotal || 0), 0);
    const watchedSecondsLast7 = rows.reduce((sum, item) => sum + Number(item.watchedSecondsLast7 || 0), 0);
    const activeThisWeek = rows.filter((item) => Number(item.activeDaysLast7 || 0) > 0).length;
    const currentStreak = rows.reduce((max, item) => Math.max(max, Number(item.currentStreak || 0)), 0);
    const activityDates = Array.from(new Set(rows.flatMap((item) => (
      Array.isArray(item.activityDates) ? item.activityDates.map(String) : []
    ))));
    const atRisk = rows.filter((item) => ['at_risk', 'inactive'].includes(String(item.consistencyStatus || ''))).length;
    const strong = rows.filter((item) => String(item.consistencyStatus || '') === 'strong').length;
    const building = rows.filter((item) => ['building', 'started'].includes(String(item.consistencyStatus || ''))).length;
    return {
      totalCourses,
      totalWatchSeconds,
      watchedSecondsLast7,
      activeThisWeek,
      currentStreak,
      bestStreak: currentStreak,
      activityDates,
      atRisk,
      strong,
      building,
      activeRate: totalCourses ? Math.round((activeThisWeek / totalCourses) * 100) : 0,
      riskRate: totalCourses ? Math.round((atRisk / totalCourses) * 100) : 0,
    };
  }, [learning]);
  const studentWeeklyActivity = useMemo(() => {
    const now = new Date();
    const dayIndex = (now.getDay() + 6) % 7;
    const monday = new Date(now);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(now.getDate() - dayIndex);
    const activitySet = new Set(studentConsistencyOverview.activityDates || []);
    const days = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      return {
        key,
        label: ['M', 'T', 'W', 'T', 'F', 'S', 'S'][index],
        active: activitySet.has(key),
        future: date.getTime() > now.getTime(),
      };
    });
    const learnedMinutes = Math.max(0, Math.round(Number(studentConsistencyOverview.watchedSecondsLast7 || 0) / 60));
    const targetMinutes = studentGoal.enabled ? Math.max(1, Number(studentGoal.minutes || 0)) * 7 : 0;
    return {
      days,
      activeDays: days.filter((day) => day.active).length,
      learnedMinutes,
      targetMinutes,
      remainingMinutes: Math.max(0, targetMinutes - learnedMinutes),
      progress: targetMinutes ? Math.min(100, Math.round((learnedMinutes / targetMinutes) * 100)) : 0,
    };
  }, [studentConsistencyOverview.activityDates, studentConsistencyOverview.watchedSecondsLast7, studentGoal.enabled, studentGoal.minutes]);
  const studentLearnCollections = useMemo(() => {
    const rows = [...learning];
    const byRecentAccess = (a, b) => {
      const aTime = a.learningSummary?.lastAccessedAt ? new Date(a.learningSummary.lastAccessedAt).getTime() : 0;
      const bTime = b.learningSummary?.lastAccessedAt ? new Date(b.learningSummary.lastAccessedAt).getTime() : 0;
      return bTime - aTime;
    };
    const completed = rows.filter((row) => isCompletedLearningRow(row));
    const inProgress = rows
      .filter((row) => {
        const completion = Number(row.learningSummary?.completionPercent || 0);
        return completion > 0 && completion < 100;
      })
      .sort(byRecentAccess);
    const recentlyAccessed = rows
      .filter((row) => row.learningSummary?.lastAccessedAt)
      .sort(byRecentAccess)
      .slice(0, 4);
    const pending = rows
      .filter((row) => !isCompletedLearningRow(row) && Number(row.learningSummary?.pendingLessons || 0) > 0)
      .sort((a, b) => Number(b.learningSummary?.completionPercent || 0) - Number(a.learningSummary?.completionPercent || 0))
      .slice(0, 4);
    const continueCourse = studentLearningOverview.continueCourse
      || rows.find((row) => !isCompletedLearningRow(row))
      || null;
    return {
      completed,
      inProgress,
      recentlyAccessed,
      pending,
      continueCourse,
      watchedSeconds: rows.reduce((sum, row) => sum + Number(row.learningSummary?.watchedSeconds || 0), 0),
      completedLessons: rows.reduce((sum, row) => sum + Number(row.learningSummary?.completedLessons || 0), 0),
    };
  }, [learning, studentLearningOverview.continueCourse]);
  const studentLearningModeProfile = useMemo(() => {
    const counts = learning.reduce((acc, row) => {
      const mode = inferCourseMode(row?.course || {});
      acc[mode] = (acc[mode] || 0) + 1;
      return acc;
    }, { on_demand: 0, live: 0 });
    const predominantMode = counts.on_demand >= counts.live ? 'on_demand' : 'live';
    return {
      counts,
      predominantMode,
      hasOnDemand: counts.on_demand > 0,
      hasOfflineOrLive: counts.live > 0,
    };
  }, [learning]);
  const studentIsOnDemandPrimary = studentLearningModeProfile.predominantMode === 'on_demand';
  const studentIsOfflinePrimary = !studentIsOnDemandPrimary;
  const migrationStatusByCourse = useMemo(() => migrationStatus || {}, [migrationStatus]);
  const filteredWorkspaceCourses = useMemo(() => {
    const search = String(workspaceFilters.search || '').trim().toLowerCase();
    const migration = String(workspaceFilters.migration || 'all').trim().toLowerCase();

    return [...studioCourses]
      .filter((course) => {
        const migrationRow = migrationStatusByCourse[String(course._id)] || null;
        const status = String(migrationRow?.status || 'not_started').trim().toLowerCase();
        const matchesSearch = !search || [
          course.courseName || '',
          course.courseCode || '',
          course.category || '',
          course.shortDescription || '',
          course.description || '',
        ].join(' ').toLowerCase().includes(search);
        const matchesMigration = migration === 'all' || status === migration;
        return matchesSearch && matchesMigration;
      })
      .sort((a, b) => String(a.courseName || '').localeCompare(String(b.courseName || '')));
  }, [studioCourses, workspaceFilters, migrationStatusByCourse]);
  const workspaceMigrationSummary = useMemo(() => ({
    total: studioCourses.length,
    filtered: filteredWorkspaceCourses.length,
    legacy: studioCourses.filter((course) => String(migrationStatusByCourse[String(course._id)]?.status || 'not_started') === 'not_started').length,
    partial: studioCourses.filter((course) => String(migrationStatusByCourse[String(course._id)]?.status || '') === 'partial').length,
    migrated: studioCourses.filter((course) => String(migrationStatusByCourse[String(course._id)]?.status || '') === 'migrated').length,
  }), [studioCourses, filteredWorkspaceCourses, migrationStatusByCourse]);
  const workspaceActionQueue = useMemo(() => {
    const pendingOrders = orders.filter((row) => ['created', 'payment_pending'].includes(String(row.status || '')));
    const failedOrders = orders.filter((row) => String(row.status || '') === 'failed');
    const urgentRenewals = renewals
      .filter((row) => row.lifecycle?.isExpired || Number(row.lifecycle?.daysRemaining ?? 9999) <= 7)
      .sort((a, b) => Number(a.lifecycle?.daysRemaining ?? 9999) - Number(b.lifecycle?.daysRemaining ?? 9999));
    const migrationBacklog = studioCourses
      .filter((course) => {
        const status = String(migrationStatusByCourse[String(course._id)]?.status || 'not_started');
        return status === 'not_started' || status === 'partial';
      })
      .sort((a, b) => String(a.courseName || '').localeCompare(String(b.courseName || '')));
    return {
      pendingOrders,
      failedOrders,
      urgentRenewals,
      migrationBacklog,
    };
  }, [orders, renewals, studioCourses, migrationStatusByCourse]);
  const filteredAdminOrders = useMemo(() => {
    if (!canWrite) return orders;
    if (orderQueueMode === 'pending') {
      return orders.filter((row) => ['created', 'payment_pending'].includes(String(row.status || '')));
    }
    if (orderQueueMode === 'failed') {
      return orders.filter((row) => String(row.status || '') === 'failed');
    }
    return orders;
  }, [canWrite, orders, orderQueueMode]);
  const filteredRenewals = useMemo(() => {
    if (!canWrite) return renewals;
    if (renewalQueueMode === 'expired') {
      return renewals.filter((row) => row.lifecycle?.isExpired || String(row.status || '') === 'expired');
    }
    if (renewalQueueMode === 'urgent') {
      return renewals.filter((row) => !row.lifecycle?.isExpired && Number(row.lifecycle?.daysRemaining ?? 9999) <= 7);
    }
    return renewals;
  }, [canWrite, renewals, renewalQueueMode]);
  const studentSubscriptionState = useMemo(() => {
    const expired = subscriptions.filter((row) => row.lifecycle?.isExpired);
    const expiringSoon = subscriptions
      .filter((row) => !row.lifecycle?.isExpired && row.lifecycle?.isExpiringSoon)
      .sort((a, b) => Number(a.lifecycle?.daysRemaining ?? 9999) - Number(b.lifecycle?.daysRemaining ?? 9999));
    const renewable = subscriptions.filter((row) => row.lifecycle?.canRenew);
    return {
      expired,
      expiringSoon,
      renewable,
      primaryAlert: expired[0] || expiringSoon[0] || null,
    };
  }, [subscriptions]);
  const studentRenewalSummary = useMemo(() => {
    const renewable = [...(studentSubscriptionState.renewable || [])].sort((a, b) => {
      const aDays = Number(a.lifecycle?.daysRemaining ?? 9999);
      const bDays = Number(b.lifecycle?.daysRemaining ?? 9999);
      return aDays - bDays;
    });
    const nextUp = renewable[0] || null;
    return {
      renewable,
      expiredCount: studentSubscriptionState.expired.length,
      dueSoonCount: studentSubscriptionState.expiringSoon.length,
      nextUp,
    };
  }, [studentSubscriptionState]);
  const renewalQueueSummary = useMemo(() => ({
    expired: renewals.filter((row) => row.lifecycle?.isExpired).length,
    todayTo3: renewals.filter((row) => !row.lifecycle?.isExpired && Number(row.lifecycle?.daysRemaining ?? 9999) <= 3).length,
    week: renewals.filter((row) => !row.lifecycle?.isExpired && Number(row.lifecycle?.daysRemaining ?? 9999) > 3 && Number(row.lifecycle?.daysRemaining ?? 9999) <= 7).length,
  }), [renewals]);
  const renewalByOrderId = useMemo(() => {
    const map = new Map();
    (renewals || []).forEach((row) => {
      const orderId = String(row.orderId || '');
      if (orderId) map.set(orderId, row);
    });
    return map;
  }, [renewals]);
  const studentAccessOverview = useMemo(() => {
    const accessibleCourses = catalog.filter((course) => course.enrollment || course.access?.allowed);
    const lockedCourses = catalog.filter((course) => !course.enrollment && !course.access?.allowed);
    const expiringPlans = subscriptions.filter((row) => row.lifecycle?.isExpiringSoon);
    const quotaCourses = catalog.filter((course) => course.access?.remainingQuota != null);
    const quotaBuckets = {
      low: quotaCourses.filter((course) => Number(course.access?.remainingQuota || 0) > 0 && Number(course.access?.remainingQuota || 0) <= 2),
      available: quotaCourses.filter((course) => Number(course.access?.remainingQuota || 0) > 2),
      exhausted: quotaCourses.filter((course) => Number(course.access?.remainingQuota || 0) <= 0),
    };
    return {
      accessibleCourses,
      lockedCourses,
      expiringPlans,
      quotaCourses,
      quotaBuckets,
    };
  }, [catalog, subscriptions]);
  const filteredCatalog = useMemo(() => {
    const search = String(catalogFilters.search || '').trim().toLowerCase();
    const category = String(catalogFilters.category || '').trim().toLowerCase();
    const access = String(catalogFilters.access || 'all').trim().toLowerCase();

    const rows = catalog.filter((course) => {
      const matchesSearch = !search || [
        course.courseName || '',
        course.courseCode || '',
        course.category || '',
        course.shortDescription || '',
        course.description || '',
      ].join(' ').toLowerCase().includes(search);
      const matchesCategory = !category || String(course.category || '').trim().toLowerCase() === category;
      const isAccessible = !!(course.enrollment || course.access?.allowed);
      const isLocked = !course.enrollment && !course.access?.allowed;
      const matchesAccess = access === 'all'
        || (access === 'accessible' && isAccessible)
        || (access === 'locked' && isLocked)
        || (access === 'enrolled' && !!course.enrollment)
        || (access === 'free' && isFreeLearningCourse(course))
        || (access === 'renew' && course.access?.mode === 'expired_membership');
      return matchesSearch && matchesCategory && matchesAccess;
    });

    return rows.sort((a, b) => {
      const aEnrolled = a.enrollment ? 1 : 0;
      const bEnrolled = b.enrollment ? 1 : 0;
      if (aEnrolled !== bEnrolled) return bEnrolled - aEnrolled;
      const aAllowed = a.access?.allowed ? 1 : 0;
      const bAllowed = b.access?.allowed ? 1 : 0;
      if (aAllowed !== bAllowed) return bAllowed - aAllowed;
      return String(a.courseName || '').localeCompare(String(b.courseName || ''));
    });
  }, [catalog, catalogFilters]);
  const careerQuizRecommendations = useMemo(() => {
    const selectedOptions = CAREER_QUIZ_QUESTIONS.flatMap((question) => {
      const selectedValue = careerQuizAnswers[question.id];
      return question.options.filter((option) => option.value === selectedValue);
    });
    if (!selectedOptions.length) return [];
    const selectedTerms = selectedOptions.flatMap((option) => option.terms || []);
    const selectedFormat = careerQuizAnswers.format;
    return catalog
      .map((course) => {
        const searchable = [course.courseName, course.courseCode, course.category, course.shortDescription, course.description].filter(Boolean).join(' ').toLowerCase();
        const termScore = selectedTerms.reduce((score, term) => score + (searchable.includes(String(term).toLowerCase()) ? 2 : 0), 0);
        const mode = inferCourseMode(course);
        const formatScore = selectedFormat === 'project'
          ? (/project|internship|practical|hands-on/.test(searchable) ? 3 : 0)
          : selectedFormat && mode === selectedFormat ? 3 : 0;
        return { course, score: termScore + formatScore };
      })
      .sort((a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        const aAccess = (a.course.enrollment ? 2 : 0) + (a.course.access?.allowed ? 1 : 0);
        const bAccess = (b.course.enrollment ? 2 : 0) + (b.course.access?.allowed ? 1 : 0);
        return bAccess - aAccess;
      })
      .filter((row, index, rows) => row.score > 0 || !rows.some((item) => item.score > 0))
      .slice(0, 6)
      .map((row) => row.course);
  }, [careerQuizAnswers, catalog]);
  const studentCatalogPlans = useMemo(
    () => plans.filter((plan) => plan.isActive !== false),
    [plans]
  );
  const studentMembershipEligible = useMemo(() => {
    return isCollegeAuthorized;
  }, [isCollegeAuthorized]);
  const studentHasSubscriptionTab = studentMembershipEligible;
  const studentVisiblePlans = useMemo(
    () => (studentMembershipEligible ? studentCatalogPlans.filter((plan) => plan.availability?.isPurchasable !== false) : []),
    [studentCatalogPlans, studentMembershipEligible]
  );
  const studentTheme = STUDENT_DASHBOARD_THEMES[0];
  const dashboardPromotions = useMemo(() => {
    const rows = (promotions || []).filter((item) => (
      item?.isActive !== false
      && ['carousel_slide', 'promotion'].includes(String(item.type || ''))
      && isWithinDateWindow(item.startsAt, item.endsAt)
    ));
    return rows.length ? rows : [];
  }, [promotions]);
  const activeDashboardPromotion = useMemo(
    () => dashboardPromotions.length ? dashboardPromotions[heroSlideIndex % dashboardPromotions.length] : null,
    [dashboardPromotions, heroSlideIndex]
  );
  const studentLearningEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (promotions || [])
      .filter((item) => {
        if (item?.isActive === false || String(item?.type || '') !== 'learning_event') return false;
        if (!item.eventDate) return true;
        const eventDate = new Date(item.eventDate);
        return !Number.isNaN(eventDate.getTime()) && eventDate >= today;
      })
      .sort((left, right) => new Date(left.eventDate || 0) - new Date(right.eventDate || 0));
  }, [promotions]);
  const studentLiveClassEvents = useMemo(
    () => studentLearningEvents.filter((item) => item.eventKind === 'free_live_class'),
    [studentLearningEvents]
  );
  const studentWorkshopEvents = useMemo(
    () => studentLearningEvents.filter((item) => ['workshop', 'webinar'].includes(String(item.eventKind || ''))),
    [studentLearningEvents]
  );
  const studentGeneralEvents = useMemo(
    () => studentLearningEvents.filter((item) => item.eventKind === 'event'),
    [studentLearningEvents]
  );
  const livePromotionMessages = useMemo(
    () => [
      ...studentLearningEvents,
      ...(promotions || []).filter((item) => item.liveSession?.sessionDate || item.liveSession?.meetingNote || (Array.isArray(item.deliveryModes) && item.deliveryModes.includes('live'))),
    ].filter((item, index, rows) => rows.findIndex((row) => String(row._id) === String(item._id)) === index).slice(0, 4),
    [promotions, studentLearningEvents]
  );
  const nextCourseLiveClass = useMemo(
    () => studentLiveClassEvents[0] || livePromotionMessages[0] || null,
    [livePromotionMessages, studentLiveClassEvents]
  );
  const studentWorkshopDisplayItems = useMemo(
    () => studentWorkshopEvents.length
      ? studentWorkshopEvents
      : livePromotionMessages.filter((item) => String(item.type || '') !== 'learning_event'),
    [livePromotionMessages, studentWorkshopEvents]
  );
  const showcasePromotions = useMemo(
    () => (promotions || []).filter((item) => ['certificate_showcase', 'video_review'].includes(String(item.type || ''))),
    [promotions]
  );
  const studentActiveSubscription = useMemo(
    () => subscriptions.find((row) => ['active', 'trial'].includes(String(row.status || '').toLowerCase())) || null,
    [subscriptions]
  );
  const studentDashboardMembershipPlan = useMemo(() => {
    const activePlan = studentActiveSubscription?.planSnapshot
      || studentVisiblePlans.find((plan) => String(plan._id) === String(studentActiveSubscription?.planId))
      || null;
    if (activePlan) return activePlan;
    return studentVisiblePlans.find((plan) => plan.isDefaultBasic)
      || studentVisiblePlans.find((plan) => Number(plan.salePrice || plan.price || 0) <= 0)
      || studentVisiblePlans[0]
      || null;
  }, [studentActiveSubscription, studentVisiblePlans]);
  const selectedPlanScope = useMemo(() => {
    if (selectedPlanScopeId) {
      const explicitPlan = studentVisiblePlans.find((plan) => String(plan._id) === String(selectedPlanScopeId));
      if (explicitPlan) return explicitPlan;
    }
    return studentDashboardMembershipPlan || null;
  }, [selectedPlanScopeId, studentDashboardMembershipPlan, studentVisiblePlans]);
  const studentPrimaryCategory = useMemo(() => {
    const subscribedCategory = studentActiveSubscription?.planSnapshot?.rules?.find((rule) => Array.isArray(rule?.categories) && rule.categories.length)?.categories?.[0];
    return String(
      selectedLearningCourse?.course?.category
      || learning[0]?.course?.category
      || subscribedCategory
      || catalog[0]?.category
      || ''
    ).trim();
  }, [catalog, learning, selectedLearningCourse, studentActiveSubscription]);
  const studentDashboardPlanCards = useMemo(() => {
    const currentPlan = studentDashboardMembershipPlan || null;
    const currentPlanId = String(studentActiveSubscription?.planId || currentPlan?._id || '');
    const currentPlanName = String(currentPlan?.name || currentPlan?.title || '').trim().toLowerCase();
    const primaryCategory = String(studentPrimaryCategory || '').trim().toLowerCase();
    const alternatives = studentCatalogPlans
      .filter((plan) => {
        const planId = String(plan?._id || '');
        const planName = String(plan?.name || plan?.title || '').trim().toLowerCase();
        if (currentPlanId && planId === currentPlanId) return false;
        return !currentPlanName || planName !== currentPlanName;
      })
      .map((plan) => {
        const categories = (plan.rules || []).flatMap((rule) => rule?.categories || []).map((value) => String(value).trim().toLowerCase());
        const searchable = [plan.name, plan.title, plan.description, ...categories].filter(Boolean).join(' ').toLowerCase();
        const categoryMatch = !!primaryCategory && (categories.includes(primaryCategory) || searchable.includes(primaryCategory));
        return { plan, categoryMatch };
      })
      .sort((a, b) => Number(b.categoryMatch) - Number(a.categoryMatch));

    return [
      ...(currentPlan ? [{ plan: currentPlan, isCurrent: true, categoryMatch: false }] : []),
      ...alternatives.map((item) => ({ ...item, isCurrent: false })),
    ];
  }, [studentActiveSubscription, studentCatalogPlans, studentDashboardMembershipPlan, studentPrimaryCategory]);
  const studentFeaturedCourses = useMemo(() => {
    const normalizedPrimary = String(studentPrimaryCategory || '').trim().toLowerCase();
    const merged = [
      ...learning.map((row) => ({
        source: 'learning',
        course: row.course || {},
        learningRow: row,
        progress: Number(row.learningSummary?.completionPercent || 0),
      })),
      ...catalog
        .filter((course) => !learning.some((row) => String(row.course?._id || row.course) === String(course._id)))
        .map((course) => ({
          source: 'catalog',
          course,
          learningRow: null,
          progress: 0,
        })),
    ];
    const ranked = merged
      .sort((a, b) => {
        const aMatch = String(a.course?.category || '').trim().toLowerCase() === normalizedPrimary ? 1 : 0;
        const bMatch = String(b.course?.category || '').trim().toLowerCase() === normalizedPrimary ? 1 : 0;
        if (aMatch !== bMatch) return bMatch - aMatch;
        return Number(b.progress || 0) - Number(a.progress || 0);
      });
    return orderCoursesFreeFirst(ranked, { paidLimit: 1, freeBeforePaid: carouselFreeBeforePaid, limit: 20 });
  }, [carouselFreeBeforePaid, catalog, learning, studentPrimaryCategory]);
  const studentRecommendedRails = useMemo(() => {
    const topPicks = studentFeaturedCourses.slice(0, 6);
    const continueWatching = learning
      .filter((row) => (
        Number(row.learningSummary?.completionPercent || 0) < 100
        && (
          Number(row.learningSummary?.completionPercent || 0) > 0
          || Number(row.learningSummary?.inProgressLessons || 0) > 0
          || Number(row.learningSummary?.completedLessons || 0) > 0
          || !!row.learningSummary?.lastAccessedAt
        )
      ))
      .sort((a, b) => {
        const aTime = a.learningSummary?.lastAccessedAt ? new Date(a.learningSummary.lastAccessedAt).getTime() : 0;
        const bTime = b.learningSummary?.lastAccessedAt ? new Date(b.learningSummary.lastAccessedAt).getTime() : 0;
        return bTime - aTime;
      })
      .slice(0, 6);
    const trending = orderCoursesFreeFirst(catalog
      .filter((course) => !topPicks.some((row) => String(row.course?._id || row.course) === String(course._id)))
      , { paidLimit: 1, freeBeforePaid: carouselFreeBeforePaid, limit: 20 });
    return { topPicks, continueWatching, trending };
  }, [carouselFreeBeforePaid, catalog, learning, studentFeaturedCourses]);
  const studentFreeCourseCards = useMemo(() => {
    const rows = catalog.filter(isFreeLearningCourse);
    return rotateCoursesDaily(rows).slice(0, 20);
  }, [catalog]);
  const studentFreeCourseShowcase = useMemo(() => {
    return studentFreeCourseCards.slice(0, 6);
  }, [studentFreeCourseCards]);
  const studentRecommendedCourses = useMemo(() => {
    if (!isCollegePortalStudent) return [];
    const enrolledIds = new Set(learning.map((row) => String(row.course?._id || row.course)));
    const available = filteredCatalog.filter((course) => !enrolledIds.has(String(course._id)));
    return orderCoursesFreeFirst(available, { paidLimit: 1, freeBeforePaid: carouselFreeBeforePaid, limit: 20 });
  }, [carouselFreeBeforePaid, filteredCatalog, isCollegePortalStudent, learning]);
  const studentRecentInterestCourses = useMemo(() => {
    const activeSourceText = [
      selectedLearningCourse?.course?.courseName || '',
      selectedLearningCourse?.course?.category || '',
      learning[0]?.course?.courseName || '',
      learning[0]?.course?.category || '',
    ].join(' ');
    const relatedTerms = inferRelatedLearningTerms(activeSourceText);
    const currentCourseId = String(selectedLearningCourse?.course?._id || '');
    const scored = filteredCatalog
      .filter((course) => String(course._id) !== currentCourseId)
      .map((course) => {
        const hay = `${course.courseName || ''} ${course.category || ''} ${course.shortDescription || ''}`.toLowerCase();
        const score = relatedTerms.reduce((sum, term) => sum + (hay.includes(term) ? 1 : 0), 0);
        return { course, score };
      })
      .filter((row) => relatedTerms.length ? row.score > 0 : true)
      .sort((a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        const aAccessible = (a.course.enrollment ? 2 : 0) + (a.course.access?.allowed ? 1 : 0);
        const bAccessible = (b.course.enrollment ? 2 : 0) + (b.course.access?.allowed ? 1 : 0);
        if (aAccessible !== bAccessible) return bAccessible - aAccessible;
        return String(a.course.courseName || '').localeCompare(String(b.course.courseName || ''));
      })
      .map((row) => row.course);
    return scored.slice(0, 8);
  }, [filteredCatalog, learning, selectedLearningCourse]);
  const studentRealRecommendedCourses = useMemo(() => {
    const aiMlCourses = catalog.filter((course) => {
      const searchable = `${course.courseName || ''} ${course.category || ''} ${course.shortDescription || ''}`.toLowerCase();
      return /artificial intelligence|machine learning|ai\s*(?:&|and|\/)?\s*ml|\bai\b|\bml\b/.test(searchable);
    });
    const source = [
      ...studentRecentInterestCourses.slice(0, 1),
      ...aiMlCourses,
      ...studentRecentInterestCourses.slice(1),
      ...studentRecommendedCourses,
      ...catalog,
    ];
    const seen = new Set();
    return orderCoursesFreeFirst(source.filter((course) => {
      const courseId = String(course?._id || '');
      if (!courseId || seen.has(courseId)) return false;
      seen.add(courseId);
      return true;
    }), { paidLimit: 1, freeBeforePaid: carouselFreeBeforePaid, limit: 20 });
  }, [carouselFreeBeforePaid, catalog, studentRecentInterestCourses, studentRecommendedCourses]);
  const studentDashboardRecommendedCourses = useMemo(() => {
    if (!isCollegePortalStudent) return studentRealRecommendedCourses;
    return orderCoursesContentFirst(studentRealRecommendedCourses, {
      paidLimit: 1,
      freeBeforePaid: carouselFreeBeforePaid,
      limit: 20,
    });
  }, [carouselFreeBeforePaid, isCollegePortalStudent, studentRealRecommendedCourses]);
  const studentDashboardContinueCourses = useMemo(() => {
    if (!isCollegePortalStudent) return [];
    return [...learning]
      .filter((row) => (
        hasPublishedCourseContent(row)
        && Number(row.learningSummary?.completionPercent || 0) < 100
      ))
      .sort((left, right) => {
        const leftTime = left.learningSummary?.lastAccessedAt ? new Date(left.learningSummary.lastAccessedAt).getTime() : 0;
        const rightTime = right.learningSummary?.lastAccessedAt ? new Date(right.learningSummary.lastAccessedAt).getTime() : 0;
        if (leftTime !== rightTime) return rightTime - leftTime;
        return Number(right.learningSummary?.completionPercent || 0) - Number(left.learningSummary?.completionPercent || 0);
      })
      .slice(0, 3);
  }, [isCollegePortalStudent, learning]);
  const forYouInterestCourses = useMemo(
    () => (studentRecentInterestCourses.length ? studentRecentInterestCourses : studentRecommendedCourses).slice(0, 6),
    [studentRecentInterestCourses, studentRecommendedCourses]
  );
  const forYouPrimaryCourses = useMemo(() => {
    const rows = [...filteredCatalog];
    rows.sort((a, b) => {
      const paidDifference = Number(!isFreeLearningCourse(b)) - Number(!isFreeLearningCourse(a));
      if (paidDifference !== 0) return paidDifference;
      const aScore = (a.enrollment ? 3 : 0) + (a.access?.allowed ? 2 : 0);
      const bScore = (b.enrollment ? 3 : 0) + (b.access?.allowed ? 2 : 0);
      if (aScore !== bScore) return bScore - aScore;
      return String(a.courseName || '').localeCompare(String(b.courseName || ''));
    });
    return orderCoursesFreeFirst(rows, { paidLimit: 1, freeBeforePaid: carouselFreeBeforePaid, limit: 20 });
  }, [carouselFreeBeforePaid, filteredCatalog]);
  const exploreAllCategoryCourses = useMemo(() => [
    ...rotateCoursesDaily(filteredCatalog.filter(isFreeLearningCourse)),
    ...rotateCoursesDaily(filteredCatalog.filter((course) => !isFreeLearningCourse(course))),
  ], [filteredCatalog]);
  const exploreSpotlightCourses = useMemo(() => {
    const categoryCourses = exploreAllCategoryCourses.filter(isFreeLearningCourse);
    if (categoryCourses.length) return categoryCourses;
    if (exploreAllCategoryCourses.length) return exploreAllCategoryCourses;
    return studentFreeCourseCards;
  }, [exploreAllCategoryCourses, studentFreeCourseCards]);
  const exploreSpotlightCourse = useMemo(() => {
    if (!isCollegePortalStudent) return studentFreeCourseShowcase[0] || null;
    if (!exploreSpotlightCourses.length) return null;
    return exploreSpotlightCourses[exploreSpotlightSlot % exploreSpotlightCourses.length];
  }, [exploreSpotlightCourses, exploreSpotlightSlot, isCollegePortalStudent, studentFreeCourseShowcase]);
  const exploreTrendingCourses = useMemo(
    () => (expandedExploreSections.trending ? exploreAllCategoryCourses : forYouPrimaryCourses.slice(0, 8)),
    [expandedExploreSections.trending, exploreAllCategoryCourses, forYouPrimaryCourses]
  );
  useEffect(() => {
    if (!isCollegePortalStudent || activeTab !== 'for-you') return undefined;
    let intervalId;
    const delay = EXPLORE_SPOTLIGHT_ROTATION_MS - (Date.now() % EXPLORE_SPOTLIGHT_ROTATION_MS);
    const timeoutId = window.setTimeout(() => {
      setExploreSpotlightSlot(getExploreSpotlightSlot());
      intervalId = window.setInterval(
        () => setExploreSpotlightSlot(getExploreSpotlightSlot()),
        EXPLORE_SPOTLIGHT_ROTATION_MS
      );
    }, delay);
    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [activeTab, isCollegePortalStudent]);
  useEffect(() => {
    setExpandedExploreSections({ trending: false, live: false });
    if (exploreTrendingRailRef.current) exploreTrendingRailRef.current.scrollLeft = 0;
    if (exploreLiveRailRef.current) exploreLiveRailRef.current.scrollLeft = 0;
  }, [catalogFilters.category, catalogFilters.search]);
  useEffect(() => {
    const rail = exploreTrendingRailRef.current;
    if (activeTab !== 'for-you' || !rail || !forYouPrimaryCourses.length) return undefined;
    let timer = null;
    const reportReach = () => {
      const cards = Array.from(rail.querySelectorAll('.student-explore-trending-item'));
      if (!cards.length) return;
      const visibleRight = rail.scrollLeft + rail.clientWidth;
      let furthestPosition = 1;
      cards.forEach((card, index) => {
        const revealThreshold = Math.min(card.getBoundingClientRect().width * 0.25, 48);
        if (card.offsetLeft + revealThreshold <= visibleRight) furthestPosition = index + 1;
      });
      if (furthestPosition <= exploreTrendingReachRef.current) return;
      exploreTrendingReachRef.current = furthestPosition;
      reportCourseCarouselReach({ surface: 'explore-trending', maxVisiblePosition: furthestPosition, totalItems: cards.length });
    };
    const scheduleReport = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(reportReach, 300);
    };
    exploreTrendingReachRef.current = 0;
    scheduleReport();
    rail.addEventListener('scroll', scheduleReport, { passive: true });
    window.addEventListener('resize', scheduleReport);
    return () => {
      window.clearTimeout(timer);
      rail.removeEventListener('scroll', scheduleReport);
      window.removeEventListener('resize', scheduleReport);
    };
  }, [activeTab, forYouPrimaryCourses.length, reportCourseCarouselReach]);
  const forYouLiveCourses = useMemo(
    () => orderCoursesFreeFirst(filteredCatalog.filter((course) => {
      const category = String(course.category || '').trim().toLowerCase();
      const deliveryMode = String(course.deliveryMode || course.mode || course.courseMode || '').trim().toLowerCase();
      return ['live', 'hybrid'].includes(deliveryMode)
        || inferCourseMode(course) === 'live'
        || category.includes('live')
        || category.includes('online');
    }), { paidLimit: 1, freeBeforePaid: carouselFreeBeforePaid, limit: 20 }),
    [carouselFreeBeforePaid, filteredCatalog]
  );
  const studentUpcomingLiveItems = useMemo(() => [
    ...studentLiveClassEvents.map((item) => ({
      ...item,
      _learningEvent: true,
      courseName: item.promoTitle || 'Free Live Class',
    })),
    ...forYouLiveCourses,
  ], [forYouLiveCourses, studentLiveClassEvents]);
  const exploreAllLiveCourses = useMemo(() => {
    const rows = filteredCatalog.filter((course) => {
      const category = String(course.category || '').trim().toLowerCase();
      const deliveryMode = String(course.deliveryMode || course.mode || course.courseMode || '').trim().toLowerCase();
      return ['live', 'hybrid'].includes(deliveryMode)
        || inferCourseMode(course) === 'live'
        || category.includes('live')
        || category.includes('online');
    });
    return [
      ...rotateCoursesDaily(rows.filter(isFreeLearningCourse)),
      ...rotateCoursesDaily(rows.filter((course) => !isFreeLearningCourse(course))),
    ];
  }, [filteredCatalog]);
  const forYouInternshipCourses = useMemo(
    () => orderCoursesFreeFirst(filteredCatalog.filter((course) => {
      const category = String(course.category || '').trim().toLowerCase();
      return category.includes('internship') || category.includes('project');
    }), { paidLimit: 1, freeBeforePaid: carouselFreeBeforePaid, limit: 20 }),
    [carouselFreeBeforePaid, filteredCatalog]
  );
  const buildForYouShowcase = useCallback((courses) => {
    const baseCourses = courses.length ? courses : [];
    const rows = [];
    baseCourses.forEach((course, index) => {
      rows.push({ type: 'course', course });
      if ((index + 1) % 4 === 0 && showcasePromotions.length) {
        const promo = showcasePromotions[Math.floor(index / 4) % showcasePromotions.length];
        rows.push({ type: 'showcase', promo });
      }
    });
    return rows;
  }, [showcasePromotions]);
  const forYouLiveCards = useMemo(() => buildForYouShowcase(forYouLiveCourses), [buildForYouShowcase, forYouLiveCourses]);
  const forYouInternshipCards = useMemo(() => buildForYouShowcase(forYouInternshipCourses), [buildForYouShowcase, forYouInternshipCourses]);
  const exploreLiveCards = useMemo(() => {
    const eventCards = studentLiveClassEvents.map((promo) => ({ type: 'event', promo }));
    const courseCards = expandedExploreSections.live
      ? exploreAllLiveCourses.map((course) => ({ type: 'course', course }))
      : forYouLiveCards.slice(0, 2);
    return [...eventCards, ...courseCards];
  }, [expandedExploreSections.live, exploreAllLiveCourses, forYouLiveCards, studentLiveClassEvents]);
  const forYouLiveMaxIndex = useMemo(
    () => Math.max(0, forYouLiveCards.length - 5),
    [forYouLiveCards.length]
  );
  const forYouInternshipVisible = useMemo(
    () => forYouInternshipCards.slice(forYouSpotlightIndex, forYouSpotlightIndex + 5),
    [forYouInternshipCards, forYouSpotlightIndex]
  );
  const forYouInternshipMaxIndex = useMemo(
    () => Math.max(0, forYouInternshipCards.length - 5),
    [forYouInternshipCards.length]
  );
  const studentDiscountCards = useMemo(
    () => (dashboardPromotions.length > 1 ? dashboardPromotions.slice(1) : dashboardPromotions).slice(0, 6),
    [dashboardPromotions]
  );
  const studentVisibleCoupons = useMemo(() => {
    const activePlanId = String(studentActiveSubscription?.planId || studentDashboardMembershipPlan?._id || '');
    const fallbackPlanIds = new Set(studentVisiblePlans.map((plan) => String(plan._id)));
    return (coupons || []).filter((coupon) => {
      const approvalStatus = String(coupon?.approvalStatus || '').toLowerCase();
      const packageIds = Array.isArray(coupon?.packageIds) ? coupon.packageIds.map(String) : [];
      const appliesToPlan = !packageIds.length
        || (activePlanId && packageIds.includes(activePlanId))
        || packageIds.some((id) => fallbackPlanIds.has(id));
      return coupon?.isActive !== false
        && approvalStatus === 'approved'
        && appliesToPlan
        && isWithinDateWindow(coupon.validFrom, coupon.validTo);
    });
  }, [coupons, studentActiveSubscription, studentDashboardMembershipPlan, studentVisiblePlans]);
  const studentSubscriptionUsage = useMemo(() => {
    const activePlan = studentActiveSubscription?.planSnapshot
      || studentVisiblePlans.find((plan) => String(plan._id) === String(studentActiveSubscription?.planId))
      || studentDashboardMembershipPlan
      || null;
    const totalCourses = Number(activePlan?.quotas?.anyCourseLimit || 0);
    const enrolledCount = learning.length;
    const totalHours = Math.round(learning.reduce((sum, row) => sum + (Number(row.learningSummary?.watchedSeconds || 0) / 3600), 0));
    return {
      plan: activePlan,
      planName: activePlan?.name || activePlan?.title || '',
      subscriptionStatus: studentActiveSubscription ? String(studentActiveSubscription.status || 'active').replace(/_/g, ' ') : 'basic',
      renewalState: studentActiveSubscription?.lifecycle?.isExpired
        ? 'Expired'
        : studentActiveSubscription?.lifecycle?.isExpiringSoon
          ? 'Expiring soon'
          : (studentActiveSubscription ? 'Current' : 'Open access'),
      expiresAt: studentActiveSubscription?.expiresAt || studentActiveSubscription?.endDate || activePlan?.expiryDate || '',
      enrolledCount,
      totalHours,
      certificates: learning.filter((row) => isCompletedLearningRow(row)).length,
      downloads: learning.filter((row) => row.course?.resourceUrl || row.course?.downloadable).length,
      usagePercent: totalCourses > 0 ? Math.min(100, Math.round((enrolledCount / totalCourses) * 100)) : 0,
    };
  }, [learning, studentActiveSubscription, studentDashboardMembershipPlan, studentVisiblePlans]);
  const studentCurrentMembershipPrice = Number(
    studentSubscriptionUsage.plan?.salePrice
    || studentSubscriptionUsage.plan?.price
    || studentActiveSubscription?.pricing?.finalAmount
    || 0
  );
  const studentCurrentMembershipIsFree = studentCurrentMembershipPrice <= 0;
  const studentCurrentMembershipBaseName = studentSubscriptionUsage.planName || 'Basic';
  const studentCurrentMembershipDisplayName = /\b(plan|membership)\b/i.test(studentCurrentMembershipBaseName)
    ? studentCurrentMembershipBaseName
    : `${studentCurrentMembershipBaseName} Plan`;
  const studentProgressSnapshot = useMemo(() => {
    const completedLessons = learning.reduce((sum, row) => sum + Number(row.learningSummary?.completedLessons || 0), 0);
    const watchedSeconds = learning.reduce((sum, row) => sum + Number(row.learningSummary?.watchedSeconds || 0), 0);
    const averageCompletion = Math.max(0, Math.min(100, Number(studentLearningOverview.avgCompletion || 0)));
    const assessmentScore = Math.max(0, Math.min(100, Number(studentLearningOverview.averageScore || 0)));
    const certificateScore = Math.min(100, Number(studentSubscriptionUsage.certificates || 0) * 20);
    const readinessScore = Math.round((averageCompletion + assessmentScore + certificateScore) / 3);
    const skills = learning.slice(0, 4).map((row, index) => ({
      id: row._id || row.course?._id || index,
      label: row.course?.courseName || row.course?.title || 'Course skill',
      value: Math.max(0, Math.min(100, Number(row.learningSummary?.completionPercent || 0))),
      icon: ['bi-filetype-py', 'bi-briefcase-fill', 'bi-cpu-fill', 'bi-vector-pen'][index % 4],
      color: ['#f4b000', '#0f9b72', '#0ea5c6', '#8b5cf6'][index % 4],
      bg: ['#fff7db', '#e9fbf3', '#ecfbff', '#f5efff'][index % 4],
    }));
    const achievements = [
      { icon: 'bi-star-fill', label: 'Streak Master', value: `${studentConsistencyOverview.bestStreak || 0} day streak`, color: '#7c3aed', bg: 'linear-gradient(180deg, #a855f7, #7c3aed)' },
      { icon: 'bi-fire', label: 'Consistent Learner', value: `${studentConsistencyOverview.activeThisWeek || 0} active this week`, color: '#f97316', bg: 'linear-gradient(180deg, #fb923c, #f97316)' },
      { icon: 'bi-book-half', label: 'Quick Learner', value: `${completedLessons} lessons completed`, color: '#059669', bg: 'linear-gradient(180deg, #34d399, #059669)' },
      { icon: 'bi-code-slash', label: 'Project Starter', value: `${studentLearningOverview.activeCourses || 0} project created`, color: '#2563eb', bg: 'linear-gradient(180deg, #60a5fa, #2563eb)' },
      { icon: 'bi-trophy-fill', label: 'Certificate Earned', value: `${studentSubscriptionUsage.certificates || 0} certificates`, color: '#a855f7', bg: 'linear-gradient(180deg, #c084fc, #9333ea)' },
    ];
    return {
      completedLessons,
      watchedSeconds,
      averageCompletion,
      assessmentScore,
      certificateScore,
      readinessScore,
      skills,
      achievements,
    };
  }, [learning, studentConsistencyOverview, studentLearningOverview, studentSubscriptionUsage.certificates]);
  const studentSubscriptionDetails = useMemo(() => {
    const latestPaidOrder = [...orders]
      .filter((row) => ['paid', 'completed', 'success'].includes(String(row.status || '').toLowerCase()))
      .sort((a, b) => new Date(b.paidAt || b.createdAt || 0).getTime() - new Date(a.paidAt || a.createdAt || 0).getTime())[0] || null;
    const activePlan = studentSubscriptionUsage.plan || null;
    const scopedCategories = Array.from(new Set((activePlan?.rules || []).flatMap((rule) => rule?.categories || []).filter(Boolean)));
    const benefits = [
      ...(scopedCategories.length ? scopedCategories.slice(0, 3).map((category) => `Access to ${formatCourseCategoryLabel(category)} courses`) : []),
      activePlan?.quotas?.unlimitedOnDemand ? 'Unlimited on-demand course access' : null,
      Number(activePlan?.quotas?.liveCourseLimit || 0) > 0 ? `${activePlan.quotas.liveCourseLimit} live course slot${Number(activePlan.quotas.liveCourseLimit) === 1 ? '' : 's'}` : null,
      studentSubscriptionUsage.certificates > 0 ? 'Certificate-ready completed courses' : 'Certificate eligibility on course completion',
      studentSubscriptionUsage.downloads > 0 ? 'Downloadable learning resources included' : 'Downloadable resources when provided',
      'Priority access to your active membership content',
    ].filter(Boolean);
    const hoursTarget = Math.max(20, Math.max(studentSubscriptionUsage.totalHours || 0, 1) + 12);
    const coursesTarget = Math.max(1, Number(activePlan?.quotas?.anyCourseLimit || 0) || Math.max(studentSubscriptionUsage.enrolledCount || 0, 1));
    const downloadsTarget = Math.max(1, (studentSubscriptionUsage.downloads || 0) + 12);
    return {
      latestPaidOrder,
      benefits: benefits.slice(0, 6),
      billingCycle: activePlan?.durationDays ? `${activePlan.durationDays} days` : 'Flexible',
      amountLabel: fmtMoney(latestPaidOrder?.pricing?.finalAmount || latestPaidOrder?.payment?.amount || activePlan?.salePrice || activePlan?.price || 0),
      paymentMethod: String(latestPaidOrder?.provider || latestPaidOrder?.payment?.method || 'Membership').replace(/_/g, ' '),
      nextBillingDate: studentActiveSubscription?.expiresAt || studentActiveSubscription?.endDate || activePlan?.expiryDate || '',
      hoursTarget,
      coursesTarget,
      downloadsTarget,
      recommendedPlan: studentVisiblePlans.find((plan) => String(plan._id) !== String(activePlan?._id) && plan.isDefaultBasic !== true) || studentVisiblePlans.find((plan) => String(plan._id) !== String(activePlan?._id)) || null,
    };
  }, [orders, studentActiveSubscription, studentSubscriptionUsage, studentVisiblePlans]);
  const studentFeaturedCoupon = useMemo(
    () => studentVisibleCoupons[0] || null,
    [studentVisibleCoupons]
  );
  const studentProfileBrandLogo = useMemo(
    () => normalizeMediaUrl(tenant?.branding?.logoUrl || tenant?.logoUrl || ''),
    [tenant]
  );
  const studentProfileName = useMemo(
    () => `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Student',
    [user]
  );
  const studentProfileInitials = useMemo(
    () => getInitials(user?.firstName, user?.lastName, tenant?.name),
    [tenant, user]
  );
  const studentAdvisorContact = useMemo(() => {
    const settings = tenant?.settings || {};
    const address = tenant?.address || {};
    const addressText = typeof address === 'string'
      ? address
      : [address.street, address.city, address.state, address.country, address.pincode].filter(Boolean).join(', ');
    return {
      organization: tenant?.name || 'Student Support',
      email: tenant?.contactEmail || settings.supportEmail || settings.contactEmail || '',
      phone: tenant?.contactPhone || settings.supportPhone || settings.contactPhone || '',
      website: tenant?.website || settings.website || '',
      address: addressText,
    };
  }, [tenant]);
  const studentProfileRoleLabel = useMemo(() => {
    const primaryCategory = formatCourseCategoryLabel(studentPrimaryCategory || '');
    if (primaryCategory && primaryCategory !== 'No Category') return primaryCategory;
    return studentIsOnDemandPrimary ? 'On-demand Learner' : 'Offline Learner';
  }, [studentIsOnDemandPrimary, studentPrimaryCategory]);
  const studentProfileOverviewCards = useMemo(() => ([
    {
      label: 'My Subscription',
      value: studentSubscriptionUsage.planName || (studentActiveSubscription ? 'Active plan' : 'Free plan'),
      hint: 'Manage your plan, billing and renewal',
      icon: 'bi-credit-card-2-front',
      iconBg: 'rgba(99,102,241,0.12)',
      iconColor: '#635bff',
      action: () => openTab('subscription-details'),
    },
    {
      label: 'My Courses',
      value: `${studentSubscriptionUsage.enrolledCount || 0}`,
      hint: 'Continue enrolled and accessible courses',
      icon: 'bi-book-half',
      iconBg: 'rgba(59,130,246,0.12)',
      iconColor: '#2563eb',
      action: () => {
        setLearningFilters((prev) => ({ ...prev, status: 'all' }));
        openTab('my-courses');
      },
    },
    {
      label: 'Saved Courses',
      value: `${wishlistCourseIds.length}`,
      hint: 'View and manage your saved courses',
      icon: 'bi-bookmark',
      iconBg: 'rgba(34,197,94,0.12)',
      iconColor: '#16a34a',
      action: () => openTab('saved-courses'),
    },
    {
      label: 'My Certificates',
      value: `${studentSubscriptionUsage.certificates || 0}`,
      hint: 'View your earned certificates',
      icon: 'bi-award',
      iconBg: 'rgba(245,158,11,0.12)',
      iconColor: '#d97706',
      action: () => navigate('/certificates'),
    },
    {
      label: 'Billing & Payments',
      value: studentSubscriptionDetails.paymentMethod || 'Membership',
      hint: 'Review orders, totals and payment providers',
      icon: 'bi-wallet2',
      iconBg: 'rgba(59,130,246,0.12)',
      iconColor: '#2563eb',
      action: () => openTab('billing'),
    },
    {
      label: 'Learning Resources',
      value: `${studentSubscriptionUsage.downloads || 0}`,
      hint: 'Open files and links attached to lessons',
      icon: 'bi-download',
      iconBg: 'rgba(168,85,247,0.12)',
      iconColor: '#9333ea',
      action: () => openTab('downloads'),
    },
  ]), [navigate, openTab, studentActiveSubscription, studentSubscriptionDetails.paymentMethod, studentSubscriptionUsage.certificates, studentSubscriptionUsage.downloads, studentSubscriptionUsage.enrolledCount, studentSubscriptionUsage.planName, wishlistCourseIds.length]);
  const studentProfileSupportCards = useMemo(() => ([
    {
      label: 'Share Programs',
      value: '',
      hint: 'Share published learning programs with friends',
      icon: 'bi-gift',
      iconBg: 'rgba(168,85,247,0.12)',
      iconColor: '#9333ea',
      action: () => openTab('share-programs'),
    },
    {
      label: 'Help Center',
      value: '',
      hint: 'Get help and find answers',
      icon: 'bi-question-circle',
      iconBg: 'rgba(59,130,246,0.12)',
      iconColor: '#2563eb',
      action: () => openTab('help-center'),
    },
    {
      label: 'Notifications',
      value: '',
      hint: 'Manage your notification preferences',
      icon: 'bi-bell',
      iconBg: 'rgba(249,115,22,0.12)',
      iconColor: '#ea580c',
      action: () => navigate('/notifications'),
    },
    {
      label: 'Settings',
      value: '',
      hint: 'Manage account and app preferences',
      icon: 'bi-gear',
      iconBg: 'rgba(148,163,184,0.16)',
      iconColor: '#64748b',
      action: () => openTab('help-center'),
    },
  ]), [navigate, openTab]);
  const studentCurrentPlanBenefits = useMemo(
    () => (studentSubscriptionDetails.benefits || []).slice(0, 6),
    [studentSubscriptionDetails.benefits]
  );
  const studentRecommendedPlanBenefits = useMemo(() => {
    const plan = studentSubscriptionDetails.recommendedPlan;
    if (!plan) return [];
    const categories = Array.from(new Set((plan.rules || []).flatMap((rule) => rule?.categories || []).filter(Boolean)));
    const list = [
      ...(categories.length ? categories.slice(0, 4).map((category) => `Access to ${formatCourseCategoryLabel(category)} courses`) : []),
      plan.quotas?.unlimitedOnDemand ? 'Unlimited recorded lessons' : null,
      Number(plan.quotas?.liveCourseLimit || 0) > 0 ? `${plan.quotas.liveCourseLimit} live interactive classes` : null,
      'Career guidance and mentor support',
      'Verified certificate access',
      'Projects and advanced practice',
    ].filter(Boolean);
    return list.slice(0, 7);
  }, [studentSubscriptionDetails.recommendedPlan]);
  const studentDashboardUpgrade = useMemo(() => {
    const plan = studentSubscriptionDetails.recommendedPlan;
    if (!plan) return null;
    const categories = Array.from(new Set(
      (plan.rules || []).flatMap((rule) => rule?.categories || []).filter(Boolean)
    ));
    const features = [
      plan.quotas?.unlimitedOnDemand ? 'Unlimited on-demand courses' : null,
      Number(plan.quotas?.anyCourseLimit || 0) > 0 ? `${plan.quotas.anyCourseLimit} courses included` : null,
      Number(plan.quotas?.liveCourseLimit || 0) > 0 ? `${plan.quotas.liveCourseLimit} live course ${Number(plan.quotas.liveCourseLimit) === 1 ? 'slot' : 'slots'}` : null,
      categories.length ? `${categories.length} learning ${categories.length === 1 ? 'track' : 'tracks'}` : null,
      Number(plan.durationDays || 0) > 0 ? `${plan.durationDays} days of access` : null,
    ].filter(Boolean);
    return { plan, features: features.slice(0, 3) };
  }, [studentSubscriptionDetails.recommendedPlan]);
  const checkoutPresentation = useMemo(() => {
    const plan = checkoutState.plan;
    if (!plan) return null;
    const listPrice = Number(plan.price || 0);
    const planPrice = Number(plan.salePrice || 0) > 0 ? Number(plan.salePrice) : listPrice;
    const orderPricing = checkoutState.order?.pricing || null;
    const categories = Array.from(new Set(
      (plan.rules || []).flatMap((rule) => rule?.categories || []).filter(Boolean)
    ));
    const deliveryModes = Array.from(new Set(
      (plan.rules || []).flatMap((rule) => rule?.deliveryModes || []).filter(Boolean)
    ));
    const ruleTitles = (plan.rules || []).map((rule) => String(rule?.title || '').trim()).filter(Boolean);
    const benefits = [
      plan.quotas?.unlimitedOnDemand
        ? 'Unlimited on-demand course access'
        : Number(plan.quotas?.onDemandCourseLimit || 0) > 0
          ? `${plan.quotas.onDemandCourseLimit} on-demand course ${Number(plan.quotas.onDemandCourseLimit) === 1 ? 'slot' : 'slots'}`
          : null,
      Number(plan.quotas?.liveCourseLimit || 0) > 0
        ? `${plan.quotas.liveCourseLimit} live course ${Number(plan.quotas.liveCourseLimit) === 1 ? 'slot' : 'slots'}`
        : null,
      Number(plan.quotas?.anyCourseLimit || 0) > 0
        ? `Choose up to ${plan.quotas.anyCourseLimit} ${Number(plan.quotas.anyCourseLimit) === 1 ? 'course' : 'courses'}`
        : null,
      ...categories.slice(0, 3).map((category) => `${formatCourseCategoryLabel(category)} learning access`),
      ...deliveryModes.slice(0, 2).map((mode) => `${toTitleWords(mode)} learning format`),
      ...ruleTitles.slice(0, 2),
      Number(plan.durationDays || 0) > 0 ? `${plan.durationDays} days of membership access` : null,
    ].filter(Boolean);
    return {
      listPrice,
      planPrice,
      planDiscount: Math.max(0, listPrice - planPrice),
      baseAmount: orderPricing ? Number(orderPricing.baseAmount || 0) : planPrice,
      couponDiscount: orderPricing ? Number(orderPricing.discountAmount || 0) : 0,
      finalAmount: orderPricing ? Number(orderPricing.finalAmount || 0) : planPrice,
      benefits: Array.from(new Set(benefits)).slice(0, 8),
    };
  }, [checkoutState.order, checkoutState.plan]);
  const programPreviewPlan = useMemo(() => {
    if (!programPreviewCourse?._id) return null;
    const explicitPlanId = String(programPreviewCourse.access?.planId || '');
    const purchasablePlans = studentCatalogPlans.filter((plan) => (
      plan.isActive !== false && plan.availability?.isPurchasable !== false
    ));
    const explicitPlan = explicitPlanId
      ? purchasablePlans.find((plan) => String(plan._id) === explicitPlanId)
      : null;
    if (explicitPlan) return explicitPlan;
    return purchasablePlans
      .filter((plan) => (plan.rules || []).some((rule) => studentPlanRuleMatchesCourse(rule, programPreviewCourse)))
      .sort((a, b) => {
        const aPaid = Number(a.salePrice || a.price || 0) > 0 ? 1 : 0;
        const bPaid = Number(b.salePrice || b.price || 0) > 0 ? 1 : 0;
        if (aPaid !== bPaid) return bPaid - aPaid;
        return Number(a.salePrice || a.price || 0) - Number(b.salePrice || b.price || 0);
      })[0] || null;
  }, [programPreviewCourse, studentCatalogPlans]);
  const programPreviewDetails = useMemo(() => {
    if (!programPreviewCourse) return null;
    const syllabus = Array.isArray(programPreviewCourse.syllabus) ? programPreviewCourse.syllabus : [];
    const skills = Array.isArray(programPreviewCourse.skills) ? programPreviewCourse.skills.filter(Boolean) : [];
    const prerequisites = Array.isArray(programPreviewCourse.prerequisites) ? programPreviewCourse.prerequisites.filter(Boolean) : [];
    const searchable = [
      programPreviewCourse.courseName,
      programPreviewCourse.category,
      programPreviewCourse.shortDescription,
      programPreviewCourse.description,
      programPreviewCourse.notes,
      ...skills,
      ...prerequisites,
      ...syllabus.flatMap((module) => [module?.module, ...(module?.topics || [])]),
    ].filter(Boolean).join(' ').toLowerCase();
    const durationDays = Number(programPreviewCourse.duration?.days || programPreviewCourse.durationDays || 0);
    const durationHours = Number(programPreviewCourse.totalHours || programPreviewCourse.duration?.hours || 0);
    const durationMonths = Number(programPreviewCourse.numberOfMonths || 0);
    const lessonCount = Number(programPreviewCourse.totalLessons || 0)
      || syllabus.reduce((sum, module) => sum + Number(module?.topics?.length || 0), 0);
    const mode = inferCourseMode(programPreviewCourse);
    const originalPrice = Number(programPreviewCourse.totalFees || programPreviewCourse.fees?.original || 0);
    const coursePrice = Number(programPreviewCourse.discountedPrice || programPreviewCourse.fees?.discounted || programPreviewCourse.salePrice || originalPrice || 0);
    const planPrice = Number(programPreviewPlan?.salePrice || programPreviewPlan?.price || 0);
    const valueHighlights = [
      /internship/.test(searchable) ? { icon: 'bi-briefcase-fill', title: 'Internship Included', text: 'Internship experience is included in this program.', tone: 'green' } : null,
      /certificate|certification/.test(searchable) ? { icon: 'bi-patch-check-fill', title: 'Completion Certificate', text: 'Earn the configured certificate after successful completion.', tone: 'orange' } : null,
      /placement|job assistance|career support/.test(searchable) ? { icon: 'bi-person-workspace', title: 'Placement Assistance', text: 'Receive the career or placement support included with this program.', tone: 'blue' } : null,
      /project|practical|hands-on/.test(searchable) ? { icon: 'bi-code-square', title: 'Practical Training', text: 'Apply your learning through the practical work listed in the curriculum.', tone: 'purple' } : null,
      skills.length ? { icon: 'bi-graph-up-arrow', title: 'Skill Development', text: `Develop ${skills.slice(0, 3).join(', ')}${skills.length > 3 ? ' and more' : ''}.`, tone: 'cyan' } : null,
    ].filter(Boolean);
    if (!valueHighlights.length) {
      valueHighlights.push(
        { icon: 'bi-journal-richtext', title: 'Structured Curriculum', text: `${syllabus.length || 0} curriculum modules are organised into a guided learning path.`, tone: 'purple' },
        { icon: mode === 'on_demand' ? 'bi-play-btn-fill' : 'bi-camera-video-fill', title: mode === 'on_demand' ? 'Flexible Learning' : 'Instructor-led Learning', text: mode === 'on_demand' ? 'Learn through the published on-demand course content.' : 'Learn through the configured live or instructor-led format.', tone: 'blue' }
      );
    }
    return {
      syllabus,
      skills,
      prerequisites,
      durationLabel: durationDays ? `${durationDays} days` : durationMonths ? `${durationMonths} month${durationMonths === 1 ? '' : 's'}` : 'Flexible duration',
      durationHours,
      lessonCount,
      modeLabel: mode === 'on_demand' ? 'On-demand learning' : 'Live / instructor-led',
      originalPrice,
      displayPrice: planPrice || coursePrice,
      priceSource: programPreviewPlan ? `${programPreviewPlan.name || programPreviewPlan.title || 'Membership'} access` : 'program fee',
      valueHighlights,
    };
  }, [programPreviewCourse, programPreviewPlan]);
  const liveCatalogGroups = useMemo(() => {
    const rows = filteredCatalog.filter((course) => !isOnDemandCategory(course.category) && inferCourseMode(course) !== 'on_demand');
    const grouped = rows.reduce((acc, course) => {
      const key = String(course.category || 'Other Courses').trim() || 'Other Courses';
      if (!acc[key]) acc[key] = [];
      acc[key].push(course);
      return acc;
    }, {});
    return Object.entries(grouped)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([category, courses]) => ({ category, courses }));
  }, [filteredCatalog]);
  const wishlistedCatalogCourses = useMemo(
    () => catalog.filter((course) => wishlistCourseIds.includes(String(course._id))),
    [catalog, wishlistCourseIds]
  );
  const studentResourceLessons = useMemo(
    () => (learnState.outline || []).flatMap((section) => (section.lessons || [])
      .filter((lesson) => lesson?.resourceUrl)
      .map((lesson) => ({
        ...lesson,
        sectionTitle: section.title || section.name || 'Course resources',
      }))),
    [learnState.outline]
  );
  const studentBillingOrders = useMemo(
    () => [...(orders || [])].sort((a, b) => new Date(b.paidAt || b.createdAt || 0).getTime() - new Date(a.paidAt || a.createdAt || 0).getTime()),
    [orders]
  );
  const wishlistCourseIdSet = useMemo(() => new Set(wishlistCourseIds.map(String)), [wishlistCourseIds]);
  const filteredLearning = useMemo(() => {
    const search = String(learningFilters.search || '').trim().toLowerCase();
    const status = String(learningFilters.status || 'all').trim().toLowerCase();
    const sort = String(learningFilters.sort || 'recent').trim().toLowerCase();

    const rows = learning.filter((row) => {
      const progressState = getLearningProgressState(row);
      const completion = Number(row.learningSummary?.completionPercent || 0);
      const pending = Number(row.learningSummary?.pendingLessons || 0) > 0;
      const matchesSearch = !search || [
        row.course?.courseName || '',
        row.course?.courseCode || '',
        row.course?.category || '',
      ].join(' ').toLowerCase().includes(search);
      const isWishlisted = wishlistCourseIdSet.has(String(row.course?._id || row.course));
      const matchesStatus = status === 'all'
        || (status === 'completed' && completion >= 100)
        || (status === 'in_progress' && (progressState === 'in_progress' || progressState === 'not_started'))
        || (status === 'pending' && pending && progressState !== 'completed')
        || (status === 'not_started' && progressState === 'not_started')
        || (status === 'wishlist' && isWishlisted);
      return matchesSearch && matchesStatus;
    });

    const sorted = [...rows];
    sorted.sort((a, b) => {
      if (sort === 'progress') {
        return Number(b.learningSummary?.completionPercent || 0) - Number(a.learningSummary?.completionPercent || 0);
      }
      if (sort === 'name') {
        return String(a.course?.courseName || '').localeCompare(String(b.course?.courseName || ''));
      }
      if (sort === 'score') {
        return Number(b.learningSummary?.averageScore || -1) - Number(a.learningSummary?.averageScore || -1);
      }
      const aTime = a.learningSummary?.lastAccessedAt ? new Date(a.learningSummary.lastAccessedAt).getTime() : 0;
      const bTime = b.learningSummary?.lastAccessedAt ? new Date(b.learningSummary.lastAccessedAt).getTime() : 0;
      return bTime - aTime;
    });
    return sorted;
  }, [learning, learningFilters, wishlistCourseIdSet]);
  const accessibleLearningRows = useMemo(() => {
    if (!isCollegePortalStudent) return [];
    const search = String(learningFilters.search || '').trim().toLowerCase();
    const status = String(learningFilters.status || 'all').trim().toLowerCase();
    if (status === 'wishlist') return [];
    if (!['all', 'not_started', 'pending', 'in_progress'].includes(status)) return [];

    const learningCourseIds = new Set(learning.map((row) => String(row.course?._id || row.course || '')));
    return catalog
      .filter((course) => {
        const courseId = String(course._id || '');
        if (!courseId || learningCourseIds.has(courseId)) return false;
        if (!course.access?.allowed) return false;
        if (selectedPlanScope?.rules?.length) {
          const matchesSelectedPlan = selectedPlanScope.rules.some((rule) => studentPlanRuleMatchesCourse(rule, course));
          if (!matchesSelectedPlan) return false;
        }
        if (search && ![
          course.courseName || '',
          course.courseCode || '',
          course.category || '',
        ].join(' ').toLowerCase().includes(search)) {
          return false;
        }
        return true;
      })
      .map((course) => ({
        _id: `accessible-${course._id}`,
        isVirtualEnrollment: true,
        course,
        batch: null,
        status: 'active',
        source: 'membership',
        learningSummary: {
          completionPercent: 0,
          completedLessons: 0,
          pendingLessons: 0,
          inProgressLessons: 0,
          averageScore: null,
          lastAccessedAt: null,
          consistency: {},
        },
      }));
  }, [catalog, isCollegePortalStudent, learning, learningFilters, selectedPlanScope]);
  const visibleLearningRows = useMemo(() => [...filteredLearning, ...accessibleLearningRows], [accessibleLearningRows, filteredLearning]);
  const accessibleCourseCount = useMemo(() => (
    isCollegePortalStudent ? catalog.filter((course) => {
      const courseId = String(course._id || '');
      if (!courseId) return false;
      if (learning.some((row) => String(row.course?._id || row.course) === courseId)) return false;
      if (!course.access?.allowed) return false;
      if (selectedPlanScope?.rules?.length) {
        const matchesSelectedPlan = selectedPlanScope.rules.some((rule) => studentPlanRuleMatchesCourse(rule, course));
        if (!matchesSelectedPlan) return false;
      }
      return true;
    }).length : 0
  ), [catalog, isCollegePortalStudent, learning, selectedPlanScope]);
  const learningStatusCounts = useMemo(() => ({
    inProgress: learning.filter((row) => {
      const state = getLearningProgressState(row);
      return state === 'in_progress' || state === 'not_started';
    }).length + accessibleCourseCount,
    completed: learning.filter((row) => getLearningProgressState(row) === 'completed').length,
    notStarted: learning.filter((row) => getLearningProgressState(row) === 'not_started').length,
    wishlist: wishlistCourseIds.length,
  }), [accessibleCourseCount, learning, wishlistCourseIds.length]);
  const enrolledCourseSliderCards = useMemo(() => {
    return [
      ...filteredLearning.map((row) => ({
        type: row.isVirtualEnrollment ? 'accessible' : 'enrolled',
        key: `${row.isVirtualEnrollment ? 'accessible' : 'enrolled'}-${row._id}`,
        row,
        course: row.isVirtualEnrollment ? row.course : undefined,
      })),
      ...accessibleLearningRows.map((row) => ({
        type: 'accessible',
        key: row._id,
        row,
        course: row.course,
      })),
    ];
  }, [accessibleLearningRows, filteredLearning]);
  const enrolledCourseVisibleCards = useMemo(
    () => enrolledCourseSliderCards.slice(enrolledCourseCarouselIndex, enrolledCourseCarouselIndex + 3),
    [enrolledCourseCarouselIndex, enrolledCourseSliderCards]
  );
  const enrolledCourseMaxIndex = Math.max(0, enrolledCourseSliderCards.length - 3);
  useEffect(() => {
    if (enrolledCourseCarouselIndex > enrolledCourseMaxIndex) {
      setEnrolledCourseCarouselIndex(0);
    }
  }, [enrolledCourseCarouselIndex, enrolledCourseMaxIndex]);
  useEffect(() => {
    setEnrolledCourseCarouselIndex(0);
  }, [learningFilters.search, learningFilters.status, learningFilters.sort]);
  useEffect(() => {
    if (!selectedPlanScopeId && studentDashboardMembershipPlan?._id) {
      setSelectedPlanScopeId(String(studentDashboardMembershipPlan._id));
    }
  }, [selectedPlanScopeId, studentDashboardMembershipPlan]);
  useEffect(() => {
    if (learningFilters.status === 'wishlist') return;
    if (!visibleLearningRows.length) return;
    const isSelectedStillVisible = visibleLearningRows.some(
      (row) => String(row.course?._id || row.course) === String(selectedCourseId)
    );
    if (!isSelectedStillVisible) {
      setSelectedCourseId(String(visibleLearningRows[0].course?._id || visibleLearningRows[0].course || ''));
    }
  }, [visibleLearningRows, learningFilters.status, selectedCourseId]);
  useEffect(() => {
    const maxIndex = Math.max(forYouLiveMaxIndex, forYouInternshipMaxIndex);
    if (forYouSpotlightIndex > maxIndex) {
      setForYouSpotlightIndex(0);
    }
  }, [forYouInternshipMaxIndex, forYouLiveMaxIndex, forYouSpotlightIndex]);
  const studioLessonRows = useMemo(() => {
    const now = new Date();
    const rows = (learnState.outline || []).flatMap((section) => (section.lessons || []).map((lesson) => {
      const releaseDate = lesson.releaseAt ? new Date(lesson.releaseAt) : null;
      const isScheduled = !!(releaseDate && !Number.isNaN(releaseDate.getTime()) && releaseDate > now);
      return {
        ...lesson,
        sectionTitle: section.title,
        isScheduled,
        releaseStatus: !lesson.isPublished ? 'draft' : (isScheduled ? 'scheduled' : 'live'),
      };
    }));
    const search = String(studioLessonFilters.search || '').trim().toLowerCase();
    const release = String(studioLessonFilters.release || 'all').trim().toLowerCase();
    return rows
      .filter((lesson) => {
        const matchesSearch = !search || [
          lesson.title || '',
          lesson.sectionTitle || '',
          lesson.lessonType || '',
          lesson.summary || '',
        ].join(' ').toLowerCase().includes(search);
        const matchesRelease = release === 'all' || lesson.releaseStatus === release;
        return matchesSearch && matchesRelease;
      })
      .sort((a, b) => {
        if (a.releaseStatus !== b.releaseStatus) return String(a.releaseStatus).localeCompare(String(b.releaseStatus));
        return String(a.title || '').localeCompare(String(b.title || ''));
      });
  }, [learnState.outline, studioLessonFilters]);
  const planEditorSummary = useMemo(() => {
    const rules = planForm.rules || [];
    const categories = rules.map((rule) => String(rule.category || '').trim()).filter(Boolean);
    const fullCategoryRules = rules.filter((rule) => rule.category && rule.allowAllCourses !== false).length;
    const scopedCourseCount = rules.reduce((sum, rule) => sum + (rule.allowAllCourses !== false ? 0 : (rule.courseIds || []).length), 0);
    const basePrice = Math.max(0, Number(planForm.price || 0));
    const salePrice = Math.max(0, Number(planForm.salePrice || 0));
    const effectivePrice = basePrice <= 0 ? 0 : (salePrice > 0 ? Math.min(salePrice, basePrice) : basePrice);
    return {
      categoryCount: new Set(categories).size,
      ruleCount: rules.length,
      fullCategoryRules,
      scopedCourseCount,
      effectivePrice,
      basePrice,
      discountAmount: salePrice > 0 && basePrice > salePrice ? (basePrice - salePrice) : 0,
      anyCourseLimit: Number(planForm.quotas?.anyCourseLimit || 0),
      liveCourseLimit: Number(planForm.quotas?.liveCourseLimit || 0),
      onDemandCourseLimit: Number(planForm.quotas?.onDemandCourseLimit || 0),
      unlimitedOnDemand: !!planForm.quotas?.unlimitedOnDemand,
      planWindowLabel: formatPlanWindowLabel(planForm.validFrom, planForm.validTo),
      expiryDateLabel: fmtDate(planForm.expiryDate),
    };
  }, [planForm]);

  const promotionScopedCourses = useMemo(() => {
    const selectedCategories = new Set((promotionForm.categories || []).map((item) => String(item || '').trim()).filter(Boolean));
    if (!selectedCategories.size) return membershipCourses;
    return membershipCourses.filter((course) => selectedCategories.has(String(course.category || '').trim()));
  }, [promotionForm.categories, membershipCourses]);

  useEffect(() => {
    if (!planForm.validFrom) {
      if (planForm.validTo) {
        setPlanForm((prev) => ({ ...prev, validTo: '' }));
      }
      return;
    }
    const computedValidTo = addDaysToIsoDate(planForm.validFrom, planForm.durationDays);
    if (computedValidTo && computedValidTo !== planForm.validTo) {
      setPlanForm((prev) => ({ ...prev, validTo: computedValidTo }));
    }
  }, [planForm.validFrom, planForm.durationDays, planForm.validTo]);

  const refreshCore = useCallback(async () => {
    if (authLoading || !user?._id) {
      return;
    }
    setLoading(true);
    try {
      if (!canWrite) {
        const baseRequests = [
          api.get('/training/memberships/my-learning').catch(() => ({ data: { data: [] } })),
          api.get('/training/resumes/jobs').catch(() => ({ data: { data: [] } })),
        ];
        const baseResults = await Promise.all(baseRequests);
        const rawLearning = baseResults[0]?.data?.records || baseResults[0]?.data?.data || [];
        const nextJobs = baseResults[1]?.data?.records || baseResults[1]?.data?.data || [];
        let nextLearning = rawLearning.map((row) => normalizeLearningRow(row));
        console.info('[lms][student-refresh][base]', {
          tab: studentRouteTab || 'dashboard',
          learningCount: rawLearning.length,
          learningCourses: rawLearning.map((row) => row?.course?.courseName || row?.course?.courseCode || row?.course?._id || row?.course).filter(Boolean),
        });
        setStudentJobs(nextJobs);

        const learningModeCounts = rawLearning.reduce((acc, row) => {
          const mode = inferCourseMode(row?.course || {});
          acc[mode] = (acc[mode] || 0) + 1;
          return acc;
        }, { on_demand: 0, live: 0 });
        const hasOnDemandLearning = Number(learningModeCounts.on_demand || 0) > 0;
        const shouldLoadMembershipData = hasOnDemandLearning || isCollegePortalStudent || [
          'my-subscription',
          'subscription-details',
          'saved-courses',
          'billing',
          'downloads',
          'share-programs',
          'help-center',
          'advisor-contact',
          'career-quiz',
          'checkout',
          'recommended',
          'for-you',
          'dashboard',
        ].includes(String(studentRouteTab || ''));

        let nextCatalog = [];
        if (shouldLoadMembershipData) {
          const membershipResults = await Promise.all([
            api.get('/training/memberships/catalog').catch(() => ({ data: { data: [] } })),
            api.get('/training/memberships/plans', { params: { includeUnavailable: 1 } }).catch(() => ({ data: { data: [] } })),
            api.get('/training/memberships/my-subscriptions').catch(() => ({ data: { data: [] } })),
            api.get('/training/memberships/my-orders').catch(() => ({ data: { data: [] } })),
            api.get('/training/memberships/checkout/providers').catch(() => ({ data: { data: { defaultProvider: 'test', providers: [] } } })),
            api.get('/training/memberships/coupons').catch(() => ({ data: { data: [] } })),
            api.get('/training/promotions').catch(() => ({ data: { data: [] } })),
          ]);
          nextCatalog = (membershipResults[0]?.data?.records || membershipResults[0]?.data?.data || [])
            .map((course) => normalizeCourseMedia(course));
          const catalogMap = new Map(nextCatalog.map((course) => [String(course._id), course]));
          nextLearning = rawLearning.map((row) => normalizeLearningRow(row, catalogMap));
          console.info('[lms][student-refresh][membership]', {
            catalogCount: nextCatalog.length,
            accessibleCatalogCourses: nextCatalog
              .filter((course) => course?.access?.allowed)
              .map((course) => course.courseName || course.courseCode || course._id),
            learningCount: nextLearning.length,
          });
          setCatalog(nextCatalog);
          setLearning(nextLearning);
          setPlans(membershipResults[1]?.data?.records || membershipResults[1]?.data?.data || []);
          setSubscriptions(membershipResults[2]?.data?.records || membershipResults[2]?.data?.data || []);
          setOrders(membershipResults[3]?.data?.records || membershipResults[3]?.data?.data || []);
          setPaymentProviders(membershipResults[4]?.data?.data || { defaultProvider: 'test', providers: [] });
          setCoupons(membershipResults[5]?.data?.records || membershipResults[5]?.data?.data || []);
          setPromotions(membershipResults[6]?.data?.records || membershipResults[6]?.data?.data || []); 
        } else {
          setCatalog([]);
          setLearning(nextLearning);
          setPlans([]);
          setSubscriptions([]);
          setOrders([]);
          setCoupons([]);
          setPromotions([]);
          setPaymentProviders({ defaultProvider: 'test', providers: [] });
        }

        const fallbackAccessibleCourseId = nextCatalog.find((course) => course?.access?.allowed)?._id || '';
        const preferredCourseId = selectedCourseId || nextLearning[0]?.course?._id || nextLearning[0]?.course || fallbackAccessibleCourseId;
        if (!selectedCourseId && preferredCourseId) setSelectedCourseId(String(preferredCourseId));
        setLoading(false);
        return;
      }

      const requests = [
        api.get('/training/memberships/catalog'),
        api.get('/training/memberships/plans', { params: { admin: canWrite ? 1 : undefined } }),
        api.get('/training/memberships/my-subscriptions'),
        api.get(canWrite ? '/training/memberships/orders' : '/training/memberships/my-orders', { params: canWrite ? orderFilters : undefined }),
        api.get('/training/memberships/my-learning'),
        api.get('/training/memberships/checkout/providers'),
        api.get('/training/memberships/coupons').catch(() => ({ data: { data: [] } })),
        api.get('/training/promotions').catch(() => ({ data: { data: [] } })),
        api.get('/training/resumes/jobs').catch(() => ({ data: { data: [] } })),
      ];
      if (canWrite) {
        requests.push(
          api.get('/training/memberships/renewals', { params: renewalFilters }),
          api.get('/training/courses', { params: { limit: 200 } }),
          api.get('/training/memberships/analytics', { params: analyticsRange }),
          api.get('/training/college-access/partner-colleges').catch(() => ({ data: { data: [] } })),
          api.get('/training/college-access/authorizations/pending').catch(() => ({ data: { data: [] } })),
          api.get('/training/college-access/summary').catch(() => ({ data: { data: {} } })),
        );
        if (SHOW_LMS_MIGRATION_TOOLS) {
          requests.push(api.get('/training/course-content/lms-migration-status'));
        }
      }
      const results = await Promise.all(requests);
      const nextCatalog = (results[0]?.data?.records || results[0]?.data?.data || [])
        .map((course) => normalizeCourseMedia(course));
      setPlans(results[1]?.data?.records || results[1]?.data?.data || []);
      setSubscriptions(results[2]?.data?.records || results[2]?.data?.data || []);
      setOrders(results[3]?.data?.records || results[3]?.data?.data || []);
      const rawLearning = results[4]?.data?.records || results[4]?.data?.data || [];
      const catalogMap = new Map(nextCatalog.map((course) => [String(course._id), course]));
      const nextLearning = rawLearning.map((row) => normalizeLearningRow(row, catalogMap));
      setCatalog(nextCatalog);
      setLearning(nextLearning);
      setPaymentProviders(results[5]?.data?.data || { defaultProvider: 'test', providers: [] });
      setCoupons(results[6]?.data?.records || results[6]?.data?.data || []);
      setPromotions(results[7]?.data?.records || results[7]?.data?.data || []);
      setStudentJobs(results[8]?.data?.records || results[8]?.data?.data || []);
      if (canWrite) {
        setRenewals(results[9]?.data?.records || results[9]?.data?.data || []);
        setCourses(results[10]?.data?.records || results[10]?.data?.data || []);
        setAnalytics(results[11]?.data?.data || { overview: {}, orderStatus: [], topPlans: [], quotaScopeBreakdown: [], quotaPlanUsage: [], courseEngagement: [], trendDays: [], range: {} });
        setPartnerColleges(results[12]?.data?.records || results[12]?.data?.data || []);
        setPendingCollegeAuthorizations(results[13]?.data?.records || results[13]?.data?.data || []);
        setCollegeSummary(results[14]?.data?.data || { collegeCount: 0, pendingCount: 0, authorizedCount: 0, rejectedCount: 0, activeSubscriptions: 0 });
        const migrationRows = SHOW_LMS_MIGRATION_TOOLS ? (results[15]?.data?.data || results[15]?.data?.records || []) : [];
        setMigrationStatus(Object.fromEntries((migrationRows || []).map((row) => [String(row.courseId), row])));
      }
      const preferredCourseId = selectedCourseId || nextLearning[0]?.course?._id || nextLearning[0]?.course;
      if (!selectedCourseId && preferredCourseId) setSelectedCourseId(String(preferredCourseId));
    } catch (err) {
      notify(err.response?.data?.message || 'Could not load on-demand learning data.', 'danger');
    }
    setLoading(false);
  }, [analyticsRange, authLoading, canWrite, isCollegePortalStudent, notify, orderFilters, renewalFilters, selectedCourseId, studentRouteTab, user?._id]);

  const studentDashboardJobs = useMemo(
    () => (studentJobs || []).filter((job) => job?.isActive !== false).slice(0, 4),
    [studentJobs]
  );

  const loadOutline = useCallback(async (courseId) => {
    if (!courseId) {
      setLearnState({ course: null, summary: null, outline: [] });
      setSelectedLessonId('');
      return;
    }
    try {
      const buildStudentCourseContentOutline = async (resolvedCourseId, baseCourse = null, baseSummary = null) => {
        const curriculumResponse = await api.get(`/training/course-content/curriculum/${resolvedCourseId}`);
        const curriculum = curriculumResponse.data?.data?.curriculum || [];
        const topicProgressMap = curriculumResponse.data?.data?.topicProgress || {};
        const topicRows = curriculum.flatMap((moduleRow) => (moduleRow.topics || []).map((topicRow) => ({
          topic: topicRow,
          module: moduleRow,
        })));
        const topicItemResponses = await Promise.all(
          topicRows.map(async ({ topic, module }) => {
            try {
              const itemResponse = await api.get(`/training/course-content/topic-items/${topic._id}`);
              return {
                topic,
                module,
                items: itemResponse.data?.data?.items || [],
              };
            } catch (_) {
              return { topic, module, items: [] };
            }
          })
        );
        const outlineRows = topicItemResponses.map(({ topic, module, items }) => {
          const topicProgress = topicProgressMap[String(topic._id)] || null;
          return {
            _id: topic._id,
            tenantId: topic.tenantId,
            courseId: topic.courseId,
            title: topic.title,
            description: topic.description || '',
            order: Number(topic.order || 0),
            isPublished: true,
            createdAt: topic.createdAt,
            updatedAt: topic.updatedAt,
            moduleId: topic.moduleId,
            moduleTitle: module?.title || '',
            sourceType: 'course_content',
            lessons: (items || []).map((item) => ({
              _id: item._id,
              tenantId: item.tenantId,
              courseId: item.courseId,
              sectionId: topic._id,
              title: item.title,
              summary: item.description || '',
              lessonType: inferStudentLessonTypeFromItem(item),
              order: Number(item.order || 0),
              isPublished: item.status === 'approved' && item.isActive !== false,
              durationSeconds: Number(item?.lmsLesson?.durationSeconds || 0),
              thumbnailUrl: withAuthToken(String(item?.lmsLesson?.thumbnailUrl || '').trim()),
              uploadedVideoUrl: item.filePath ? withAuthToken(`/media/course-content/${String(item.filePath).replace(/\\/g, '/').replace(/^uploads\/course-content\//, '')}`) : '',
              embeddedVideoUrl: String(item?.lmsLesson?.embeddedVideoUrl || '').trim(),
              imageUrl: withAuthToken(String(item?.lmsLesson?.imageUrl || item?.lmsLesson?.promoImageUrl || '').trim()),
              htmlContent: buildStudentHtmlContent(item),
              resourceUrl: item.externalUrl ? withAuthToken(item.externalUrl) : (item.filePath ? withAuthToken(`/media/course-content/${String(item.filePath).replace(/\\/g, '/').replace(/^uploads\/course-content\//, '')}`) : ''),
              quizConfig: {
                passPercent: Math.max(0, Math.min(100, Number(item?.lmsLesson?.passPercent || 60))),
                questions: Array.isArray(item?.lmsLesson?.quizQuestions) ? item.lmsLesson.quizQuestions : [],
              },
              quizId: item?.lmsLesson?.quizRef || null,
              quizRequirement: item?.lmsLesson?.quizRequirement || 'optional',
              requiresLessonIds: Array.isArray(item?.lmsLesson?.requiresItemIds) ? item.lmsLesson.requiresItemIds : [],
              releaseAt: item?.lmsLesson?.releaseAt || null,
              sourceType: 'course_content',
              sourceItemType: item.type,
              topicTitle: topic.title || '',
              progress: topicProgress?.isCompleted
                ? {
                    status: 'completed',
                    completedAt: topicProgress.completedAt || topicProgress.updatedAt || null,
                  }
                : null,
              access: { isLocked: false, requiredIds: [], explicitRequiredIds: [], gateRequiredIds: [], missingRequiredIds: [] },
            })),
          };
        });
        const totalLessons = outlineRows.reduce((sum, section) => sum + Number((section.lessons || []).length), 0);
        const completedLessons = outlineRows.reduce(
          (sum, section) => sum + (section.lessons || []).filter((lesson) => lesson.progress?.status === 'completed').length,
          0
        );
        const watchedSeconds = outlineRows.reduce(
          (sum, section) => sum + (section.lessons || [])
            .filter((lesson) => lesson.progress?.status === 'completed')
            .reduce((lessonSum, lesson) => lessonSum + Number(lesson.durationSeconds || 0), 0),
          0
        );
        const totalDurationSeconds = outlineRows.reduce(
          (sum, section) => sum + (section.lessons || []).reduce(
            (lessonSum, lesson) => lessonSum + Math.max(0, Number(lesson.durationSeconds || 0)),
            0
          ),
          0
        );
        const nextLesson = outlineRows.flatMap((section) => section.lessons || []).find((lesson) => lesson.progress?.status !== 'completed') || null;
        const computedSummary = {
          totalLessons,
          completedLessons,
          inProgressLessons: completedLessons > 0 && completedLessons < totalLessons ? 1 : 0,
          pendingLessons: Math.max(0, totalLessons - completedLessons),
          lockedLessons: 0,
          accessibleLessons: totalLessons,
          gateLessons: 0,
          averageScore: 0,
          nextLessonId: nextLesson?._id || outlineRows.flatMap((section) => section.lessons || [])[0]?._id || null,
          nextLessonTitle: nextLesson?.title || outlineRows.flatMap((section) => section.lessons || [])[0]?.title || '',
          completionPercent: totalLessons ? Math.round((completedLessons / totalLessons) * 100) : 0,
          sectionProgress: [],
          consistency: {},
          totalDurationSeconds,
          watchedSeconds,
          remainingDurationSeconds: Math.max(0, totalDurationSeconds - watchedSeconds),
        };
        return {
          course: baseCourse,
          summary: {
            ...computedSummary,
            ...(baseSummary || {}),
            totalLessons: totalLessons || Number(baseSummary?.totalLessons || 0),
            completedLessons: Math.max(completedLessons, Number(baseSummary?.completedLessons || 0)),
            inProgressLessons: Math.max(computedSummary.inProgressLessons, Number(baseSummary?.inProgressLessons || 0)),
            pendingLessons: totalLessons
              ? Math.max(0, totalLessons - Math.max(completedLessons, Number(baseSummary?.completedLessons || 0)))
              : Number(baseSummary?.pendingLessons || 0),
            completionPercent: totalLessons
              ? Math.round((Math.max(completedLessons, Number(baseSummary?.completedLessons || 0)) / totalLessons) * 100)
              : Number(baseSummary?.completionPercent || 0),
            nextLessonId: computedSummary.nextLessonId || baseSummary?.nextLessonId || null,
            nextLessonTitle: computedSummary.nextLessonTitle || baseSummary?.nextLessonTitle || '',
            watchedSeconds: Math.max(watchedSeconds, Number(baseSummary?.watchedSeconds || 0)),
            totalDurationSeconds: totalDurationSeconds || Number(baseSummary?.totalDurationSeconds || 0),
            remainingDurationSeconds: Math.max(
              0,
              (totalDurationSeconds || Number(baseSummary?.totalDurationSeconds || 0))
                - Math.max(watchedSeconds, Number(baseSummary?.watchedSeconds || 0))
            ),
          },
          outline: outlineRows,
        };
      };
      const route = canWrite
        ? `/training/on-demand/courses/${courseId}/outline`
        : `/training/on-demand/courses/${courseId}/learn`;
      const response = await api.get(route);
      const data = response.data?.data || {};
      let outline = data.outline || [];
      let summary = data.summary || null;
      let course = data.course || null;
      const lessonCount = outline.reduce((sum, section) => sum + Number((section?.lessons || []).length), 0);
      const responseCourseMode = inferCourseMode(course || {});
      const responseRenderableLessonCount = countRenderableOutlineLessons(outline);
      if (!canWrite && (lessonCount === 0 || responseCourseMode !== 'on_demand')) {
        const fallback = await buildStudentCourseContentOutline(courseId, course, summary);
        const fallbackOutline = fallback.outline || [];
        const fallbackRenderableLessonCount = countRenderableOutlineLessons(fallbackOutline);
        const shouldPreferFallback = lessonCount === 0
          || fallbackRenderableLessonCount > responseRenderableLessonCount;
        if (shouldPreferFallback) {
          outline = fallbackOutline;
          summary = fallback.summary || summary;
          course = fallback.course || course;
        }
      }
      setLearnState({
        course,
        summary,
        outline,
      });
      const firstLesson = outline.flatMap((section) => section.lessons || [])[0];
      const preferredLesson = (!canWrite && summary?.nextLessonId)
        ? outline.flatMap((section) => section.lessons || []).find((item) => String(item._id) === String(summary.nextLessonId))
        : null;
      const fallbackLessonId = preferredLesson ? String(preferredLesson._id) : (firstLesson ? String(firstLesson._id) : '');
      const fallbackSectionId = preferredLesson?.sectionId
        ? String(preferredLesson.sectionId)
        : firstLesson?.sectionId
          ? String(firstLesson.sectionId)
          : (outline[0]?._id ? String(outline[0]._id) : '');
      setSelectedOutlineTopicId(fallbackSectionId);
      if (!selectedLessonId) {
        setSelectedLessonId(fallbackLessonId);
      }
      if (selectedLessonId && !outline.flatMap((section) => section.lessons || []).some((item) => String(item._id) === String(selectedLessonId))) {
        setSelectedLessonId(fallbackLessonId);
      }
    } catch (err) {
      setLearnState({ course: null, summary: null, outline: [] });
      setSelectedLessonId('');
      if (!isStudent || activeTab === 'my-courses') {
        notify(err.response?.data?.message || 'Could not load course outline.', 'danger');
      }
    }
  }, [activeTab, canWrite, isStudent, notify, selectedLessonId]);

  const loadCourseDiscussions = useCallback(async (courseId) => {
    if (!courseId || canWrite) {
      setCourseDiscussions([]);
      return;
    }
    setCourseDiscussionsLoading(true);
    try {
      const response = await api.get('/training/course-discussions', { params: { courseId } });
      setCourseDiscussions(response.data?.records || response.data?.data || []);
    } catch (err) {
      setCourseDiscussions([]);
      notify(err.response?.data?.message || 'Could not load course questions.', 'danger');
    }
    setCourseDiscussionsLoading(false);
  }, [canWrite, notify]);

  useEffect(() => {
    if (authLoading || !user?._id) return;
    refreshCore();
  }, [authLoading, refreshCore, user?._id]);

  useEffect(() => {
    if (!pendingCollegeAuthorizations.length) {
      if (selectedCollegeAuthorizationId) setSelectedCollegeAuthorizationId('');
      return;
    }
    if (!selectedCollegeAuthorizationId || !pendingCollegeAuthorizations.some((row) => String(row._id) === String(selectedCollegeAuthorizationId))) {
      setSelectedCollegeAuthorizationId(String(pendingCollegeAuthorizations[0]._id));
    }
  }, [pendingCollegeAuthorizations, selectedCollegeAuthorizationId]);

  useEffect(() => {
    setCollegeReviewNotes(String(selectedCollegeAuthorization?.collegeAccess?.reviewNotes || ''));
  }, [selectedCollegeAuthorization]);

  useEffect(() => {
    if (selectedCourseId && (canWrite || showCourseWorkspaceModal)) loadOutline(selectedCourseId);
  }, [canWrite, loadOutline, selectedCourseId, showCourseWorkspaceModal]);

  useEffect(() => {
    const hasEnrollment = learning.some((row) => (
      !row.isVirtualEnrollment
      && String(row.course?._id || row.course) === String(selectedCourseId)
    ));
    if (selectedCourseId && !canWrite && showCourseWorkspaceModal && hasEnrollment) {
      loadCourseDiscussions(selectedCourseId);
    }
  }, [canWrite, learning, loadCourseDiscussions, selectedCourseId, showCourseWorkspaceModal]);

  useEffect(() => {
    setCourseWorkspaceTab('content');
  }, [selectedCourseId]);

  useEffect(() => {
    if (!showSelectedCourseAssessments || !selectedLearningCourse?.course?._id) {
      setStudentCourseAssessments([]);
      setStudentCourseAssessmentsLoading(false);
      return;
    }
    let cancelled = false;
    const fetchCourseAssessments = async () => {
      setStudentCourseAssessmentsLoading(true);
      try {
        const response = await api.get('/training/assessments', {
          params: { courseId: selectedLearningCourse.course._id, limit: 100 },
        });
        if (cancelled) return;
        const payload = response.data;
        const rows = Array.isArray(payload?.records)
          ? payload.records
          : Array.isArray(payload?.data?.rows)
            ? payload.data.rows
            : Array.isArray(payload?.data)
              ? payload.data
              : Array.isArray(payload)
                ? payload
                : [];
        console.log('[student-course-assessments] loaded', {
          courseId: selectedLearningCourse.course._id,
          count: rows.length,
          rows: rows.map((row) => ({
            id: row?._id,
            title: row?.title,
            status: row?.status,
          })),
        });
        setStudentCourseAssessments(rows);
      } catch (err) {
        if (!cancelled) {
          setStudentCourseAssessments([]);
          notify(err.response?.data?.message || 'Could not load course assessments.', 'danger');
        }
      }
      if (!cancelled) setStudentCourseAssessmentsLoading(false);
    };
    fetchCourseAssessments();
    return () => {
      cancelled = true;
    };
  }, [notify, selectedLearningCourse, showSelectedCourseAssessments]);

  useEffect(() => {
    setQuizAnswers({});
  }, [selectedLessonId]);

  useEffect(() => {
    if (canWrite) return;
    if (!studentModuleGroups.length) {
      if (expandedLearningModuleId) setExpandedLearningModuleId('');
      return;
    }
    if (!studentModuleGroups.some((group) => String(group.key) === String(expandedLearningModuleId))) {
      setExpandedLearningModuleId(String(studentModuleGroups[0].key));
    }
  }, [canWrite, expandedLearningModuleId, studentModuleGroups]);

  useEffect(() => {
    if (canWrite || !selectedLessonId || !studentModuleGroups.length) return;
    const ownerModule = studentModuleGroups.find((group) =>
      (group.sections || []).some((section) =>
        (section.lessons || []).some((lesson) => String(lesson._id) === String(selectedLessonId))
      )
    );
    if (ownerModule) {
      setExpandedLearningModuleId(String(ownerModule.key));
    }
  }, [canWrite, selectedLessonId, studentModuleGroups]);

  useEffect(() => {
    if (canWrite || !selectedLessonId || !learnState.outline.length) return;
    const ownerSection = learnState.outline.find((section) =>
      (section.lessons || []).some((lesson) => String(lesson._id) === String(selectedLessonId))
    );
    if (ownerSection) {
      setSelectedOutlineTopicId(String(ownerSection._id));
    }
  }, [canWrite, learnState.outline, selectedLessonId]);

  useEffect(() => {
    if (canWrite) return;
    localStorage.setItem('student_lms_has_subscription', studentActiveSubscription ? '1' : '0');
    localStorage.setItem('student_lms_has_orders', orders.length ? '1' : '0');
    localStorage.setItem('student_lms_has_subscription_menu', studentHasSubscriptionTab ? '1' : '0');
    localStorage.setItem('student_lms_mobile_shell_on_demand', studentIsOnDemandPrimary ? '1' : '0');
    window.dispatchEvent(new CustomEvent('student-lms-menu-update', {
      detail: {
        hasSubscription: !!studentActiveSubscription,
        hasOrders: orders.length > 0,
        showSubscriptionMenu: !!studentHasSubscriptionTab,
        mobileShellOnDemand: !!studentIsOnDemandPrimary,
      },
    }));
  }, [canWrite, orders.length, studentActiveSubscription, studentHasSubscriptionTab, studentIsOnDemandPrimary]);

  useEffect(() => {
    if (canWrite) return;
    localStorage.setItem('student_lms_wishlist', JSON.stringify(wishlistCourseIds));
  }, [canWrite, wishlistCourseIds]);

  const resetPlanForm = () => {
    setEditingPlanId('');
    setPlanForm(defaultPlan());
    setShowPlanEditor(false);
  };
  const openNewPlanEditor = () => {
    setEditingPlanId('');
    setPlanForm(defaultPlan());
    setShowPlanEditor(true);
  };
  const openEditPlanEditor = (plan) => {
    setEditingPlanId(plan._id);
    setPlanForm({
      ...defaultPlan(),
      ...plan,
      price: plan.price ?? '',
      salePrice: plan.salePrice ?? '',
      durationDays: plan.durationDays ?? 30,
      validFrom: plan.validFrom ? String(plan.validFrom).slice(0, 10) : '',
      validTo: plan.validTo ? String(plan.validTo).slice(0, 10) : '',
      expiryDate: plan.expiryDate ? String(plan.expiryDate).slice(0, 10) : '',
      quotas: {
        anyCourseLimit: plan.quotas?.anyCourseLimit ?? '',
        liveCourseLimit: plan.quotas?.liveCourseLimit ?? '',
        onDemandCourseLimit: plan.quotas?.onDemandCourseLimit ?? '',
        unlimitedOnDemand: !!plan.quotas?.unlimitedOnDemand,
      },
      rules: (plan.rules || []).length ? plan.rules.map((rule) => ({
        title: rule.title || '',
        category: (rule.categories || [])[0] || '',
        allowAllCourses: !(rule.courseIds || []).length,
        courseIds: rule.courseIds || [],
        selectionLimit: rule.selectionLimit ?? '',
        discountType: rule.discountType || 'none',
        discountValue: rule.discountValue ?? '',
        deliveryModes: rule.deliveryModes || [],
        startDate: rule.startDate ? String(rule.startDate).slice(0, 10) : '',
        expiryDate: rule.expiryDate ? String(rule.expiryDate).slice(0, 10) : '',
        priceOverride: rule.priceOverride ?? '',
      })) : defaultPlan().rules,
    });
    setShowPlanEditor(true);
  };

  const setPlanRule = (index, patch) => {
    setPlanForm((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, ruleIndex) => (ruleIndex === index ? { ...rule, ...patch } : rule)),
    }));
  };

  const togglePlanAccordion = (planId) => {
    setExpandedPlanIds((prev) => (
      prev.includes(String(planId))
        ? prev.filter((item) => item !== String(planId))
        : [...prev, String(planId)]
    ));
  };

  const addPlanRule = () => {
    setPlanForm((prev) => ({ ...prev, rules: [...(prev.rules || []), defaultAccessRule()] }));
  };

  const removePlanRule = (index) => {
    setPlanForm((prev) => {
      const nextRules = (prev.rules || []).filter((_, ruleIndex) => ruleIndex !== index);
      return { ...prev, rules: nextRules.length ? nextRules : [defaultAccessRule()] };
    });
  };

  const movePlanRule = (index, direction) => {
    setPlanForm((prev) => {
      const nextRules = [...(prev.rules || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= nextRules.length) return prev;
      const [picked] = nextRules.splice(index, 1);
      nextRules.splice(targetIndex, 0, picked);
      return { ...prev, rules: nextRules };
    });
  };

  const duplicatePlanRule = (index) => {
    setPlanForm((prev) => {
      const nextRules = [...(prev.rules || [])];
      const source = nextRules[index] || defaultAccessRule();
      nextRules.splice(index + 1, 0, {
        ...source,
        courseIds: [...(source.courseIds || [])],
        deliveryModes: [...(source.deliveryModes || [])],
      });
      return { ...prev, rules: nextRules };
    });
  };

  const resetCouponForm = () => {
    setEditingCouponId('');
    setCouponForm(defaultCoupon());
    setShowCouponEditor(false);
  };
  const openNewCouponEditor = () => {
    setEditingCouponId('');
    setCouponForm(defaultCoupon());
    setShowCouponEditor(true);
  };
  const openEditCouponEditor = (coupon) => {
    setEditingCouponId(coupon._id);
    setCouponForm({
      ...defaultCoupon(),
      ...coupon,
      packageIds: coupon.packageIds || [],
      discountValue: coupon.discountValue ?? '',
      maxUses: coupon.maxUses ?? '',
      maxUsesPerUser: coupon.maxUsesPerUser ?? 1,
      validFrom: coupon.validFrom ? String(coupon.validFrom).slice(0, 10) : '',
      validTo: coupon.validTo ? String(coupon.validTo).slice(0, 10) : '',
    });
    setShowCouponEditor(true);
  };

  const openCheckout = (plan) => {
    setCheckoutState({
      plan,
      order: null,
      stage: 'review',
      couponCode: '',
      provider: paymentProviders.defaultProvider || 'test',
    });
    if (canWrite) {
      setTab('checkout');
      return;
    }
    navigate({ pathname: location.pathname, search: 'tab=checkout' }, { replace: false });
  };

  const joinPreviewProgram = async () => {
    if (!programPreviewCourse?._id) return;
    if (programPreviewCourse.access?.allowed || isSelfServeLearningCourse(programPreviewCourse)) {
      const course = programPreviewCourse;
      setProgramPreviewCourse(null);
      const enrolled = await enrollCourse(course);
      if (enrolled) {
        openTab('my-courses');
        openCourseWorkspace(String(course._id));
      }
      return;
    }
    const course = programPreviewCourse;
    setProgramPreviewCourse(null);
    openLeadCapture('course', course, {
      title: 'Join this program',
      subtitle: course.courseName || 'Selected program',
    });
  };

  async function activateFreePlanAndOpenCourses(plan) {
    if (!plan?._id) {
      openTab('my-courses');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/training/memberships/activate-free-plan/${plan._id}`);
      await refreshCore();
      setSelectedPlanScopeId(String(plan._id));
      setLearningFilters((prev) => ({ ...prev, status: 'all' }));
      openTab('my-courses');
    } catch (err) {
      notify(err.response?.data?.message || 'Could not activate the free membership.', 'danger');
    }
    setSaving(false);
  }

  const resetCheckout = () => {
    setCheckoutState({ plan: null, order: null, stage: 'review', couponCode: '', provider: paymentProviders.defaultProvider || 'test' });
  };

  const openRenewalAction = (subscription, action) => {
    const defaultExpiry = (() => {
      const dt = subscription?.expiresAt ? new Date(subscription.expiresAt) : null;
      if (dt && !Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
      const future = new Date(Date.now() + 30 * 86400000);
      return future.toISOString().slice(0, 10);
    })();
    setRenewalActionState({
      show: true,
      subscription,
      action,
      expiresAt: defaultExpiry,
      note: subscription?.notes || '',
    });
  };

  const closeRenewalAction = () => {
    setRenewalActionState({ show: false, subscription: null, action: 'extend', expiresAt: '', note: '' });
  };

  const resetPromotionForm = () => {
    setEditingPromotionId('');
    setPromotionForm(defaultPromotion());
    setShowPromotionEditor(false);
  };
  const openNewPromotionEditor = () => {
    setEditingPromotionId('');
    setPromotionForm(defaultPromotion());
    setShowPromotionEditor(true);
  };
  const openEditPromotionEditor = (promotion) => {
    setEditingPromotionId(promotion._id);
    setPromotionForm({
      ...defaultPromotion(),
      ...promotion,
      categories: promotion.categories || [],
      startsAt: promotion.startsAt ? String(promotion.startsAt).slice(0, 10) : '',
      endsAt: promotion.endsAt ? String(promotion.endsAt).slice(0, 10) : '',
      courseIds: promotion.courseIds || [],
      deliveryModes: promotion.deliveryModes || [],
      liveSession: {
        sessionDate: promotion.liveSession?.sessionDate ? String(promotion.liveSession.sessionDate).slice(0, 10) : '',
        sessionTime: promotion.liveSession?.sessionTime || '',
        meetingUrl: promotion.liveSession?.meetingUrl || '',
        meetingNote: promotion.liveSession?.meetingNote || '',
      },
      audienceRoleKeys: promotion.audienceRoleKeys || [],
    });
    setShowPromotionEditor(true);
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const normalizedRules = (planForm.rules || []).map((rule) => ({
        title: String(rule.title || rule.category || '').trim(),
        categories: rule.category ? [String(rule.category).trim()] : [],
        courseIds: rule.allowAllCourses ? [] : (rule.courseIds || []),
        selectionLimit: Number(rule.selectionLimit || 0),
        discountType: ['percent', 'fixed'].includes(String(rule.discountType || '')) ? String(rule.discountType) : 'none',
        discountValue: Number(rule.discountValue || 0),
        deliveryModes: [],
        startDate: null,
        expiryDate: null,
        priceOverride: null,
      })).filter((rule) => rule.categories.length || rule.courseIds.length);

      if (planForm.validFrom && planForm.validTo && new Date(planForm.validTo) < new Date(planForm.validFrom)) {
        notify('Plan valid-to date cannot be before the valid-from date.', 'danger');
        setSaving(false);
        return;
      }
      if (Number(planForm.price || 0) <= 0 && Number(planForm.salePrice || 0) > 0) {
        notify('Discounted price cannot be greater than zero when membership price is zero.', 'danger');
        setSaving(false);
        return;
      }

      const missingScopedCourses = (planForm.rules || []).find((rule) => (
        rule.category && rule.allowAllCourses === false && !(rule.courseIds || []).length
      ));
      if (missingScopedCourses) {
        notify('Select at least one course when full-category access is disabled.', 'danger');
        setSaving(false);
        return;
      }
      const overLimitedRule = (planForm.rules || []).find((rule) => (
        rule.allowAllCourses === false
        && Number(rule.selectionLimit || 0) > 0
        && Number((rule.courseIds || []).length) > Number(rule.selectionLimit || 0)
      ));
      if (overLimitedRule) {
        notify('Selected courses exceed the limit set inside Included Access.', 'danger');
        setSaving(false);
        return;
      }

      const payload = {
        ...planForm,
        code: String(planForm.code || generatePlanCode(planForm.name)).trim().toUpperCase(),
        price: Math.max(0, Number(planForm.price || 0)),
        salePrice: Number(planForm.price || 0) <= 0 ? 0 : Math.max(0, Math.min(Number(planForm.salePrice || 0), Number(planForm.price || 0))),
        durationDays: Number(planForm.durationDays || 30),
        validFrom: planForm.validFrom || null,
        validTo: planForm.validTo || null,
        expiryDate: planForm.expiryDate || null,
        quotas: {
          anyCourseLimit: Number(planForm.quotas.anyCourseLimit || 0),
          liveCourseLimit: Number(planForm.quotas.liveCourseLimit || 0),
          onDemandCourseLimit: Number(planForm.quotas.onDemandCourseLimit || 0),
          unlimitedOnDemand: !!planForm.quotas.unlimitedOnDemand,
        },
        rules: normalizedRules,
      };
      if (editingPlanId) await api.put(`/training/memberships/plans/${editingPlanId}`, payload);
      else await api.post('/training/memberships/plans', payload);
      notify(`Membership plan ${editingPlanId ? 'updated' : 'created'}.`);
      resetPlanForm();
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not save membership plan.', 'danger');
    }
    setSaving(false);
  };

  const deletePlan = async (plan) => {
    if (!plan?._id) return;
    if (!window.confirm(`Delete membership "${plan.name || 'this membership'}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/training/memberships/plans/${plan._id}`);
      notify('Membership deleted.');
      if (editingPlanId && String(editingPlanId) === String(plan._id)) resetPlanForm();
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not delete membership.', 'danger');
    }
    setSaving(false);
  };

  const saveCoupon = async () => {
    setSaving(true);
    try {
      const payload = {
        ...couponForm,
        discountValue: Number(couponForm.discountValue || 0),
        maxUses: Number(couponForm.maxUses || 0),
        maxUsesPerUser: Number(couponForm.maxUsesPerUser || 1),
        isActive: false,
      };
      if (editingCouponId) await api.put(`/training/memberships/coupons/${editingCouponId}`, payload);
      else await api.post('/training/memberships/coupons', payload);
      notify(`Coupon ${editingCouponId ? 'updated' : 'created'}.`);
      resetCouponForm();
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not save coupon.', 'danger');
    }
    setSaving(false);
  };

  const approveCoupon = async (coupon, action) => {
    if (!coupon?._id) return;
    if (!window.confirm(`${action === 'approve' ? 'Approve' : 'Reject'} coupon "${coupon.code || 'this coupon'}"?`)) return;
    setSaving(true);
    try {
      await api.post(`/training/memberships/coupons/${coupon._id}/approval`, { action });
      notify(`Coupon ${action}d successfully.`);
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || `Could not ${action} coupon.`, 'danger');
    }
    setSaving(false);
  };

  const savePromotion = async () => {
    setSaving(true);
    try {
      const payload = {
        ...promotionForm,
        categories: (promotionForm.categories || []).map((item) => String(item || '').trim()).filter(Boolean),
      };
      if (editingPromotionId) await api.put(`/training/memberships/access-promotions/${editingPromotionId}`, payload);
      else await api.post('/training/memberships/access-promotions', payload);
      notify(`Access promotion ${editingPromotionId ? 'updated' : 'created'}.`);
      resetPromotionForm();
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not save access promotion.', 'danger');
    }
    setSaving(false);
  };

  const resetPartnerCollegeForm = useCallback(() => {
    setEditingPartnerCollegeId('');
    setPartnerCollegeForm(defaultPartnerCollege());
    setPartnerCollegeUpload({ logo: null, qrCode: null });
  }, []);

  const editPartnerCollege = useCallback((record) => {
    if (!record?._id) return;
    setEditingPartnerCollegeId(String(record._id));
    setPartnerCollegeForm({
      name: record.name || '',
      code: record.code || '',
      uniqueAccessCode: record.uniqueAccessCode || '',
      description: record.description || '',
      websiteUrl: record.websiteUrl || '',
      contactNumber: record.contactNumber || '',
      supportMessage: record.supportMessage || '',
      logoUrl: record.logoUrl || '',
      qrCodeUrl: record.qrCodeUrl || '',
      autoApproveEnabled: record.autoApproveEnabled !== false,
      acceptedYears: Array.isArray(record.acceptedYears) ? record.acceptedYears.join(', ') : '',
    });
    setPartnerCollegeUpload({ logo: null, qrCode: null });
    setShowPartnerCollegeEditor(true);
  }, []);

  const savePartnerCollege = async () => {
    if (!String(partnerCollegeForm.name || '').trim()) {
      notify('College name is required.', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = new FormData();
      Object.entries(partnerCollegeForm).forEach(([key, value]) => {
        payload.append(key, typeof value === 'boolean' ? String(value) : (value || ''));
      });
      if (partnerCollegeUpload.logo) payload.append('logo', partnerCollegeUpload.logo);
      if (editingPartnerCollegeId) {
        await api.put(`/training/college-access/partner-colleges/${editingPartnerCollegeId}`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await uploadFile('/training/college-access/partner-colleges', payload);
      }
      notify(`MoU signed college ${editingPartnerCollegeId ? 'updated' : 'created'}.`);
      resetPartnerCollegeForm();
      setShowPartnerCollegeEditor(false);
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not save partner college.', 'danger');
    }
    setSaving(false);
  };

  const copyPartnerCollegeValue = useCallback(async (value, successMessage) => {
    const text = String(value || '').trim();
    if (!text) {
      notify('Nothing to copy yet.', 'warning');
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const temp = document.createElement('textarea');
        temp.value = text;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      notify(successMessage || 'Copied.');
    } catch (_) {
      notify('Could not copy right now.', 'danger');
    }
  }, [notify]);

  const updateCollegeAuthorization = async (userId, action) => {
    if (!userId) return;
    const reviewNotes = String(collegeReviewNotes || '').trim();
    if (action === 'reject' && !reviewNotes) {
      notify('Enter a reject reason before rejecting this registration.', 'warning');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/training/college-access/authorizations/${userId}/${action}`, { reviewNotes });
      notify(`College authorization ${action === 'approve' ? 'approved' : 'rejected'}.`);
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || `Could not ${action} this authorization.`, 'danger');
    }
    setSaving(false);
  };

  const deleteCollegeAuthorization = async (userId) => {
    if (!userId) return;
    const shouldDelete = window.confirm('Delete this pending college registration? The student will need to register again.');
    if (!shouldDelete) return;
    setSaving(true);
    try {
      await api.delete(`/training/college-access/authorizations/${userId}`);
      notify('College registration deleted.');
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not delete this college registration.', 'danger');
    }
    setSaving(false);
  };

  const saveSection = async () => {
    if (!selectedCourseId) return notify('Select an on-demand or hybrid course first.', 'warning');
    setSaving(true);
    try {
      if (editingSectionId) await api.put(`/training/on-demand/sections/${editingSectionId}`, sectionForm);
      else await api.post(`/training/on-demand/courses/${selectedCourseId}/sections`, sectionForm);
      notify(`Section ${editingSectionId ? 'updated' : 'created'}.`);
      setEditingSectionId('');
      setSectionForm(defaultSection());
      await loadOutline(selectedCourseId);
    } catch (err) {
      notify(err.response?.data?.message || `Could not ${editingSectionId ? 'update' : 'create'} section.`, 'danger');
    }
    setSaving(false);
  };

  const editSection = useCallback((section) => {
    if (!section?._id) return;
    setEditingSectionId(String(section._id));
    setSectionForm({
      ...defaultSection(),
      title: section.title || '',
      description: section.description || '',
      isPublished: section.isPublished !== false,
    });
  }, []);

  const resetSectionForm = useCallback(() => {
    setEditingSectionId('');
    setSectionForm(defaultSection());
  }, []);

  const deleteSection = async (section) => {
    if (!section?._id) return;
    if (!window.confirm(`Delete section "${section.title || 'Untitled'}" and its lessons?`)) return;
    setSaving(true);
    try {
      await api.delete(`/training/on-demand/sections/${section._id}`);
      notify('Section deleted.');
      if (String(editingSectionId) === String(section._id)) resetSectionForm();
      await loadOutline(selectedCourseId);
    } catch (err) {
      notify(err.response?.data?.message || 'Could not delete section.', 'danger');
    }
    setSaving(false);
  };

  const moveSection = async (sectionId, direction) => {
    const sections = learnState.outline || [];
    const index = sections.findIndex((section) => String(section._id) === String(sectionId));
    if (index < 0) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sections.length) return;
    const reordered = [...sections];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setSaving(true);
    try {
      await Promise.all(reordered.map((section, order) => api.put(`/training/on-demand/sections/${section._id}`, {
        title: section.title,
        description: section.description,
        isPublished: section.isPublished !== false,
        order,
      })));
      notify('Section order updated.');
      await loadOutline(selectedCourseId);
    } catch (err) {
      notify(err.response?.data?.message || 'Could not reorder sections.', 'danger');
    }
    setSaving(false);
  };

  const resetLessonForm = useCallback(() => {
    setEditingLessonId('');
    setLessonForm(defaultLesson());
    setLessonUploadState({ thumbnail: false, video: false, image: false });
  }, []);

  const uploadLessonAsset = useCallback(async (kind, file) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append('file', file);
    const route = kind === 'video' ? '/training/on-demand/upload-video' : '/training/on-demand/upload-thumbnail';
    const stateKey = kind === 'video' ? 'video' : kind;
    setLessonUploadState((prev) => ({ ...prev, [stateKey]: true }));
    try {
      const response = await uploadFile(route, formData);
      const url = response?.url || response?.data?.url || '';
      if (!url) throw new Error('Upload response missing file URL.');
      return url;
    } finally {
      setLessonUploadState((prev) => ({ ...prev, [stateKey]: false }));
    }
  }, []);

  const saveLesson = async () => {
    if (!selectedCourseId) return notify('Select an on-demand or hybrid course first.', 'warning');
    setSaving(true);
    try {
      const payload = {
        ...lessonForm,
        durationSeconds: Number(lessonForm.durationSeconds || 0),
        quizConfig: {
          passPercent: 60,
          questions: Array.isArray(lessonForm.quizConfig?.questions)
            ? lessonForm.quizConfig.questions.map((question, index) => ({
              question: String(question.question || '').trim(),
              options: Array.isArray(question.options) ? question.options.map((item) => String(item || '').trim()).filter(Boolean) : [],
              correctOptionIndex: Number.isFinite(Number(question.correctOptionIndex)) ? Number(question.correctOptionIndex) : 0,
              order: index,
            }))
            : [],
        },
      };
      if (editingLessonId) await api.put(`/training/on-demand/lessons/${editingLessonId}`, payload);
      else await api.post(`/training/on-demand/courses/${selectedCourseId}/lessons`, payload);
      notify(`Lesson ${editingLessonId ? 'updated' : 'created'}.`);
      resetLessonForm();
      await loadOutline(selectedCourseId);
    } catch (err) {
      notify(err.response?.data?.message || `Could not ${editingLessonId ? 'update' : 'create'} lesson.`, 'danger');
    }
    setSaving(false);
  };

  const editLesson = useCallback((lesson) => {
    if (!lesson?._id) return;
    setEditingLessonId(String(lesson._id));
    setLessonForm({
      ...defaultLesson(),
      ...lesson,
      sectionId: String(lesson.sectionId || ''),
      durationSeconds: lesson.durationSeconds ?? '',
      thumbnailUrl: lesson.thumbnailUrl || '',
      uploadedVideoUrl: lesson.uploadedVideoUrl || '',
      embeddedVideoUrl: lesson.embeddedVideoUrl || '',
      imageUrl: lesson.imageUrl || '',
      htmlContent: lesson.htmlContent || '',
      resourceUrl: lesson.resourceUrl || '',
      requiresLessonIds: Array.isArray(lesson.requiresLessonIds) ? lesson.requiresLessonIds.map(String) : [],
      quizConfig: {
        passPercent: lesson.quizConfig?.passPercent ?? 60,
        questions: Array.isArray(lesson.quizConfig?.questions) && lesson.quizConfig.questions.length
          ? lesson.quizConfig.questions.map((question) => ({
            question: question.question || '',
            options: Array.isArray(question.options) && question.options.length ? question.options : ['', ''],
            correctOptionIndex: Number.isFinite(Number(question.correctOptionIndex)) ? Number(question.correctOptionIndex) : 0,
          }))
          : [defaultQuizQuestion()],
      },
    });
  }, []);

  const deleteLesson = async (lesson) => {
    if (!lesson?._id) return;
    if (!window.confirm(`Delete lesson "${lesson.title || 'Untitled'}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/training/on-demand/lessons/${lesson._id}`);
      notify('Lesson deleted.');
      if (String(editingLessonId) === String(lesson._id)) resetLessonForm();
      if (String(selectedLessonId) === String(lesson._id)) setSelectedLessonId('');
      await loadOutline(selectedCourseId);
    } catch (err) {
      notify(err.response?.data?.message || 'Could not delete lesson.', 'danger');
    }
    setSaving(false);
  };

  const toggleLessonPublish = async (lesson) => {
    if (!lesson?._id) return;
    setSaving(true);
    try {
      await api.put(`/training/on-demand/lessons/${lesson._id}`, {
        ...lesson,
        isPublished: !lesson.isPublished,
        sectionId: lesson.sectionId,
        requiresLessonIds: Array.isArray(lesson.requiresLessonIds) ? lesson.requiresLessonIds : [],
        releaseAt: lesson.releaseAt ? new Date(lesson.releaseAt).toISOString() : null,
        quizConfig: lesson.quizConfig || { passPercent: 60, questions: [] },
      });
      notify(`Lesson ${lesson.isPublished ? 'unpublished' : 'published'}.`);
      await loadOutline(selectedCourseId);
    } catch (err) {
      notify(err.response?.data?.message || 'Could not update lesson visibility.', 'danger');
    }
    setSaving(false);
  };

  const moveLesson = async (lesson, direction) => {
    if (!lesson?._id || !lesson?.sectionId) return;
    const section = (learnState.outline || []).find((item) => String(item._id) === String(lesson.sectionId));
    const lessons = section?.lessons || [];
    const index = lessons.findIndex((item) => String(item._id) === String(lesson._id));
    if (index < 0) return;
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= lessons.length) return;
    const reordered = [...lessons];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setSaving(true);
    try {
      await Promise.all(reordered.map((item, order) => api.put(`/training/on-demand/lessons/${item._id}`, {
        sectionId: item.sectionId,
        title: item.title,
        summary: item.summary,
        lessonType: item.lessonType,
        order,
        isPublished: item.isPublished !== false,
        durationSeconds: Number(item.durationSeconds || 0),
        thumbnailUrl: item.thumbnailUrl || '',
        uploadedVideoUrl: item.uploadedVideoUrl || '',
        embeddedVideoUrl: item.embeddedVideoUrl || '',
        imageUrl: item.imageUrl || '',
        htmlContent: item.htmlContent || '',
        resourceUrl: item.resourceUrl || '',
        quizConfig: item.quizConfig || { passPercent: 60, questions: [] },
        requiresLessonIds: Array.isArray(item.requiresLessonIds) ? item.requiresLessonIds : [],
        releaseAt: item.releaseAt ? new Date(item.releaseAt).toISOString() : null,
      })));
      notify('Lesson order updated.');
      await loadOutline(selectedCourseId);
    } catch (err) {
      notify(err.response?.data?.message || 'Could not reorder lessons.', 'danger');
    }
    setSaving(false);
  };

  const submitEnrollment = async (course, batchId = null) => {
    await api.post(`/training/memberships/catalog/${course._id}/enroll`, batchId ? { batchId } : {});
    notify(`${course.courseName} added to your learning.`);
    setBatchPicker({ show: false, course: null, batches: [] });
    setSelectedBatchId('');
    await refreshCore();
    setSelectedCourseId(String(course._id));
    setTab(canWrite ? 'learning' : 'my-courses');
  };

  const migrateLegacyCourse = async (course) => {
    if (!course?._id) return;
    const confirmed = window.confirm(`Migrate legacy LMS lessons for "${course.courseName}" into Course Content?\n\nThis keeps the old data in place and creates Course Content LMS items for the same course.`);
    if (!confirmed) return;
    setMigratingCourseId(String(course._id));
    try {
      const response = await api.post('/training/course-content/migrate-on-demand-course', {
        courseId: course._id,
        replaceExisting: false,
        copyProgress: true,
      });
      notify(response.data?.message || `Migrated ${course.courseName}.`);
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not migrate legacy LMS lessons.', 'danger');
    }
    setMigratingCourseId('');
  };

  const bulkMigrateWorkspaceCourses = async () => {
    const candidates = filteredWorkspaceCourses.filter((course) => {
      const status = String(migrationStatusByCourse[String(course._id)]?.status || 'not_started');
      return status === 'not_started' || status === 'partial';
    });
    if (!candidates.length) {
      notify('No legacy LMS courses found in the current workspace filter.', 'warning');
      return;
    }
    const confirmed = window.confirm(`Migrate ${candidates.length} course(s) into Course Content LMS items?\n\nThis keeps the existing legacy LMS data and copies progress where possible.`);
    if (!confirmed) return;

    setSaving(true);
    let successCount = 0;
    let failedCount = 0;
    for (const course of candidates) {
      setMigratingCourseId(String(course._id));
      try {
        await api.post('/training/course-content/migrate-on-demand-course', {
          courseId: course._id,
          replaceExisting: false,
          copyProgress: true,
        });
        successCount += 1;
      } catch (err) {
        failedCount += 1;
      }
    }
    setMigratingCourseId('');
    setSaving(false);
    await refreshCore();
    if (failedCount) {
      notify(`Bulk migration finished. ${successCount} migrated, ${failedCount} failed.`, 'warning');
      return;
    }
    notify(`Bulk migration finished. ${successCount} course${successCount === 1 ? '' : 's'} migrated.`);
  };

  const startCheckout = async (planId) => {
    setSaving(true);
    try {
      const orderResponse = await api.post('/training/memberships/checkout/orders', {
        planId,
        couponCode: checkoutState.couponCode || couponCode,
        provider: checkoutState.provider || 'test',
      });
      setCheckoutState((prev) => ({
        ...prev,
        order: orderResponse.data?.data || null,
        stage: 'payment',
      }));
    } catch (err) {
      notify(err.response?.data?.message || 'Test payment failed.', 'danger');
    }
    setSaving(false);
  };

  const completeCheckout = async () => {
    if (!checkoutState.order?._id) return;
    setSaving(true);
    try {
      await api.post(`/training/memberships/checkout/orders/${checkoutState.order._id}/complete`, { outcome: 'success' });
      notify(`${checkoutState.provider === 'test' ? 'Test payment' : 'Payment'} completed. Membership is now active.`);
      setCouponCode('');
      resetCheckout();
      openTab(canWrite ? 'plans' : 'my-subscription');
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not complete payment.', 'danger');
    }
    setSaving(false);
  };

  const submitRenewalAction = async () => {
    if (!renewalActionState.subscription?._id) return;
    setSaving(true);
    try {
      const payload = {
        action: renewalActionState.action,
        note: renewalActionState.note,
      };
      if (['extend', 'reactivate'].includes(renewalActionState.action)) payload.expiresAt = renewalActionState.expiresAt;
      await api.post(`/training/memberships/subscriptions/${renewalActionState.subscription._id}/admin-action`, payload);
      notify('Subscription updated.');
      closeRenewalAction();
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not update subscription.', 'danger');
    }
    setSaving(false);
  };

  const resyncMembershipOrder = async (orderId) => {
    if (!orderId) return;
    setSaving(true);
    try {
      await api.post(`/training/memberships/orders/${orderId}/admin-resync`);
      notify('Order reconciliation completed.');
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not resync provider order.', 'danger');
    }
    setSaving(false);
  };

  const launchRazorpayCheckout = async () => {
    if (!checkoutState.order?._id) return;
    const providerInfo = (paymentProviders.providers || []).find((item) => item.key === 'razorpay');
    if (!providerInfo?.publicKey) {
      notify('Razorpay public key is not configured.', 'warning');
      return;
    }
    setSaving(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded || !window.Razorpay) {
        notify('Could not load Razorpay checkout.', 'danger');
        setSaving(false);
        return;
      }
      const gatewayOrder = checkoutState.order?.paymentMeta?.gatewayOrder || {};
      const razorpay = new window.Razorpay({
        key: providerInfo.publicKey,
        amount: gatewayOrder.amount,
        currency: gatewayOrder.currency || 'INR',
        name: checkoutState.plan?.name || 'Membership Package',
        description: checkoutState.plan?.description || 'LMS membership purchase',
        order_id: checkoutState.order.providerOrderId,
        prefill: {
          name: [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim(),
          email: user?.email || '',
          contact: user?.phone || '',
        },
        notes: {
          membershipOrderId: checkoutState.order._id,
          planCode: checkoutState.plan?.code || '',
        },
        handler: async (response) => {
          try {
            await api.post(`/training/memberships/checkout/orders/${checkoutState.order._id}/verify-razorpay`, response);
            notify('Razorpay payment verified. Membership is now active.');
            setCouponCode('');
            resetCheckout();
            openTab(canWrite ? 'plans' : 'my-subscription');
            await refreshCore();
          } catch (err) {
            notify(err.response?.data?.message || 'Could not verify Razorpay payment.', 'danger');
          }
        },
        modal: {
          ondismiss: () => setSaving(false),
        },
      });
      razorpay.open();
    } catch (err) {
      notify(err.response?.data?.message || err.message || 'Could not start Razorpay checkout.', 'danger');
    }
    setSaving(false);
  };

  const enrollCourse = async (course) => {
    if (!course?._id) return false;
    setSaving(true);
    try {
      await submitEnrollment(course);
      setSaving(false);
      return true;
    } catch (err) {
      const availableBatches = err.response?.data?.availableBatches || [];
      if (availableBatches.length > 0) {
        setBatchPicker({ show: true, course, batches: availableBatches });
        setSelectedBatchId(String(availableBatches[0]?._id || ''));
        notify('Select a batch to complete enrollment.', 'warning');
      } else {
        notify(err.response?.data?.message || 'Enrollment failed.', 'danger');
      }
    }
    setSaving(false);
    return false;
  };
  const confirmBatchEnrollment = async () => {
    if (!batchPicker.course?._id || !selectedBatchId) {
      notify('Select a batch to continue.', 'warning');
      return;
    }
    setSaving(true);
    try {
      await submitEnrollment(batchPicker.course, selectedBatchId);
    } catch (err) {
      notify(err.response?.data?.message || 'Enrollment failed.', 'danger');
    }
    setSaving(false);
  };

  const markLessonComplete = async () => {
    if (!activeLesson?._id) return;
    if (!canWrite && activeLesson.access?.isLocked) {
      notify('Complete the prerequisite lessons first.', 'warning');
      return;
    }
    const watchedPercent = getLessonWatchPercent(activeLesson);
    const watchedSeconds = getLessonWatchedSeconds(activeLesson);
    const requiredWatchSeconds = getLessonRequiredWatchSeconds(activeLesson);
    if (!canWrite && isTrackedVideoLesson(activeLesson) && !isLessonWatchRequirementMet(activeLesson)) {
      const watchRequirement = requiredWatchSeconds > 0 ? ` and ${Math.ceil(requiredWatchSeconds)} seconds` : '';
      notify(`Watch at least ${VIDEO_COMPLETION_PERCENT}%${watchRequirement} of the video before marking it complete.`, 'warning');
      return;
    }
    setSaving(true);
    try {
      if (!canWrite && isTrackedVideoLesson(activeLesson)) {
        await api.post(`/training/on-demand/lessons/${activeLesson._id}/progress`, {
          completed: false,
          watchedSeconds,
          watchedPercent,
          lastPositionSeconds: Number(activeLesson.progress?.lastPositionSeconds || 0),
          videoDurationSeconds: Number(videoDurations[String(activeLesson._id)] || activeLesson.durationSeconds || 0),
        });
      }
      await api.post(`/training/on-demand/lessons/${activeLesson._id}/progress`, {
        completed: true,
        watchedSeconds,
        watchedPercent,
        lastPositionSeconds: Number(activeLesson.progress?.lastPositionSeconds || 0),
        videoDurationSeconds: Number(videoDurations[String(activeLesson._id)] || activeLesson.durationSeconds || 0),
      });
      notify('Lesson marked as completed.');
      await loadOutline(selectedCourseId);
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not update progress.', 'danger');
    }
    setSaving(false);
  };

  const submitCourseDiscussion = async (event) => {
    event.preventDefault();
    const message = courseDiscussionMessage.trim();
    if (!selectedCourseId || message.length < 3) return;
    setCourseDiscussionSubmitting(true);
    try {
      await api.post('/training/course-discussions', {
        courseId: selectedCourseId,
        lessonId: activeLesson?._id || '',
        lessonTitle: activeLesson?.title || '',
        message,
      });
      setCourseDiscussionMessage('');
      notify('Your question has been sent to the faculty.');
      await loadCourseDiscussions(selectedCourseId);
    } catch (err) {
      notify(err.response?.data?.message || 'Could not send your question.', 'danger');
    }
    setCourseDiscussionSubmitting(false);
  };

  const openLesson = useCallback((lessonId) => {
    if (!lessonId) return;
    let targetLesson = null;
    for (const section of learnState.outline || []) {
      targetLesson = (section.lessons || []).find((lesson) => String(lesson._id) === String(lessonId)) || null;
      if (targetLesson) {
        setSelectedOutlineTopicId(String(section._id));
        break;
      }
    }
    if (!canWrite && targetLesson?.access?.isLocked) {
      notify('Complete the previous lesson before continuing.', 'warning');
      return;
    }
    setCourseWorkspaceTab('content');
    setLessonDetailTab('overview');
    setSelectedLessonId(String(lessonId));
  }, [canWrite, learnState.outline, notify]);

  const openCourseWorkspace = useCallback((courseId) => {
    if (!courseId) return;
    setSelectedCourseId(String(courseId));
    setSelectedLessonId('');
    setSelectedOutlineTopicId('');
    setCourseWorkspaceTab('content');
    setLessonDetailTab('overview');
    setShowCourseWorkspaceModal(true);
  }, []);

  const closeCourseWorkspace = useCallback(() => {
    setShowCourseWorkspaceModal(false);
    setCourseWorkspaceTab('content');
  }, []);

  const openDashboardShortcut = useCallback((action) => {
    const normalized = String(action || '').trim().toLowerCase();
    if (normalized.includes('certificate')) {
      if (studentLearningOverview.completedCourses > 0 && studentLearningOverview.continueCourse?.course?._id) {
        openTab('my-courses');
        openCourseWorkspace(String(studentLearningOverview.continueCourse.course._id));
        return;
      }
      openTab('my-courses');
      return;
    }
    if (normalized.includes('progress') || normalized.includes('course')) {
      if (studentLearningOverview.continueCourse?.course?._id) {
        openTab('my-courses');
        openCourseWorkspace(String(studentLearningOverview.continueCourse.course._id));
        return;
      }
      openTab('my-courses');
      return;
    }
    if (normalized.includes('goal')) return;
    if (normalized.includes('subscription') || normalized.includes('membership')) {
      openTab('my-subscription');
      return;
    }
    openTab('for-you');
  }, [openCourseWorkspace, openTab, studentLearningOverview.completedCourses, studentLearningOverview.continueCourse]);

  const openStudentCourseCard = useCallback(async (course, { fallbackTab = 'for-you' } = {}) => {
    const resolvedCourseId = course?.sourceCourseId || course?._id;
    const resolvedCourse = resolvedCourseId
      ? (catalog.find((row) => String(row._id) === String(resolvedCourseId)) || course)
      : course;
    if (!resolvedCourse?._id) {
      openTab(fallbackTab);
      return;
    }
    const alreadyEnrolled = !!resolvedCourse.enrollment || learning.some((row) => (
      !row.isVirtualEnrollment
      && String(row.course?._id || row.course) === String(resolvedCourse._id)
    ));
    if (alreadyEnrolled) {
      openTab('my-courses');
      openCourseWorkspace(String(resolvedCourse._id));
      return;
    }
    if (resolvedCourse.access?.allowed || isSelfServeLearningCourse(resolvedCourse)) {
      const enrolled = await enrollCourse(resolvedCourse);
      if (!enrolled) return;
      openTab('my-courses');
      openCourseWorkspace(String(resolvedCourse._id));
      return;
    }
    setProgramPreviewCourse(resolvedCourse);
    openTab(fallbackTab);
  }, [catalog, enrollCourse, learning, openCourseWorkspace, openTab]);

  const toggleWishlist = useCallback((courseId) => {
    if (!courseId) return;
    setWishlistCourseIds((prev) => {
      const key = String(courseId);
      return prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key];
    });
  }, []);

  const shareStudentProgram = useCallback(async (course) => {
    if (!course) return;
    const title = course.courseName || course.title || 'TopSkilled learning program';
    const shareUrl = `${window.location.origin}${location.pathname}?tab=for-you`;
    const shareData = {
      title,
      text: `Explore ${title} on ${tenant?.name || 'TopSkilled Academy'}.`,
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(`${shareData.text} ${shareUrl}`);
      notify('Program link copied to clipboard.');
    } catch (err) {
      if (err?.name !== 'AbortError') notify('Could not share this program.', 'warning');
    }
  }, [location.pathname, notify, tenant?.name]);

  const openExpandedLessonPreview = useCallback((lesson) => {
    if (!lesson) return;
    setExpandedLessonPreview(lesson);
  }, []);

  const closeExpandedLessonPreview = useCallback(() => {
    setExpandedLessonPreview(null);
  }, []);

  const submitQuizGate = async () => {
    if (!activeLesson?._id || activeLesson.lessonType !== 'quiz_gate') return;
    if (!activeQuizQuestions.length) return notify('No quiz questions are configured for this lesson.', 'warning');
    const missingAnswer = activeQuizQuestions.some((_, index) => quizAnswers[index] === undefined || quizAnswers[index] === '');
    if (missingAnswer) return notify('Answer all quiz questions before submitting.', 'warning');
    setSaving(true);
    try {
      const response = await api.post(`/training/on-demand/lessons/${activeLesson._id}/progress`, {
        quizAnswers: activeQuizQuestions.map((_, index) => Number(quizAnswers[index])),
        watchedPercent: 100,
      });
      const progress = response.data?.data || {};
      if (progress.status === 'completed') notify(`Quiz passed with score ${progress.score ?? 0}%.`);
      else notify(`Quiz submitted. Score ${progress.score ?? 0}%. Pass percentage not reached yet.`, 'warning');
      await loadOutline(selectedCourseId);
      await refreshCore();
    } catch (err) {
      notify(err.response?.data?.message || 'Could not submit quiz.', 'danger');
    }
    setSaving(false);
  };

  const stats = useMemo(() => ({
    catalogCount: catalog.length,
    activePlans: plans.filter((row) => row.isActive).length,
    activeSubscriptions: subscriptions.filter((row) => row.status === 'active').length,
    paidOrders: orders.filter((row) => row.status === 'paid').length,
    renewalQueue: renewals.length,
    learningCourses: learning.length,
  }), [catalog, plans, subscriptions, orders, renewals, learning]);

  if (loading) {
    return (
      <div className="py-5 text-center">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: !canWrite && ['dashboard', 'progress'].includes(activeTab) ? 'auto' : '100vh', background: canWrite ? 'radial-gradient(circle at top left, rgba(96,165,250,0.18) 0%, rgba(255,255,255,0) 32%), radial-gradient(circle at top right, rgba(34,197,94,0.16) 0%, rgba(255,255,255,0) 30%), linear-gradient(180deg, #f8fbff 0%, #f4f7fb 100%)' : studentTheme.pageBg, paddingBottom: !canWrite && ['dashboard', 'progress'].includes(activeTab) ? (isStudentMobile ? 0 : 16) : 24 }}>
      {canWrite && (
        <PageHeader
          title="Online Learning"
          icon="bi-play-btn"
          subtitle="Membership access, subscriptions, and learner activity"
        />
      )}

      {canWrite && (
        <AdminSurface className="mb-4" style={{ overflow: 'hidden' }}>
          <div className="row g-0">
            <div className="col-12 col-xxl-8">
              <div style={{ padding: 28, background: softHeroGradient, color: '#fff', height: '100%', position: 'relative' }}>
                <div style={{ position: 'absolute', inset: 16, borderRadius: 28, ...softGlassInset }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div className="text-uppercase small fw-semibold text-white-50 mb-2">Admin LMS Console</div>
                  <div className="fw-semibold mb-2" style={{ fontSize: 34, lineHeight: 1.1 }}>Operate memberships, subscriptions, and learning access</div>
                  <div className="text-white-50" style={{ maxWidth: 760 }}>
                    A focused LMS control center for package access, subscription tracking, commerce, and learner movement.
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-xxl-4">
              <div style={{ padding: 28, background: 'linear-gradient(180deg, rgba(255,255,255,0.76) 0%, rgba(248,250,252,0.88) 100%)', height: '100%' }}>
                <AdminSectionTitle
                  eyebrow="Snapshot"
                  title="Current LMS health"
                  subtitle="A quick operational read before moving into memberships, subscriptions, or analytics."
                  dense
                />
                <div className="row g-3">
                  <div className="col-6"><AdminMetricTile label="Paid Orders" value={stats.paidOrders} icon="bi-bag-check" accent="#16a34a" /></div>
                  <div className="col-6"><AdminMetricTile label="Renewals" value={stats.renewalQueue} icon="bi-arrow-repeat" accent="#f59e0b" /></div>
                </div>
              </div>
            </div>
          </div>
        </AdminSurface>
      )}

      {canWrite && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-xl-3"><AdminMetricTile label="Course Master" value={stats.catalogCount} icon="bi-grid" accent="#6366f1" hint="Courses available for LMS access" /></div>
          <div className="col-6 col-xl-3"><AdminMetricTile label="Plans" value={stats.activePlans} icon="bi-box-seam" accent="#0891b2" hint="Active memberships" /></div>
          <div className="col-6 col-xl-3"><AdminMetricTile label="Active Subs" value={stats.activeSubscriptions} icon="bi-patch-check" accent="#16a34a" hint="Current learner packages" /></div>
          <div className="col-6 col-xl-3"><AdminMetricTile label="Learning Records" value={stats.learningCourses} icon="bi-collection-play" accent="#ea580c" hint="Enrollments with on-demand progress" /></div>
        </div>
      )}

      {canWrite && (
        <ul className="nav nav-pills mb-4 flex-wrap gap-2" style={{ ...glassPanel, padding: 10, borderRadius: 22 }}>
          <li className="nav-item"><button className={`nav-link ${tab === 'workspace' ? 'active' : ''}`} style={adminTabButtonStyle(tab === 'workspace')} onClick={() => setTab('workspace')}>Dashboard</button></li>
          <li className="nav-item"><button className={`nav-link ${tab === 'plans' ? 'active' : ''}`} style={adminTabButtonStyle(tab === 'plans')} onClick={() => setTab('plans')}>Memberships</button></li>
          <li className="nav-item"><button className={`nav-link ${tab === 'orders' ? 'active' : ''}`} style={adminTabButtonStyle(tab === 'orders')} onClick={() => setTab('orders')}>Subscriptions</button></li>
          <li className="nav-item"><button className={`nav-link ${tab === 'coupons' ? 'active' : ''}`} style={adminTabButtonStyle(tab === 'coupons')} onClick={() => setTab('coupons')}>Coupons</button></li>
          <li className="nav-item"><button className={`nav-link ${tab === 'analytics' ? 'active' : ''}`} style={adminTabButtonStyle(tab === 'analytics')} onClick={() => setTab('analytics')}>Analytics</button></li>
          <li className="nav-item"><button className={`nav-link ${tab === 'college-access' ? 'active' : ''}`} style={adminTabButtonStyle(tab === 'college-access')} onClick={() => setTab('college-access')}>MoU Signed Colleges</button></li>
          <li className="nav-item"><button className={`nav-link ${tab === 'college-review' ? 'active' : ''}`} style={adminTabButtonStyle(tab === 'college-review')} onClick={() => setTab('college-review')}>Pending Authorization</button></li>
        </ul>
      )}

      {tab === 'workspace' && canWrite && (
        <div className="row g-3 mb-1">
          <div className="col-12">
            <AdminSurface style={{ padding: 24 }}>
              <AdminSectionTitle
                eyebrow="Action Queue"
                title="What needs attention now"
                subtitle="Use the queue below to move into renewals and payment exceptions without switching through each LMS tab first."
              />
              <div className="row g-3 mb-4">
                <div className="col-12 col-xl-4">
                  <div className="border h-100" style={{ borderRadius: 18, borderColor: '#e2e8f0', background: '#fff', padding: 18 }}>
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div>
                        <div className="small text-muted">Renewals</div>
                        <div className="fw-semibold" style={{ fontSize: 26, lineHeight: 1.1 }}>{workspaceActionQueue.urgentRenewals.length}</div>
                      </div>
                      <Pill color="warning">0-7 days</Pill>
                    </div>
                    <div className="small text-muted mb-3">Subscriptions already expired or reaching expiry within 7 days.</div>
                    <div className="d-flex flex-column gap-2 small mb-3">
                      {workspaceActionQueue.urgentRenewals.slice(0, 3).map((row) => (
                        <div key={`renewal-queue-${row._id}`} className="d-flex justify-content-between gap-2">
                          <span className="text-dark text-truncate">{row.customer?.name || row.userId?.email || row.planId?.name || 'Subscription'}</span>
                          <span className="text-muted">{row.lifecycle?.isExpired ? 'Expired' : `${row.lifecycle?.daysRemaining ?? '-'}d`}</span>
                        </div>
                      ))}
                      {!workspaceActionQueue.urgentRenewals.length && <span className="text-muted">No urgent renewals.</span>}
                    </div>
                    <button type="button" className="btn btn-outline-warning btn-sm" onClick={() => openRenewalsQueue('urgent')}>Open Renewals</button>
                  </div>
                </div>
                <div className="col-12 col-xl-4">
                  <div className="border h-100" style={{ borderRadius: 18, borderColor: '#e2e8f0', background: '#fff', padding: 18 }}>
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div>
                        <div className="small text-muted">Pending Orders</div>
                        <div className="fw-semibold" style={{ fontSize: 26, lineHeight: 1.1 }}>{workspaceActionQueue.pendingOrders.length}</div>
                      </div>
                      <Pill color="info">Follow-up</Pill>
                    </div>
                    <div className="small text-muted mb-3">Orders created but not yet activated or completed.</div>
                    <div className="d-flex flex-column gap-2 small mb-3">
                      {workspaceActionQueue.pendingOrders.slice(0, 3).map((row) => (
                        <div key={`pending-order-${row._id}`} className="d-flex justify-content-between gap-2">
                          <span className="text-dark text-truncate">{row.customer?.name || row.userId?.email || row.planId?.name || 'Order'}</span>
                          <span className="text-muted text-capitalize">{String(row.status || '').replaceAll('_', ' ')}</span>
                        </div>
                      ))}
                      {!workspaceActionQueue.pendingOrders.length && <span className="text-muted">No pending orders.</span>}
                    </div>
                    <button type="button" className="btn btn-outline-info btn-sm" onClick={() => openOrdersQueue('pending')}>Open Orders</button>
                  </div>
                </div>
                <div className="col-12 col-xl-4">
                  <div className="border h-100" style={{ borderRadius: 18, borderColor: '#e2e8f0', background: '#fff', padding: 18 }}>
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div>
                        <div className="small text-muted">Failed Orders</div>
                        <div className="fw-semibold" style={{ fontSize: 26, lineHeight: 1.1 }}>{workspaceActionQueue.failedOrders.length}</div>
                      </div>
                      <Pill color="danger">Exceptions</Pill>
                    </div>
                    <div className="small text-muted mb-3">Payment attempts that failed and may need retry or support follow-up.</div>
                    <div className="d-flex flex-column gap-2 small mb-3">
                      {workspaceActionQueue.failedOrders.slice(0, 3).map((row) => (
                        <div key={`failed-order-${row._id}`} className="d-flex justify-content-between gap-2">
                          <span className="text-dark text-truncate">{row.customer?.name || row.userId?.email || row.planId?.name || 'Order'}</span>
                          <span className="text-muted">{fmtMoney(row.payment?.amount || 0)}</span>
                        </div>
                      ))}
                      {!workspaceActionQueue.failedOrders.length && <span className="text-muted">No failed orders.</span>}
                    </div>
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => openOrdersQueue('failed')}>Review Orders</button>
                  </div>
                </div>
              </div>
              <AdminSectionTitle
                eyebrow="Structure"
                title="Source modules for LMS dashboard"
                subtitle="Use this page for memberships and learner commerce. Use the source modules below for master data and content authoring."
              />
              <div className="row g-3">
                <div className="col-12 col-xl-4">
                  <AdminWorkspaceTile
                    title="Courses"
                    description="Course category, price, thumbnails, promo media, and delivery mode remain owned by the Course module."
                    icon="bi-grid"
                    accent="#6366f1"
                    ctaLabel="Open Courses"
                    onClick={() => navigate('/courses')}
                    stats={[
                      { label: 'Active courses', value: stats.catalogCount },
                      { label: 'On-demand / Hybrid', value: studioCourses.length },
                      { label: 'Categories', value: courseCategories.length },
                    ]}
                  />
                </div>
                <div className="col-12 col-xl-4">
                  <AdminWorkspaceTile
                    title="Course Content"
                    description="Modules, topics, reusable assets, and lesson media should be managed in Course Content instead of a parallel LMS studio."
                    icon="bi-journal-richtext"
                    accent="#0891b2"
                    ctaLabel="Open Course Content"
                    onClick={() => navigate('/course-content')}
                    stats={[
                      { label: 'Reusable content', value: 'Managed there' },
                      { label: 'Video lessons', value: 'Managed there' },
                      { label: 'Approval flow', value: 'Managed there' },
                    ]}
                  />
                </div>
                <div className="col-12 col-xl-4">
                  <AdminWorkspaceTile
                    title="Enrollments"
                    description="Learner-course ownership, payment history, fee balance, and completion state stay in the Enrollment module."
                    icon="bi-person-check"
                    accent="#ea580c"
                    ctaLabel="Open Enrollments"
                    onClick={() => navigate('/enrollments?source=lms')}
                    stats={[
                      { label: 'Learning records', value: stats.learningCourses },
                      { label: 'Active subscriptions', value: stats.activeSubscriptions },
                      { label: 'Renewal queue', value: stats.renewalQueue },
                    ]}
                  />
                </div>
              </div>
            </AdminSurface>
          </div>
        </div>
      )}

      {tab === 'analytics' && canWrite && (
        <div className="row g-3">
          <div className="col-12">
            <AdminSurface style={{ padding: 24 }}>
              <AdminSectionTitle
                eyebrow="Analytics"
                title="Membership analytics"
                subtitle="Revenue, subscriptions, enrollments, and quota movement across the selected period."
                action={(
                <div className="d-flex align-items-center gap-2">
                  <input type="date" className="form-control form-control-sm" value={analyticsRange.dateFrom} onChange={(e) => setAnalyticsRange((prev) => ({ ...prev, dateFrom: e.target.value }))} />
                  <input type="date" className="form-control form-control-sm" value={analyticsRange.dateTo} onChange={(e) => setAnalyticsRange((prev) => ({ ...prev, dateTo: e.target.value }))} />
                  <button className="btn btn-outline-primary btn-sm" onClick={refreshCore}>Refresh</button>
                </div>
              )}
              />
              <div className="row g-3">
                <div className="col-6 col-xl-2"><AdminMetricTile label="Revenue" value={fmtMoney(analytics.overview?.revenue || 0)} icon="bi-currency-rupee" accent="#16a34a" /></div>
                <div className="col-6 col-xl-2"><AdminMetricTile label="Paid Orders" value={analytics.overview?.paidOrders || 0} icon="bi-bag-check" accent="#2563eb" /></div>
                <div className="col-6 col-xl-2"><AdminMetricTile label="Subscriptions" value={analytics.overview?.subscriptionsSold || 0} icon="bi-patch-check" accent="#7c3aed" /></div>
                <div className="col-6 col-xl-2"><AdminMetricTile label="Active Subs" value={analytics.overview?.activeSubscriptions || 0} icon="bi-stars" accent="#0891b2" /></div>
                <div className="col-6 col-xl-2"><AdminMetricTile label="Enrollments" value={analytics.overview?.enrollments || 0} icon="bi-person-check" accent="#ea580c" /></div>
                <div className="col-6 col-xl-2"><AdminMetricTile label="Reservations" value={analytics.overview?.reservations || 0} icon="bi-bookmark-check" accent="#f59e0b" /></div>
                <div className="col-6 col-xl-4"><AdminMetricTile label="Quota Consumed" value={analytics.overview?.quotaConsumed || 0} icon="bi-bar-chart-steps" accent="#dc2626" /></div>
                <div className="col-6 col-xl-4"><AdminMetricTile label="Quota Released" value={analytics.overview?.quotaReleased || 0} icon="bi-arrow-counterclockwise" accent="#16a34a" /></div>
                <div className="col-6 col-xl-4"><AdminMetricTile label="Quota Adjusted" value={analytics.overview?.quotaAdjusted || 0} icon="bi-sliders" accent="#7c3aed" /></div>
              </div>
            </AdminSurface>
          </div>
          <div className="col-12 col-xl-4">
            <CardShell title="Order Status">
              <MiniBarList rows={analytics.orderStatus || []} color="#2563eb" />
            </CardShell>
          </div>
          <div className="col-12 col-xl-4">
            <CardShell title="Top Plans by Revenue">
              <MiniBarList rows={(analytics.topPlans || []).map((row) => ({ ...row, value: row.revenue }))} color="#16a34a" />
            </CardShell>
          </div>
          <div className="col-12 col-xl-4">
            <CardShell title="Top Plans by Paid Orders">
              <MiniBarList rows={(analytics.topPlans || []).map((row) => ({ ...row, value: row.paidOrders }))} color="#7c3aed" />
            </CardShell>
          </div>
          <div className="col-12 col-xl-4">
            <CardShell title="Quota by Scope">
              <MiniBarList rows={(analytics.quotaScopeBreakdown || []).map((row) => ({ label: row.label, value: row.consumed }))} color="#dc2626" />
              <div className="border-top mt-3 pt-3">
                <div className="small fw-semibold mb-2">Released quantity</div>
                <MiniBarList rows={(analytics.quotaScopeBreakdown || []).map((row) => ({ label: row.label, value: row.released }))} color="#16a34a" />
              </div>
            </CardShell>
          </div>
          <div className="col-12 col-xl-4">
            <CardShell title="Top Plans by Quota Usage">
              <MiniBarList rows={(analytics.quotaPlanUsage || []).map((row) => ({ label: row.label, value: row.consumed }))} color="#ea580c" />
            </CardShell>
          </div>
          <div className="col-12 col-xl-4">
            <CardShell title="Course Engagement">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Enrollments</th>
                      <th>Completion</th>
                      <th>Avg Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics.courseEngagement || []).map((row) => (
                      <tr key={row.courseId}>
                        <td>
                          <div className="fw-semibold">{row.label}</div>
                          <div className="small text-muted">{row.category || 'No category'}</div>
                        </td>
                        <td>{row.enrollments || 0}</td>
                        <td style={{ minWidth: 160 }}>
                          <div className="small mb-1">{row.completionPercent || 0}%</div>
                          <ProgressBar value={row.completionPercent || 0} color="#ea580c" />
                        </td>
                        <td>{row.averageScore ?? '-'}</td>
                      </tr>
                    ))}
                    {!analytics.courseEngagement?.length && (
                      <tr><td colSpan="4" className="text-muted text-center py-4">No learning activity in the selected range.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardShell>
          </div>
          <div className="col-12 col-xl-7">
            <CardShell title="Course Carousel Reach">
              <div className="row g-2 mb-3">
                <div className="col-4"><AdminMetricTile label="Tracked sessions" value={carouselStrategy.sampleSize || 0} icon="bi-people" accent="#2563eb" /></div>
                <div className="col-4"><AdminMetricTile label="Median card reached" value={carouselStrategy.medianReach || '-'} icon="bi-bar-chart-steps" accent="#7c3aed" /></div>
                <div className="col-4"><AdminMetricTile label="Paid course slot" value={carouselStrategy.paidSlot || 5} icon="bi-bullseye" accent="#ea580c" /></div>
              </div>
              <MiniBarList
                rows={(carouselStrategy.reachByPosition || []).map((row) => ({ label: `Card ${row.position}`, value: row.users }))}
                color="#4f46e5"
              />
              {carouselStrategy.sampleSize < carouselStrategy.minimumSamples && (
                <div className="small text-muted mt-3">
                  The paid slot remains at the safe default until {carouselStrategy.minimumSamples} student carousel sessions are available.
                </div>
              )}
            </CardShell>
          </div>
          <div className="col-12 col-xl-5">
            <CardShell title="Daily Trend">
              <MiniBarList rows={(analytics.trendDays || []).map((row) => ({ label: row.date, value: row.revenue }))} color="#0891b2" />
              <div className="border-top mt-3 pt-3">
                <div className="small fw-semibold mb-2">Subscriptions per day</div>
                <MiniBarList rows={(analytics.trendDays || []).map((row) => ({ label: row.date, value: row.subscriptions }))} color="#f59e0b" />
              </div>
            </CardShell>
          </div>
          <div className="col-12">
            <CardShell title="Quota Plan Breakdown">
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Consumed</th>
                      <th>Released</th>
                      <th>Adjusted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics.quotaPlanUsage || []).map((row) => (
                      <tr key={row.planId}>
                        <td><div className="fw-semibold">{row.label}</div></td>
                        <td>{row.consumed || 0}</td>
                        <td>{row.released || 0}</td>
                        <td>{row.adjusted || 0}</td>
                      </tr>
                    ))}
                    {!analytics.quotaPlanUsage?.length && (
                      <tr><td colSpan="4" className="text-muted text-center py-4">No quota usage found in the selected range.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardShell>
          </div>
        </div>
      )}

      {tab === 'college-access' && canWrite && (
        <div className="row g-3">
          <div className="col-12">
            <AdminSurface style={{ padding: 24 }}>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                  <AdminSectionTitle
                    eyebrow="College Access"
                    title="MoU signed colleges"
                    subtitle="Create college-specific registration links and manage college-level access settings."
                  />
                </div>
                <button type="button" className="btn btn-primary" onClick={() => { resetPartnerCollegeForm(); setShowPartnerCollegeEditor((prev) => !prev); }}>
                  <i className="bi bi-plus-circle me-2" />
                  {showPartnerCollegeEditor ? 'Close Editor' : 'Add Partner College'}
                </button>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-6 col-xl-3"><AdminMetricTile label="Colleges" value={collegeSummary.collegeCount || 0} icon="bi-building" accent="#2563eb" /></div>
                <div className="col-6 col-xl-3"><AdminMetricTile label="Pending Review" value={collegeSummary.pendingCount || 0} icon="bi-hourglass-split" accent="#f59e0b" /></div>
                <div className="col-6 col-xl-3"><AdminMetricTile label="Authorized" value={collegeSummary.authorizedCount || 0} icon="bi-patch-check" accent="#16a34a" /></div>
                <div className="col-6 col-xl-3"><AdminMetricTile label="Rejected" value={collegeSummary.rejectedCount || 0} icon="bi-x-octagon" accent="#dc2626" /></div>
              </div>

              {showPartnerCollegeEditor && (
                <div className="border rounded-4 p-4 mb-4" style={{ borderColor: '#dbeafe', background: '#f8fbff' }}>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label small fw-semibold">College Name</label>
                      <input className="form-control" value={partnerCollegeForm.name} onChange={(e) => setPartnerCollegeForm((prev) => ({ ...prev, name: e.target.value }))} />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label small fw-semibold">College Code</label>
                      <input className="form-control" value={partnerCollegeForm.code} onChange={(e) => setPartnerCollegeForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))} />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label small fw-semibold">Access Code</label>
                      <input className="form-control" value={partnerCollegeForm.uniqueAccessCode} onChange={(e) => setPartnerCollegeForm((prev) => ({ ...prev, uniqueAccessCode: e.target.value.toUpperCase() }))} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Description</label>
                      <textarea className="form-control" rows="2" value={partnerCollegeForm.description} onChange={(e) => setPartnerCollegeForm((prev) => ({ ...prev, description: e.target.value }))} />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label small fw-semibold">Website URL</label>
                      <input className="form-control" value={partnerCollegeForm.websiteUrl} onChange={(e) => setPartnerCollegeForm((prev) => ({ ...prev, websiteUrl: e.target.value }))} />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label small fw-semibold">Contact Number</label>
                      <input className="form-control" value={partnerCollegeForm.contactNumber} onChange={(e) => setPartnerCollegeForm((prev) => ({ ...prev, contactNumber: e.target.value }))} />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label small fw-semibold">Support Message</label>
                      <input className="form-control" value={partnerCollegeForm.supportMessage} onChange={(e) => setPartnerCollegeForm((prev) => ({ ...prev, supportMessage: e.target.value }))} />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label small fw-semibold">Accepted Years</label>
                      <input className="form-control" placeholder="2025, 2026" value={partnerCollegeForm.acceptedYears} onChange={(e) => setPartnerCollegeForm((prev) => ({ ...prev, acceptedYears: e.target.value }))} />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label small fw-semibold">Logo</label>
                      <input type="file" className="form-control" accept=".jpg,.jpeg,.png,.webp,.svg" onChange={(e) => setPartnerCollegeUpload((prev) => ({ ...prev, logo: e.target.files?.[0] || null }))} />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label small fw-semibold">Share QR</label>
                      <div className="form-control d-flex align-items-center" style={{ minHeight: 42, background: '#f8fafc' }}>
                        Generated automatically after the college is saved.
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" checked={partnerCollegeForm.autoApproveEnabled} onChange={(e) => setPartnerCollegeForm((prev) => ({ ...prev, autoApproveEnabled: e.target.checked }))} />
                        <label className="form-check-label small fw-semibold">Enable auto approval when proof matches</label>
                      </div>
                    </div>
                    <div className="col-12 d-flex gap-2">
                      <button type="button" className="btn btn-primary" disabled={saving} onClick={savePartnerCollege}>{saving ? 'Saving...' : (editingPartnerCollegeId ? 'Update College' : 'Create College')}</button>
                      <button type="button" className="btn btn-outline-secondary" onClick={resetPartnerCollegeForm}>Reset</button>
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex flex-column gap-3">
                <CardShell title="Configured Colleges">
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Access Code</th>
                            <th>Share Link</th>
                            <th>QR</th>
                            <th>Mode</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {partnerColleges.map((row) => (
                            <tr key={row._id}>
                              <td>
                                <div className="fw-semibold">{row.name}</div>
                                <div className="small text-muted">{row.code}</div>
                              </td>
                              <td><code>{row.uniqueAccessCode}</code></td>
                              <td style={{ minWidth: 260 }}>
                                <div className="small text-break mb-2">{row.shareUrl || '-'}</div>
                                <div className="d-flex gap-2 flex-wrap">
                                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => copyPartnerCollegeValue(row.shareUrl, 'Registration link copied.')}>
                                    Copy Link
                                  </button>
                                  {row.shareUrl && (
                                    <a href={row.shareUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
                                      Open Link
                                    </a>
                                  )}
                                </div>
                              </td>
                              <td style={{ minWidth: 160 }}>
                                {row.qrPreviewUrl ? (
                                  <div className="d-flex flex-column align-items-start gap-2">
                                    <img src={row.qrPreviewUrl} alt={`${row.name} QR`} style={{ width: 92, height: 92, objectFit: 'cover', borderRadius: 12, border: '1px solid #dbeafe', background: '#fff' }} />
                                    <div className="d-flex gap-2 flex-wrap">
                                      <a href={row.qrPreviewUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                                        Open QR
                                      </a>
                                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => copyPartnerCollegeValue(row.shareUrl, 'Registration link copied for QR sharing.')}>
                                        Copy
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted">Generated after save</span>
                                )}
                              </td>
                              <td>{row.autoApproveEnabled ? 'Auto + Manual fallback' : 'Manual only'}</td>
                              <td>
                                <div className="d-flex gap-2 flex-wrap">
                                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => editPartnerCollege(row)}>Edit</button>
                                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => copyPartnerCollegeValue(row.uniqueAccessCode, 'Access code copied.')}>Copy Code</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {!partnerColleges.length && (
                            <tr><td colSpan="6" className="text-center text-muted py-4">No MoU signed colleges configured yet.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardShell>
              </div>
            </AdminSurface>
          </div>
        </div>
      )}

      {tab === 'college-review' && canWrite && (
        <div className="row g-3">
          <div className="col-12">
            <AdminSurface style={{ padding: 24 }}>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <AdminSectionTitle
                  eyebrow="College Access"
                  title="Pending student authorization"
                  subtitle="Review one student at a time. After approve, reject, or delete, the next student loads automatically."
                />
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge rounded-pill text-bg-light border px-3 py-2">Pending {pendingCollegeAuthorizations.length}</span>
                  <button type="button" className="btn btn-outline-secondary" onClick={refreshCore}>
                    Refresh Queue
                  </button>
                </div>
              </div>

              {!pendingCollegeAuthorizations.length && (
                <CardShell title="Authorization Queue">
                  <div className="text-center text-muted py-5">No pending student authorization requests.</div>
                </CardShell>
              )}

              {!!pendingCollegeAuthorizations.length && (
                <div className="row g-3">
                  <div className="col-12 col-xl-4">
                    <CardShell title="Queue">
                      <div className="d-flex flex-column gap-2" style={{ maxHeight: 720, overflowY: 'auto' }}>
                        {pendingCollegeAuthorizations.map((row, index) => (
                          <button
                            key={row._id}
                            type="button"
                            className="btn text-start"
                            onClick={() => setSelectedCollegeAuthorizationId(String(row._id))}
                            style={{
                              borderRadius: 18,
                              border: `1px solid ${String(selectedCollegeAuthorization?._id) === String(row._id) ? '#2563eb' : '#dbe5f1'}`,
                              background: String(selectedCollegeAuthorization?._id) === String(row._id) ? '#eff6ff' : '#ffffff',
                              padding: '14px 16px',
                              boxShadow: String(selectedCollegeAuthorization?._id) === String(row._id) ? '0 12px 28px rgba(37, 99, 235, 0.12)' : 'none',
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-start gap-3">
                              <div>
                                <div className="fw-semibold">{`${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email}</div>
                                <div className="small text-muted">{row.email}</div>
                                <div className="small text-muted">{row.phone || '-'}</div>
                              </div>
                              <span className="badge rounded-pill text-bg-light border">#{index + 1}</span>
                            </div>
                            <div className="small text-muted mt-2">
                              {row.collegeAccess?.partnerCollegeName || row.collegeAccess?.partnerCollegeId?.name || 'College portal user'}
                            </div>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              <span className={`badge rounded-pill ${String(row.collegeAccess?.status || '') === 'rejected' ? 'text-bg-danger' : 'text-bg-warning'}`}>
                                {String(row.collegeAccess?.status || 'pending_review').replace(/_/g, ' ')}
                              </span>
                              <span className="badge rounded-pill text-bg-light border">
                                Auto: {String(row.collegeAccess?.autoVerificationStatus || 'not_attempted').replace(/_/g, ' ')}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </CardShell>
                  </div>

                  <div className="col-12 col-xl-8">
                    {selectedCollegeAuthorization && (
                      <CardShell title="Student Review">
                        <div className="row g-3">
                          <div className="col-12 col-lg-5">
                            <div className="border rounded-4 p-3 h-100" style={{ background: '#f8fbff', borderColor: '#dbeafe' }}>
                              <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                                <div className="fw-semibold">ID Proof</div>
                                {selectedCollegeAuthorization.collegeAccess?.idCardProofUrl && (
                                  <a href={selectedCollegeAuthorization.collegeAccess.idCardProofUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                                    Open
                                  </a>
                                )}
                              </div>
                              {selectedCollegeAuthorization.collegeAccess?.idCardProofUrl ? (
                                /\.(pdf)(\?|$)/i.test(String(selectedCollegeAuthorization.collegeAccess.idCardProofUrl)) ? (
                                  <iframe
                                    title="College ID proof"
                                    src={selectedCollegeAuthorization.collegeAccess.idCardProofUrl}
                                    style={{ width: '100%', minHeight: 540, border: '1px solid #dbeafe', borderRadius: 16, background: '#fff' }}
                                  />
                                ) : (
                                  <img
                                    src={selectedCollegeAuthorization.collegeAccess.idCardProofUrl}
                                    alt="College ID proof"
                                    style={{ width: '100%', minHeight: 540, maxHeight: 720, objectFit: 'contain', borderRadius: 16, border: '1px solid #dbeafe', background: '#fff' }}
                                  />
                                )
                              ) : (
                                <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 540, borderRadius: 16, border: '1px dashed #cbd5e1', background: '#fff' }}>
                                  No proof uploaded
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="col-12 col-lg-7">
                            <div className="border rounded-4 p-3 mb-3" style={{ background: '#ffffff', borderColor: '#dbe5f1' }}>
                              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                                <div>
                                  <div className="small text-muted">Selected Student</div>
                                  <div className="fw-semibold" style={{ fontSize: 20 }}>
                                    {`${selectedCollegeAuthorization.firstName || ''} ${selectedCollegeAuthorization.lastName || ''}`.trim() || selectedCollegeAuthorization.email}
                                  </div>
                                  <div className="small text-muted">{selectedCollegeAuthorization.email} | {selectedCollegeAuthorization.phone || '-'}</div>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                  <span className={`badge rounded-pill ${String(selectedCollegeAuthorization.collegeAccess?.status || '') === 'rejected' ? 'text-bg-danger' : 'text-bg-warning'}`}>
                                    {String(selectedCollegeAuthorization.collegeAccess?.status || 'pending_review').replace(/_/g, ' ')}
                                  </span>
                                  <span className="badge rounded-pill text-bg-light border">
                                    Score: {selectedCollegeAuthorization.collegeAccess?.autoVerificationScore ? `${Math.round(Number(selectedCollegeAuthorization.collegeAccess.autoVerificationScore) * 100)}%` : '-'}
                                  </span>
                                </div>
                              </div>

                              <div className="row g-3">
                                <div className="col-12 col-md-6">
                                  <div className="fw-semibold mb-2">Declared Details</div>
                                  <div className="small text-muted mb-1">College: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.partnerCollegeName || selectedCollegeAuthorization.collegeAccess?.partnerCollegeId?.name || '-'}</span></div>
                                  <div className="small text-muted mb-1">Access Code: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.collegePortalCode || selectedCollegeAuthorization.collegeAccess?.partnerCollegeId?.uniqueAccessCode || '-'}</span></div>
                                  <div className="small text-muted mb-1">Validation Year: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.declaredPassedOutYear || '-'}</span></div>
                                  <div className="small text-muted mb-1">Department: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.declaredDepartment || '-'}</span></div>
                                  <div className="small text-muted">Registered At: <span className="text-dark fw-semibold">{fmtDateTime(selectedCollegeAuthorization.createdAt)}</span></div>
                                </div>
                                <div className="col-12 col-md-6">
                                  <div className="fw-semibold mb-2">Verification Details</div>
                                  <div className="small text-muted mb-1">Auto Check: <span className="text-dark fw-semibold">{String(selectedCollegeAuthorization.collegeAccess?.autoVerificationStatus || 'not_attempted').replace(/_/g, ' ')}</span></div>
                                  <div className="small text-muted mb-1">OCR Status: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.ocrStatus || 'not_attempted'}</span></div>
                                  <div className="small text-muted mb-1">Engine: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.ocrEngine || '-'}</span></div>
                                  <div className="small text-muted mb-1">Confidence: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.ocrConfidence ? `${Math.round(Number(selectedCollegeAuthorization.collegeAccess.ocrConfidence) * 100)}%` : '-'}</span></div>
                                  <div className="small text-muted">Reason: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.autoVerificationReason || selectedCollegeAuthorization.collegeAccess?.ocrReason || 'No verification note returned.'}</span></div>
                                </div>
                                <div className="col-12">
                                  <div className="fw-semibold mb-2">Extracted Result</div>
                                  <div className="small text-muted mb-1">Extracted College: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.extractedCollegeName || '-'}</span></div>
                                  <div className="small text-muted mb-1">Extracted Year: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.extractedPassedOutYear || '-'}</span></div>
                                  <div className="small text-muted mb-1">Extracted Department: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.extractedDepartment || '-'}</span></div>
                                  <div className="small text-muted">OCR Text: <span className="text-dark fw-semibold">{selectedCollegeAuthorization.collegeAccess?.ocrText ? `${String(selectedCollegeAuthorization.collegeAccess.ocrText).slice(0, 320)}${String(selectedCollegeAuthorization.collegeAccess.ocrText).length > 320 ? '...' : ''}` : '-'}</span></div>
                                </div>
                              </div>
                            </div>

                            <div className="border rounded-4 p-3" style={{ background: '#f8fbff', borderColor: '#dbeafe' }}>
                              <label className="form-label small fw-semibold">Reject Reason / Review Notes</label>
                              <textarea
                                className="form-control"
                                rows="4"
                                placeholder="Enter the reject reason or internal review note."
                                value={collegeReviewNotes}
                                onChange={(e) => setCollegeReviewNotes(e.target.value)}
                              />
                              <div className="d-flex flex-wrap gap-2 mt-3">
                                <button type="button" className="btn btn-success" disabled={saving} onClick={() => updateCollegeAuthorization(selectedCollegeAuthorization._id, 'approve')}>
                                  {saving ? 'Processing...' : 'Approve'}
                                </button>
                                <button type="button" className="btn btn-outline-danger" disabled={saving} onClick={() => updateCollegeAuthorization(selectedCollegeAuthorization._id, 'reject')}>
                                  Reject
                                </button>
                                <button type="button" className="btn btn-outline-dark" disabled={saving} onClick={() => deleteCollegeAuthorization(selectedCollegeAuthorization._id)}>
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardShell>
                    )}
                  </div>
                </div>
              )}
            </AdminSurface>
          </div>
        </div>
      )}

      {activeTab === 'dashboard' && !canWrite && (
        <div className="student-dashboard-page d-flex flex-column gap-3">
          {/* Keep the dashboard information architecture identical on every viewport.
              Individual grids remain responsive through Bootstrap and inline constraints. */}
          {true ? (
            <StudentSurface
              bordered={!isStudentMobile}
              className="student-reference-dashboard"
              style={{
                padding: isStudentMobile ? 0 : 32,
                maxWidth: 'none',
                margin: 0,
                background: isStudentMobile ? 'transparent' : '#f8fafc',
                border: 'none',
                borderRadius: 0,
                boxShadow: 'none',
              }}
            >
              <div className="dashboard-mobile-greeting d-flex justify-content-between align-items-center gap-2 mb-3">
                <div className="flex-fill" style={{ minWidth: 0 }}>
                  <div
                    className="fw-bold mb-1 text-truncate"
                    style={{
                      fontSize: isStudentMobile ? 18 : 28,
                      lineHeight: 1.18,
                      color: '#0f172a',
                      fontFamily: "'Segoe UI', 'Inter', sans-serif",
                      letterSpacing: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Good morning, {user?.firstName || 'Learner'}
                  </div>
                  <div
                    className="text-truncate"
                    style={{ color: '#64748b', fontSize: isStudentMobile ? 12 : 14, whiteSpace: 'nowrap' }}
                  >
                    Continue your learning journey.
                  </div>
                </div>
                <div
                  className="dashboard-streak-chip d-inline-flex align-items-center gap-2 flex-shrink-0"
                  style={{
                    borderRadius: 10,
                    padding: isStudentMobile ? '7px 9px' : '8px 11px',
                    background: '#ffffff',
                    border: '1px solid #fde7d8',
                    color: '#f97316',
                    boxShadow: '0 6px 16px rgba(15,23,42,0.04)',
                    fontSize: isStudentMobile ? 11 : 13,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <i className="bi bi-fire" />
                  <span className="d-flex flex-column" style={{ lineHeight: 1.15 }}>
                    <span className="fw-semibold" style={{ color: studentConsistencyOverview.currentStreak ? '#ea580c' : '#0f172a', paddingRight: 2 }}>
                      {studentConsistencyOverview.currentStreak
                        ? `${studentConsistencyOverview.currentStreak} day streak`
                        : 'Start your streak today!'}
                    </span>
                    <small style={{ color: '#64748b' }}>
                      {studentConsistencyOverview.currentStreak ? 'Keep the momentum going!' : 'Learn for 10 minutes'}
                    </small>
                  </span>
                </div>
              </div>


              <DashboardRecommendedCarousel
                courses={studentDashboardRecommendedCourses}
                learning={learning}
                isMobile={isStudentMobile}
                surface="dashboard-recommended"
                onReach={reportCourseCarouselReach}
                onOpen={(course) => openStudentCourseCard(course, { fallbackTab: 'for-you' })}
                onSeeAll={() => openTab('recommended')}
              />

              <DashboardContinueLearningCarousel
                rows={studentDashboardContinueCourses}
                isMobile={isStudentMobile}
                onSeeAll={() => openTab('my-courses')}
                onOpen={(row) => {
                  const courseId = String(row.course?._id || row.course || '');
                  if (!courseId) return;
                  openTab('my-courses');
                  if (row.isVirtualEnrollment) {
                    openStudentCourseCard(row.course, { fallbackTab: 'my-courses' });
                  } else {
                    openCourseWorkspace(courseId);
                  }
                }}
              />

              {(!isCollegePortalStudent || studentDashboardContinueCourses.length < 2) && (
              <div
                className="dashboard-learning-hero mb-4"
                style={{
                  borderRadius: isStudentMobile ? 14 : 18,
                  padding: isStudentMobile ? 18 : 30,
                  background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 52%, #4f46e5 100%)',
                  boxShadow: '0 24px 44px rgba(79,70,229,0.28)',
                  color: '#ffffff',
                }}
              >
                <div className="dashboard-learning-hero-main d-flex justify-content-between gap-3 align-items-center mb-3">
                  <div className="flex-fill">
                    <span
                      className="badge rounded-pill"
                      style={{ background: 'rgba(255,255,255,0.16)', color: '#ffffff', padding: '10px 14px', fontWeight: 600 }}
                    >
                      Continue Learning
                    </span>
                    <div className="fw-semibold mt-3" style={{ fontSize: isStudentMobile ? 22 : 38, lineHeight: 1.12 }}>
                      {studentLearningOverview.continueCourse?.course?.courseName || studentFreeCourseShowcase[0]?.courseName || 'Start your learning path'}
                    </div>
                    <div className="mt-2" style={{ color: 'rgba(255,255,255,0.82)' }}>
                      {isStudentMobile
                        ? (inferCourseMode(studentLearningOverview.continueCourse?.course || studentFreeCourseShowcase[0] || {}) === 'on_demand' ? 'On-demand' : 'Instructor-led')
                        : (studentLearningOverview.continueCourse?.currentModuleTitle
                          || studentLearningOverview.continueCourse?.course?.category
                          || studentPrimaryCategory
                          || 'Build your next skill')}
                    </div>
                  </div>
                  <div className="dashboard-learning-hero-art" aria-hidden="true">
                    <span className="dashboard-learning-hero-orbit dashboard-learning-hero-orbit-one" />
                    <span className="dashboard-learning-hero-orbit dashboard-learning-hero-orbit-two" />
                    <img src={pythonLearningHero} alt="" />
                  </div>
                </div>
                <div className="fw-semibold mb-2" style={{ fontSize: 18 }}>
                  {Number(studentLearningOverview.continueCourse?.learningSummary?.completionPercent || 0)}% complete
                </div>
                <ProgressBar value={Number(studentLearningOverview.continueCourse?.learningSummary?.completionPercent || 0)} color="#ffffff" trackColor="rgba(255,255,255,0.24)" />
                <button
                  type="button"
                  className="btn btn-light w-100 mt-3 fw-semibold"
                  style={{ borderRadius: 12, padding: '14px 16px', color: '#1e1b4b' }}
                  onClick={() => {
                    if (studentLearningOverview.continueCourse?.course?._id) {
                      openTab('my-courses');
                      if (studentLearningOverview.continueCourse.isVirtualEnrollment) {
                        openStudentCourseCard(studentLearningOverview.continueCourse.course, { fallbackTab: 'my-courses' });
                      } else {
                        openCourseWorkspace(String(studentLearningOverview.continueCourse.course._id));
                      }
                      return;
                    }
                    if (studentFreeCourseShowcase[0]) {
                      openStudentCourseCard(studentFreeCourseShowcase[0], { fallbackTab: 'my-courses' });
                    }
                  }}
                >
                  <span className="d-flex w-100 align-items-center justify-content-between gap-3">
                    <span>Continue Learning</span>
                    <i className="bi bi-arrow-right" />
                  </span>
                </button>
              </div>
              )}

              {false && studentHasSubscriptionTab && !!studentDashboardPlanCards.length && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <div>
                      <div className="fw-semibold" style={{ color: '#0f172a', fontSize: isStudentMobile ? 16 : 20 }}>Affordable Programs for Future-Ready Skills</div>
                      <div style={{ color: '#64748b', fontSize: isStudentMobile ? 10 : 12 }}>
                        Start free or choose a low-cost guided plan built for your career goals.
                      </div>
                    </div>
                    <span className="badge flex-shrink-0" style={{ borderRadius: 7, padding: '6px 8px', color: '#4f46e5', background: '#eef2ff', fontSize: isStudentMobile ? 9 : 11 }}>
                      {studentDashboardPlanCards.length} {studentDashboardPlanCards.length === 1 ? 'plan' : 'plans'}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isStudentMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
                      gap: isStudentMobile ? 8 : 12,
                    }}
                  >
                    {studentDashboardPlanCards.map(({ plan, isCurrent, categoryMatch }, index) => {
                      const categories = Array.from(new Set((plan.rules || []).flatMap((rule) => rule?.categories || []).filter(Boolean)));
                      const availabilityStatus = String(plan.availability?.status || 'available');
                      const isPurchasable = plan.availability?.isPurchasable !== false;
                      const isPaidPlan = Number(plan.salePrice || plan.price || 0) > 0;
                      const durationLabel = Number(plan.durationDays || 0) > 0 ? `${plan.durationDays} days` : 'Flexible access';
                      const accessLabel = plan.quotas?.unlimitedOnDemand
                        ? 'Unlimited courses'
                        : Number(plan.quotas?.anyCourseLimit || 0) > 0
                          ? `${plan.quotas.anyCourseLimit} courses included`
                          : categories.length
                            ? `${categories.length} learning ${categories.length === 1 ? 'track' : 'tracks'}`
                            : 'Curated course access';
                      const accent = isCurrent ? '#4f46e5' : !isPurchasable ? '#64748b' : index % 2 ? '#059669' : '#7c3aed';
                      const tagline = isCurrent
                        ? (studentActiveSubscription ? 'Your active membership, ready whenever you are.' : 'Your exclusive free start to build job-ready skills.')
                        : categoryMatch && studentPrimaryCategory
                          ? `A focused career path for ${formatCourseCategoryLabel(studentPrimaryCategory)} learners.`
                          : isPaidPlan
                            ? 'Advance faster with guided learning, projects, and career support.'
                            : 'Build your foundation and discover your next learning goal.';
                      const statusLabel = isCurrent
                        ? 'Current plan'
                        : availabilityStatus === 'coming_soon'
                          ? 'Coming soon'
                          : availabilityStatus === 'closed'
                            ? 'Enrollment closed'
                            : categoryMatch
                              ? 'Recommended'
                              : 'Available';
                      return (
                        <div
                          key={`dashboard-membership-${plan._id || plan.name || index}`}
                          className="d-flex flex-column border position-relative overflow-hidden"
                          style={{
                            minWidth: 0,
                            minHeight: isStudentMobile ? 174 : 190,
                            padding: isStudentMobile ? '12px 10px 10px' : '16px 14px 14px',
                            borderRadius: 10,
                            borderColor: isCurrent ? '#a5b4fc' : '#e2e8f0',
                            background: isCurrent ? 'linear-gradient(155deg, #eef2ff 0%, #ffffff 62%)' : '#ffffff',
                            boxShadow: isCurrent ? '0 12px 26px rgba(79,70,229,0.12)' : '0 10px 22px rgba(15,23,42,0.05)',
                          }}
                        >
                          <div style={{ position: 'absolute', inset: '0 0 auto 0', height: 3, background: accent }} />
                          <div className="d-flex justify-content-between align-items-center gap-1 mb-2">
                            <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: isStudentMobile ? 32 : 38, height: isStudentMobile ? 32 : 38, borderRadius: 8, color: accent, background: `${accent}14` }}>
                              <i className={`bi ${isCurrent ? 'bi-patch-check-fill' : 'bi-stars'}`} />
                            </div>
                            <span className="badge" style={{ borderRadius: 6, padding: '4px 6px', fontSize: isStudentMobile ? 8 : 10, color: isCurrent ? '#3730a3' : !isPurchasable ? '#475569' : categoryMatch ? '#047857' : '#6d28d9', background: isCurrent ? '#e0e7ff' : !isPurchasable ? '#f1f5f9' : categoryMatch ? '#ecfdf5' : '#f5f3ff' }}>
                              {statusLabel}
                            </span>
                          </div>
                          <div className="fw-semibold" style={{ color: '#0f172a', fontSize: isStudentMobile ? 14 : 17, lineHeight: 1.2 }}>
                            {plan.name || plan.title || 'Membership Plan'}
                          </div>
                          <div className="mb-2" style={{ color: isCurrent ? '#4f46e5' : '#64748b', fontSize: isStudentMobile ? 9 : 11, lineHeight: 1.3 }}>
                            {tagline}
                          </div>
                          <div className="d-flex flex-wrap gap-1 mb-3" style={{ color: '#475569', fontSize: isStudentMobile ? 9 : 11 }}>
                            <span style={{ padding: '4px 5px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0' }}><i className="bi bi-calendar3 me-1" />{durationLabel}</span>
                            <span style={{ padding: '4px 5px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0' }}><i className="bi bi-journal-check me-1" />{accessLabel}</span>
                          </div>
                          <button
                            type="button"
                            className={`btn btn-sm w-100 fw-semibold mt-auto ${isCurrent ? 'btn-outline-primary' : 'btn-primary'}`}
                            style={{ borderRadius: 8, padding: isStudentMobile ? '6px 7px' : '8px 10px', fontSize: isStudentMobile ? 10 : 12 }}
                            disabled={!isCurrent && !isPurchasable}
                            onClick={() => {
                              if (isCurrent) {
                                openTab(studentActiveSubscription ? 'my-subscription' : 'my-courses');
                                return;
                              }
                              openCheckout(plan);
                            }}
                          >
                            {isCurrent
                              ? (studentActiveSubscription ? 'View Membership' : 'Explore Courses')
                              : availabilityStatus === 'coming_soon'
                                ? 'Opens Soon'
                                : availabilityStatus === 'closed'
                                  ? 'Enrollment Closed'
                                  : 'Explore Plan'} <i className={`bi ${isPurchasable || isCurrent ? 'bi-arrow-right' : 'bi-lock'} ms-1`} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {isStudentMobile ? (
                  <section className="dashboard-weekly-goal mb-3" aria-labelledby="dashboard-today-goal-title">
                    <div className="dashboard-weekly-goal-heading">
                      <div className="dashboard-weekly-goal-info">
                        <div className="dashboard-weekly-goal-icon" aria-hidden="true"><i className="bi bi-fire" /></div>
                        <div>
                          <div id="dashboard-today-goal-title" className="dashboard-compact-title">Today's Goal</div>
                          <div className="dashboard-weekly-goal-value">
                            {Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60))} <span>/ {studentGoal.minutes || 60} min</span>
                          </div>
                        </div>
                      </div>
                      <div className="dashboard-weekly-goal-remaining">
                        <i className={`bi ${Math.max(0, (studentGoal.minutes || 60) - Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60))) > 0 ? 'bi-hourglass-split' : 'bi-trophy-fill'}`} />
                        {Math.max(0, (studentGoal.minutes || 60) - Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60))) > 0
                          ? `${Math.max(0, (studentGoal.minutes || 60) - Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60)))} min remaining`
                          : 'Goal reached'}
                      </div>
                    </div>
                    <div className="dashboard-weekly-goal-progress">
                      <ProgressBar
                        value={studentGoal.enabled ? Math.min(100, Math.round((Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60)) / Math.max(1, studentGoal.minutes || 60)) * 100)) : 0}
                        color="#f97316"
                        trackColor="#e8eaf8"
                      />
                      <span>{studentGoal.enabled ? Math.min(100, Math.round((Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60)) / Math.max(1, studentGoal.minutes || 60)) * 100)) : 0}%</span>
                    </div>
                  </section>
              ) : (
                <div className="mb-3 bg-white" style={{ padding: 16, border: '1px solid #edf2ff', borderRadius: 12, boxShadow: '0 10px 24px rgba(15,23,42,0.05)' }}>
                  <div className="d-flex align-items-center gap-2">
                    <div className="d-inline-flex flex-shrink-0 align-items-center justify-content-center" style={{ width: 50, height: 50, borderRadius: 11, background: '#fff1eb', color: '#f97316', fontSize: 24 }}>
                      <i className="bi bi-fire" />
                    </div>
                    <div className="flex-fill" style={{ minWidth: 0 }}>
                      <div className="fw-semibold mb-1" style={{ color: '#0f172a', fontSize: 15 }}>Today's Goal</div>
                      <div className="fw-semibold" style={{ fontSize: 25, lineHeight: 1, color: '#0f172a', whiteSpace: 'nowrap' }}>
                        {Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60))}
                        <span style={{ fontSize: 16, color: '#475569' }}> / {studentGoal.minutes || 60} min</span>
                      </div>
                    </div>
                    <div className="fw-semibold flex-shrink-0" style={{ color: '#f97316', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {Math.max(0, (studentGoal.minutes || 60) - Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60)))} min remaining
                    </div>
                  </div>
                  <div className="mt-2">
                    <ProgressBar value={studentGoal.enabled ? Math.min(100, Math.round((Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60)) / (studentGoal.minutes || 60)) * 100)) : 0} color="#f97316" trackColor="#e5e7eb" />
                  </div>
                </div>
              )}

              {false && isStudentMobile && studentHasSubscriptionTab && studentDashboardUpgrade && (
                <section className="dashboard-personalized-upgrade mb-4" aria-labelledby="dashboard-upgrade-title">
                  <div className="dashboard-personalized-upgrade-icon" aria-hidden="true"><i className="bi bi-stars" /></div>
                  <div className="dashboard-personalized-upgrade-copy">
                    <div id="dashboard-upgrade-title" className="dashboard-section-eyebrow">Unlock more learning</div>
                    <div className="dashboard-personalized-upgrade-title">
                      You're currently on {studentSubscriptionUsage.planName || studentDashboardMembershipPlan?.name || 'your current plan'}
                    </div>
                    <div className="dashboard-personalized-upgrade-subtitle">
                      With {studentDashboardUpgrade.plan.name || studentDashboardUpgrade.plan.title}, unlock:
                    </div>
                    {!!studentDashboardUpgrade.features.length && (
                      <ul className="dashboard-personalized-upgrade-features">
                        {studentDashboardUpgrade.features.map((feature) => <li key={feature}><i className="bi bi-check-circle-fill" />{feature}</li>)}
                      </ul>
                    )}
                  </div>
                  <button type="button" className="dashboard-personalized-upgrade-action" onClick={() => openTab('my-subscription')}>
                    Explore {studentDashboardUpgrade.plan.name || studentDashboardUpgrade.plan.title || 'plan'} <i className="bi bi-arrow-right" />
                  </button>
                </section>
              )}

              {false && studentIsOnDemandPrimary ? (
                <section className="dashboard-premium-offer mb-4" aria-label="Premium learning offer">
                  <div className="dashboard-premium-offer-icon" aria-hidden="true">
                    <i className="bi bi-gem" />
                  </div>
                  <div className="dashboard-premium-offer-copy">
                    <div className="dashboard-premium-offer-label">Go Premium</div>
                    <div className="dashboard-premium-offer-title">Unlock more ways to learn and grow.</div>
                    <div className="dashboard-premium-offer-text">
                      Explore live learning, guided projects, mentor support, and career-focused benefits.
                    </div>
                  </div>
                  <button type="button" className="btn dashboard-premium-offer-action" onClick={() => openTab('for-you')}>
                    <span>Explore Premium</span>
                    <i className="bi bi-arrow-right" />
                  </button>
                </section>
              ) : null}

              {false && studentHasSubscriptionTab && (
                <div className="mb-4 rounded-4 bg-white" style={{ padding: 18, border: '1px solid #ddd6fe', boxShadow: '0 14px 34px rgba(15,23,42,0.05)' }}>
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                    <div>
                      <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 18 }}>Level up with Premium</div>
                      <div className="small" style={{ color: '#64748b' }}>Unlock unlimited learning and career support.</div>
                    </div>
                    <span className="badge rounded-pill" style={{ background: '#fff7ed', color: '#ea580c' }}>
                      {studentActiveSubscription ? 'Active Plan' : 'Most Popular'}
                    </span>
                  </div>
                  <div className="row g-3 align-items-stretch">
                    <div className="col-12 col-sm-6">
                      <div className="h-100 rounded-4" style={{ padding: 16, background: '#f8fbff', border: '1px solid #e0e7ff' }}>
                        <div className="small fw-semibold mb-2" style={{ color: '#64748b' }}>Your Current Plan</div>
                        <div className="fw-semibold mb-2" style={{ color: '#0f172a', fontSize: 18 }}>
                          {studentSubscriptionUsage.planName || studentDashboardMembershipPlan?.name || 'Free Plan'}
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {(studentCurrentPlanBenefits.length ? studentCurrentPlanBenefits : ['Access to free courses', 'Recorded lessons', 'Practice quizzes']).slice(0, 5).map((item) => (
                            <div key={`mobile-current-plan-${item}`} className="d-flex align-items-start gap-2 small" style={{ color: '#334155' }}>
                              <i className="bi bi-check-circle-fill" style={{ color: '#22c55e', marginTop: 2 }} />
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-sm-6">
                      <div className="h-100 rounded-4" style={{ padding: 16, background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(168,85,247,0.08) 100%)', border: '1px solid #ddd6fe' }}>
                        <div className="small fw-semibold mb-2" style={{ color: '#7c3aed' }}>
                          Unlock with {studentSubscriptionDetails.recommendedPlan?.name || 'Pro Plan'}
                        </div>
                        <div className="fw-semibold mb-2" style={{ color: '#0f172a', fontSize: 18 }}>
                          {studentSubscriptionDetails.recommendedPlan?.name || 'Upgrade plan'}
                        </div>
                        <div className="d-flex flex-column gap-2">
                          {(studentRecommendedPlanBenefits.length ? studentRecommendedPlanBenefits : ['Live interactive classes', 'Mentor support', 'Projects and advanced practice']).slice(0, 6).map((item) => (
                            <div key={`mobile-premium-plan-${item}`} className="d-flex align-items-start gap-2 small" style={{ color: '#334155' }}>
                              <i className="bi bi-check-circle-fill" style={{ color: '#4f46e5', marginTop: 2 }} />
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary w-100 mt-3 fw-semibold"
                          style={{ borderRadius: 16, background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', border: 'none' }}
                          onClick={() => (studentSubscriptionDetails.recommendedPlan ? openCheckout(studentSubscriptionDetails.recommendedPlan) : openTab('my-subscription'))}
                        >
                          {studentActiveSubscription ? 'View Membership' : 'Upgrade to Pro'} <i className="bi bi-arrow-right ms-1" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!!studentFreeCourseShowcase.length && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 18 }}>Free Courses to Start Now</div>
                      <div className="small text-muted">Explore useful skills and begin learning without a payment step.</div>
                    </div>
                    <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ color: '#7c3aed' }} onClick={() => openTab('for-you')}>
                      See all <i className="bi bi-chevron-right" />
                    </button>
                  </div>
                  <div className={isStudentMobile ? 'd-flex flex-column gap-3' : 'row g-4'}>
                    {studentFreeCourseShowcase.slice(0, 3).map((course, index) => {
                      const durationDays = Number(course.duration?.days || course.durationDays || (Number(course.numberOfMonths || 0) * 30) || 0);
                      const lessonCount = Number(course.totalLessons || course.lessonCount || 0);
                      const enrollmentCount = Number(course.enrollmentCount || course.enrollmentsCount || 0);
                      const deliveryMode = String(course.deliveryMode || course.mode || '').toLowerCase();
                      const featureSource = [
                        course.category,
                        course.shortDescription,
                        course.description,
                        course.notes,
                      ].join(' ').toLowerCase();
                      const featureBadges = [
                        { label: 'Live Classes', icon: 'bi-camera-video', visible: ['live', 'hybrid'].includes(deliveryMode) || /live class|live session/.test(featureSource) },
                        { label: 'Projects', icon: 'bi-code-slash', visible: /project/.test(featureSource) },
                        { label: 'Internship Included', icon: 'bi-briefcase', visible: /internship/.test(featureSource) },
                        { label: 'Mentor Support', icon: 'bi-people', visible: /mentor/.test(featureSource) },
                        { label: 'Recorded Lessons', icon: 'bi-play-btn', visible: deliveryMode === 'on_demand' || /recorded lesson/.test(featureSource) },
                      ].filter((item) => item.visible).slice(0, 3);
                      return (
                        <button
                          key={`mobile-premium-program-${course._id || index}`}
                          type="button"
                          className={`${isStudentMobile ? 'w-100' : 'col-12 col-xl-4'} text-start bg-white p-0 dashboard-featured-free-card`}
                          onClick={() => openStudentCourseCard(course, { fallbackTab: 'for-you' })}
                          style={{ borderRadius: 20, overflow: 'hidden', boxShadow: '0 14px 30px rgba(15,23,42,0.05)', border: '1px solid #edf2ff' }}
                        >
                        <div className={`dashboard-featured-free-layout ${isStudentMobile ? '' : 'is-desktop'}`}>
                          <div className="dashboard-featured-free-artwork" style={{ width: isStudentMobile ? 132 : '100%', minWidth: isStudentMobile ? 132 : 0, borderRight: isStudentMobile ? '1px solid #edf2ff' : 'none', borderBottom: isStudentMobile ? 'none' : '1px solid #edf2ff' }}>
                            <CourseArtwork title={course.courseName} category={course.category} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || (index % 2 === 0 ? '#4f46e5' : '#0f766e')} height={isStudentMobile ? 220 : 210} compact />
                            <span className="badge position-absolute dashboard-course-free-badge" style={{ top: 10, left: 10, padding: '6px 8px', borderRadius: 8 }}>
                              <i className="bi bi-unlock-fill me-1" /><span>Free to learn</span>
                            </span>
                            <span className="dashboard-featured-course-icon" aria-hidden="true"><i className={`bi ${getCourseArtworkTheme(course.courseName, course.category, course.courseColor).icon}`} /></span>
                          </div>
                          <div className="flex-fill dashboard-featured-free-body" style={{ padding: 14 }}>
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                              <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 20, lineHeight: 1.15 }}>
                                {course.courseName || 'Program'}
                              </div>
                            </div>
                            <div className="small mb-2 text-truncate" title={course.shortDescription || course.category || 'Career-focused learning program'} style={{ color: '#64748b' }}>
                              {course.shortDescription || course.category || 'Career-focused learning path'}
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2 small" style={{ color: '#64748b' }}>
                              <span><i className="bi bi-calendar3 me-1" />{durationDays ? `${durationDays} days` : 'Flexible duration'}</span>
                              <span><i className="bi bi-journal-text me-1" />{lessonCount} lessons</span>
                              <span><i className="bi bi-people-fill me-1" />{enrollmentCount.toLocaleString('en-IN')} enrolled</span>
                            </div>
                            {featureBadges.length ? (
                              <div className="d-flex flex-wrap gap-1 mb-3">
                                {featureBadges.map((feature) => (
                                  <span key={`${course._id || index}-${feature.label}`} className="badge border" style={{ padding: '6px 8px', borderRadius: 7, color: '#4338ca', background: '#f5f3ff', borderColor: '#ddd6fe' }}>
                                    <i className={`bi ${feature.icon} me-1`} />{feature.label}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            <div className="d-flex justify-content-between align-items-center gap-2">
                              <span className="d-inline-flex align-items-center gap-2 small fw-semibold" style={{ padding: '7px 9px', borderRadius: 8, background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', color: '#ffffff', boxShadow: '0 6px 13px rgba(79,70,229,0.18)' }}>
                                <i className="bi bi-people" />
                                {getLearningFocusedCourseCta(course)}
                              </span>
                            </div>
                          </div>
                        </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {false && studentHasSubscriptionTab && (
                <div className="mb-4">
                  <div className="fw-semibold mb-2" style={{ color: '#0f172a', fontSize: 18 }}>Why students upgrade</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
                    {[
                      { title: 'Live Mentor Sessions', text: 'Learn from experts in real time.', icon: 'bi-camera-video-fill', bg: '#f3e8ff', color: '#8b5cf6' },
                      { title: 'Industry Projects', text: 'Build real-world portfolio work.', icon: 'bi-briefcase-fill', bg: '#e8fbf2', color: '#10b981' },
                      { title: 'Certificates', text: 'Earn recognized outcomes.', icon: 'bi-award-fill', bg: '#fff1eb', color: '#f97316' },
                      { title: 'Career Support', text: 'Get review and placement help.', icon: 'bi-people-fill', bg: '#eef2ff', color: '#3b82f6' },
                    ].map((item) => (
                      <div
                        key={`mobile-upgrade-${item.title}`}
                        className="d-flex flex-column align-items-center justify-content-center text-center bg-white border"
                        style={{ minWidth: 0, minHeight: 106, padding: '9px 5px', borderRadius: 10, borderColor: '#edf2ff' }}
                      >
                        <div className="d-inline-flex align-items-center justify-content-center mb-2" style={{ width: 32, height: 32, borderRadius: 9, background: item.bg, color: item.color }}>
                          <i className={`bi ${item.icon}`} />
                        </div>
                        <div className="fw-semibold" style={{ color: '#0f172a', lineHeight: 1.15, fontSize: 11 }}>{item.title}</div>
                        <div className="mt-1" style={{ color: '#64748b', fontSize: 9, lineHeight: 1.25 }}>{item.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {studentHasSubscriptionTab && (
                <div className="mb-4 bg-white" style={{ padding: 14, borderRadius: 10, border: '1px solid #edf2ff', boxShadow: '0 10px 22px rgba(15,23,42,0.05)' }}>
                  <div className="d-flex align-items-start gap-2 mb-3">
                    <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 48, height: 48, borderRadius: 10, background: '#eef2ff', color: '#4f46e5', fontSize: 21 }}>
                      <i className="bi bi-person-circle" />
                    </div>
                    <div className="flex-fill">
                      <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 18 }}>
                        {activeDashboardPromotion?.promoTitle || activeDashboardPromotion?.title || 'Top Skilled Academy learner'}
                      </div>
                      <div className="small mb-2" style={{ color: '#7c3aed' }}>
                        {studentPrimaryCategory || 'Career track'}
                      </div>
                      <div className="mb-2" style={{ color: '#f59e0b' }}>
                        <i className="bi bi-star-fill me-1" />
                        <i className="bi bi-star-fill me-1" />
                        <i className="bi bi-star-fill me-1" />
                        <i className="bi bi-star-fill me-1" />
                        <i className="bi bi-star-fill" />
                      </div>
                      <div className="small" style={{ color: '#475569', lineHeight: 1.55 }}>
                        {activeDashboardPromotion?.promoText || activeDashboardPromotion?.subtitle || 'Structured learning, mentor guidance, and projects help students move faster with a clear learning path.'}
                      </div>
                    </div>
                  </div>
                  <div className="row g-0">
                    <div className="col-6">
                      <div className="pe-3" style={{ borderRight: '1px solid #edf2ff' }}>
                        <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#8b5cf6' }}>
                          <i className="bi bi-people-fill" />
                          <span className="small fw-semibold">Active learners</span>
                        </div>
                        <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 20 }}>{Math.max(learning.length, studentLearningOverview.totalCourses || 0)}</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="ps-3">
                        <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#10b981' }}>
                          <i className="bi bi-star-fill" />
                          <span className="small fw-semibold">Student rating</span>
                        </div>
                        <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 20 }}>
                          {studentLearningOverview.averageScore != null ? `${Math.max(4, Math.min(5, Number(studentLearningOverview.averageScore || 0) / 20)).toFixed(1)}/5` : '4.8/5'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {studentHasSubscriptionTab && (
                <div className="mb-4" style={{ padding: 10, borderRadius: 10, background: 'linear-gradient(135deg, rgba(139,92,246,0.10) 0%, rgba(99,102,241,0.10) 100%)', border: '1px solid #ddd6fe' }}>
                  <div className="d-flex align-items-center gap-2">
                    <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 8, background: 'linear-gradient(135deg, #0ea5e9 0%, #4f46e5 100%)', color: '#fff', fontSize: 14 }}>
                      <i className="bi bi-mortarboard-fill" />
                    </div>
                    <div className="flex-fill" style={{ minWidth: 0 }}>
                      <div className="fw-semibold" style={{ color: '#4338ca', fontSize: 13, lineHeight: 1.2 }}>More learning is free for you</div>
                      <div style={{ color: '#475569', fontSize: 10, lineHeight: 1.3 }}>Explore free courses, lessons, and live learning batches.</div>
                    </div>
                    <button type="button" className="btn btn-light btn-sm fw-semibold flex-shrink-0" style={{ color: '#4338ca', borderRadius: 8, padding: '6px 8px', fontSize: 10 }} onClick={() => openTab('for-you')}>
                      Learn Free <i className="bi bi-arrow-right ms-1" />
                    </button>
                  </div>
                </div>
              )}

              {studentHasSubscriptionTab && (
                <StudentUpcomingLiveClasses
                  courses={studentUpcomingLiveItems}
                  isMobile={isStudentMobile}
                  className="mb-4"
                  onViewAll={() => openTab('for-you')}
                  onOpen={(course) => course._learningEvent
                    ? openStudentCtaLink(course.registrationUrl || course.meetingUrl || '/lms?tab=for-you')
                    : openStudentCourseCard(course, { fallbackTab: 'for-you' })}
                />
              )}

              {isStudentMobile && (
                <section className="dashboard-learning-activity mb-3" aria-labelledby="dashboard-learning-activity-title">
                  <div className="d-flex justify-content-between align-items-center gap-2">
                    <div className="dashboard-learning-activity-heading">
                      <span aria-hidden="true"><i className="bi bi-activity" /></span>
                      <div>
                        <div id="dashboard-learning-activity-title" className="dashboard-compact-title">Learning Activity</div>
                        <small>Your weekly rhythm</small>
                      </div>
                    </div>
                    <button type="button" className="dashboard-activity-link" onClick={() => openTab('progress')}>View activity <i className="bi bi-arrow-right" /></button>
                  </div>
                  <div className="dashboard-activity-week" aria-label={`${studentWeeklyActivity.activeDays} active learning days this week`}>
                    {studentWeeklyActivity.days.map((day) => (
                      <button
                        key={day.key}
                        type="button"
                        className="dashboard-activity-day"
                        onClick={() => openTab('progress')}
                        title={`${day.label}: ${day.active ? 'Learning completed' : day.future ? 'Upcoming day' : 'No learning activity'}`}
                        aria-label={`${day.label}: ${day.active ? 'learning completed' : day.future ? 'upcoming day' : 'no learning activity'}. View progress.`}
                      >
                        <span className={`${day.active ? 'is-active' : ''} ${day.future ? 'is-future' : ''}`}>
                          <i className={`bi ${day.active ? 'bi-lightning-charge-fill' : day.future ? 'bi-lock-fill' : 'bi-circle-fill'}`} />
                        </span>
                        <small>{day.label}</small>
                      </button>
                    ))}
                  </div>
                  <div className="dashboard-activity-summary">
                    <span><i className="bi bi-check-circle-fill" /> {studentWeeklyActivity.activeDays} {studentWeeklyActivity.activeDays === 1 ? 'day' : 'days'} active</span>
                    <span aria-hidden="true">·</span>
                    <span><i className="bi bi-stopwatch-fill" /> {fmtDuration(studentWeeklyActivity.learnedMinutes * 60)} learned</span>
                  </div>
                </section>
              )}

              <div className="mb-3 bg-white" style={{ padding: isStudentMobile ? 12 : 16, border: '1px solid #edf2ff', borderRadius: 12, boxShadow: '0 10px 24px rgba(15,23,42,0.05)' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 18 }}>Learning Overview</div>
                  <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ color: '#7c3aed' }} onClick={() => openTab('my-courses')}>
                    View all <i className="bi bi-chevron-right" />
                  </button>
                </div>
                <div className="row g-2">
                  {[
                    { label: 'Courses', value: studentLearningOverview.totalCourses || 0, hint: 'Enrolled', icon: 'bi-book', accentBg: '#eef2ff', accent: '#4f46e5' },
                    { label: 'Lessons', value: learning.reduce((sum, row) => sum + Number(row.learningSummary?.completedLessons || 0), 0), hint: 'Completed', icon: 'bi-play-circle', accentBg: '#e8fbf2', accent: '#10b981' },
                    { label: 'Certificates', value: studentSubscriptionUsage.certificates || 0, hint: 'Earned', icon: 'bi-award', accentBg: '#f3e8ff', accent: '#8b5cf6' },
                  ].map((item) => (
                    <div className="col-4" key={`mobile-overview-${item.label}`}>
                      <div className="h-100 d-flex align-items-center gap-2 border bg-white" style={{ minHeight: isStudentMobile ? 62 : 72, padding: isStudentMobile ? 8 : 11, borderColor: '#edf2ff', borderRadius: 10 }}>
                        <div className="d-inline-flex flex-shrink-0 align-items-center justify-content-center" style={{ width: isStudentMobile ? 30 : 36, height: isStudentMobile ? 30 : 36, borderRadius: 9, background: item.accentBg, color: item.accent, fontSize: isStudentMobile ? 13 : 16 }}>
                          <i className={`bi ${item.icon}`} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="text-truncate" style={{ color: '#64748b', fontSize: isStudentMobile ? 9 : 11 }}>{item.label}</div>
                          <div className="fw-semibold" style={{ fontSize: isStudentMobile ? 17 : 20, lineHeight: 1.05, color: '#0f172a' }}>{item.value}</div>
                          <div className="text-truncate" style={{ color: '#64748b', fontSize: isStudentMobile ? 9 : 10 }}>{item.hint}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {studentHasSubscriptionTab && (
                <div className="text-white" style={{ padding: 12, borderRadius: 10, background: 'linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)', boxShadow: '0 14px 28px rgba(79,70,229,0.20)' }}>
                  <div className="d-flex gap-2">
                    <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(255,255,255,0.12)', color: '#facc15', fontSize: 19 }}>
                      <i className="bi bi-book-half" />
                    </div>
                    <div className="flex-fill">
                      <div className="fw-semibold mb-1" style={{ fontSize: 16, lineHeight: 1.15 }}>Keep Growing with Your Free Plan</div>
                      <div style={{ color: 'rgba(255,255,255,0.82)', fontSize: 11, lineHeight: 1.35 }}>Discover free courses, recorded lessons, practice content, and available live batches.</div>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-1 mt-2 mb-2">
                    {['Free Courses', 'Recorded Lessons', 'Live Batches'].map((item) => (
                      <span key={item} className="badge" style={{ background: 'rgba(255,255,255,0.12)', color: '#ffffff', padding: '5px 7px', borderRadius: 6, fontSize: 9 }}>{item}</span>
                    ))}
                  </div>
                  <button type="button" className="btn btn-light w-100 fw-semibold" style={{ borderRadius: 8, color: '#4338ca', padding: '7px 10px', fontSize: 12 }} onClick={() => openTab('for-you')}>
                    Explore Free Learning <i className="bi bi-arrow-right ms-1" />
                  </button>
                </div>
              )}
            </StudentSurface>
          ) : (
          <StudentSurface style={{ padding: studentPagePadding, background: studentTheme.shellBg, borderColor: studentTheme.shellBorder, boxShadow: '0 24px 60px rgba(15, 23, 42, 0.10)' }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
              <div className="flex-fill" style={{ minWidth: isStudentMobile ? '100%' : 280 }}>
                <div className="fw-semibold mb-1" style={{ fontSize: studentHeroTitleSize, lineHeight: 1.1, color: studentTheme.title }}>Welcome back, {user?.firstName || 'Learner'}!</div>
                <div style={{ color: studentTheme.muted }}>Let's continue your learning journey.</div>
              </div>
            </div>

            <div className="row g-3 mb-3">
                {[
                  { label: 'Your Certificates', value: studentSubscriptionUsage.certificates, hint: 'Ready to view', meta: `${Math.max(0, studentLearningOverview.completedCourses || 0)} completed`, action: 'Certificates', actionIcon: 'bi-arrow-right', icon: 'bi-patch-check-fill', accent: '#8b5cf6' },
                  { label: 'Course Completion', value: `${studentLearningOverview.completedCourses || 0}/${studentLearningOverview.totalCourses || 0}`, hint: `${studentLearningOverview.avgCompletion || 0}% average progress`, meta: `${studentLearningOverview.totalPendingLessons || 0} lessons pending`, action: 'Track progress', actionIcon: 'bi-bar-chart-line', icon: 'bi-graph-up-arrow', accent: '#60a5fa' },
                  { label: 'Courses Enrolled', value: studentLearningOverview.totalCourses, hint: studentPrimaryCategory || 'All categories', meta: `${studentLearningOverview.activeCourses || 0} active now`, action: 'Open courses', actionIcon: 'bi-box-arrow-up-right', icon: 'bi-journal-bookmark-fill', accent: '#4ade80' },
                ].map((card) => (
                  <div className="col-12 col-sm-6 col-xl-3" key={`dashboard-stat-${card.label}`}>
                    <button
                      type="button"
                      className="h-100 d-flex flex-column justify-content-between text-start w-100 border-0"
                      onClick={() => openDashboardShortcut(card.action)}
                      style={{ borderRadius: 20, padding: '14px 14px 12px', minHeight: 132, background: studentTheme.cardBg, border: `1px solid ${studentTheme.panelBorder}`, boxShadow: '0 10px 20px rgba(15, 23, 42, 0.04)' }}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-3">
                        <div>
                          <div className="small mb-1" style={{ color: studentTheme.muted }}>{card.label}</div>
                          <div className="fw-semibold" style={{ fontSize: isStudentMobile ? 19 : 22, lineHeight: 1.05, color: studentTheme.title }}>{card.value}</div>
                          <div className="small mt-1" style={{ color: studentTheme.accent }}>{card.hint}</div>
                        </div>
                        <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 36, height: 36, borderRadius: 12, background: `${card.accent}20`, color: card.accent }}>
                          <i className={`bi ${card.icon}`} />
                        </div>
                      </div>
                      <div className="d-flex justify-content-between align-items-center gap-2 mt-3 pt-2" style={{ borderTop: `1px solid ${studentTheme.panelBorder}` }}>
                        <div className="small" style={{ color: studentTheme.text }}>{card.meta}</div>
                        <div className="small fw-semibold d-inline-flex align-items-center gap-1" style={{ color: card.accent }}>
                          <i className={`bi ${card.actionIcon}`} />
                          <span>{card.action}</span>
                        </div>
                      </div>
                    </button>
                  </div>
              ))}
            </div>

            {false && (
            <div className="row g-3 mb-4">
              <div className="col-12 col-xl-8">
                <div
                  style={{
                    borderRadius: 28,
                    padding: 24,
                    minHeight: 280,
                    background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 52%, #4f46e5 100%)',
                    boxShadow: '0 26px 48px rgba(79,70,229,0.24)',
                    color: '#ffffff',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
                    <div className="flex-fill">
                      <span className="badge rounded-pill" style={{ background: 'rgba(255,255,255,0.16)', color: '#ffffff', padding: '10px 14px', fontWeight: 600 }}>
                        Continue Learning
                      </span>
                      <div className="fw-semibold mt-3" style={{ fontSize: 38, lineHeight: 1.06 }}>
                        {studentLearningOverview.continueCourse?.course?.courseName || studentFreeCourseShowcase[0]?.courseName || 'Start your learning path'}
                      </div>
                      <div className="mt-2 mb-4" style={{ color: 'rgba(255,255,255,0.82)', fontSize: 17 }}>
                        {studentLearningOverview.continueCourse?.currentModuleTitle
                          || studentLearningOverview.continueCourse?.course?.category
                          || studentPrimaryCategory
                          || 'Build your next skill'}
                      </div>
                    </div>
                    <div style={{ width: 220, minWidth: 220 }}>
                      <CourseArtwork
                        title={studentLearningOverview.continueCourse?.course?.courseName || studentFreeCourseShowcase[0]?.courseName || 'Course'}
                        imageUrl={studentLearningOverview.continueCourse?.course?.thumbnailUrl || studentLearningOverview.continueCourse?.course?.promoImageUrl || studentFreeCourseShowcase[0]?.thumbnailUrl || studentFreeCourseShowcase[0]?.promoImageUrl}
                        accent={studentLearningOverview.continueCourse?.course?.courseColor || '#60a5fa'}
                        height={190}
                        compact
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
                    <div className="fw-semibold" style={{ fontSize: 24 }}>
                      {Number(studentLearningOverview.continueCourse?.learningSummary?.completionPercent || 0)}% complete
                    </div>
                    <div className="small" style={{ color: 'rgba(255,255,255,0.78)' }}>
                      {studentLearningOverview.continueCourse?.currentTopicTitle || 'Keep moving through your next lesson'}
                    </div>
                  </div>
                  <ProgressBar value={Number(studentLearningOverview.continueCourse?.learningSummary?.completionPercent || 0)} color="#ffffff" trackColor="rgba(255,255,255,0.24)" />
                  <button
                    type="button"
                    className="btn btn-light mt-4 fw-semibold"
                    style={{ borderRadius: 18, padding: '14px 22px', minWidth: 240, color: '#1e1b4b' }}
                    onClick={() => {
                      if (studentLearningOverview.continueCourse?.course?._id) {
                        openTab('my-courses');
                        if (studentLearningOverview.continueCourse.isVirtualEnrollment) {
                          openStudentCourseCard(studentLearningOverview.continueCourse.course, { fallbackTab: 'my-courses' });
                        } else {
                          openCourseWorkspace(String(studentLearningOverview.continueCourse.course._id));
                        }
                        return;
                      }
                      if (studentFreeCourseShowcase[0]) {
                        openStudentCourseCard(studentFreeCourseShowcase[0], { fallbackTab: 'my-courses' });
                      }
                    }}
                  >
                    <span className="d-flex align-items-center justify-content-between gap-3">
                      <span>Continue Learning</span>
                      <i className="bi bi-arrow-right" />
                    </span>
                  </button>
                </div>
              </div>
              <div className="col-12 col-xl-4">
                <div className="d-flex flex-column gap-3 h-100">
                  <div
                    className="rounded-4 bg-white"
                    style={{ padding: 22, border: '1px solid #fde7d8', boxShadow: '0 12px 28px rgba(15,23,42,0.05)' }}
                  >
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                      <div className="fw-semibold" style={{ color: '#0f172a' }}>Today's Goal</div>
                      <div className="d-inline-flex align-items-center gap-2" style={{ color: '#f97316' }}>
                        <i className="bi bi-fire" />
                        <span className="fw-semibold">{studentConsistencyOverview.bestStreak || 0} day streak</span>
                      </div>
                    </div>
                    <div className="fw-semibold mb-2" style={{ fontSize: 34, lineHeight: 1, color: '#0f172a' }}>
                      {Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60))}
                      <span style={{ fontSize: 18, color: '#475569' }}> / {studentGoal.minutes || 60} min</span>
                    </div>
                    <div className="small mb-3" style={{ color: '#f97316' }}>
                      {Math.max(0, (studentGoal.minutes || 60) - Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60)))} min remaining
                    </div>
                    <ProgressBar
                      value={studentGoal.enabled ? Math.min(100, Math.round((Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60)) / (studentGoal.minutes || 60)) * 100)) : 0}
                      color="#f97316"
                      trackColor="#e5e7eb"
                    />
                  </div>
                  {false && studentHasSubscriptionTab && (
                    <div className="rounded-4 bg-white" style={{ padding: 22, border: '1px solid #ddd6fe', boxShadow: '0 12px 28px rgba(15,23,42,0.05)' }}>
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div>
                          <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 20 }}>Level up with Premium</div>
                          <div className="small" style={{ color: '#64748b' }}>Unlock unlimited learning and career support.</div>
                        </div>
                        <span className="badge rounded-pill" style={{ background: '#fff7ed', color: '#ea580c' }}>
                          {studentActiveSubscription ? 'Active Plan' : 'Most Popular'}
                        </span>
                      </div>
                      <div className="row g-3 align-items-stretch">
                        <div className="col-12">
                          <div className="rounded-4" style={{ padding: 16, background: '#f8fbff', border: '1px solid #e0e7ff' }}>
                            <div className="small fw-semibold mb-2" style={{ color: '#64748b' }}>Your Current Plan</div>
                            <div className="fw-semibold mb-2" style={{ color: '#0f172a', fontSize: 18 }}>
                              {studentSubscriptionUsage.planName || studentDashboardMembershipPlan?.name || 'Free Plan'}
                            </div>
                            <div className="d-flex flex-column gap-2">
                              {(studentCurrentPlanBenefits.length ? studentCurrentPlanBenefits : ['Access to free courses', 'Recorded lessons', 'Practice quizzes']).slice(0, 5).map((item) => (
                                <div key={`desktop-current-plan-${item}`} className="d-flex align-items-start gap-2 small" style={{ color: '#334155' }}>
                                  <i className="bi bi-check-circle-fill" style={{ color: '#22c55e', marginTop: 2 }} />
                                  <span>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="rounded-4" style={{ padding: 16, background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(168,85,247,0.08) 100%)', border: '1px solid #ddd6fe' }}>
                            <div className="small fw-semibold mb-2" style={{ color: '#7c3aed' }}>
                              Unlock with {studentSubscriptionDetails.recommendedPlan?.name || 'Pro Plan'}
                            </div>
                            <div className="d-flex flex-column gap-2">
                              {(studentRecommendedPlanBenefits.length ? studentRecommendedPlanBenefits : ['Live interactive classes', 'Mentor support', 'Projects and advanced practice']).slice(0, 6).map((item) => (
                                <div key={`desktop-premium-plan-${item}`} className="d-flex align-items-start gap-2 small" style={{ color: '#334155' }}>
                                  <i className="bi bi-check-circle-fill" style={{ color: '#4f46e5', marginTop: 2 }} />
                                  <span>{item}</span>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              className="btn btn-primary w-100 mt-3 fw-semibold"
                              style={{ borderRadius: 16, background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', border: 'none' }}
                              onClick={() => (studentSubscriptionDetails.recommendedPlan ? openCheckout(studentSubscriptionDetails.recommendedPlan) : openTab('my-subscription'))}
                            >
                              {studentActiveSubscription ? 'View Membership' : 'Upgrade to Pro'} <i className="bi bi-arrow-right ms-1" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}

            {studentHasSubscriptionTab && (
              <div className="mb-4">
                <div>
                  <div className="h-100 rounded-4 bg-white" style={{ padding: 22, border: '1px solid #edf2ff', boxShadow: '0 14px 30px rgba(15,23,42,0.05)' }}>
                    <div className="d-flex align-items-start gap-3 mb-3">
                      <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 84, height: 84, borderRadius: '50%', background: 'linear-gradient(135deg, #dbeafe 0%, #ede9fe 100%)', color: '#4f46e5', fontSize: 30 }}>
                        <i className="bi bi-person-circle" />
                      </div>
                      <div className="flex-fill">
                        <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 24 }}>
                          {activeDashboardPromotion?.promoTitle || activeDashboardPromotion?.title || 'Top Skilled Academy learner'}
                        </div>
                        <div className="small mb-2" style={{ color: '#7c3aed' }}>
                          {studentPrimaryCategory || 'Career track'}
                        </div>
                        <div className="mb-2" style={{ color: '#f59e0b' }}>
                          <i className="bi bi-star-fill me-1" />
                          <i className="bi bi-star-fill me-1" />
                          <i className="bi bi-star-fill me-1" />
                          <i className="bi bi-star-fill me-1" />
                          <i className="bi bi-star-fill" />
                        </div>
                        <div style={{ color: '#475569', lineHeight: 1.7 }}>
                          {activeDashboardPromotion?.promoText || activeDashboardPromotion?.subtitle || 'Structured learning, mentor guidance, and projects help students move faster with a clear learning path.'}
                        </div>
                      </div>
                    </div>
                    <div className="row g-0 mt-4">
                      <div className="col-12 col-md-6">
                        <div className="pe-md-4 pb-3 pb-md-0" style={{ borderRight: '1px solid #edf2ff' }}>
                          <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#8b5cf6' }}>
                            <i className="bi bi-people-fill" />
                            <span className="small fw-semibold">Active learners</span>
                          </div>
                          <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 32 }}>{Math.max(learning.length, studentLearningOverview.totalCourses || 0)}</div>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="ps-md-4 pt-3 pt-md-0">
                          <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#10b981' }}>
                            <i className="bi bi-star-fill" />
                            <span className="small fw-semibold">Student rating</span>
                          </div>
                          <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 32 }}>
                            {studentLearningOverview.averageScore != null ? `${Math.max(4, Math.min(5, Number(studentLearningOverview.averageScore || 0) / 20)).toFixed(1)}/5` : '4.8/5'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {studentHasSubscriptionTab && (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                  <div>
                    <div className="fw-semibold" style={{ fontSize: 24, color: studentTheme.title }}>Upcoming Live Classes for You</div>
                    <div className="small" style={{ color: studentTheme.muted }}>Live and instructor-led learning opportunities.</div>
                  </div>
                  <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ color: studentTheme.accent }} onClick={() => openTab('for-you')}>
                    View all
                  </button>
                </div>
                <div className="row g-3">
                  {forYouLiveCourses.slice(0, 2).map((course, index) => (
                    <div className="col-12 col-xl-6" key={`desktop-cohort-${course._id || index}`}>
                      <div className="h-100 rounded-4 bg-white border" style={{ padding: 20, borderColor: '#edf2ff', boxShadow: '0 14px 30px rgba(15,23,42,0.05)' }}>
                        <div className="d-flex align-items-start gap-3">
                          <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 64, height: 64, borderRadius: 20, background: index % 2 === 0 ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' : 'linear-gradient(135deg, #10b981 0%, #16a34a 100%)', color: '#fff', fontSize: 28 }}>
                            <i className={`bi ${index % 2 === 0 ? 'bi-code-slash' : 'bi-cpu-fill'}`} />
                          </div>
                          <div className="flex-fill">
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                              <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 24, lineHeight: 1.15 }}>{course.courseName || 'Live Course'}</div>
                              <span className="badge rounded-pill" style={{ background: '#fff7ed', color: '#ea580c' }}>{index === 0 ? 'Seats filling fast' : 'Limited seats'}</span>
                            </div>
                            <div className="small mb-2" style={{ color: '#64748b' }}>
                              <i className="bi bi-camera-video-fill me-1" />
                              Live Online
                            </div>
                            <div className="small mb-3" style={{ color: '#64748b' }}>
                              <i className="bi bi-clock me-1" />
                              {course.durationDays ? `${course.durationDays} days` : 'Flexible duration'}
                            </div>
                            <button type="button" className={`btn ${index === 0 ? 'btn-primary' : 'btn-success'} px-4`} style={{ borderRadius: 14 }} onClick={() => openStudentCourseCard(course, { fallbackTab: 'for-you' })}>
                              {getLearningFocusedCourseCta(course)} <i className="bi bi-arrow-right ms-1" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!forYouLiveCourses.length && (
                    <div className="col-12">
                      <div className="rounded-4 border bg-white p-4 text-center" style={{ borderColor: '#edf2ff', color: '#64748b' }}>
                        New live cohorts will be listed here when they are published.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {studentHasSubscriptionTab && !!studentRecommendedCourses.length && (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                  <div>
                    <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 24 }}>Premium Career Programs</div>
                    <div className="small" style={{ color: '#64748b' }}>Career-focused programs and advanced learning tracks.</div>
                  </div>
                  <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ color: '#7c3aed' }} onClick={() => openTab('for-you')}>
                    See all <i className="bi bi-chevron-right" />
                  </button>
                </div>
                <div className="row g-3">
                  {studentRecommendedCourses.slice(0, 3).map((course, index) => {
                    const isFree = isFreeLearningCourse(course);
                    return (
                    <div className="col-12 col-md-4" key={`desktop-premium-program-${course._id || index}`}>
                      <button
                        type="button"
                        className="w-100 h-100 text-start border-0 bg-white dashboard-career-program-card"
                        onClick={() => openStudentCourseCard(course, { fallbackTab: 'for-you' })}
                      >
                        <div className="position-relative dashboard-career-program-artwork">
                          <CourseArtwork title={course.courseName} category={course.category} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || (index % 2 === 0 ? '#4f46e5' : '#0f766e')} height={210} compact />
                          {isFree && <span className="dashboard-career-free-badge"><i className="bi bi-gem" /> FREE</span>}
                        </div>
                        <div className="d-flex flex-column dashboard-career-program-body">
                          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                            <div className="fw-semibold" style={{ color: '#0f172a', fontSize: 22, lineHeight: 1.12 }}>
                              {course.courseName || 'Program'}
                            </div>
                            {!isFree && <span className="badge rounded-pill" style={{ background: index === 0 ? '#eef2ff' : '#fff7ed', color: index === 0 ? '#4f46e5' : '#ea580c' }}>{index === 0 ? 'Most Popular' : 'High Demand'}</span>}
                          </div>
                          <div className="small mb-3" style={{ color: '#64748b', maxWidth: 720 }}>
                            {course.shortDescription || course.category || 'Career-focused learning path'}
                          </div>
                          <div className="d-flex flex-wrap gap-2 mb-3">
                            {[course.category || 'Career Track', `${Number(course.totalLessons || course.lessonCount || 0)} lessons`, course.level || 'Structured path'].map((tag) => (
                              <span key={`${course._id || index}-${tag}`} className="badge rounded-pill text-bg-light border">
                                {tag}
                              </span>
                            ))}
                          </div>
                          <div className="d-flex justify-content-between align-items-center gap-2 mt-auto">
                            <div className="small fw-semibold" style={{ color: '#475569' }}>
                              {isFree ? <span className="dashboard-career-free-value"><i className="bi bi-lightning-charge-fill" /> Free access</span> : fmtMoney(course.totalFees || course.salePrice || course.price || 0)}
                            </div>
                            <span className="small fw-semibold" style={{ color: '#4f46e5' }}>
                              Explore <i className="bi bi-arrow-right-short" />
                            </span>
                          </div>
                        </div>
                      </button>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {studentHasSubscriptionTab && (
              <div className="mb-4">
                <div className="fw-semibold mb-2" style={{ color: '#0f172a', fontSize: 20 }}>Why students upgrade</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
                  {[
                    { title: 'Live Mentor Sessions', text: 'Learn from experts in real time.', icon: 'bi-camera-video-fill', bg: '#f3e8ff', color: '#8b5cf6' },
                    { title: 'Industry Projects', text: 'Build real-world portfolio work.', icon: 'bi-briefcase-fill', bg: '#e8fbf2', color: '#10b981' },
                    { title: 'Certificates', text: 'Earn recognized outcomes.', icon: 'bi-award-fill', bg: '#fff1eb', color: '#f97316' },
                    { title: 'Career Support', text: 'Get review and placement help.', icon: 'bi-people-fill', bg: '#eef2ff', color: '#3b82f6' },
                  ].map((item) => (
                    <div
                      key={`desktop-upgrade-${item.title}`}
                      className="d-flex flex-column align-items-center justify-content-center text-center bg-white border"
                      style={{ minWidth: 0, minHeight: 118, padding: '12px 10px', borderRadius: 10, borderColor: '#edf2ff' }}
                    >
                      <div className="d-inline-flex align-items-center justify-content-center mb-2" style={{ width: 38, height: 38, borderRadius: 10, background: item.bg, color: item.color }}>
                        <i className={`bi ${item.icon}`} />
                      </div>
                      <div className="fw-semibold" style={{ color: '#0f172a', lineHeight: 1.15, fontSize: 14 }}>{item.title}</div>
                      <div className="mt-1 small" style={{ color: '#64748b', lineHeight: 1.3 }}>{item.text}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {studentHasSubscriptionTab && (
              <div className="mb-4 rounded-4 text-white" style={{ padding: 22, background: 'linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)', boxShadow: '0 20px 40px rgba(79,70,229,0.24)' }}>
                <div className="d-flex align-items-center gap-4">
                  <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 64, height: 64, borderRadius: 18, background: 'rgba(255,255,255,0.12)', color: '#facc15', fontSize: 30 }}>
                    <i className="bi bi-gem" />
                  </div>
                  <div className="flex-fill">
                    <div className="fw-semibold mb-2" style={{ fontSize: 28, lineHeight: 1.12 }}>Join a paid program to unlock more</div>
                    <div style={{ color: 'rgba(255,255,255,0.82)' }}>Access premium courses, expert mentorship, project reviews, and placement support.</div>
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      {['Expert Mentorship', 'Real-world Projects', 'Placement Support'].map((item) => (
                        <span key={item} className="badge rounded-pill" style={{ background: 'rgba(255,255,255,0.12)', color: '#ffffff', padding: '8px 12px' }}>{item}</span>
                      ))}
                    </div>
                  </div>
                  <button type="button" className="btn btn-light fw-semibold px-4" style={{ borderRadius: 16, color: '#4338ca' }} onClick={() => openTab('my-subscription')}>
                    Explore Programs <i className="bi bi-arrow-right ms-1" />
                  </button>
                </div>
              </div>
            )}

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                <div>
                  <div className="fw-semibold" style={{ fontSize: 24, color: studentTheme.title }}>Continue Watching</div>
                  <div className="small" style={{ color: studentTheme.muted }}>Pick up where you left off.</div>
                </div>
                <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ color: studentTheme.accent }} onClick={() => openTab('my-courses')}>See All</button>
              </div>
              <div className="row g-3">
                {studentRecommendedRails.continueWatching.slice(0, 5).map((row) => {
                  const course = row.course || {};
                  const courseKey = String(course._id || row.course);
                  return (
                    <div className="col-6 col-md-4 col-xl-2" key={`dashboard-continue-${row._id || course._id}`}>
                      <div
                        role="button"
                        tabIndex={0}
                        className="w-100"
                        onClick={() => {
                          if (!course?._id) return;
                          openTab('my-courses');
                          if (row.isVirtualEnrollment) openStudentCourseCard(course, { fallbackTab: 'my-courses' });
                          else openCourseWorkspace(String(course._id));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            if (!course?._id) return;
                            openTab('my-courses');
                            if (row.isVirtualEnrollment) openStudentCourseCard(course, { fallbackTab: 'my-courses' });
                            else openCourseWorkspace(String(course._id));
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="position-relative" style={{ borderRadius: 18, overflow: 'hidden', background: studentTheme.cardBg, border: `1px solid ${studentTheme.panelBorder}` }}>
                          <button
                            type="button"
                            className="btn btn-sm position-absolute top-0 end-0 m-2"
                            style={{ zIndex: 2, borderRadius: 999, background: 'rgba(255,255,255,0.92)', border: '1px solid #e2e8f0', color: wishlistCourseIdSet.has(courseKey) ? '#fb7185' : '#64748b' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleWishlist(courseKey);
                            }}
                          >
                            <i className={`bi ${wishlistCourseIdSet.has(courseKey) ? 'bi-heart-fill' : 'bi-heart'}`} />
                          </button>
                          <CourseArtwork title={course.courseName} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#8b5cf6'} height={118} compact />
                          <div style={{ padding: 12 }}>
                            <div className="small fw-semibold mb-1 text-truncate" style={{ color: studentTheme.title }}>{course.courseName || 'Course'}</div>
                            <div className="small mb-2" style={{ color: studentTheme.muted }}>{Number(row.learningSummary?.completionPercent || 0)}% complete</div>
                            <ProgressBar value={Number(row.learningSummary?.completionPercent || 0)} color={studentTheme.accent} />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!studentRecommendedRails.continueWatching.length && (
                  <div className="col-12">
                    <div className="border rounded-4 p-4 text-center" style={{ background: studentTheme.cardBg, borderColor: studentTheme.panelBorder, color: studentTheme.muted }}>
                      Start a course from My Courses to build your continue-watching list.
                    </div>
                  </div>
                )}
              </div>
            </div>

            {!!studentVisibleCoupons.length && (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                  <div>
                    <div className="fw-semibold" style={{ fontSize: 22, color: studentTheme.title }}>Exclusive offers for you</div>
                    <div className="small" style={{ color: studentTheme.muted }}>Approved membership coupon codes available for you right now.</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'minmax(220px, 1fr)', gap: 14, overflowX: 'auto', paddingBottom: 4 }}>
                  {studentVisibleCoupons.map((item, index) => (
                    <div key={`dashboard-discount-${item._id || index}`} style={{ borderRadius: 18, overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)', border: `1px solid ${studentTheme.panelBorder}`, minWidth: 220, boxShadow: '0 16px 34px rgba(37,99,235,0.08)' }}>
                      <div style={{ height: 132, background: `radial-gradient(circle at 18% 18%, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0) 24%), linear-gradient(135deg, ${index % 3 === 0 ? '#1d4ed8' : index % 3 === 1 ? '#7c3aed' : '#0f766e'} 0%, ${index % 3 === 0 ? '#0ea5e9' : index % 3 === 1 ? '#ec4899' : '#14b8a6'} 100%)`, position: 'relative' }}>
                        <div style={{ position: 'absolute', right: -12, top: -8, width: 90, height: 90, borderRadius: 24, background: 'rgba(255,255,255,0.18)', transform: 'rotate(18deg)' }} />
                        <div style={{ position: 'absolute', left: 14, bottom: 16, width: 58, height: 58, borderRadius: 16, background: 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                          <i className={`bi ${item.discountType === 'fixed' ? 'bi-cash-stack' : 'bi-tags-fill'}`} style={{ fontSize: 24 }} />
                        </div>
                        <div style={{ position: 'absolute', right: 14, bottom: 18, color: '#fff', textAlign: 'right' }}>
                          <div className="small text-white-50">{item.discountType === 'fixed' ? 'SAVE' : 'DISCOUNT'}</div>
                          <div className="fw-semibold" style={{ fontSize: 24, lineHeight: 1 }}>
                            {item.discountType === 'fixed' ? fmtMoney(item.discountValue || 0) : `${item.discountValue || 0}%`}
                          </div>
                        </div>
                      </div>
                      <div style={{ padding: 12 }}>
                        <div className="fw-semibold mb-1" style={{ color: studentTheme.title, lineHeight: 1.2 }}>{item.title || item.code || 'Offer details'}</div>
                        <div className="small mb-2" style={{ color: studentTheme.muted }}>{item.description || 'Use this code during membership checkout.'}</div>
                        <div className="small fw-semibold mb-2" style={{ color: studentTheme.accent }}>{item.code}</div>
                        <button type="button" className="btn btn-outline-primary btn-sm w-100" onClick={() => openTab('my-subscription')}>
                          Apply offer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                <div>
                  <div className="fw-semibold" style={{ fontSize: 24, color: studentTheme.title }}>Level Up Your Skills</div>
                  <div className="small" style={{ color: studentTheme.muted }}>Free courses and starter categories available for you right now.</div>
                </div>
                <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ color: studentTheme.accent }} onClick={() => openTab('for-you')}>Explore More</button>
              </div>
              <div className="row g-3">
                {studentFreeCourseShowcase.slice(0, 6).map((course, index) => (
                  <div className="col-12 col-md-6 col-xl-3" key={`dashboard-free-${course._id || index}`}>
                    <div className="position-relative" style={{ borderRadius: 20, overflow: 'hidden', background: studentTheme.cardBg, border: `1px solid ${studentTheme.panelBorder}` }}>
                      <button
                        type="button"
                        className="btn btn-sm position-absolute top-0 end-0 m-2"
                        style={{ zIndex: 2, borderRadius: 999, background: 'rgba(255,255,255,0.92)', border: '1px solid #e2e8f0', color: wishlistCourseIdSet.has(String(course._id)) ? '#fb7185' : '#64748b' }}
                        onClick={() => toggleWishlist(course._id)}
                      >
                        <i className={`bi ${wishlistCourseIdSet.has(String(course._id)) ? 'bi-heart-fill' : 'bi-heart'}`} />
                      </button>
                      <CourseArtwork title={course.courseName} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#60a5fa'} height={132} compact />
                      <div style={{ padding: 14 }}>
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                          <div className="small badge" style={{ background: studentTheme.accentSoft, color: studentTheme.accent }}>{course.category || 'Free Course'}</div>
                        </div>
                        <div
                          className="fw-semibold mb-2"
                          style={{
                            lineHeight: 1.2,
                            minHeight: 44,
                            color: studentTheme.title,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {course.courseName || 'Course'}
                        </div>
                        <div
                          className="small mb-2"
                          style={{
                            color: studentTheme.muted,
                            minHeight: 38,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {course.shortDescription || ''}
                        </div>
                        <button className="btn btn-outline-primary btn-sm w-100" onClick={() => openStudentCourseCard(course, { fallbackTab: 'for-you' })}>
                          {getStudentCourseActionLabel(course)}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                <div>
                  <div className="fw-semibold" style={{ fontSize: 24, color: studentTheme.title }}>Recommended For You</div>
                  <div className="small" style={{ color: studentTheme.muted }}>Paid and advanced picks beyond your current package path.</div>
                </div>
                <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ color: studentTheme.accent }} onClick={() => openTab('for-you')}>Go to recommendations</button>
              </div>
              <div className="row g-3">
                {studentRecommendedCourses.slice(0, 6).map((course, index) => (
                  <div className="col-12 col-md-6 col-xl-3" key={`dashboard-recommended-${course._id || index}`}>
                    <div className="position-relative" style={{ borderRadius: 20, overflow: 'hidden', background: studentTheme.cardBg, border: `1px solid ${studentTheme.panelBorder}` }}>
                      <CourseArtwork title={course.courseName} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#8b5cf6'} height={132} compact />
                      <div style={{ padding: 14 }}>
                        <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                          <div className="small badge" style={{ background: 'rgba(124,58,237,0.10)', color: '#7c3aed' }}>{course.category || 'Recommended'}</div>
                          <div className="small" style={{ color: studentTheme.muted }}>{fmtMoney(course.totalFees || 0)}</div>
                        </div>
                        <div
                          className="fw-semibold mb-2"
                          style={{
                            lineHeight: 1.2,
                            minHeight: 44,
                            color: studentTheme.title,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {course.courseName || 'Course'}
                        </div>
                        <div
                          className="small mb-2"
                          style={{
                            color: studentTheme.muted,
                            minHeight: 38,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {course.shortDescription || ''}
                        </div>
                        <button className="btn btn-primary btn-sm w-100" onClick={() => openTab('for-you')}>
                          Explore in recommendations
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-12 col-xl-4">
                <div className="h-100" style={{ borderRadius: 20, padding: 18, background: studentTheme.panelBg, border: `1px solid ${studentTheme.panelBorder}` }}>
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                    <div className="fw-semibold" style={{ color: studentTheme.title }}>Learning Focus</div>
                    <span className="small" style={{ color: studentTheme.accent }}>{studentPrimaryCategory || 'All tracks'}</span>
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {[
                      { label: 'Current track', value: studentPrimaryCategory || 'Recommended path', progress: Math.max(20, studentLearningOverview.avgCompletion || 0) },
                      { label: 'Consistency score', value: `${studentConsistencyOverview.streakDays || 0} day streak`, progress: Math.min(100, (studentConsistencyOverview.streakDays || 0) * 12) },
                      { label: 'Course coverage', value: `${studentLearningOverview.activeCourses} active`, progress: Math.min(100, studentLearningOverview.activeCourses * 16) },
                    ].map((item) => (
                      <div key={`focus-${item.label}`}>
                        <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
                          <span className="small" style={{ color: studentTheme.text }}>{item.label}</span>
                          <span className="small fw-semibold" style={{ color: studentTheme.title }}>{item.value}</span>
                        </div>
                        <ProgressBar value={item.progress} color={studentTheme.accent} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-12 col-xl-4">
                <div className="h-100" style={{ borderRadius: 20, padding: 18, background: studentTheme.panelBg, border: `1px solid ${studentTheme.panelBorder}` }}>
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                    <div className="fw-semibold" style={{ color: studentTheme.title }}>Recommended Actions</div>
                    <i className="bi bi-lightning-charge-fill" style={{ color: studentTheme.accent }} />
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {[
                      { title: 'Continue active lesson', text: 'Resume the course you touched most recently.', action: () => openTab('my-courses') },
                      {
                        title: studentIsOnDemandPrimary ? 'Browse next picks' : 'Review remaining lessons',
                        text: studentIsOnDemandPrimary ? 'Open recommendations for the next category step.' : 'Open your active courses and clear the remaining topics.',
                        action: () => openTab(studentIsOnDemandPrimary ? 'for-you' : 'my-courses'),
                      },
                      studentHasSubscriptionTab
                        ? {
                          title: studentIsOnDemandPrimary ? 'Review subscription' : 'Check membership access',
                          text: studentIsOnDemandPrimary ? 'Check plan coverage and renewal details.' : 'See optional membership access for on-demand learning.',
                          action: () => openTab('my-subscription'),
                        }
                        : {
                          title: 'Explore more courses',
                          text: 'Open the recommendation page for the next available learning path.',
                          action: () => openTab('for-you'),
                        },
                    ].map((item) => (
                      <button
                        key={`action-${item.title}`}
                        type="button"
                        className="btn text-start w-100"
                        onClick={item.action}
                        style={{
                          borderRadius: 16,
                          padding: '12px 14px',
                          border: `1px solid ${studentTheme.panelBorder}`,
                          background: studentTheme.cardBg,
                          color: studentTheme.title,
                        }}
                      >
                        <div className="fw-semibold small mb-1">{item.title}</div>
                        <div className="small" style={{ color: studentTheme.muted }}>{item.text}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-12 col-xl-4">
                <div className="h-100" style={{ borderRadius: 20, padding: 18, background: studentTheme.panelBg, border: `1px solid ${studentTheme.panelBorder}` }}>
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                    <div className="fw-semibold" style={{ color: studentTheme.title }}>
                      {studentIsOnDemandPrimary ? 'Membership Snapshot' : 'Academic Snapshot'}
                    </div>
                    <span className="small" style={{ color: studentTheme.accent }}>
                      {studentIsOnDemandPrimary ? (studentSubscriptionUsage.subscriptionStatus || 'Basic') : `${studentLearningOverview.completedCourses || 0} completed`}
                    </span>
                  </div>
                  <div className="rounded-4 p-3 mb-3" style={{ background: studentTheme.cardBg, border: `1px solid ${studentTheme.panelBorder}` }}>
                    <div className="small mb-1" style={{ color: studentTheme.muted }}>
                      {studentIsOnDemandPrimary ? 'Active membership' : 'Current learning mode'}
                    </div>
                    <div className="fw-semibold" style={{ color: studentTheme.title }}>
                      {studentIsOnDemandPrimary ? (studentSubscriptionUsage.planName || 'Basic plan') : `${studentLearningModeProfile.counts.live} offline/live course${studentLearningModeProfile.counts.live === 1 ? '' : 's'}`}
                    </div>
                    <div className="small mt-2" style={{ color: studentTheme.text }}>
                      {studentIsOnDemandPrimary
                        ? `Expires: ${studentSubscriptionUsage.expiresAt ? fmtDate(studentSubscriptionUsage.expiresAt) : 'Open access'}`
                        : `Average score: ${studentLearningOverview.averageScore != null ? `${studentLearningOverview.averageScore}%` : 'Not available yet'}`}
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {studentIsOnDemandPrimary ? (
                      <>
                        <div className="d-flex justify-content-between small" style={{ color: studentTheme.text }}><span>Courses unlocked</span><span className="fw-semibold">{studentLearningOverview.totalCourses}</span></div>
                        <div className="d-flex justify-content-between small" style={{ color: studentTheme.text }}><span>Renewal status</span><span className="fw-semibold">{studentSubscriptionUsage.renewalState || 'Current'}</span></div>
                        <div className="d-flex justify-content-between small" style={{ color: studentTheme.text }}><span>Downloads / offline</span><span className="fw-semibold">{studentSubscriptionUsage.downloads || 0}</span></div>
                      </>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between small" style={{ color: studentTheme.text }}><span>Pending lessons</span><span className="fw-semibold">{studentLearningOverview.totalPendingLessons || 0}</span></div>
                        <div className="d-flex justify-content-between small" style={{ color: studentTheme.text }}><span>Active courses</span><span className="fw-semibold">{studentLearningOverview.activeCourses || 0}</span></div>
                        <div className="d-flex justify-content-between small" style={{ color: studentTheme.text }}><span>Certificates earned</span><span className="fw-semibold">{studentSubscriptionUsage.certificates || 0}</span></div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-12 col-xl-4">
                <div className="h-100" style={{ borderRadius: 20, padding: 18, background: studentTheme.panelBg, border: `1px solid ${studentTheme.panelBorder}` }}>
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                    <div className="fw-semibold" style={{ color: studentTheme.title }}>
                      {studentIsOnDemandPrimary ? 'Live Class Info' : 'Course Progress Highlights'}
                    </div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      style={{ color: studentTheme.accent }}
                      onClick={() => openTab(studentIsOnDemandPrimary ? 'for-you' : 'my-courses')}
                    >
                      See All
                    </button>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {studentIsOnDemandPrimary ? (
                      <>
                        {livePromotionMessages.slice(0, 3).map((item, index) => (
                      <div key={`dashboard-live-${item._id || index}`} className="rounded-4 p-3" style={{ background: '#fff', border: `1px solid ${studentTheme.panelBorder}` }}>
                        <div className="small fw-semibold mb-1" style={{ color: studentTheme.title }}>{item.promoTitle || item.title || item.name || 'Live class update'}</div>
                        <div className="small" style={{ color: studentTheme.muted }}>
                          {[item.liveSession?.sessionDate, item.liveSession?.sessionTime, item.liveSession?.meetingNote || item.promoText].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    ))}
                        {!livePromotionMessages.length && <div className="small" style={{ color: studentTheme.muted }}>No live updates right now.</div>}
                      </>
                    ) : (
                      <>
                        {learning.slice(0, 3).map((row, index) => (
                          <div key={`dashboard-progress-${row._id || row.course?._id || index}`} className="rounded-4 p-3" style={{ background: '#fff', border: `1px solid ${studentTheme.panelBorder}` }}>
                            <div className="small fw-semibold mb-1" style={{ color: studentTheme.title }}>
                              {row.course?.title || row.course?.name || 'Course progress'}
                            </div>
                            <div className="small mb-2" style={{ color: studentTheme.muted }}>
                              {[row.course?.category, row.course?.mode, `${Math.round(Number(row.progressPercent || 0))}% complete`].filter(Boolean).join(' · ')}
                            </div>
                            <div className="d-flex justify-content-between small" style={{ color: studentTheme.text }}>
                              <span>Pending lessons</span>
                              <span className="fw-semibold">{Math.max(0, Number(row.totalLessons || 0) - Number(row.completedLessons || 0))}</span>
                            </div>
                          </div>
                        ))}
                        {!learning.length && <div className="small" style={{ color: studentTheme.muted }}>No course progress is available right now.</div>}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-12 col-xl-4">
                <div className="h-100" style={{ borderRadius: 20, padding: 18, background: studentTheme.panelBg, border: `1px solid ${studentTheme.panelBorder}` }}>
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                    <div className="fw-semibold" style={{ color: studentTheme.title }}>Your Achievements</div>
                    <span className="small" style={{ color: studentTheme.accent }}>{studentPerformanceInsights.completedThisMonth} this month</span>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {[
                      { label: 'Learning Streak', value: `${studentConsistencyOverview.bestStreak} day${studentConsistencyOverview.bestStreak === 1 ? '' : 's'}` },
                      { label: 'Momentum', value: studentPerformanceInsights.momentumLabel },
                      { label: 'Strong Score Bands', value: studentPerformanceInsights.scoreBands.strong },
                    ].map((item) => (
                      <div key={`achievement-${item.label}`} className="rounded-4 p-3 d-flex justify-content-between align-items-center gap-2" style={{ background: '#fff', border: `1px solid ${studentTheme.panelBorder}` }}>
                        <span className="small" style={{ color: studentTheme.text }}>{item.label}</span>
                        <span className="fw-semibold" style={{ color: studentTheme.title }}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="col-12 col-xl-4">
                <div className="h-100" style={{ borderRadius: 20, padding: 18, background: studentTheme.panelBg, border: `1px solid ${studentTheme.panelBorder}` }}>
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                    <div className="fw-semibold" style={{ color: studentTheme.title }}>
                      {studentLearningModeProfile.predominantMode === 'on_demand' ? 'Learning Analytics' : 'Course Analytics'}
                    </div>
                    <span className="small" style={{ color: studentTheme.accent }}>
                      {studentLearningModeProfile.predominantMode === 'on_demand'
                        ? `${studentLearningOverview.avgCompletion}% avg progress`
                        : `${studentLearningOverview.completedCourses}/${studentLearningOverview.totalCourses} courses completed`}
                    </span>
                  </div>
                  {studentLearningModeProfile.predominantMode === 'on_demand' ? (
                    <>
                      <div className="d-flex align-items-end gap-2 mb-3" style={{ minHeight: 110 }}>
                        {studentPerformanceInsights.recentActivityRows.slice(0, 6).map((row, index) => (
                          <div key={`analytics-bar-${row.label}-${index}`} className="flex-fill">
                            <div style={{ height: `${Math.max(18, Number(row.value || 0))}%`, minHeight: 18, borderRadius: 999, background: `linear-gradient(180deg, ${studentTheme.accent} 0%, #6366f1 100%)` }} />
                          </div>
                        ))}
                        {!studentPerformanceInsights.recentActivityRows.length && (
                          <div className="small" style={{ color: studentTheme.muted }}>No recent analytics yet.</div>
                        )}
                      </div>
                      <div className="small" style={{ color: studentTheme.muted }}>
                        Recent activity stays strongest when at least half of your courses are touched every week.
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="d-flex flex-column gap-3 mb-3">
                        {[
                          {
                            label: 'Active courses',
                            value: studentLearningOverview.activeCourses || 0,
                            progress: studentLearningOverview.totalCourses ? Math.round(((studentLearningOverview.activeCourses || 0) / studentLearningOverview.totalCourses) * 100) : 0,
                            color: '#2563eb',
                          },
                          {
                            label: 'Pending lessons',
                            value: studentLearningOverview.totalPendingLessons || 0,
                            progress: Math.min(100, Math.max(12, Number(studentLearningOverview.avgCompletion || 0))),
                            color: '#f59e0b',
                          },
                          {
                            label: 'Completed this month',
                            value: studentPerformanceInsights.completedThisMonth || 0,
                            progress: studentLearningOverview.totalCourses ? Math.round(((studentPerformanceInsights.completedThisMonth || 0) / studentLearningOverview.totalCourses) * 100) : 0,
                            color: '#16a34a',
                          },
                        ].map((row) => (
                          <div key={`offline-analytics-${row.label}`}>
                            <div className="d-flex justify-content-between align-items-center small mb-1">
                              <span style={{ color: studentTheme.text }}>{row.label}</span>
                              <span className="fw-semibold" style={{ color: studentTheme.title }}>{row.value}</span>
                            </div>
                            <ProgressBar value={row.progress} color={row.color} />
                          </div>
                        ))}
                      </div>
                      <div className="rounded-4 p-3" style={{ background: '#fff', border: `1px solid ${studentTheme.panelBorder}` }}>
                        <div className="d-flex justify-content-between align-items-center small mb-2">
                          <span style={{ color: studentTheme.text }}>Average score</span>
                          <span className="fw-semibold" style={{ color: studentTheme.title }}>
                            {studentLearningOverview.averageScore != null ? `${studentLearningOverview.averageScore}%` : '-'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center small mb-2">
                          <span style={{ color: studentTheme.text }}>Learning mode mix</span>
                          <span className="fw-semibold" style={{ color: studentTheme.title }}>
                            {studentLearningModeProfile.counts.live} offline/live
                          </span>
                        </div>
                        <div className="small" style={{ color: studentTheme.muted }}>
                          Offline and live analytics focus on course completion, pending lessons, and overall academic progress instead of watch activity.
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                <div>
                  <div className="fw-semibold" style={{ fontSize: 22, color: studentTheme.title }}>Job Opportunities</div>
                  <div className="small" style={{ color: studentTheme.muted }}>Latest openings visible to students from the resume and placement module.</div>
                </div>
                <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" style={{ color: studentTheme.accent }} onClick={() => navigate('/resumes?tab=jobs')}>
                  View all jobs
                </button>
              </div>
              <div className="row g-3">
                {studentDashboardJobs.length ? studentDashboardJobs.map((job, index) => (
                  <div className="col-12 col-md-6 col-xl-3" key={`student-job-${job._id || index}`}>
                    <div style={{ borderRadius: 20, overflow: 'hidden', background: studentTheme.panelBg, border: `1px solid ${studentTheme.panelBorder}`, height: '100%' }}>
                      <div style={{ height: 136, background: job.imageUrl ? `linear-gradient(135deg, rgba(15,23,42,0.28) 0%, rgba(15,23,42,0.10) 100%), url(${job.imageUrl}) center/cover` : `linear-gradient(135deg, ${index % 2 === 0 ? '#1d4ed8' : '#7c3aed'} 0%, ${index % 2 === 0 ? '#38bdf8' : '#ec4899'} 100%)`, position: 'relative' }}>
                        <div className="badge text-bg-light position-absolute top-0 start-0 m-3">{String(job.jobType || 'full_time').replace(/_/g, ' ')}</div>
                      </div>
                      <div style={{ padding: 14 }}>
                        <div className="fw-semibold mb-1" style={{ color: studentTheme.title, lineHeight: 1.2, minHeight: 40 }}>{job.title || 'Job opening'}</div>
                        <div className="small mb-2" style={{ color: studentTheme.muted }}>{job.companyName || 'Company'}{job.location ? ` • ${job.location}` : ''}</div>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          <span className="badge rounded-pill text-bg-light border">{job.workMode || 'onsite'}</span>
                          {(Number(job.minSalary || 0) || Number(job.maxSalary || 0)) ? (
                            <span className="badge rounded-pill text-bg-light border">
                              {Number(job.minSalary || 0) && Number(job.maxSalary || 0)
                                ? `${fmtMoney(job.minSalary)} - ${fmtMoney(job.maxSalary)}`
                                : Number(job.minSalary || 0)
                                  ? `From ${fmtMoney(job.minSalary)}`
                                  : `Up to ${fmtMoney(job.maxSalary)}`}
                            </span>
                          ) : null}
                        </div>
                        <div className="small mb-3" style={{ color: studentTheme.text, minHeight: 38 }}>
                          {job.description || 'New placement opportunity available for students.'}
                        </div>
                        <button type="button" className="btn btn-outline-primary btn-sm w-100" onClick={() => navigate('/resumes?tab=jobs')}>
                          Explore
                        </button>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="col-12">
                    <div className="rounded-4 p-4 text-center" style={{ background: studentTheme.panelBg, border: `1px solid ${studentTheme.panelBorder}`, color: studentTheme.muted }}>
                      No active jobs have been posted yet.
                    </div>
                  </div>
                )}
              </div>
            </div>

          </StudentSurface>
          )}
        </div>
      )}

      {activeTab === 'career-quiz' && !canWrite && (
        <div className="student-career-quiz-page">
          <StudentAccountPageHeader
            icon="bi-compass"
            title="Career Path Quiz"
            subtitle="Answer three quick questions and discover published programs that fit your goals."
            onBack={() => openTab('for-you')}
          />
          {!careerQuizComplete ? (() => {
            const question = CAREER_QUIZ_QUESTIONS[careerQuizStep];
            const selectedValue = careerQuizAnswers[question.id];
            const progress = Math.round(((careerQuizStep + 1) / CAREER_QUIZ_QUESTIONS.length) * 100);
            return (
              <section className="student-career-quiz-card">
                <div className="student-career-quiz-progress"><div><span>Question {careerQuizStep + 1} of {CAREER_QUIZ_QUESTIONS.length}</span><strong>{progress}%</strong></div><ProgressBar value={progress} color="#6366f1" /></div>
                <div className="student-career-quiz-question"><small>Find your direction</small><h2>{question.title}</h2><p>{question.subtitle}</p></div>
                <div className="student-career-quiz-options">
                  {question.options.map((option) => (
                    <button type="button" key={option.value} className={selectedValue === option.value ? 'is-selected' : ''} onClick={() => setCareerQuizAnswers((prev) => ({ ...prev, [question.id]: option.value }))}>
                      <span><i className={`bi ${option.icon}`} /></span><strong>{option.label}</strong><i className={`bi ${selectedValue === option.value ? 'bi-check-circle-fill' : 'bi-circle'}`} />
                    </button>
                  ))}
                </div>
                <div className="student-career-quiz-actions">
                  <button type="button" className="is-secondary" disabled={careerQuizStep === 0} onClick={() => setCareerQuizStep((prev) => Math.max(0, prev - 1))}><i className="bi bi-arrow-left" /> Back</button>
                  <button type="button" className="is-primary" disabled={!selectedValue} onClick={() => { if (careerQuizStep < CAREER_QUIZ_QUESTIONS.length - 1) setCareerQuizStep((prev) => prev + 1); else setCareerQuizComplete(true); }}>{careerQuizStep === CAREER_QUIZ_QUESTIONS.length - 1 ? 'See My Matches' : 'Next Question'} <i className="bi bi-arrow-right" /></button>
                </div>
              </section>
            );
          })() : (
            <>
              <section className="student-career-result-hero"><span><i className="bi bi-stars" /></span><div><small>Your personalized direction</small><h2>Programs matched to your answers</h2><p>These recommendations are selected from the currently published course catalog. Open any program to review its access, curriculum, and enrollment options.</p></div><button type="button" onClick={() => { setCareerQuizAnswers({}); setCareerQuizStep(0); setCareerQuizComplete(false); }}><i className="bi bi-arrow-repeat" /> Retake quiz</button></section>
              <section className="student-career-results">
                <div className="student-career-results-heading"><div><small>Recommended next steps</small><h2>Your Course Matches</h2></div><button type="button" onClick={() => openTab('for-you')}>Back to Explore <i className="bi bi-arrow-right" /></button></div>
                {careerQuizRecommendations.length ? <div className="student-career-result-grid">{careerQuizRecommendations.map((course, index) => (
                  <article key={`career-match-${course._id || index}`}>
                    <div className="student-career-result-art"><CourseArtwork title={course.courseName || 'Course'} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#6366f1'} height={142} compact /><span>Match {index + 1}</span></div>
                    <div className="student-career-result-body"><small>{isFreeLearningCourse(course) ? 'Free course' : formatCourseCategoryLabel(course.category || inferCourseMode(course))}</small><h3>{course.courseName || 'Published program'}</h3><p>{course.shortDescription || 'Open this program to review its curriculum and learning format.'}</p><button type="button" onClick={() => openStudentCourseCard(course, { fallbackTab: 'for-you' })}><span>{getLearningFocusedCourseCta(course)}</span><i className="bi bi-arrow-right" /></button></div>
                  </article>
                ))}</div> : <div className="student-career-no-results"><i className="bi bi-journal-x" /><h2>No matching published programs yet</h2><p>Your answers are saved for this result view, but the current catalog does not contain a suitable published program.</p><button type="button" onClick={() => openTab('for-you')}>Browse all programs</button></div>}
              </section>
            </>
          )}
        </div>
      )}

      {activeTab === 'recommended' && !canWrite && (
        <div className="student-recommended-page">
          <header className="student-recommended-header">
            <div><span>Free-first discovery</span><h1>Recommended for You</h1><p>Start with useful free courses selected from the current catalog, then explore one advanced recommendation when it fits your goals.</p></div>
            <button type="button" onClick={() => openTab('for-you')}>Explore all courses <i className="bi bi-arrow-right" /></button>
          </header>

          <DashboardRecommendedCarousel
            courses={studentRealRecommendedCourses}
            learning={learning}
            isMobile={isStudentMobile}
            surface="recommended-page"
            onReach={reportCourseCarouselReach}
            onOpen={(course) => openStudentCourseCard(course, { fallbackTab: 'recommended' })}
            onSeeAll={() => openTab('for-you')}
          />

          {!!studentFreeCourseShowcase.length && (
            <section className="student-recommended-free-grid">
              <div className="student-recommended-section-heading"><div><small>Learn without waiting</small><h2>Free Courses You Can Start</h2></div><button type="button" onClick={() => openTab('for-you')}>View all <i className="bi bi-chevron-right" /></button></div>
              <div>{studentFreeCourseShowcase.slice(0, 4).map((course) => (
                <article key={`recommended-free-${course._id}`}>
                  <CourseArtwork title={course.courseName} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#4f46e5'} height={132} compact />
                  <div><small><i className="bi bi-unlock-fill" /> Free course</small><h3>{course.courseName || 'Course'}</h3><p>{course.shortDescription || course.category || 'Start learning at your own pace.'}</p><button type="button" onClick={() => openStudentCourseCard(course, { fallbackTab: 'recommended' })}>{getLearningFocusedCourseCta(course)} <i className="bi bi-arrow-right" /></button></div>
                </article>
              ))}</div>
            </section>
          )}

          {!!forYouLiveCourses.filter(isFreeLearningCourse).length && (
            <section className="student-recommended-free-batches">
              <div className="student-recommended-section-heading"><div><small>Learn together</small><h2>Upcoming Free Batches</h2></div></div>
              <div>{forYouLiveCourses.filter(isFreeLearningCourse).slice(0, 2).map((course) => (
                <article key={`recommended-batch-${course._id}`}><span><i className="bi bi-camera-video" /></span><div><small>Live learning</small><h3>{course.courseName || 'Free learning batch'}</h3><p>{course.durationDays ? `${course.durationDays} days` : 'Schedule available in course details'}</p></div><button type="button" onClick={() => openStudentCourseCard(course, { fallbackTab: 'recommended' })}>Enroll Now <i className="bi bi-arrow-right" /></button></article>
              ))}</div>
            </section>
          )}
        </div>
      )}

      {activeTab === 'for-you' && !canWrite && (
        <div className="row g-3 student-explore-page">
          {!canWrite ? (
            <>
              <div className="col-12">
                <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-1 student-explore-heading">
                  <div>
                    <div className="fw-semibold" style={{ color: '#0f172a', fontSize: isStudentMobile ? 34 : 42, lineHeight: 1.08 }}>Explore</div>
                    <div className="mt-2" style={{ color: '#64748b', fontSize: isStudentMobile ? 16 : 18 }}>Discover the right skills. Build your future.</div>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <StudentSurface className="student-explore-search" style={{ padding: isStudentMobile ? 10 : 14 }}>
                  <div className="row g-3 align-items-center">
                    <div className="col-12">
                      <div className="d-flex gap-2">
                      <div className="input-group flex-grow-1">
                        <span className="input-group-text border-0" style={{ background: '#eef2ff', color: '#4f46e5', minHeight: 36, paddingTop: 6, paddingBottom: 6 }}>
                          <i className="bi bi-search" />
                        </span>
                        <input
                          className="form-control border-0"
                          value={catalogFilters.search}
                          onChange={(e) => setCatalogFilters((prev) => ({ ...prev, search: e.target.value }))}
                          placeholder="Search for courses, topics or skills..."
                          style={{ background: '#f8fafc', boxShadow: 'none', minHeight: 36, paddingTop: 6, paddingBottom: 6, fontSize: isStudentMobile ? 13 : 16 }}
                        />
                      </div>
                      <button type="button" className="btn flex-shrink-0" title="Find a learning path" style={{ width: 42, height: 36, padding: 0, borderRadius: 10, background: '#5b3df5', color: '#fff' }} onClick={() => setCatalogFilters((prev) => ({ ...prev, access: 'all' }))}><i className="bi bi-stars" /></button>
                      </div>
                    </div>
                  </div>
                </StudentSurface>
              </div>

              <div className="col-12">
                <div className="rounded-4 overflow-hidden position-relative student-career-path-card" style={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', padding: isStudentMobile ? 18 : 28, minHeight: isStudentMobile ? 242 : 224, background: 'linear-gradient(90deg, #f5f3ff 0%, #ede9fe 100%)', border: '1px solid #ddd6fe', boxShadow: '0 16px 34px rgba(79,70,229,0.10)' }}>
                  <img src={careerRoadmapBanner} alt="Career learning roadmap" className="position-absolute" style={{ right: 0, top: 0, width: isStudentMobile ? '92%' : '64%', height: '100%', objectFit: 'cover', objectPosition: 'right center', pointerEvents: 'none' }} />
                  <div className="position-absolute top-0 bottom-0 start-0 end-0" style={{ background: 'linear-gradient(90deg, rgba(245,243,255,1) 0%, rgba(245,243,255,0.94) 42%, rgba(245,243,255,0.18) 100%)', pointerEvents: 'none' }} />
                  <div className="row align-items-center g-3 position-relative" style={{ zIndex: 1, marginLeft: 0, marginRight: 0, width: '100%' }}>
                    <div className="col-12 col-lg-7">
                      <div className="fw-semibold" style={{ color: '#1e1b4b', fontSize: isStudentMobile ? 23 : 30, lineHeight: 1.15 }}>Find the right path for you <span aria-hidden="true">&#127919;</span></div>
                      <div className="mt-2" style={{ color: '#5b6478', maxWidth: 330 }}>Take a quick career quiz and get a personalized roadmap.</div>
                      <button type="button" className="btn btn-primary mt-3 fw-semibold" style={{ borderRadius: 12, padding: '9px 14px' }} onClick={() => { setCareerQuizAnswers({}); setCareerQuizStep(0); setCareerQuizComplete(false); openTab('career-quiz'); }}>Take Career Quiz <i className="bi bi-arrow-right ms-2" /></button>
                    </div>
                    <div className="col-12 col-lg-5 d-none">
                      <div className="d-flex justify-content-lg-end">
                        <div style={{ width: isStudentMobile ? '100%' : 240, maxWidth: 280 }}>
                          <CourseArtwork
                            title={forYouPrimaryCourses[0]?.courseName || 'Your learning path'}
                            imageUrl={forYouPrimaryCourses[0]?.thumbnailUrl || forYouPrimaryCourses[0]?.promoImageUrl}
                            accent={forYouPrimaryCourses[0]?.courseColor || '#4f46e5'}
                            height={isStudentMobile ? 118 : 160}
                            compact
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="d-flex gap-2 overflow-auto pb-1 student-explore-categories" style={{ scrollbarWidth: 'thin' }}>
                  {[
                    { label: 'All', category: '', icon: 'bi-grid' },
                    ...catalogCategories.slice(0, 5).map((category, index) => ({ label: formatCourseCategoryLabel(category), category, icon: ['bi-code-slash', 'bi-bar-chart-line', 'bi-cpu', 'bi-palette', 'bi-briefcase'][index % 5] })),
                  ].map((chip) => (
                    <button
                      key={`search-chip-${chip.category || 'all'}`}
                      type="button"
                      className="btn btn-sm text-nowrap"
                      style={{ borderRadius: 16, padding: '9px 14px', background: catalogFilters.category === chip.category ? '#f1efff' : '#fff', color: catalogFilters.category === chip.category ? '#5b3df5' : '#475569', border: `1px solid ${catalogFilters.category === chip.category ? '#a78bfa' : '#e2e8f0'}`, boxShadow: catalogFilters.category === chip.category ? '0 4px 12px rgba(91,61,245,0.12)' : 'none' }}
                      onClick={() => setCatalogFilters((prev) => ({ ...prev, category: chip.category }))}
                    >
                      <i className={`bi ${chip.icon} me-2`} />{chip.label}
                    </button>
                  ))}
                </div>
              </div>

              {!!exploreSpotlightCourse && (() => {
                const spotlightCourse = exploreSpotlightCourse;
                const spotlightIsUiUx = /ui\s*\/?\s*ux|figma|design/i.test(`${spotlightCourse.courseName || ''} ${spotlightCourse.category || ''}`);
                const spotlightIsFree = isFreeLearningCourse(spotlightCourse);
                return (
                  <div className="col-12">
                    <section className="student-explore-free-spotlight" aria-labelledby="student-free-spotlight-title">
                      <div className="student-explore-free-spotlight-copy">
                        <span className="student-explore-free-spotlight-label"><i className="bi bi-stars" /> {spotlightIsFree ? 'Free learning spotlight' : 'Skill spotlight'}</span>
                        <h2 id="student-free-spotlight-title">Build a New Skill Today</h2>
                        <p id="student-free-spotlight-description">Start {spotlightCourse.courseName || 'a free course'} and learn at your own pace with published lessons from the current catalog.</p>
                        <div className="student-explore-free-spotlight-meta">
                          <span><i className={`bi ${spotlightIsFree ? 'bi-unlock-fill' : 'bi-mortarboard-fill'}`} /> {spotlightIsFree ? 'Free access' : formatCourseCategoryLabel(spotlightCourse.category || 'Course')}</span>
                          <span><i className="bi bi-journal-text" /> {Number(spotlightCourse.totalLessons || spotlightCourse.lessonCount || 0)} lessons</span>
                        </div>
                        <button type="button" onClick={() => openStudentCourseCard(spotlightCourse, { fallbackTab: 'for-you' })}>
                          Start Learning <i className="bi bi-arrow-right" />
                        </button>
                      </div>
                      <div className="student-explore-free-spotlight-art" aria-hidden="true">
                        <span className="student-explore-free-spotlight-spark spark-one" />
                        <span className="student-explore-free-spotlight-spark spark-two" />
                        <CourseArtwork
                          title={spotlightCourse.courseName}
                          category={spotlightCourse.category}
                          imageUrl={spotlightCourse.thumbnailUrl || spotlightCourse.promoImageUrl || (spotlightIsUiUx ? uiUxDesignSpotlight : '')}
                          accent={spotlightCourse.courseColor || '#2563eb'}
                          height={isStudentMobile ? 118 : 170}
                          compact
                        />
                      </div>
                    </section>
                  </div>
                );
              })()}

              <div className="col-12">
                <StudentSectionTitle
                  title="Trending Programs"
                  action={(
                    <div className="d-flex align-items-center gap-2">
                      <button type="button" className="btn dashboard-course-carousel-control d-none d-md-inline-flex" aria-label="Previous trending programs" onClick={() => scrollCourseRail(exploreTrendingRailRef, -1)}><i className="bi bi-chevron-left" /></button>
                      <button type="button" className="btn dashboard-course-carousel-control d-none d-md-inline-flex" aria-label="Next trending programs" onClick={() => scrollCourseRail(exploreTrendingRailRef, 1)}><i className="bi bi-chevron-right" /></button>
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold" style={{ color: '#6d28d9' }} onClick={() => setExpandedExploreSections((prev) => ({ ...prev, trending: !prev.trending }))}>{expandedExploreSections.trending ? 'Show less' : 'See all'} <i className={`bi ${expandedExploreSections.trending ? 'bi-arrow-up' : 'bi-arrow-right'} ms-1`} /></button>
                    </div>
                  )}
                />
              </div>

              <div className="col-12">
                <div ref={exploreTrendingRailRef} className="dashboard-course-carousel-rail student-trending-rail">
              {exploreTrendingCourses.map((course, index) => {
                const progress = learning.find((row) => String(row.course?._id || row.course) === String(course._id))?.learningSummary?.completionPercent || 0;
                const isFree = isFreeLearningCourse(course);
                return (
                  <div className="student-explore-trending-item" key={`for-you-pick-${course._id || index}`}>
                    <StudentSurface className="h-100 position-relative overflow-hidden bg-white student-trending-card dashboard-course-carousel-card student-explore-dashboard-card" style={{ borderRadius: 14 }}>
                      <button
                        type="button"
                        className="btn btn-sm position-absolute student-explore-wishlist"
                        aria-label={wishlistCourseIdSet.has(String(course._id)) ? `Remove ${course.courseName || 'course'} from saved courses` : `Save ${course.courseName || 'course'}`}
                        style={{ zIndex: 2, borderRadius: 999, background: 'rgba(255,255,255,0.94)', border: '1px solid #e2e8f0', color: wishlistCourseIdSet.has(String(course._id)) ? '#fb7185' : '#64748b' }}
                        onClick={(event) => { event.stopPropagation(); toggleWishlist(course._id); }}
                      >
                        <i className={`bi ${wishlistCourseIdSet.has(String(course._id)) ? 'bi-heart-fill' : 'bi-heart'}`} />
                      </button>
                      <div className="position-relative dashboard-course-carousel-artwork">
                        <CourseArtwork title={course.courseName} category={course.category} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#2563eb'} height={isStudentMobile ? 178 : 228} compact />
                        <span className={`badge position-absolute ${isFree ? 'dashboard-course-free-badge' : ''}`} style={{ top: 10, left: 10, background: isFree ? 'linear-gradient(135deg, #fffbea, #fef3c7 55%, #fde68a)' : 'rgba(255,255,255,0.94)', color: isFree ? '#92400e' : '#5b3df5', boxShadow: isFree ? '0 0 0 2px rgba(251,191,36,.12), 0 7px 16px rgba(146,64,14,.16)' : '0 5px 14px rgba(15,23,42,0.12)', padding: '6px 9px', borderRadius: 8, fontWeight: isFree ? 900 : 700 }}>
                          <i className={`bi ${isFree ? 'bi-unlock-fill' : index === 0 ? 'bi-star-fill' : 'bi-lightning-charge-fill'} me-1`} /><span>{isFree ? 'Free to learn' : 'Recommended'}</span>
                        </span>
                      </div>
                      <div className="dashboard-course-carousel-body d-flex flex-column">
                        <div className="fw-semibold dashboard-course-carousel-title">{course.courseName || 'Course'}</div>
                        <div className="dashboard-course-carousel-meta student-explore-course-inline-meta">
                          <span title={formatCourseCategoryLabel(course.category || course.level || 'Course')}><i className="bi bi-grid" />{formatCourseCategoryLabel(course.category || course.level || 'Course')}</span>
                          <span><i className="bi bi-journal-text" />{Number(course.totalLessons || course.lessonCount || 0)} Lessons</span>
                          <span><i className="bi bi-graph-up-arrow" />{progress}% Progress</span>
                        </div>
                        <ProgressBar value={progress} color={index % 3 === 0 ? '#2563eb' : index % 3 === 1 ? '#8b5cf6' : '#14b8a6'} trackColor="#e5e7eb" />
                        <button
                          type="button"
                          className="dashboard-course-carousel-cta border-0 mt-auto"
                          onClick={(event) => {
                            event.stopPropagation();
                            openStudentCourseCard(course, { fallbackTab: 'for-you' });
                          }}
                        >
                          <span>{getLearningFocusedCourseCta(course)}</span>
                          <i className="bi bi-arrow-right" />
                        </button>
                      </div>
                    </StudentSurface>
                  </div>
                );
              })}
                </div>
              </div>
              {!forYouPrimaryCourses.length && (
                <div className="col-12">
                  <StudentSurface style={{ padding: 18 }}>
                    <div className="text-center text-muted">No recommended courses match the selected filters.</div>
                  </StudentSurface>
                </div>
              )}

              <div className="col-12 mt-2 d-none">
                <StudentSectionTitle
                  eyebrow="Keep Going"
                  title="You might be interested in"
                  subtitle="Suggestions based on your recently opened course path."
                />
              </div>

              {forYouInterestCourses.slice(0, 3).map((row, index) => {
                const course = row.course || row || {};
                const linkedLearning = learning.find((item) => String(item.course?._id || item.course) === String(course._id));
                return (
                  <div className="col-12 col-md-6 col-xl-4 d-none" key={`for-you-related-${row._id || course._id || index}`}>
                    <StudentSurface className="h-100" style={{ padding: 18, background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(245,247,255,0.92) 100%)' }}>
                      <div className="d-flex flex-column gap-3 h-100">
                        <div className="position-relative">
                          <CourseArtwork title={course.courseName} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#2563eb'} height={150} compact />
                          <div className="position-absolute top-0 start-0 m-2 badge rounded-pill" style={{ background: 'rgba(15,23,42,0.72)', color: '#fff' }}>
                            Related Path
                          </div>
                        </div>
                        <div className="flex-fill d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                            <div className="small badge" style={{ background: 'rgba(59,130,246,0.10)', color: '#2563eb' }}>{course.category || 'Recommended'}</div>
                            <div className="small" style={{ color: studentTheme.muted }}>{fmtMoney(course.totalFees || 0)}</div>
                          </div>
                          <div className="fw-semibold mb-2" style={{ fontSize: 19, lineHeight: 1.25, color: studentTheme.title }}>{course.courseName || 'Course'}</div>
                          <div className="small mb-3" style={{ color: studentTheme.muted, minHeight: 42 }}>{course.shortDescription || course.category || 'Related recommendation'}</div>
                          <div className="rounded-4 p-3 mb-3" style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
                            <div className="d-flex justify-content-between align-items-center small mb-1">
                              <span style={{ color: studentTheme.text }}>Progress</span>
                              <span className="fw-semibold" style={{ color: studentTheme.title }}>{Number(linkedLearning?.learningSummary?.completionPercent || 0)}%</span>
                            </div>
                            <ProgressBar value={Number(linkedLearning?.learningSummary?.completionPercent || 0)} color={studentTheme.accent} />
                          </div>
                          <div className="d-flex gap-2 mt-auto">
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => toggleWishlist(course._id)}
                            >
                              <i className={`bi ${wishlistCourseIdSet.has(String(course._id)) ? 'bi-heart-fill' : 'bi-heart'}`} />
                            </button>
                            <button className="btn btn-primary btn-sm flex-fill" onClick={() => {
                              if (linkedLearning || course.enrollment) {
                                openStudentCourseCard(course, { fallbackTab: 'my-courses' });
                                return;
                              }
                              openStudentCourseCard(course, { fallbackTab: 'for-you' });
                            }}>
                              {linkedLearning ? 'Continue' : getLearningFocusedCourseCta(course)}
                            </button>
                          </div>
                        </div>
                      </div>
                    </StudentSurface>
                  </div>
                );
              })}
              {!forYouInterestCourses.length && (
                <div className="col-12 d-none">
                  <StudentSurface style={{ padding: 18 }}>
                    <div className="text-center text-muted">No related course suggestions are available for the current filters yet.</div>
                  </StudentSurface>
                </div>
              )}

              <div className="col-12">
                <StudentSectionTitle
                  title="Upcoming Free Live Classes for You"
                  action={(
                    <div className="d-flex align-items-center gap-2">
                      <button type="button" className="btn dashboard-course-carousel-control d-none d-md-inline-flex" aria-label="Previous upcoming classes" onClick={() => scrollCourseRail(exploreLiveRailRef, -1)}><i className="bi bi-chevron-left" /></button>
                      <button type="button" className="btn dashboard-course-carousel-control d-none d-md-inline-flex" aria-label="Next upcoming classes" onClick={() => scrollCourseRail(exploreLiveRailRef, 1)}><i className="bi bi-chevron-right" /></button>
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold" style={{ color: '#6d28d9' }} onClick={() => setExpandedExploreSections((prev) => ({ ...prev, live: !prev.live }))}>{expandedExploreSections.live ? 'Show less' : 'See all'} <i className={`bi ${expandedExploreSections.live ? 'bi-arrow-up' : 'bi-arrow-right'} ms-1`} /></button>
                    </div>
                  )}
                />
              </div>

              {!!exploreLiveCards.length && <div className="col-12">
                <div ref={exploreLiveRailRef} className="dashboard-course-carousel-rail student-trending-rail">
              {exploreLiveCards.map((entry, index) => {
                const isShowcase = entry.type !== 'course';
                const isLearningEvent = entry.type === 'event';
                const item = isShowcase ? entry.promo : entry.course;
                const title = item?.promoTitle || item?.title || item?.courseName || 'Upcoming cohort';
                const session = item?.liveSession || {};
                const eventDate = isLearningEvent && item?.eventDate ? new Date(item.eventDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                const schedule = [eventDate || session.sessionDate, item?.startTime || session.sessionTime || session.time].filter(Boolean).join(' | ') || (item?.durationDays ? `${item.durationDays} days` : 'Live online');
                const accent = index % 2 ? '#059669' : '#5b3df5';
                return (
                  <div className="student-explore-trending-item" key={`cohort-${item?._id || index}`}>
                    <StudentSurface className="h-100 student-cohort-card" style={{ padding: isStudentMobile ? 10 : 14, borderRadius: 10 }}>
                      {isLearningEvent && item?.imageUrl && <img src={item.imageUrl} alt={title} className="w-100 mb-2" style={{ aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 8 }} />}
                      <div className="d-flex justify-content-between align-items-start gap-1 mb-2">
                        <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: isStudentMobile ? 34 : 40, height: isStudentMobile ? 34 : 40, borderRadius: 8, background: index % 2 ? 'linear-gradient(135deg, #10b981 0%, #16a34a 100%)' : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', color: '#fff', fontSize: isStudentMobile ? 16 : 18 }}><i className={`bi ${index % 2 ? 'bi-cpu-fill' : 'bi-code-slash'}`} /></div>
                        <span className="badge" style={{ maxWidth: 90, whiteSpace: 'normal', textAlign: 'right', background: '#fff7ed', color: '#ea580c', borderRadius: 6, padding: '4px 5px', fontSize: isStudentMobile ? 8 : 9, lineHeight: 1.15 }}>{index % 2 ? 'Limited seats' : 'Seats filling fast'}</span>
                      </div>
                      <div className="fw-semibold mb-2" style={{ color: '#0f172a', fontSize: isStudentMobile ? 13 : 15, lineHeight: 1.2, minHeight: isStudentMobile ? 31 : 36, overflow: 'hidden' }}>{title}</div>
                      <div style={{ color: '#64748b', fontSize: isStudentMobile ? 10 : 11 }}><i className="bi bi-camera-video-fill me-1" />Live Online</div>
                      <div className="text-truncate mt-1" style={{ color: '#64748b', fontSize: isStudentMobile ? 10 : 11 }}><i className="bi bi-clock me-1" />{schedule}</div>
                      <button type="button" className="btn btn-sm w-100 mt-3 fw-semibold" style={{ color: '#fff', background: accent, borderRadius: 8, padding: '6px 8px', fontSize: 11 }} onClick={() => isShowcase ? openStudentCtaLink(item?.registrationUrl || item?.meetingUrl || item?.ctaLink || item?.linkUrl || '/lms?tab=for-you') : openLeadCapture('live', item, { title: isFreeLearningCourse(item) ? 'Enroll now' : 'Start your career' })}>{isLearningEvent ? 'Register' : isShowcase ? 'Start Your Career' : getLearningFocusedCourseCta(item)} <i className="bi bi-arrow-right ms-1" /></button>
                    </StudentSurface>
                  </div>
                );
              })}
                </div>
              </div>}
              {!exploreLiveCards.length && (
                <div className="col-12">
                  <StudentSurface style={{ padding: 18 }}>
                    <div className="text-center text-muted">No live or online programs match the selected filters.</div>
                  </StudentSurface>
                </div>
              )}

              <div className="col-12 mt-2 d-none">
                <StudentSectionTitle
                  eyebrow="Internship Tracks"
                  title="Internships and project programs"
                  subtitle="Dedicated internship and project suggestions with their own filtered card rail."
                  action={(
                    <div className="d-flex gap-2">
                      <button type="button" className="btn btn-outline-secondary btn-sm" disabled={forYouSpotlightIndex <= 0} onClick={() => setForYouSpotlightIndex((prev) => Math.max(0, prev - 1))}>
                        <i className="bi bi-chevron-left" />
                      </button>
                      <button type="button" className="btn btn-outline-secondary btn-sm" disabled={forYouSpotlightIndex >= forYouInternshipMaxIndex} onClick={() => setForYouSpotlightIndex((prev) => Math.min(forYouInternshipMaxIndex, prev + 1))}>
                        <i className="bi bi-chevron-right" />
                      </button>
                    </div>
                  )}
                />
              </div>

              {forYouInternshipVisible.map((entry, index) => entry.type === 'showcase' ? (
                <div className="col-12 col-md-6 col-xl-4 d-none" key={`internship-showcase-${entry.promo?._id || index}`}>
                  <StudentSurface className="h-100 position-relative" style={{ overflow: 'hidden', background: 'linear-gradient(135deg, #1e293b 0%, #312e81 100%)', color: '#fff' }}>
                    {entry.promo?.imageUrl ? (
                      <img src={entry.promo.imageUrl} alt={entry.promo?.promoTitle || 'Showcase'} className="w-100" style={{ height: 180, objectFit: 'cover' }} />
                    ) : (
                      <div style={{ height: 180, background: 'linear-gradient(135deg, #0f766e 0%, #2563eb 100%)' }} />
                    )}
                    <div style={{ padding: 20 }}>
                      <div className="badge rounded-pill mb-2" style={{ background: 'rgba(255,255,255,0.16)', color: '#fff' }}>Student Showcase</div>
                      <div className="fw-semibold mb-2" style={{ fontSize: 20, lineHeight: 1.2 }}>{entry.promo?.promoTitle || entry.promo?.title || 'Project success stories'}</div>
                      <div className="small mb-3" style={{ color: 'rgba(255,255,255,0.76)', minHeight: 42 }}>{entry.promo?.promoText || entry.promo?.subtitle || 'See internship outcomes and student project highlights.'}</div>
                      <button className="btn btn-light btn-sm" onClick={() => openStudentCtaLink(entry.promo?.ctaLink || entry.promo?.linkUrl || '/lms?tab=for-you')}>
                        Explore
                      </button>
                    </div>
                  </StudentSurface>
                </div>
              ) : (
                <div className="col-12 col-md-6 col-xl-4 d-none" key={`internship-course-${entry.course?._id || index}`}>
                  <StudentSurface className="h-100 position-relative" style={{ overflow: 'hidden', background: '#fff7ed' }}>
                    <button
                      type="button"
                      className="btn btn-sm position-absolute top-0 end-0 m-3"
                      style={{ zIndex: 2, borderRadius: 999, background: 'rgba(255,255,255,0.92)', border: '1px solid #e2e8f0', color: wishlistCourseIdSet.has(String(entry.course?._id)) ? '#fb7185' : '#64748b' }}
                      onClick={() => toggleWishlist(entry.course?._id)}
                    >
                      <i className={`bi ${wishlistCourseIdSet.has(String(entry.course?._id)) ? 'bi-heart-fill' : 'bi-heart'}`} />
                    </button>
                    <CourseArtwork title={entry.course?.courseName} imageUrl={entry.course?.thumbnailUrl || entry.course?.promoImageUrl} accent={entry.course?.courseColor || '#f97316'} height={180} />
                    <div style={{ padding: 22 }}>
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                        <div className="pe-2">
                          <div className="fw-semibold" style={{ fontSize: 20, lineHeight: 1.2 }}>{entry.course?.courseName}</div>
                          <div className="small text-muted mt-1">{entry.course?.category || 'Internship'}</div>
                        </div>
                        <div className="badge rounded-pill" style={{ background: 'rgba(249,115,22,0.12)', color: '#c2410c', border: '1px solid rgba(249,115,22,0.18)' }}>{entry.course?.category || 'Internship'}</div>
                      </div>
                      <div className="text-muted mb-3" style={{ minHeight: 48 }}>
                        {entry.course?.shortDescription || 'Hands-on internship and project execution path.'}
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <button className="btn btn-primary" onClick={() => openLeadCapture(String(entry.course?.category || '').toLowerCase().includes('project') ? 'project' : 'internship', entry.course, {
                          title: String(entry.course?.category || '').toLowerCase().includes('project') ? 'Join this project program' : 'Join this internship program',
                        })}>
                          {getLearningFocusedCourseCta(entry.course)}
                        </button>
                      </div>
                    </div>
                  </StudentSurface>
                </div>
              ))}
              {!forYouInternshipVisible.length && (
                <div className="col-12 d-none">
                  <StudentSurface style={{ padding: 18 }}>
                    <div className="text-center text-muted">No internship or project programs match the selected filters.</div>
                  </StudentSurface>
                </div>
              )}

              <div className="col-12 mt-2 student-explore-lower-grid">
                <div className="row g-3">
                  <div className="col-6">
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2 student-mini-section-heading"><div className="fw-semibold" style={{ color: studentTheme.title }}>Free Workshops</div><button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => openTab('for-you')}>See all <i className="bi bi-arrow-right" /></button></div>
                    <StudentSurface className="student-workshop-card" style={{ padding: isStudentMobile ? 12 : 18, borderRadius: isStudentMobile ? 16 : 22 }}>
                      <div className="d-flex flex-column gap-2">
                        {studentWorkshopDisplayItems.slice(0, 2).map((item, index) => {
                          const session = item.liveSession || {};
                          const workshopCourse = forYouLiveCourses[index] || null;
                          const eventDate = item.eventDate ? new Date(item.eventDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';
                          const schedule = [eventDate || session.sessionDate, item.startTime || session.sessionTime || session.time, item.venue || session.meetingNote].filter(Boolean).join(' | ');
                          return (
                            <div key={item._id || `${item.promoTitle || item.title}-${index}`} className="d-flex flex-column align-items-stretch gap-2 rounded-3 p-2" style={{ border: '1px solid #e2e8f0', background: '#fff' }}>
                              {item.imageUrl ? <img src={item.imageUrl} alt="" className="w-100" style={{ aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 8 }} /> : <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 36, height: 36, borderRadius: 10, background: '#ecfdf5', color: '#10b981' }}><i className="bi bi-camera-video-fill" /></div>}
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="fw-semibold text-truncate" style={{ color: studentTheme.title }}>{item.promoTitle || item.title || item.name || 'Live workshop'}</div>
                                <div className="small text-truncate" style={{ color: studentTheme.muted }}>{schedule || 'Schedule will be announced soon'}</div>
                              </div>
                              <button type="button" className="btn btn-sm fw-semibold w-100" style={{ background: '#4f46e5', color: '#fff', borderRadius: 10, fontSize: 12 }} onClick={() => {
                                if (String(item.type || '') === 'learning_event') openStudentCtaLink(item.registrationUrl || item.meetingUrl || '/lms?tab=for-you');
                                else if (workshopCourse) openLeadCapture('live', workshopCourse, { title: 'Register for this workshop' });
                                else openStudentCtaLink(item.ctaLink || item.linkUrl || '/lms?tab=for-you');
                              }}>
                                Register
                              </button>
                            </div>
                          );
                        })}
                        {!studentWorkshopDisplayItems.length && (
                          <div className="student-explore-empty-feature">
                            <span style={{ color: '#059669', background: '#ecfdf5' }}><i className="bi bi-camera-video-fill" /></span>
                            <strong>Free workshops</strong>
                            <p>New workshop dates will appear here when they are published.</p>
                          </div>
                        )}
                      </div>
                    </StudentSurface>
                  </div>

                  <div className="col-6">
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2 student-mini-section-heading"><div className="fw-semibold" style={{ color: studentTheme.title }}>Mentor Picks</div><button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => openTab('for-you')}>See all <i className="bi bi-arrow-right" /></button></div>
                    <StudentSurface className="student-mentor-card" style={{ padding: isStudentMobile ? 12 : 18, background: '#f5f3ff', borderRadius: isStudentMobile ? 16 : 22 }}>
                      {(() => {
                        const mentorPick = showcasePromotions[0] || activeDashboardPromotion;
                        if (!mentorPick) {
                          return <div className="student-explore-empty-feature"><span style={{ color: '#7c3aed', background: '#ede9fe' }}><i className="bi bi-person-workspace" /></span><strong>Mentor picks</strong><p>Featured mentor guidance will appear here when it is published.</p></div>;
                        }
                        return (
                          <div className="d-flex flex-column gap-2 align-items-start student-mentor-content">
                            {mentorPick.imageUrl ? (
                              <img src={mentorPick.imageUrl} alt="Featured learner story" style={{ width: isStudentMobile ? 46 : 72, height: isStudentMobile ? 46 : 72, borderRadius: 12, objectFit: 'cover' }} />
                            ) : (
                              <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: isStudentMobile ? 46 : 72, height: isStudentMobile ? 46 : 72, borderRadius: 12, background: '#ede9fe', color: '#7c3aed', fontSize: isStudentMobile ? 20 : 28 }}><i className="bi bi-person-workspace" /></div>
                            )}
                            <div className="flex-grow-1">
                              <div className="fw-semibold mb-1" style={{ color: studentTheme.title }}>{mentorPick.promoTitle || mentorPick.title || 'Featured learning insight'}</div>
                              <div className="small mb-2 student-mentor-copy" style={{ color: studentTheme.muted }}>{mentorPick.promoText || mentorPick.subtitle || mentorPick.description || 'Explore the latest guidance shared by the learning team.'}</div>
                              <button type="button" className="btn btn-outline-primary btn-sm w-100 student-mentor-action" style={{ fontSize: 12 }} onClick={() => openStudentCtaLink(mentorPick.ctaLink || mentorPick.linkUrl || '/lms?tab=for-you')}>View details <i className="bi bi-arrow-right ms-1" /></button>
                            </div>
                          </div>
                        );
                      })()}
                    </StudentSurface>
                  </div>
                </div>
              </div>

              {!!studentGeneralEvents.length && (
                <div className="col-12 mt-2">
                  <StudentSectionTitle title="Upcoming Events" />
                  <div className="row g-2">
                    {studentGeneralEvents.slice(0, 3).map((item) => (
                      <div className="col-12 col-md-4" key={`learning-event-${item._id}`}>
                        <StudentSurface className="h-100 overflow-hidden" style={{ padding: 0, borderRadius: 10 }}>
                          <img src={item.imageUrl} alt={item.promoTitle || 'Upcoming event'} className="w-100" style={{ aspectRatio: '16 / 9', objectFit: 'cover' }} />
                          <div style={{ padding: isStudentMobile ? 12 : 14 }}>
                            <div className="fw-semibold text-truncate" style={{ color: studentTheme.title }}>{item.promoTitle || 'Upcoming Event'}</div>
                            <div className="small mt-1" style={{ color: studentTheme.muted }}><i className="bi bi-calendar-event me-1" />{item.eventDate ? new Date(item.eventDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Date to be announced'}{item.startTime ? ` | ${item.startTime}` : ''}</div>
                            <button type="button" className="btn btn-outline-primary btn-sm mt-2" onClick={() => openStudentCtaLink(item.registrationUrl || item.meetingUrl || '/lms?tab=for-you')}>View Event <i className="bi bi-arrow-right ms-1" /></button>
                          </div>
                        </StudentSurface>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(studentFreeCourseShowcase.length > 0 || forYouPrimaryCourses.some(isFreeLearningCourse)) && (
                <div className="col-12">
                  <div className="rounded-4 text-white overflow-hidden position-relative student-explore-promotion" style={{ padding: isStudentMobile ? 18 : 24, background: 'linear-gradient(135deg, #0f766e 0%, #2563eb 48%, #4f46e5 100%)', boxShadow: '0 18px 38px rgba(37,99,235,0.24)' }}>
                    <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-3">
                      <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 58, height: 58, borderRadius: 16, background: 'rgba(255,255,255,0.14)', color: '#fef08a', fontSize: 28 }}><i className="bi bi-journal-code" /></div>
                      <div className="flex-grow-1">
                        <div className="fw-semibold" style={{ fontSize: isStudentMobile ? 20 : 24 }}>Explore More Free Learning</div>
                        <div className="small mt-1" style={{ color: 'rgba(255,255,255,0.86)' }}>Start free courses, recorded lessons, and available live classes without a payment step.</div>
                      </div>
                      <button type="button" className="btn btn-light fw-semibold student-explore-promotion-action" style={{ color: '#1d4ed8', borderRadius: 9 }} onClick={() => setCatalogFilters((prev) => ({ ...prev, access: 'free' }))}>Explore Free Courses <i className="bi bi-arrow-right ms-1" /></button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            (catalog).map((course) => (
              <div className="col-12 col-lg-6 col-xxl-4" key={course._id}>
                <AdminSurface className="h-100" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: 20, borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div className="pe-2">
                        <div className="fw-semibold" style={{ fontSize: 20, lineHeight: 1.2 }}>{course.courseName}</div>
                        <div className="small text-muted mt-1">{course.category || 'No category'}</div>
                      </div>
                      <Pill color={isOnDemandCategory(course.category) ? 'primary' : 'warning'}>{formatCourseCategoryLabel(course.category)}</Pill>
                    </div>
                  </div>
                  <div style={{ padding: 20 }}>
                  <div className="small text-muted mb-2">{course.category || 'No category'}</div>
                  <div className="mb-2">{course.shortDescription || 'No description added yet.'}</div>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <Pill>{course.access?.cta || 'Locked'}</Pill>
                    {course.access?.remainingQuota != null && <Pill color="info">Quota left: {course.access.remainingQuota}</Pill>}
                    {course.enrollment && <Pill color="success">Enrolled</Pill>}
                    {course.access?.mode === 'expired_membership' && <Pill color="danger">Expired access</Pill>}
                  </div>
                  {course.access?.reason && <div className="small text-danger mb-2">{course.access.reason}</div>}
                  <div className="small text-muted mb-3">
                    Price: {fmtMoney(course.totalFees)} {course.discountPercent ? ` | Discount ${course.discountPercent}%` : ''}
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    {course.enrollment ? (
                      <button className="btn btn-primary btn-sm" onClick={() => openStudentCourseCard(course, { fallbackTab: 'my-courses' })}>
                        {getStudentCourseActionLabel(course)}
                      </button>
                    ) : course.access?.allowed ? (
                      <button className="btn btn-success btn-sm" disabled={saving} onClick={() => enrollCourse(course)}>
                        {getLearningFocusedCourseCta(course)}
                      </button>
                    ) : course.access?.mode === 'expired_membership' && course.access?.planId ? (
                      <button
                        className="btn btn-outline-warning btn-sm"
                        onClick={() => {
                          const renewPlan = plans.find((plan) => String(plan._id) === String(course.access.planId));
                          if (renewPlan) openCheckout(renewPlan);
                          else openTab('my-subscription');
                        }}
                      >
                        Renew Package
                      </button>
                    ) : (
                      <button className="btn btn-outline-primary btn-sm" onClick={() => openTab('my-subscription')}>
                        Buy Package
                      </button>
                    )}
                  </div>
                  </div>
                </AdminSurface>
              </div>
            ))
          )}
          {!canWrite && !filteredCatalog.length && (
            <div className="col-12">
              <StudentSurface style={{ padding: studentPagePadding }}>
                <div className="text-center text-muted">
                No courses match the selected filters.
                </div>
              </StudentSurface>
            </div>
          )}
        </div>
      )}

      {activeTab === 'progress' && !canWrite && (
        <div className="student-progress-page">
          <header className="student-progress-header">
            <h1>Your Progress <i className="bi bi-graph-up-arrow" /></h1>
            <p>Keep learning. Keep growing.</p>
          </header>

          <StudentSurface className="student-progress-streak-card">
            <div className="student-progress-streak-summary">
              <span className="student-progress-streak-icon"><i className="bi bi-fire" /></span>
              <div className="student-progress-streak-copy">
                <strong>{studentConsistencyOverview.bestStreak || 0}<span> day streak</span></strong>
              </div>
              <small>{(studentConsistencyOverview.bestStreak || 0) > 0 ? 'Amazing consistency!' : 'Start your streak today!'}</small>
            </div>
            <div className="student-progress-week" aria-label="Weekly learning streak">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => {
                const completed = index < Math.min(7, studentConsistencyOverview.bestStreak || 0);
                const isTodayMarker = index === 6;
                return <span key={`${day}-${index}`} className={`${completed ? 'completed' : ''} ${isTodayMarker ? 'today' : ''}`.trim()}><i className={`bi ${completed ? 'bi-check-lg' : isTodayMarker ? 'bi-fire' : 'bi-circle'}`} /><small>{day}</small></span>;
              })}
            </div>
          </StudentSurface>

          {studentHasSubscriptionTab && (
            <StudentUpcomingLiveClasses
              courses={studentUpcomingLiveItems}
              isMobile={isStudentMobile}
              onViewAll={() => openTab('for-you')}
              onOpen={(course) => course._learningEvent
                ? openStudentCtaLink(course.registrationUrl || course.meetingUrl || '/lms?tab=for-you')
                : openStudentCourseCard(course, { fallbackTab: 'for-you' })}
            />
          )}

          <StudentSurface className="student-progress-panel student-career-readiness">
            <div className="student-progress-section-heading"><div><h2>Career Readiness</h2><p>You are building job-ready momentum.</p></div><button type="button" onClick={() => navigate('/resumes?tab=jobs')}>View jobs <i className="bi bi-arrow-right" /></button></div>
            <div className="student-career-readiness-layout">
              <div className="student-career-readiness-list">
                {[
                  { label: 'Resume Score', value: studentProgressSnapshot.averageCompletion, display: studentProgressSnapshot.averageCompletion >= 70 ? 'Good' : studentProgressSnapshot.averageCompletion >= 40 ? 'Intermediate' : 'Building', color: '#10b981', icon: 'bi-file-earmark-text' },
                  { label: 'Interview Readiness', value: studentProgressSnapshot.assessmentScore, display: studentLearningOverview.averageScore != null ? (studentProgressSnapshot.assessmentScore >= 70 ? 'Intermediate' : 'Building') : 'Not scored', color: '#2563eb', icon: 'bi-magic' },
                  { label: 'Job Opportunities Fit', value: Math.min(100, studentJobs.length * 10), display: studentJobs.length > 0 ? 'High' : 'Low', color: '#059669', icon: 'bi-shield-check' },
                ].map((item) => (
                  <div key={item.label}><span><i className={`bi ${item.icon}`} />{item.label}</span><b style={{ color: item.color }}>{item.display}</b><div><i style={{ width: `${item.value}%`, background: item.color }} /></div></div>
                ))}
              </div>
              <div className="student-progress-mastery">
                <div className="student-progress-ring" style={{ '--progress-value': `${studentProgressSnapshot.readinessScore}%`, '--progress-color': '#10b981', '--progress-track': '#dcfce7' }}><span><strong>{studentProgressSnapshot.readinessScore}%</strong></span></div>
                <p>Career Readiness<br />Score</p>
              </div>
            </div>
          </StudentSurface>

          <StudentSurface className="student-progress-panel student-progress-skills">
            <div className="student-progress-section-heading"><h2>Skill Progress</h2><button type="button" onClick={() => openTab('my-courses')}>View all skills <i className="bi bi-arrow-right" /></button></div>
            <div className="student-progress-skill-layout">
              <div className="student-progress-skill-list">
                {studentProgressSnapshot.skills.map((skill) => (
                  <div className="student-progress-skill-row" key={skill.id}>
                    <span style={{ color: skill.color, background: skill.bg }}><i className={`bi ${skill.icon}`} /></span>
                    <div className="student-progress-skill-detail">
                      <div className="student-progress-skill-name">
                        <strong title={skill.label}>{skill.label}</strong>
                        <em className={`is-${skill.value >= 75 ? 'advanced' : skill.value >= 40 ? 'intermediate' : 'beginner'}`}>
                          {skill.value >= 75 ? 'Advanced' : skill.value >= 40 ? 'Intermediate' : 'Beginner'}
                        </em>
                      </div>
                      <div className="student-progress-skill-track"><i style={{ width: `${skill.value}%`, background: skill.color }} /></div>
                    </div>
                    <b><i className={`bi ${skill.value >= 100 ? 'bi-check-circle-fill' : 'bi-stars'}`} />{skill.value}%</b>
                  </div>
                ))}
                {studentProgressSnapshot.skills.length === 1 && (
                  <div className="student-progress-skill-insights" aria-label="Learning snapshot">
                    <div><i className="bi bi-journal-check" /><span><strong>{studentProgressSnapshot.completedLessons}</strong><small>Lessons done</small></span></div>
                    <div><i className="bi bi-clock-history" /><span><strong>{fmtDuration(studentProgressSnapshot.watchedSeconds)}</strong><small>Learning time</small></span></div>
                    <div><i className="bi bi-lightning-charge-fill" /><span><strong>{studentLearningOverview.averageScore != null ? `${studentProgressSnapshot.assessmentScore}%` : 'Not scored'}</strong><small>Assessment</small></span></div>
                  </div>
                )}
                {!studentProgressSnapshot.skills.length && <div className="student-progress-empty">Start a course to see your skill progress.</div>}
              </div>
              <div className="student-progress-mastery">
                <div className="student-progress-ring" style={{ '--progress-value': `${studentProgressSnapshot.averageCompletion}%`, '--progress-color': '#6d4aff' }}><span><strong>{studentProgressSnapshot.averageCompletion}%</strong></span></div>
                <p>Average Skill<br />Mastery</p>
              </div>
            </div>
          </StudentSurface>

          <StudentSurface className="student-progress-panel student-progress-achievements">
            <div className="student-progress-section-heading"><h2>Achievements</h2><button type="button" onClick={() => openTab('my-courses')}>View all <i className="bi bi-arrow-right" /></button></div>
            <div className="student-progress-achievement-grid">
              {studentProgressSnapshot.achievements.map((item) => (
                <div key={item.label}>
                  <span style={{ color: item.color, background: item.bg }}><i className={`bi ${item.icon}`} /></span>
                  <strong>{item.label}</strong>
                  <small>{item.value}</small>
                </div>
              ))}
            </div>
          </StudentSurface>

          <section className="student-progress-metrics">
            {[
              { label: 'Learning Hours', value: fmtDuration(studentProgressSnapshot.watchedSeconds), hint: `${studentConsistencyOverview.activeRate || 0}% vs last week`, icon: 'bi-clock-fill', color: '#16a36a', bg: '#eafbf3' },
              { label: 'Lessons Completed', value: studentProgressSnapshot.completedLessons, hint: `${Math.max(0, Math.round((studentProgressSnapshot.completedLessons / Math.max(1, studentProgressSnapshot.completedLessons + (studentLearningOverview.totalPendingLessons || 0))) * 100))}% vs last week`, icon: 'bi-journal-check', color: '#7c3aed', bg: '#f3efff' },
              { label: 'Certificates Earned', value: studentSubscriptionUsage.certificates || 0, hint: `${studentLearningOverview.completedCourses || 0} vs last month`, icon: 'bi-patch-check-fill', color: '#f97316', bg: '#fff3e9' },
              { label: 'Overall Progress', value: `${studentProgressSnapshot.averageCompletion}%`, hint: `${Math.max(0, Math.round(studentProgressSnapshot.averageCompletion / 7))}% vs last month`, icon: 'bi-bar-chart-line-fill', color: '#2583e9', bg: '#eaf4ff' },
            ].map((metric) => (
              <StudentSurface className="student-progress-metric" key={metric.label}>
                <span style={{ color: metric.color, background: metric.bg }}><i className={`bi ${metric.icon}`} /></span>
                <strong>{metric.value}</strong>
                <h2>{metric.label === 'Learning Hours' ? <>Learning<br />Hours</> : metric.label}</h2>
                <small><i className="bi bi-arrow-up-short" />{metric.hint}</small>
              </StudentSurface>
            ))}
          </section>

          {(studentSubscriptionDetails.recommendedPlan || studentFreeCourseShowcase.length > 0) && (
            <section className="student-progress-premium">
              <div className="student-progress-premium-copy">
                <h2>Discover More Ways to Learn</h2>
                <p>Explore new courses and live classes to keep building your skills.</p>
                <div className="student-progress-premium-benefits">
                  {['New courses', 'Live classes', 'Keep growing'].map((benefit, index) => (
                    <span key={benefit}><i className={`bi ${['bi-play-circle', 'bi-camera-video', 'bi-graph-up-arrow'][index]}`} /> {benefit}</span>
                  ))}
                </div>
                <button type="button" onClick={() => openTab('for-you')}>
                  Explore Courses <i className="bi bi-arrow-right" />
                </button>
              </div>
              <img className="student-progress-premium-art" src={progressCareerGrowth} alt="Career progress and achievement" />
            </section>
          )}
        </div>
      )}

      {false && activeTab === 'progress' && !canWrite && (
        <div className="d-flex flex-column gap-4">
          <div>
            <div className="fw-semibold" style={{ color: '#0f172a', fontSize: isStudentMobile ? 34 : 42, lineHeight: 1.08 }}>Your Progress <i className="bi bi-graph-up-arrow" style={{ color: '#10b981' }} /></div>
            <div className="mt-2" style={{ color: '#64748b', fontSize: isStudentMobile ? 16 : 18 }}>Keep learning. Keep growing.</div>
          </div>
          <StudentSurface style={{ padding: isStudentMobile ? 18 : 26 }}>
            <div className="d-flex flex-wrap align-items-center gap-4">
              <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 64, height: 64, borderRadius: '50%', background: '#fff1eb', color: '#f97316', fontSize: 30 }}><i className="bi bi-fire" /></div>
              <div><div className="fw-semibold" style={{ fontSize: 28, color: '#0f172a' }}>{studentConsistencyOverview.bestStreak || 0} day streak</div><div className="small text-muted">Your current learning consistency.</div></div>
              <div className="ms-lg-auto d-flex gap-2">{['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => <span key={`${day}-${index}`} className="d-inline-flex align-items-center justify-content-center" style={{ width: 34, height: 34, borderRadius: '50%', background: index < Math.min(7, studentConsistencyOverview.bestStreak || 0) ? '#eef2ff' : '#f8fafc', color: index < Math.min(7, studentConsistencyOverview.bestStreak || 0) ? '#4f46e5' : '#94a3b8', fontSize: 12, fontWeight: 700 }}>{index < Math.min(7, studentConsistencyOverview.bestStreak || 0) ? <i className="bi bi-check" /> : day}</span>)}</div>
            </div>
          </StudentSurface>
          <div className="row g-3">
            {[
              { label: 'Learning Hours', value: fmtDuration(learning.reduce((sum, row) => sum + Number(row.learningSummary?.watchedSeconds || 0), 0)), icon: 'bi-clock-history', bg: '#e8fbf2', color: '#10b981' },
              { label: 'Lessons Completed', value: learning.reduce((sum, row) => sum + Number(row.learningSummary?.completedLessons || 0), 0), icon: 'bi-journal-check', bg: '#f3e8ff', color: '#7c3aed' },
              { label: 'Certificates Earned', value: studentSubscriptionUsage.certificates || 0, icon: 'bi-award', bg: '#fff1eb', color: '#f97316' },
              { label: 'Overall Progress', value: `${studentLearningOverview.avgCompletion || 0}%`, icon: 'bi-graph-up-arrow', bg: '#eef2ff', color: '#2563eb' },
            ].map((metric) => <div className="col-6 col-xl-3" key={metric.label}><StudentSurface className="h-100" style={{ padding: 18 }}><div className="d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 46, height: 46, borderRadius: 14, background: metric.bg, color: metric.color }}><i className={`bi ${metric.icon} fs-5`} /></div><div className="fw-semibold" style={{ fontSize: isStudentMobile ? 22 : 28, color: '#0f172a' }}>{metric.value}</div><div className="small mt-1" style={{ color: '#64748b' }}>{metric.label}</div></StudentSurface></div>)}
          </div>
          <StudentSurface style={{ padding: isStudentMobile ? 18 : 26 }}>
            <div className="d-flex justify-content-between align-items-center mb-3"><div className="fw-semibold" style={{ color: '#0f172a', fontSize: 20 }}>Skill Progress</div><button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => openTab('my-courses')}>View all skills <i className="bi bi-arrow-right" /></button></div>
            <div className="row g-4 align-items-center">
              <div className="col-12 col-lg-8">
                <div className="d-flex flex-column gap-3">{learning.slice(0, 5).map((row, index) => <div key={`skill-${row._id || index}`}><div className="d-flex justify-content-between small fw-semibold mb-2"><span style={{ color: '#334155' }}>{row.course?.courseName || row.course?.title || 'Course skill'}</span><span style={{ color: '#4f46e5' }}>{row.learningSummary?.completionPercent || 0}%</span></div><ProgressBar value={row.learningSummary?.completionPercent || 0} color={['#7c3aed', '#10b981', '#06b6d4', '#2563eb'][index % 4]} /></div>)}{!learning.length && <div className="small text-muted">Start a course to see your skill progress.</div>}</div>
              </div>
              <div className="col-12 col-lg-4 d-flex justify-content-center">
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ width: 156, height: 156, borderRadius: '50%', background: `conic-gradient(#6d4aff ${Math.max(0, Math.min(100, Number(studentLearningOverview.avgCompletion || 0)))}%, #ede9fe 0)`, padding: 12 }}>
                  <div className="d-flex flex-column align-items-center justify-content-center bg-white" style={{ width: '100%', height: '100%', borderRadius: '50%' }}><div className="fw-semibold" style={{ fontSize: 30, color: '#312e81' }}>{studentLearningOverview.avgCompletion || 0}%</div><div className="small text-center px-2" style={{ color: '#64748b' }}>Average course progress</div></div>
                </div>
              </div>
            </div>
          </StudentSurface>
          <StudentSurface style={{ padding: isStudentMobile ? 18 : 26 }}><div className="fw-semibold mb-3" style={{ color: '#0f172a', fontSize: 20 }}>Achievements</div><div className="row g-3">{[{ icon: 'bi-fire', label: 'Consistent Learner', value: `${studentConsistencyOverview.bestStreak || 0} day streak`, color: '#f97316' }, { icon: 'bi-journal-check', label: 'Quick Learner', value: `${learning.reduce((sum, row) => sum + Number(row.learningSummary?.completedLessons || 0), 0)} lessons completed`, color: '#7c3aed' }, { icon: 'bi-award', label: 'Certificate Earned', value: `${studentSubscriptionUsage.certificates || 0} certificates`, color: '#f59e0b' }].map((item) => <div className="col-12 col-md-4" key={item.label}><div className="d-flex align-items-center gap-3 rounded-4 p-3" style={{ background: '#f8fafc' }}><i className={`bi ${item.icon} fs-3`} style={{ color: item.color }} /><div><div className="fw-semibold">{item.label}</div><div className="small text-muted">{item.value}</div></div></div></div>)}</div></StudentSurface>
          <StudentSurface style={{ padding: isStudentMobile ? 18 : 26 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3"><div><div className="fw-semibold" style={{ color: '#0f172a', fontSize: 20 }}>Career Readiness</div><div className="small" style={{ color: '#64748b' }}>A live view based on your completed learning record.</div></div><button type="button" className="btn btn-outline-primary btn-sm" onClick={() => navigate('/resumes?tab=jobs')}>View jobs <i className="bi bi-arrow-right ms-1" /></button></div>
            <div className="row g-4 align-items-center">
              <div className="col-12 col-lg-8">
                {[
                  { label: 'Course completion', value: `${studentLearningOverview.avgCompletion || 0}%`, progress: Number(studentLearningOverview.avgCompletion || 0), color: '#10b981' },
                  { label: 'Assessment score', value: studentLearningOverview.averageScore != null ? `${studentLearningOverview.averageScore}%` : 'No score yet', progress: Number(studentLearningOverview.averageScore || 0), color: '#2563eb' },
                  { label: 'Certificates earned', value: `${studentSubscriptionUsage.certificates || 0}`, progress: Math.min(100, Number(studentSubscriptionUsage.certificates || 0) * 20), color: '#f59e0b' },
                ].map((item) => <div className="mb-3" key={item.label}><div className="d-flex justify-content-between small fw-semibold mb-2"><span style={{ color: '#334155' }}>{item.label}</span><span style={{ color: item.color }}>{item.value}</span></div><ProgressBar value={item.progress} color={item.color} /></div>)}
                <div className="small" style={{ color: '#64748b' }}>{studentJobs.length ? `${studentJobs.length} active job ${studentJobs.length === 1 ? 'opportunity is' : 'opportunities are'} available to review.` : 'Job opportunities will appear here when they are published.'}</div>
              </div>
              <div className="col-12 col-lg-4 d-flex justify-content-center">
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ width: 156, height: 156, borderRadius: '50%', background: `conic-gradient(#10b981 ${Math.max(0, Math.min(100, Number(studentLearningOverview.avgCompletion || 0)))}%, #dcfce7 0)`, padding: 12 }}>
                  <div className="d-flex flex-column align-items-center justify-content-center bg-white" style={{ width: '100%', height: '100%', borderRadius: '50%' }}><div className="fw-semibold" style={{ fontSize: 30, color: '#047857' }}>{studentLearningOverview.avgCompletion || 0}%</div><div className="small text-center px-2" style={{ color: '#64748b' }}>Learning readiness</div></div>
                </div>
              </div>
            </div>
          </StudentSurface>
          {studentHasSubscriptionTab && studentVisiblePlans.some((plan) => Number(plan.salePrice ?? plan.price ?? 0) > 0) && (
            <div className="rounded-4 text-white overflow-hidden" style={{ padding: isStudentMobile ? 18 : 26, background: 'linear-gradient(135deg, #312e81 0%, #4f46e5 55%, #7c3aed 100%)', boxShadow: '0 18px 38px rgba(79,70,229,0.22)' }}>
              <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-3"><div className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 58, height: 58, borderRadius: 12, background: 'rgba(255,255,255,0.14)', color: '#facc15', fontSize: 28 }}><i className="bi bi-book-half" /></div><div className="flex-grow-1"><div className="fw-semibold" style={{ fontSize: 24 }}>Keep building your learning momentum</div><div className="small mt-1" style={{ color: 'rgba(255,255,255,0.84)' }}>Explore published free courses and continue learning at your own pace.</div></div><button type="button" className="btn btn-light fw-semibold px-4" style={{ color: '#4338ca', borderRadius: 8 }} onClick={() => openTab('for-you')}>Find Free Courses <i className="bi bi-arrow-right ms-1" /></button></div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'my-courses' && !canWrite && (
        <div className="d-flex flex-column gap-3">
          {!showCourseWorkspaceModal && (
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-3">
              <div><div className="fw-semibold" style={{ color: '#0f172a', fontSize: isStudentMobile ? 26 : 38, lineHeight: 1.12 }}>Learn</div><div className="mt-1" style={{ color: '#64748b', fontSize: isStudentMobile ? 14 : 17 }}>Explore courses, build skills, and grow your career.</div></div>
              {isCollegePortalStudent && <button type="button" className="btn btn-outline-primary" onClick={() => openTab('for-you')}><i className="bi bi-sliders me-2" />Find courses</button>}
            </div>
          )}
          {showCourseWorkspaceModal && selectedLearningCourse && (
            <StudentSurface bordered={false} className="student-course-workspace" style={{ padding: 0, borderRadius: 0, background: 'transparent', boxShadow: 'none' }}>
              <div className="student-course-workspace-stack">
                <section className="student-course-workspace-hero" style={{ '--course-accent': selectedLearningCourse.course?.courseColor || '#4f46e5' }}>
                  <div className="row g-0 align-items-stretch">
                    <div className="col-12 col-xl-8">
                      <div className="student-course-workspace-hero-copy">
                        <button type="button" className="student-course-back" onClick={closeCourseWorkspace}>
                          <i className="bi bi-arrow-left me-1" />
                          Back to My Courses
                        </button>
                        <div className="student-course-eyebrow"><i className="bi bi-mortarboard" /> {selectedLearningCourse.course?.category || 'Course'}</div>
                        <h1>{selectedLearningCourse.course?.courseName || 'Course'}</h1>
                        <p>
                          {selectedLearningCourse.course?.shortDescription || 'A structured course workspace with lesson-by-lesson progress, assessments, and completion tracking.'}
                        </p>
                        <div className="student-course-status-list">
                          <span><i className="bi bi-collection-play" /> {selectedLearningCourse.learningSummary?.totalLessons || 0} total lessons</span>
                          <span><i className="bi bi-check-circle" /> {selectedLearningCourse.learningSummary?.completedLessons || 0} lessons completed</span>
                          <span><i className="bi bi-hourglass-split" /> {selectedLearningCourse.learningSummary?.pendingLessons || 0} lessons remaining</span>
                          <span><i className="bi bi-clock" /> {fmtDuration(selectedLearningCourse.learningSummary?.totalDurationSeconds || 0)} total</span>
                          <span><i className="bi bi-play-circle" /> {fmtDuration(selectedLearningCourse.learningSummary?.watchedSeconds || 0)} learned</span>
                          <span><i className="bi bi-clock-history" /> {fmtDuration(selectedLearningCourse.learningSummary?.remainingDurationSeconds ?? Math.max(0, Number(selectedLearningCourse.learningSummary?.totalDurationSeconds || 0) - Number(selectedLearningCourse.learningSummary?.watchedSeconds || 0)))} left</span>
                          {Number(selectedLearningCourse.learningSummary?.averageScore || 0) > 0 && <span><i className="bi bi-award" /> {selectedLearningCourse.learningSummary.averageScore}% score</span>}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-xl-4">
                      <div className="student-course-progress-panel">
                        <div className="student-course-progress-heading"><span>Course progress</span><strong>{selectedLearningCourse.learningSummary?.completionPercent || 0}%</strong></div>
                        <ProgressBar value={selectedLearningCourse.learningSummary?.completionPercent || 0} color={selectedLearningCourse.course?.courseColor || '#2563eb'} />
                        <div className="student-course-metrics">
                          <div><i className="bi bi-grid" /><strong>{studentModuleGroups.length}</strong><span>Total modules</span></div>
                          <div><i className="bi bi-collection-play" /><strong>{selectedLearningCourse.learningSummary?.totalLessons || 0}</strong><span>Total lessons</span></div>
                          <div><i className="bi bi-clock" /><strong>{fmtDuration(selectedLearningCourse.learningSummary?.totalDurationSeconds || 0)}</strong><span>Course time</span></div>
                          <div><i className="bi bi-play-circle" /><strong>{fmtDuration(activeLesson?.durationSeconds || 0)}</strong><span>Current video</span></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {workspacePrimaryLesson && (
                  <section className="student-course-next-step">
                    <span className="student-course-next-icon"><i className="bi bi-play-fill" /></span>
                    <div>
                      <small>{workspacePrimaryLesson.progress?.status === 'completed' ? 'Current lesson' : 'Your next step'}</small>
                      <strong>{workspacePrimaryLesson.title}</strong>
                      <p>{workspacePrimaryLesson.sectionTitle || currentWorkspaceModule?.title || 'Continue your learning path'}{workspacePrimaryLesson.durationSeconds ? ` | ${fmtDuration(workspacePrimaryLesson.durationSeconds)}` : ''}</p>
                    </div>
                    <button type="button" onClick={() => openLesson(workspacePrimaryLesson._id)} disabled={workspacePrimaryLesson.access?.isLocked}>
                      Continue <i className="bi bi-arrow-right" />
                    </button>
                  </section>
                )}

                <div className="student-course-workspace-tabs" role="tablist" aria-label="Course workspace">
                  <button type="button" className={courseWorkspaceTab === 'content' ? 'is-active' : ''} onClick={() => setCourseWorkspaceTab('content')}>
                    <i className="bi bi-play-btn" /> Course Content
                  </button>
                  {showSelectedCourseAssessments && (
                    <button type="button" className={courseWorkspaceTab === 'assessments' ? 'is-active' : ''} onClick={() => setCourseWorkspaceTab('assessments')}>
                      <i className="bi bi-clipboard-check" /> Assessments
                    </button>
                  )}
                </div>

                {courseWorkspaceTab === 'content' && (
                  <div className="row g-3 student-course-learning-layout">
                    <div className={`col-12 ${isLearningOutlineCollapsed ? 'col-xl-1' : 'col-xl-4'} order-2 order-xl-2 student-course-outline-column`}>
                      <div className="border bg-white student-course-outline" style={{ borderColor: '#dbeafe', ...studentStickyStyle }}>
                        <div className="d-flex justify-content-between align-items-center gap-2 p-3 border-bottom" style={{ borderColor: '#e2e8f0' }}>
                          {!isLearningOutlineCollapsed && (
                            <div>
                              <div className="fw-semibold" style={{ color: studentTheme.title }}>Course Modules</div>
                              {isStudentMobile && (
                                <div className="small text-muted">
                                  {studentModuleGroups.length} module{studentModuleGroups.length === 1 ? '' : 's'} available
                                </div>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm ms-auto"
                            onClick={() => setIsLearningOutlineCollapsed((prev) => !prev)}
                            title={isLearningOutlineCollapsed ? 'Expand course modules' : 'Collapse course modules'}
                          >
                            <i className={`bi ${isLearningOutlineCollapsed ? 'bi-layout-sidebar-inset-reverse' : 'bi-layout-sidebar-inset'}`} />
                          </button>
                        </div>
                        {!isLearningOutlineCollapsed && (
                        <div className="d-flex flex-column gap-3 p-3 student-course-outline-scroll">
                          {!studentModuleGroups.length && <div className="small text-muted">No modules or lessons are available yet.</div>}
                          {studentModuleGroups.map((moduleGroup) => {
                            const isModuleExpanded = String(expandedLearningModuleId) === String(moduleGroup.key);
                            const moduleLessonCount = moduleGroup.sections.reduce((sum, section) => sum + Number((section.lessons || []).length), 0);
                            const moduleTopicCount = moduleGroup.sections.length;
                            const moduleOverview = workspaceModuleOverviewMap.get(String(moduleGroup.key));
                            return (
                              <div key={`detail-module-${moduleGroup.key}`} className="border p-3 student-course-module" style={{ borderColor: '#dbeafe' }}>
                                <button type="button" className="btn w-100 text-start p-0 border-0 bg-transparent shadow-none" onClick={() => setExpandedLearningModuleId((prev) => (String(prev) === String(moduleGroup.key) ? '' : String(moduleGroup.key)))}>
                                  <div className="d-flex justify-content-between align-items-center gap-2">
                                    <div>
                                      <div className="fw-semibold">{moduleGroup.title}</div>
                                      <div className="small text-muted">
                                        {moduleTopicCount} topic{moduleTopicCount === 1 ? '' : 's'} | {moduleLessonCount} content item{moduleLessonCount === 1 ? '' : 's'}
                                      </div>
                                      <div className="small mt-1" style={{ color: '#475569' }}>
                                        {moduleOverview?.completedLessons || 0}/{moduleOverview?.totalLessons || 0} completed
                                      </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="badge rounded-pill text-bg-light border">{moduleOverview?.completionPercent || 0}%</span>
                                      <i className={`bi ${isModuleExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                                    </div>
                                  </div>
                                </button>
                                {isModuleExpanded && (
                                  <div className="border-top mt-3 pt-3 d-flex flex-column gap-2 student-course-module-lessons">
                                    {moduleGroup.sections.map((section) => (
                                      <div key={`detail-section-${section._id}`}>
                                        <button
                                          type="button"
                                          className="btn w-100 text-start p-0 border-0 bg-transparent shadow-none mb-2"
                                          onClick={() => {
                                            setSelectedOutlineTopicId(String(section._id));
                                            const firstLesson = (section.lessons || []).find((lesson) => !lesson.access?.isLocked) || (section.lessons || [])[0];
                                            if (firstLesson?._id) openLesson(firstLesson._id);
                                            else setSelectedLessonId('');
                                          }}
                                        >
                                          <div className="d-flex justify-content-between align-items-center gap-2">
                                            <div className="small fw-semibold" style={{ color: String(selectedOutlineTopicId) === String(section._id) ? '#1d4ed8' : '#64748b' }}>{section.title}</div>
                                            <div className="small text-muted">{(section.lessons || []).length} item{(section.lessons || []).length === 1 ? '' : 's'}</div>
                                          </div>
                                        </button>
                                        {!(section.lessons || []).length && (
                                          <div className="small text-muted border rounded-3 p-2 bg-light">
                                            No approved content items are added under this topic yet.
                                          </div>
                                        )}
                                        <div className="d-flex flex-column gap-2">
                                          {(section.lessons || []).map((lesson) => (
                                            <button
                                              key={`detail-lesson-${lesson._id}`}
                                              type="button"
                                              className="btn text-start border bg-white student-course-lesson-link"
                                              disabled={lesson.access?.isLocked}
                                              style={{
                                                borderRadius: 16,
                                                borderColor: String(selectedLessonId) === String(lesson._id) ? '#60a5fa' : '#e2e8f0',
                                                background: String(selectedLessonId) === String(lesson._id) ? 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)' : '#ffffff',
                                                opacity: lesson.access?.isLocked ? 0.72 : 1,
                                              }}
                                              onClick={() => openLesson(lesson._id)}
                                            >
                                              <LessonEpisodeSummary
                                                lesson={lesson}
                                                course={selectedLearningCourse.course}
                                                isActive={String(selectedLessonId) === String(lesson._id)}
                                                sequenceNumber={flattenedLearnLessons.findIndex((item) => String(item._id) === String(lesson._id)) + 1}
                                                progressOverride={getLessonWatchPercent(lesson)}
                                              />
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        )}
                      </div>
                    </div>
                    <div className={`col-12 ${isLearningOutlineCollapsed ? 'col-xl-11' : 'col-xl-8'} order-1 order-xl-1 student-course-lesson-column`}>
                      <div className="border p-3 bg-white h-100 student-course-lesson-viewer" style={{ borderColor: '#dbeafe' }}>
                        {activeLesson ? (
                          <>
                            <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
                              <div className="small d-flex align-items-center gap-2 flex-wrap" style={{ color: '#7c3aed' }}>
                                <span>{selectedLearningCourse?.course?.courseName || 'Course'}</span>
                                <i className="bi bi-chevron-right text-muted" />
                                <span style={{ color: '#64748b' }}>{currentWorkspaceModule?.title || activeLesson.sectionTitle || 'Module'}</span>
                              </div>
                              <button type="button" className="btn btn-link p-0" style={{ color: '#64748b' }} title="Save lesson">
                                <i className="bi bi-bookmark fs-5" />
                              </button>
                            </div>
                            <div className="fw-semibold mb-3" style={{ fontSize: isStudentMobile ? 25 : 30, lineHeight: 1.12, color: '#0f172a' }}>
                              Module {Math.max(1, workspaceModuleOverview.findIndex((module) => String(module.key) === String(currentWorkspaceModule?.key)) + 1)}: {currentWorkspaceModule?.title || activeLesson.sectionTitle || activeLesson.title}
                            </div>
                            <div className="d-flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
                              <div>
                                <div className="small text-muted mb-1">Current lesson</div>
                                <div className="fw-semibold" style={{ fontSize: 22, lineHeight: 1.2 }}>{activeLesson.title}</div>
                                <div className="small text-muted mt-1">{formatLessonTypeLabel(activeLesson.lessonType)}{activeLesson.durationSeconds ? ` | ${fmtDuration(activeLesson.durationSeconds)}` : ''}</div>
                              </div>
                              <div className="d-flex gap-2 flex-wrap">
                                {previousLesson && <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openLesson(previousLesson._id)}>Previous</button>}
                                {nextLesson && <button type="button" className="btn btn-outline-secondary btn-sm" disabled={nextLesson.access?.isLocked} onClick={() => openLesson(nextLesson._id)}>Next</button>}
                              </div>
                            </div>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                              <span className="badge rounded-pill text-bg-light border">{currentWorkspaceModule?.title || 'Module'}</span>
                              <span className="badge rounded-pill text-bg-light border">{currentWorkspaceModule?.completionPercent || 0}% module progress</span>
                              {activeLesson.access?.isLocked && <span className="badge rounded-pill text-bg-warning">Locked until prerequisites are completed</span>}
                            </div>
                            {isStudentMobile && (
                              <div className="rounded-4 border p-3 mb-3" style={{ borderColor: '#e2e8f0', background: '#f8fafc' }}>
                                <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                                  <div className="fw-semibold" style={{ color: studentTheme.title }}>Lesson navigator</div>
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => setIsLearningOutlineCollapsed((prev) => !prev)}
                                  >
                                    {isLearningOutlineCollapsed ? 'Open Modules' : 'Hide Modules'}
                                  </button>
                                </div>
                                <div className="small text-muted">
                                  {activeLesson.sectionTitle || 'Current section'}{activeLesson.progress?.status === 'completed' ? ' | Completed' : ''}
                                </div>
                              </div>
                            )}
                            <div className="d-flex border-bottom mb-3" style={{ borderColor: '#e2e8f0', overflowX: 'auto' }}>
                              {[
                                { key: 'overview', label: 'Overview', icon: 'bi-journal-text' },
                                { key: 'discussion', label: 'Discussion', icon: 'bi-chat-dots' },
                              ].map((tabItem) => (
                                <button
                                  key={tabItem.key}
                                  type="button"
                                  className="btn rounded-0 border-0 fw-semibold d-inline-flex align-items-center gap-2"
                                  onClick={() => setLessonDetailTab(tabItem.key)}
                                  style={{
                                    whiteSpace: 'nowrap',
                                    padding: '12px 18px',
                                    color: lessonDetailTab === tabItem.key ? '#4f46e5' : '#64748b',
                                    borderBottom: lessonDetailTab === tabItem.key ? '3px solid #4f46e5' : '3px solid transparent',
                                    background: 'transparent',
                                  }}
                                >
                                  <i className={`bi ${tabItem.icon}`} />
                                  {tabItem.label}
                                </button>
                              ))}
                            </div>
                            {lessonDetailTab === 'overview' && (
                            <div className="row g-3">
                              <div className="col-12">
                                {activeLesson.lessonType === 'uploaded_video' && activeLesson.uploadedVideoUrl && <div className="mb-3" style={{ borderRadius: 20, overflow: 'hidden', background: '#020617' }}><div className="ratio ratio-16x9"><TrackedLessonVideo lesson={activeLesson} onProgress={handleVideoWatchProgress} className="w-100 h-100 d-block" style={{ objectFit: 'contain' }} /></div></div>}
                                {activeLesson.lessonType === 'embedded_video' && activeLesson.embeddedVideoUrl && <div className="ratio ratio-16x9 mb-3" style={{ borderRadius: 20, overflow: 'hidden' }}><iframe title={activeLesson.title} src={activeLesson.embeddedVideoUrl} allowFullScreen /></div>}
                                {activeLesson.lessonType === 'promo_video' && activeLesson.uploadedVideoUrl && <div className="mb-3" style={{ borderRadius: 20, overflow: 'hidden', background: '#020617' }}><div className="ratio ratio-16x9"><TrackedLessonVideo lesson={activeLesson} onProgress={handleVideoWatchProgress} className="w-100 h-100 d-block" style={{ objectFit: 'contain' }} /></div></div>}
                                {activeLesson.lessonType === 'promo_video' && !activeLesson.uploadedVideoUrl && activeLesson.embeddedVideoUrl && <div className="ratio ratio-16x9 mb-3" style={{ borderRadius: 20, overflow: 'hidden' }}><iframe title={activeLesson.title} src={activeLesson.embeddedVideoUrl} allowFullScreen /></div>}
                                {activeLesson.lessonType === 'promo_image' && activeLesson.imageUrl && <img src={activeLesson.imageUrl} alt={activeLesson.title} className="img-fluid mb-3" style={{ width: '100%', borderRadius: 20 }} />}
                                {activeLesson.htmlContent && <div className="border p-3 mb-3 bg-white" style={{ borderRadius: 18, borderColor: '#e2e8f0', overflowX: 'auto' }} dangerouslySetInnerHTML={{ __html: activeLesson.htmlContent }} />}
                                {activeLesson.lessonType === 'linked_quiz' && <LinkedQuizAttempt lesson={activeLesson} notify={notify} onCompleted={async () => { await loadOutline(selectedCourseId); await refreshCore(); }} />}
                                {activeLesson.resourceUrl && <div className="d-flex flex-wrap gap-2 mb-3">
                                  {activeLesson.resourceUrl && <a href={activeLesson.resourceUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">Open Resource</a>}
                                  {['uploaded_video', 'embedded_video', 'promo_video', 'promo_image'].includes(String(activeLesson.lessonType || '')) && <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openExpandedLessonPreview(activeLesson)}>Expand View</button>}
                                </div>}
                                {!hasRenderableLessonContent(activeLesson) && (
                                  <div className="border rounded-4 p-4 mb-3 text-center" style={{ borderColor: '#e2e8f0', background: '#f8fafc' }}>
                                    <div className="fw-semibold mb-2" style={{ color: studentTheme.title }}>Lesson content will appear here</div>
                                    <div className="small text-muted">
                                      The lesson is mapped to this topic, but the visible content has not been published yet.
                                    </div>
                                  </div>
                                )}
                                <div className="rounded-4 border p-3 mb-3" style={{ borderColor: '#e2e8f0', background: '#ffffff' }}>
                                  <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                      <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 38, height: 38, borderRadius: 12, background: '#f3e8ff', color: '#7c3aed' }}><i className="bi bi-journal-richtext" /></div>
                                      <div><div className="fw-semibold" style={{ color: '#0f172a' }}>Lesson Notes</div><div className="small text-muted">Key concepts and material from this lesson.</div></div>
                                    </div>
                                    {activeLesson.htmlContent && <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setLessonDetailTab('overview')}>View Notes <i className="bi bi-arrow-right ms-1" /></button>}
                                  </div>
                                  {activeLesson.summary ? <div className="small" style={{ color: '#475569', whiteSpace: 'pre-wrap' }}>{activeLesson.summary}</div> : <div className="small text-muted">Read the lesson content above to review the key concepts.</div>}
                                </div>
                                <div className="rounded-4 border p-3 mb-3" style={{ borderColor: '#e2e8f0', background: '#ffffff' }}>
                                  <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
                                    <div className="d-flex align-items-center gap-2"><div className="d-inline-flex align-items-center justify-content-center" style={{ width: 38, height: 38, borderRadius: 12, background: '#eff6ff', color: '#2563eb' }}><i className="bi bi-folder2-open" /></div><div className="fw-semibold" style={{ color: '#0f172a' }}>Resources</div></div>
                                    {activeLesson.resourceUrl && <a href={activeLesson.resourceUrl} target="_blank" rel="noreferrer" className="btn btn-link btn-sm p-0 text-decoration-none">Open resource <i className="bi bi-arrow-up-right ms-1" /></a>}
                                  </div>
                                  <div className="small text-muted">{activeLesson.resourceUrl ? 'Lesson files and links are available in the Resources tab.' : 'No additional files have been attached to this lesson.'}</div>
                                </div>
                                {nextLesson && (
                                  <div className="rounded-4 border p-3 mb-3" style={{ borderColor: '#d1fae5', background: '#ffffff' }}>
                                    <div className="d-flex justify-content-between align-items-center gap-3">
                                      <div className="d-flex align-items-center gap-3"><div className="d-inline-flex align-items-center justify-content-center" style={{ width: 42, height: 42, borderRadius: 14, background: '#e8fbf2', color: '#10b981' }}><i className="bi bi-play-circle-fill" /></div><div><div className="small text-muted">Next lesson</div><div className="fw-semibold" style={{ color: '#0f172a' }}>{nextLesson.title}</div><div className="small text-muted">{nextLesson.durationSeconds ? fmtDuration(nextLesson.durationSeconds) : formatLessonTypeLabel(nextLesson.lessonType)}</div></div></div>
                                      <button type="button" className="btn btn-outline-primary btn-sm" disabled={nextLesson.access?.isLocked} onClick={() => openLesson(nextLesson._id)}>Continue <i className="bi bi-arrow-right ms-1" /></button>
                                    </div>
                                  </div>
                                )}
                                <div className="student-course-live-class-card mb-3">
                                  <div className="student-course-live-class-icon"><i className="bi bi-camera-video-fill" /></div>
                                  <div className="student-course-live-class-copy">
                                    <small>Free Live Learning</small>
                                    <strong>{nextCourseLiveClass?.promoTitle || nextCourseLiveClass?.name || 'Free live classes for students'}</strong>
                                    <p>{nextCourseLiveClass?.promoText || nextCourseLiveClass?.description || 'Join practical live sessions, ask questions, and learn directly with our faculty.'}</p>
                                    <div>
                                      {(nextCourseLiveClass?.eventDate || nextCourseLiveClass?.liveSession?.sessionDate) && <span><i className="bi bi-calendar-event" />{fmtDate(nextCourseLiveClass.eventDate || nextCourseLiveClass.liveSession.sessionDate)}</span>}
                                      {(nextCourseLiveClass?.startTime || nextCourseLiveClass?.liveSession?.sessionTime) && <span><i className="bi bi-clock" />{nextCourseLiveClass.startTime || nextCourseLiveClass.liveSession.sessionTime}</span>}
                                      <span><i className="bi bi-broadcast" />{toTitleWords(nextCourseLiveClass?.deliveryMode || 'Online')}</span>
                                    </div>
                                  </div>
                                  <button type="button" className="btn btn-light btn-sm fw-semibold" onClick={() => openTab(isCollegePortalStudent ? 'for-you' : 'my-courses')}>
                                    View Live Classes <i className="bi bi-arrow-right ms-1" />
                                  </button>
                                </div>
                                <CourseDiscussionPanel
                                  discussions={courseDiscussions}
                                  message={courseDiscussionMessage}
                                  onMessageChange={setCourseDiscussionMessage}
                                  onSubmit={submitCourseDiscussion}
                                  loading={courseDiscussionsLoading}
                                  submitting={courseDiscussionSubmitting}
                                />
                                <div className="border rounded-4 p-3 mt-3" style={{ borderColor: '#e2e8f0', background: '#f8fafc' }}>
                                  {activeLesson.summary && (
                                    <p className="text-muted mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                                      {activeLesson.summary}
                                    </p>
                                  )}
                                  <div className="d-flex flex-wrap gap-2 mb-3">
                                    <span className="badge rounded-pill text-bg-light border">{formatLessonTypeLabel(activeLesson.lessonType)}</span>
                                    {activeLesson.durationSeconds ? <span className="badge rounded-pill text-bg-light border">{fmtDuration(activeLesson.durationSeconds)}</span> : null}
                                    {activeLesson.progress?.status === 'completed' ? <span className="badge rounded-pill text-bg-success">Completed</span> : <span className="badge rounded-pill text-bg-warning">Pending</span>}
                                    <span className="badge rounded-pill text-bg-light border">
                                      {currentWorkspaceModule?.title || 'Current module'}
                                    </span>
                                  </div>
                                  <div className="d-flex flex-wrap gap-2">
                                    {['uploaded_video', 'embedded_video', 'promo_video', 'promo_image'].includes(String(activeLesson.lessonType || '')) && <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openExpandedLessonPreview(activeLesson)}>Expand View</button>}
                                    {activeLesson.resourceUrl && <a href={activeLesson.resourceUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">Open Resource</a>}
                                    {activeLesson.lessonType === 'quiz_gate' ? (
                                      <button className="btn btn-primary btn-sm" disabled={saving || !activeQuizQuestions.length} onClick={submitQuizGate}>Submit Quiz</button>
                                    ) : activeLesson.lessonType === 'linked_quiz' ? null : (
                                      <button className="btn btn-primary btn-sm" disabled={saving || activeLesson.progress?.status === 'completed' || (!canWrite && !isLessonWatchRequirementMet(activeLesson))} onClick={markLessonComplete} title={!canWrite && !isLessonWatchRequirementMet(activeLesson) ? `Watch at least ${VIDEO_COMPLETION_PERCENT}% of the video first` : ''}>
                                        {activeLesson.progress?.status === 'completed' ? 'Completed' : 'Mark as Completed'}
                                      </button>
                                    )}
                                    {nextAvailableLesson && String(nextAvailableLesson._id) !== String(activeLesson._id) && <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openLesson(nextAvailableLesson._id)}>Open Next Available Lesson</button>}
                                  </div>
                                </div>
                              </div>
                            </div>
                            )}
                            {lessonDetailTab === 'discussion' && (
                              <CourseDiscussionPanel
                                discussions={courseDiscussions}
                                message={courseDiscussionMessage}
                                onMessageChange={setCourseDiscussionMessage}
                                onSubmit={submitCourseDiscussion}
                                loading={courseDiscussionsLoading}
                                submitting={courseDiscussionSubmitting}
                              />
                            )}
                          </>
                        ) : selectedOutlineSection ? (
                          <div className="border rounded-4 p-4 bg-light h-100 d-flex flex-column justify-content-center align-items-start" style={{ borderColor: '#e2e8f0' }}>
                            <div className="small text-muted mb-2">{selectedOutlineSection.moduleTitle || 'Course topic'}</div>
                            <div className="fw-semibold mb-2" style={{ fontSize: 22, lineHeight: 1.2, color: studentTheme.title }}>
                              {selectedOutlineSection.title || 'Selected topic'}
                            </div>
                            <div className="small mb-3" style={{ color: studentTheme.text, maxWidth: 720 }}>
                              {selectedOutlineSection.description || 'This topic is part of the course structure, but no student-visible content has been attached to it yet.'}
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                              <span className="badge rounded-pill text-bg-light border">0 content items</span>
                              <span className="badge rounded-pill text-bg-warning">Waiting for content</span>
                            </div>
                          </div>
                        ) : (
                          <div className="text-muted">Select a lesson to begin.</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {courseWorkspaceTab === 'assessments' && showSelectedCourseAssessments && (
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="border rounded-4 p-3 bg-white d-flex flex-wrap justify-content-between align-items-center gap-3" style={{ borderColor: '#dbeafe' }}>
                        <div className="d-flex flex-wrap gap-2">
                          <span className="badge rounded-pill text-bg-light border">Total {workspaceAssessmentOverview.total}</span>
                          <span className="badge rounded-pill text-bg-success">Submitted {workspaceAssessmentOverview.submittedCount}</span>
                          <span className="badge rounded-pill text-bg-warning">Pending {workspaceAssessmentOverview.pendingCount}</span>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {workspaceAssessmentOverview.firstPending && (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => openStudentAssessmentDialog(workspaceAssessmentOverview.firstPending, 'take')}
                            >
                              Attend Next Assessment
                            </button>
                          )}
                          {workspaceAssessmentOverview.latestSubmitted && (
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => openStudentAssessmentDialog(workspaceAssessmentOverview.latestSubmitted, 'review')}
                            >
                              View Latest Result
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    {studentCourseAssessmentsLoading ? (
                      <div className="col-12"><div className="border rounded-4 p-4 bg-white text-muted" style={{ borderColor: '#dbeafe' }}>Loading assessments...</div></div>
                    ) : studentCourseAssessments.length ? studentCourseAssessments.map((assessment) => (
                      <div key={`detail-assessment-${assessment._id}`} className="col-12 col-xl-6">
                        <div className="border bg-white h-100" style={{ borderRadius: 22, padding: 18, borderColor: '#dbeafe' }}>
                          <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                            <div>
                              <div className="fw-semibold">{assessment.title || 'Assessment'}</div>
                              <div className="small text-muted">{(assessment.type || 'quiz').replace(/_/g, ' ')}{assessment.duration ? ` | ${assessment.duration} min` : ''}</div>
                            </div>
                            <Pill color={assessment.mySubmission?.submittedAt ? 'success' : String(assessment.status || '').toLowerCase() === 'closed' ? 'secondary' : 'warning'}>{assessment.mySubmission?.submittedAt ? 'Submitted' : (assessment.status || 'Pending')}</Pill>
                          </div>
                          <div className="row g-2 small">
                            <div className="col-6 text-muted">Marks: <span className="text-dark fw-semibold">{assessment.totalMarks || 0}</span></div>
                            <div className="col-6 text-muted">Pass: <span className="text-dark fw-semibold">{assessment.passingMarks || 0}</span></div>
                            <div className="col-6 text-muted">Due: <span className="text-dark fw-semibold">{assessment.endDate || assessment.dueDate ? fmtDate(assessment.endDate || assessment.dueDate) : '-'}</span></div>
                            <div className="col-6 text-muted">Batch: <span className="text-dark fw-semibold">{assessment.batch?.batchNumber || '-'}</span></div>
                          </div>
                          <div className="rounded-4 p-3 mt-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            {assessment.mySubmission?.submittedAt ? (
                              <div className="row g-2 small">
                                <div className="col-6 text-muted">Status: <span className="text-dark fw-semibold">{assessment.mySubmission.status || '-'}</span></div>
                                <div className="col-6 text-muted">Score: <span className="text-dark fw-semibold">{assessment.mySubmission.percentage ?? 0}%</span></div>
                                <div className="col-12 text-muted">Submitted: <span className="text-dark fw-semibold">{assessment.mySubmission.submittedAt ? fmtDate(assessment.mySubmission.submittedAt) : '-'}</span></div>
                              </div>
                            ) : assessment.mySubmission ? (
                              <div className="small text-muted">
                                Your attempt has been started but not submitted yet.
                              </div>
                            ) : (
                              <div className="small text-muted">This assessment is available for this course when published and assigned.</div>
                            )}
                          </div>
                          <div className="d-flex flex-wrap gap-2 mt-3">
                            {assessment.mySubmission?.submittedAt ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => openStudentAssessmentDialog(assessment, 'review')}
                              >
                                <i className="bi bi-eye me-1" />
                                View score and answers
                              </button>
                            ) : assessment.mySubmission ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => openStudentAssessmentDialog(assessment, 'take')}
                              >
                                <i className="bi bi-arrow-repeat me-1" />
                                Resume assessment
                              </button>
                            ) : String(assessment.status || '').toLowerCase() === 'active' ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => openStudentAssessmentDialog(assessment, 'take')}
                              >
                                <i className="bi bi-play-circle me-1" />
                                Attend assessment
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="col-12"><div className="border rounded-4 p-4 bg-white text-muted" style={{ borderColor: '#dbeafe' }}>No assessments are linked to this course yet.</div></div>
                    )}
                  </div>
                )}
              </div>
            </StudentSurface>
          )}

          {!showCourseWorkspaceModal && (
            <div className="student-learn-page">
              {studentLearnCollections.continueCourse ? (() => {
                const row = studentLearnCollections.continueCourse;
                const courseId = String(row.course?._id || row.course || '');
                const progress = Math.max(0, Math.min(100, Number(row.learningSummary?.completionPercent || 0)));
                return (
                  <section className="student-learn-continue" aria-labelledby="student-learn-continue-title">
                    <div className="student-learn-continue-copy">
                      <span className="student-learn-kicker"><i className="bi bi-play-circle-fill" /> Continue Learning</span>
                      <h2 id="student-learn-continue-title">{row.course?.courseName || 'Your current course'}</h2>
                      <p>{inferCourseMode(row.course || {}) === 'on_demand' ? 'On-demand learning' : 'Instructor-led learning'}</p>
                      <div className="student-learn-progress-label"><strong>{progress}% complete</strong><span>{row.learningSummary?.completedLessons || 0} of {row.learningSummary?.totalLessons || 0} lessons</span></div>
                      <ProgressBar value={progress} color="#ffffff" trackColor="rgba(255,255,255,.24)" />
                      <button type="button" onClick={() => row.isVirtualEnrollment ? openStudentCourseCard(row.course, { fallbackTab: 'my-courses' }) : openCourseWorkspace(courseId)}>
                        <span>{progress > 0 ? 'Continue Learning' : 'Start Learning'}</span><i className="bi bi-arrow-right" />
                      </button>
                    </div>
                    <div className="student-learn-continue-art" aria-hidden="true">
                      <CourseArtwork title={row.course?.courseName || 'Course'} imageUrl={row.course?.thumbnailUrl || row.course?.promoImageUrl} accent={row.course?.courseColor || '#6366f1'} height={190} compact />
                    </div>
                  </section>
                );
              })() : (
                <section className="student-learn-empty-hero">
                  <span><i className="bi bi-compass" /></span>
                  <div><small>Start learning</small><h2>Choose your first course</h2><p>Explore the courses available through your current access.</p></div>
                  {isCollegePortalStudent && <button type="button" onClick={() => openTab('for-you')}>Explore Courses <i className="bi bi-arrow-right" /></button>}
                </section>
              )}

              <section className="student-learn-stat-grid" aria-label="Learning progress summary">
                {[
                  { icon: 'bi-collection-play', value: studentLearningOverview.totalCourses, label: 'Enrolled courses', tone: 'blue' },
                  { icon: 'bi-play-circle', value: studentLearnCollections.inProgress.length, label: 'In progress', tone: 'violet' },
                  { icon: 'bi-check2-circle', value: studentLearnCollections.completed.length, label: 'Completed', tone: 'green' },
                  { icon: 'bi-clock-history', value: fmtDuration(studentLearnCollections.watchedSeconds), label: 'Learning time', tone: 'orange' },
                ].map((item) => (
                  <article key={item.label} className={`is-${item.tone}`}><span><i className={`bi ${item.icon}`} /></span><div><strong>{item.value}</strong><small>{item.label}</small></div></article>
                ))}
              </section>

              <section className="student-learn-quick-actions" aria-labelledby="student-learn-actions-title">
                <div className="student-learn-section-heading"><div><small>Shortcuts</small><h2 id="student-learn-actions-title">Quick Actions</h2></div></div>
                <div>
                  <button type="button" disabled={!studentLearnCollections.continueCourse} onClick={() => {
                    const row = studentLearnCollections.continueCourse;
                    if (!row) return;
                    if (row.isVirtualEnrollment) openStudentCourseCard(row.course, { fallbackTab: 'my-courses' });
                    else openCourseWorkspace(String(row.course?._id || row.course));
                  }}><i className="bi bi-play-fill" /><span>Resume course</span></button>
                  <button type="button" onClick={() => navigate('/certificates')}><i className="bi bi-award" /><span>Certificates</span></button>
                  {isCollegePortalStudent && <button type="button" onClick={() => openTab('saved-courses')}><i className="bi bi-heart" /><span>Saved courses</span></button>}
                  {isCollegePortalStudent && <button type="button" onClick={() => openTab('for-you')}><i className="bi bi-compass" /><span>Explore</span></button>}
                </div>
              </section>

              {studentLearnCollections.recentlyAccessed.length > 0 && (
                <section className="student-learn-section" aria-labelledby="student-learn-recent-title">
                  <div className="student-learn-section-heading"><div><small>Pick up where you stopped</small><h2 id="student-learn-recent-title">Recently Accessed</h2></div></div>
                  <div className="student-learn-recent-grid">
                    {studentLearnCollections.recentlyAccessed.map((row) => {
                      const courseId = String(row.course?._id || row.course || '');
                      const progress = Number(row.learningSummary?.completionPercent || 0);
                      return (
                        <button type="button" key={`recent-${row._id || courseId}`} onClick={() => row.isVirtualEnrollment ? openStudentCourseCard(row.course, { fallbackTab: 'my-courses' }) : openCourseWorkspace(courseId)}>
                          <CourseArtwork title={row.course?.courseName || 'Course'} imageUrl={row.course?.thumbnailUrl || row.course?.promoImageUrl} accent={row.course?.courseColor || '#6366f1'} height={82} compact />
                          <span><small>Opened {fmtDate(row.learningSummary?.lastAccessedAt)}</small><strong>{row.course?.courseName || 'Course'}</strong><span>{progress}% complete</span><ProgressBar value={progress} color={row.course?.courseColor || '#6366f1'} /></span>
                          <i className="bi bi-play-circle" />
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              <section className="student-learn-section" aria-labelledby="student-learn-library-title">
                <div className="student-learn-section-heading student-learn-library-heading">
                  <div><small>Your learning library</small><h2 id="student-learn-library-title">My Courses</h2></div>
                  <label className="student-learn-search"><i className="bi bi-search" /><input value={learningFilters.search} onChange={(event) => setLearningFilters((prev) => ({ ...prev, search: event.target.value }))} placeholder="Search my courses" /></label>
                </div>
                <div className="student-learn-filter-tabs" role="tablist" aria-label="Filter courses">
                  {[
                    ['all', 'All', studentLearningOverview.totalCourses + accessibleCourseCount],
                    ['in_progress', 'In progress', learningStatusCounts.inProgress],
                    ['completed', 'Completed', learningStatusCounts.completed],
                  ].map(([value, label, count]) => <button type="button" key={value} className={learningFilters.status === value ? 'is-active' : ''} onClick={() => setLearningFilters((prev) => ({ ...prev, status: value }))}>{label} <span>{count}</span></button>)}
                </div>
                <div className="student-learn-course-grid">
                  {visibleLearningRows.map((row) => {
                    const courseId = String(row.course?._id || row.course || '');
                    const progress = Math.max(0, Math.min(100, Number(row.learningSummary?.completionPercent || 0)));
                    const completed = progress >= 100;
                    return (
                      <article key={`learn-course-${row._id || courseId}`}>
                        <div className="student-learn-course-art"><CourseArtwork title={row.course?.courseName || 'Course'} imageUrl={row.course?.thumbnailUrl || row.course?.promoImageUrl} accent={row.course?.courseColor || '#2563eb'} height={142} compact /><span>{completed ? 'Completed' : progress > 0 ? 'In progress' : 'Ready to start'}</span></div>
                        <div className="student-learn-course-body">
                          <small>{formatCourseCategoryLabel(row.course?.category || inferCourseMode(row.course || {}))}</small>
                          <h3>{row.course?.courseName || 'Course'}</h3>
                          <div className="student-learn-course-meta"><span><i className="bi bi-journal-text" /> {row.learningSummary?.totalLessons || 0} lessons</span><span><i className="bi bi-clock" /> {fmtDuration(row.learningSummary?.watchedSeconds || 0)}</span></div>
                          <div className="student-learn-course-progress"><span>{progress}% complete</span><ProgressBar value={progress} color={row.course?.courseColor || '#6366f1'} /></div>
                          <button type="button" onClick={() => row.isVirtualEnrollment ? openStudentCourseCard(row.course, { fallbackTab: 'my-courses' }) : openCourseWorkspace(courseId)}><span>{completed ? 'Review Course' : progress > 0 ? 'Resume Course' : 'Start Course'}</span><i className="bi bi-arrow-right" /></button>
                        </div>
                      </article>
                    );
                  })}
                </div>
                {!visibleLearningRows.length && <div className="student-learn-inline-empty"><i className="bi bi-search" /><div><strong>No matching courses</strong><span>Try another search or course status.</span></div></div>}
              </section>

              <div className="student-learn-two-column">
                <section className="student-learn-section" aria-labelledby="student-learn-pending-title">
                  <div className="student-learn-section-heading"><div><small>Keep moving forward</small><h2 id="student-learn-pending-title">Upcoming &amp; Pending</h2></div><span className="student-learn-count">{studentLearningOverview.totalPendingLessons}</span></div>
                  {studentLearnCollections.pending.length ? <div className="student-learn-pending-list">{studentLearnCollections.pending.map((row) => {
                    const courseId = String(row.course?._id || row.course || '');
                    return <button type="button" key={`pending-${row._id || courseId}`} onClick={() => row.isVirtualEnrollment ? openStudentCourseCard(row.course, { fallbackTab: 'my-courses' }) : openCourseWorkspace(courseId)}><span><i className="bi bi-journal-arrow-up" /></span><div><strong>{row.course?.courseName || 'Course'}</strong><small>{row.learningSummary?.pendingLessons || 0} lessons remaining</small></div><i className="bi bi-chevron-right" /></button>;
                  })}</div> : <div className="student-learn-compact-empty"><i className="bi bi-check2-circle" /><span>No pending lessons right now.</span></div>}
                </section>

                <section className="student-learn-section student-learn-habit" aria-labelledby="student-learn-habit-title">
                  <div className="student-learn-section-heading"><div><small>Build consistency</small><h2 id="student-learn-habit-title">Learning Habit</h2></div><span className="student-learn-streak"><i className="bi bi-fire" /> {studentConsistencyOverview.currentStreak} day streak</span></div>
                  <div className="student-learn-week">{studentWeeklyActivity.days.map((day) => <span key={day.key} className={`${day.active ? 'is-active' : ''} ${day.future ? 'is-future' : ''}`}><i className={`bi ${day.active ? 'bi-check2' : 'bi-circle-fill'}`} /><small>{day.label}</small></span>)}</div>
                  {studentGoal.enabled ? <div className="student-learn-goal"><div><strong>Daily goal</strong><span>{Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60))} / {studentGoal.minutes || 60} minutes</span></div><ProgressBar value={Math.min(100, Math.round((Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60)) / Math.max(1, studentGoal.minutes || 60)) * 100))} color="#f97316" /></div> : <button type="button" className="student-learn-enable-goal" onClick={() => setStudentGoal((prev) => ({ ...prev, enabled: true, minutes: Number(prev.minutes || 30) }))}>Set a daily learning goal</button>}
                </section>
              </div>

              {studentLearnCollections.completed.length > 0 && (
                <section className="student-learn-section" aria-labelledby="student-learn-completed-title">
                  <div className="student-learn-section-heading"><div><small>Your milestones</small><h2 id="student-learn-completed-title">Completed Courses</h2></div><button type="button" className="student-learn-link" onClick={() => navigate('/certificates')}>View certificates <i className="bi bi-arrow-right" /></button></div>
                  <div className="student-learn-completed-grid">{studentLearnCollections.completed.slice(0, 4).map((row) => { const courseId = String(row.course?._id || row.course || ''); return <article key={`completed-${row._id || courseId}`}><span><i className="bi bi-award" /></span><div><strong>{row.course?.courseName || 'Course'}</strong><small>Completed {row.learningSummary?.completedAt ? fmtDate(row.learningSummary.completedAt) : ''}</small></div><button type="button" onClick={() => openCourseWorkspace(courseId)}>Review</button></article>; })}</div>
                </section>
              )}

              {studentRecommendedCourses.length > 0 && (
                <section className="student-learn-section" aria-labelledby="student-learn-recommended-title">
                  <div className="student-learn-section-heading"><div><small>Based on your learning</small><h2 id="student-learn-recommended-title">Recommended Courses</h2></div><button type="button" className="student-learn-link" onClick={() => openTab('for-you')}>Explore all <i className="bi bi-arrow-right" /></button></div>
                  <div className="student-learn-recommendations">{studentRecommendedCourses.slice(0, 5).map((course) => <button type="button" key={`learn-recommend-${course._id}`} onClick={() => openStudentCourseCard(course, { fallbackTab: 'my-courses' })}><CourseArtwork title={course.courseName || 'Course'} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#6366f1'} height={112} compact /><span><small>{isFreeLearningCourse(course) ? 'Free course' : formatCourseCategoryLabel(course.category || inferCourseMode(course))}</small><strong>{course.courseName || 'Course'}</strong><span>{getLearningFocusedCourseCta(course)} <i className="bi bi-arrow-right" /></span></span></button>)}</div>
                </section>
              )}
            </div>
          )}

          {false && !showCourseWorkspaceModal && (
          <StudentSurface style={{ padding: studentPagePadding }}>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
              <div>
                <div className="fw-semibold" style={{ fontSize: studentSectionTitleSize, lineHeight: 1.08, color: studentTheme.title }}>My Courses</div>
                <div className="small mt-1" style={{ color: studentTheme.muted }}>Keep learning, keep growing.</div>
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-12">
                <label className="form-label small fw-semibold">Search</label>
                <input
                  className="form-control"
                  value={learningFilters.search}
                  onChange={(e) => setLearningFilters((prev) => ({ ...prev, search: e.target.value }))}
                  placeholder="Search for courses, topics or skills..."
                />
              </div>
              <div className="col-12">
                <div className="d-flex flex-wrap gap-3 small fw-semibold">
                  <button type="button" className="btn btn-link p-0 text-decoration-none" style={{ color: learningFilters.status === 'in_progress' ? '#7c3aed' : '#64748b', borderBottom: learningFilters.status === 'in_progress' ? '2px solid #7c3aed' : '2px solid transparent', borderRadius: 0, paddingBottom: 6 }} onClick={() => setLearningFilters((prev) => ({ ...prev, status: 'in_progress' }))}>
                    In Progress ({learningStatusCounts.inProgress})
                  </button>
                  <button type="button" className="btn btn-link p-0 text-decoration-none" style={{ color: learningFilters.status === 'completed' ? '#7c3aed' : '#64748b', borderBottom: learningFilters.status === 'completed' ? '2px solid #7c3aed' : '2px solid transparent', borderRadius: 0, paddingBottom: 6 }} onClick={() => setLearningFilters((prev) => ({ ...prev, status: 'completed' }))}>
                    Completed ({learningStatusCounts.completed})
                  </button>
                  <button type="button" className="btn btn-link p-0 text-decoration-none" style={{ color: learningFilters.status === 'wishlist' ? '#7c3aed' : '#64748b', borderBottom: learningFilters.status === 'wishlist' ? '2px solid #7c3aed' : '2px solid transparent', borderRadius: 0, paddingBottom: 6 }} onClick={() => setLearningFilters((prev) => ({ ...prev, status: 'wishlist' }))}>
                    Wishlist ({learningStatusCounts.wishlist})
                  </button>
                </div>
              </div>
            </div>
            {learningFilters.status === 'wishlist' && (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                  <div>
                    <div className="fw-semibold" style={{ fontSize: 22, color: studentTheme.title }}>My Wishlist</div>
                    <div className="small" style={{ color: studentTheme.muted }}>Courses saved for later.</div>
                  </div>
                </div>
                <div className="row g-3">
                  {wishlistedCatalogCourses.map((course) => (
                    <div className="col-12 col-md-6 col-xl-4" key={`wishlist-course-${course._id}`}>
                      <div className="border h-100 bg-white" style={{ borderRadius: 22, overflow: 'hidden', borderColor: '#e2e8f0' }}>
                        <div className="position-relative">
                          <CourseArtwork title={course.courseName} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#2563eb'} height={164} compact />
                          <button
                            type="button"
                            className="btn btn-sm position-absolute top-0 end-0 m-2"
                            style={{ borderRadius: 999, background: 'rgba(255,255,255,0.92)', border: '1px solid #e2e8f0' }}
                            onClick={() => toggleWishlist(course._id)}
                          >
                            <i className="bi bi-heart-fill text-danger" />
                          </button>
                        </div>
                        <div style={{ padding: 16 }}>
                          <div className="fw-semibold mb-1" style={{ color: studentTheme.title }}>{course.courseName || 'Course'}</div>
                          <div className="small mb-3" style={{ color: studentTheme.muted }}>{course.shortDescription || course.category || 'Saved for later'}</div>
                          <div className="d-flex gap-2 flex-wrap">
                            {course.enrollment ? (
                              <button className="btn btn-primary btn-sm" onClick={() => openStudentCourseCard(course, { fallbackTab: 'my-courses' })}>{getStudentCourseActionLabel(course)}</button>
                            ) : course.access?.allowed ? (
                              <button className="btn btn-success btn-sm" disabled={saving} onClick={() => enrollCourse(course)}>{getLearningFocusedCourseCta(course)}</button>
                            ) : (
                              <button className="btn btn-outline-primary btn-sm" onClick={() => openStudentCourseCard(course, { fallbackTab: 'for-you' })}>{getLearningFocusedCourseCta(course)}</button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!wishlistedCatalogCourses.length && (
                    <div className="col-12">
                      <div className="border rounded-4 p-4 bg-white text-center text-muted" style={{ borderColor: '#e2e8f0' }}>
                        No courses are in your wishlist yet.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {learningFilters.status !== 'wishlist' && (
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                <div>
                  <div className="fw-semibold" style={{ fontSize: 22, color: studentTheme.title }}>Enrolled Courses</div>
                  <div className="small" style={{ color: studentTheme.muted }}>Open a course card to view content and assessments inside it.</div>
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={enrolledCourseCarouselIndex <= 0}
                    onClick={() => setEnrolledCourseCarouselIndex((prev) => Math.max(0, prev - 1))}
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={enrolledCourseCarouselIndex >= enrolledCourseMaxIndex}
                    onClick={() => setEnrolledCourseCarouselIndex((prev) => Math.min(enrolledCourseMaxIndex, prev + 1))}
                  >
                    <i className="bi bi-chevron-right" />
                  </button>
                </div>
              </div>
              <div className="row g-3">
               {enrolledCourseVisibleCards.map((card) => {
                 if (card.type === 'enrolled') {
                  const row = card.row;
                  const courseKey = String(row.course?._id || row.course);
                  const isSelected = String(selectedCourseId) === courseKey;
                 return (
                 <div key={card.key} className="col-12 col-lg-6">
                    <div
                      role="button"
                      tabIndex={0}
                      className="w-100 h-100"
                      style={{
                        borderRadius: 24,
                        padding: 16,
                        background: '#0f172a',
                        borderColor: isSelected ? '#8b5cf6' : '#1e293b',
                        borderStyle: 'solid',
                        borderWidth: 1,
                        boxShadow: '0 18px 34px rgba(15,23,42,0.20)',
                        cursor: 'pointer',
                      }}
                      onClick={() => openCourseWorkspace(courseKey)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openCourseWorkspace(courseKey);
                        }
                      }}
                    >
                      {(() => {
                        const progressState = getLearningProgressState(row);
                        const statusLabel = progressState === 'completed'
                          ? 'Completed'
                          : progressState === 'in_progress'
                            ? 'In Progress'
                            : 'Ready';
                        return (
                      <div className="d-flex flex-wrap flex-md-nowrap gap-3 align-items-start">
                        <div style={{ width: 132, minWidth: 132, maxWidth: '100%' }}>
                          <CourseArtwork
                            title={row.course?.courseName || 'Course'}
                            imageUrl={row.course?.thumbnailUrl || row.course?.promoImageUrl}
                            accent={row.course?.courseColor || '#2563eb'}
                            height={126}
                            compact
                          />
                        </div>
                        <div className="flex-fill">
                          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                             <div>
                               <div className="fw-semibold" style={{ fontSize: 20, lineHeight: 1.2, color: '#f8fafc' }}>{row.course?.courseName || 'Course'}</div>
                               <div className="small mt-1" style={{ color: '#94a3b8' }}>{row.course?.shortDescription || row.course?.category || 'Keep learning, keep growing.'}</div>
                             </div>
                             <div className="d-flex align-items-center gap-2">
                               <button
                                 type="button"
                                 className="btn btn-sm"
                               style={{ borderRadius: 999, background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.16)', color: wishlistCourseIdSet.has(courseKey) ? '#fb7185' : '#e2e8f0' }}
                               onClick={(e) => {
                                  e.stopPropagation();
                                  toggleWishlist(courseKey);
                                }}
                               >
                                 <i className={`bi ${wishlistCourseIdSet.has(courseKey) ? 'bi-heart-fill' : 'bi-heart'}`} />
                               </button>
                               <span className="badge rounded-pill" style={{ background: 'rgba(139,92,246,0.18)', color: '#ddd6fe', border: '1px solid rgba(139,92,246,0.18)' }}>
                                 {statusLabel}
                               </span>
                             </div>
                           </div>
                          <div className="d-flex flex-wrap gap-4 small mb-3" style={{ color: '#cbd5e1' }}>
                            <span><i className="bi bi-journal-text me-1" />{row.learningSummary?.completedLessons || 0}/{row.learningSummary?.totalLessons || 0} Lectures</span>
                            <span><i className="bi bi-clock-history me-1" />{fmtDuration(row.learningSummary?.watchedSeconds || 0)}</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center small mb-2">
                            <span style={{ color: '#94a3b8' }}>Progress</span>
                            <span className="fw-semibold" style={{ color: '#f8fafc' }}>{row.learningSummary?.completionPercent || 0}%</span>
                          </div>
                          <ProgressBar value={row.learningSummary?.completionPercent || 0} color="#8b5cf6" />
                          <button className="btn btn-primary btn-sm w-100 mt-3" onClick={(e) => { e.stopPropagation(); openCourseWorkspace(courseKey); }}>
                            {getStudentCourseActionLabel(row)}
                          </button>
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                  </div>
                  );
                 }
                 const course = card.course;
                 const index = card.index;
                 return (
                 <div key={card.key} className="col-12 col-md-6 col-xl-4">
                   <div
                      role="button"
                      tabIndex={0}
                      className="w-100 h-100"
                     style={{
                       borderRadius: 24,
                       padding: 16,
                       background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                       borderStyle: 'solid',
                       borderWidth: 1,
                       borderColor: '#e2e8f0',
                       boxShadow: '0 16px 28px rgba(15,23,42,0.08)',
                       cursor: 'pointer',
                     }}
                     onClick={() => openStudentCourseCard(course, { fallbackTab: 'my-courses' })}
                     onKeyDown={(e) => {
                       if (e.key === 'Enter' || e.key === ' ') {
                         e.preventDefault();
                         openStudentCourseCard(course, { fallbackTab: 'my-courses' });
                       }
                     }}
                   >
                     <div className="d-flex flex-column gap-3">
                       <CourseArtwork
                         title={course.courseName || 'Course'}
                         imageUrl={course.thumbnailUrl || course.promoImageUrl}
                         accent={course.courseColor || '#2563eb'}
                         height={152}
                         compact
                       />
                       <div>
                          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                            <div className="fw-semibold" style={{ fontSize: 18, lineHeight: 1.2, color: studentTheme.title }}>{course.courseName || 'Course'}</div>
                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-sm"
                                style={{ borderRadius: 999, background: 'rgba(255,255,255,0.92)', border: '1px solid #e2e8f0', color: wishlistCourseIdSet.has(String(course._id)) ? '#fb7185' : '#64748b' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleWishlist(course._id);
                                }}
                              >
                                <i className={`bi ${wishlistCourseIdSet.has(String(course._id)) ? 'bi-heart-fill' : 'bi-heart'}`} />
                              </button>
                              <span className="badge rounded-pill" style={{ background: 'rgba(37,99,235,0.10)', color: '#2563eb', border: '1px solid rgba(191,219,254,0.9)' }}>
                                {course.category || 'Course'}
                              </span>
                            </div>
                          </div>
                         <div className="small mb-2" style={{ color: studentTheme.muted }}>{course.shortDescription || course.category || 'This course is available in your current access plan.'}</div>
                         <div className="d-flex justify-content-between align-items-center small" style={{ color: studentTheme.muted }}>
                           <span>{course.access?.allowed ? 'Accessible now' : 'Recommended'}</span>
                           <span>{fmtMoney(course.totalFees || 0)}</span>
                         </div>
                         <button
                           type="button"
                           className="btn btn-primary btn-sm w-100 mt-3"
                           onClick={(e) => {
                             e.stopPropagation();
                             openStudentCourseCard(course, { fallbackTab: 'my-courses' });
                           }}
                         >
                           Start Now
                         </button>
                        </div>
                      </div>
                    </div>
                  </div>
                 );
               })}
               </div>
            </div>
            )}
            <div className="row g-3 mb-4">
              <div className="col-12 col-xl-4">
                <div className="h-100 rounded-4 p-3" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
                  <div className="fw-semibold mb-3" style={{ color: studentTheme.title }}>Course Progress Overview</div>
                  <div className="d-flex align-items-center justify-content-between gap-3">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 108, height: 108, border: '10px solid #c4b5fd', color: '#0f172a', fontWeight: 700, fontSize: 24 }}>
                      {studentLearningOverview.avgCompletion}%
                    </div>
                      <div className="small d-flex flex-column gap-2">
                        <div><span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: '#8b5cf6' }} />In Progress {learningStatusCounts.inProgress}</div>
                        <div><span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: '#34d399' }} />Completed {learningStatusCounts.completed}</div>
                      <div><span className="d-inline-block rounded-circle me-2" style={{ width: 10, height: 10, background: '#f472b6' }} />Wishlist {learningStatusCounts.wishlist}</div>
                      </div>
                    </div>
                  </div>
              </div>
              <div className="col-6 col-xl-4">
                <div className="h-100 rounded-4 p-3" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
                  <div className="fw-semibold mb-2" style={{ color: studentTheme.title }}>Daily Learning Goal</div>
                  <div className="fw-semibold mb-2" style={{ fontSize: 28, color: studentTheme.title }}>
                    {Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60))}
                    <span className="small ms-1" style={{ color: studentTheme.muted }}>/ {studentGoal.minutes || 60} mins</span>
                  </div>
                  <ProgressBar value={studentGoal.enabled ? Math.min(100, Math.round((Math.min(studentGoal.minutes || 60, Math.round(Number(studentLearningOverview.watchedSecondsToday || 0) / 60)) / (studentGoal.minutes || 60)) * 100)) : 0} color="#8b5cf6" />
                  <div className="small mt-2" style={{ color: studentTheme.muted }}>Great! You&apos;re on track.</div>
                </div>
              </div>
              <div className="col-6 col-xl-4">
                <div className="h-100 rounded-4 p-3" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
                  <div className="fw-semibold mb-2" style={{ color: studentTheme.title }}>Pending Lessons</div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle" style={{ width: 88, height: 88, border: '8px solid #34d399', color: '#0f172a', fontWeight: 700, fontSize: 24 }}>
                      {studentLearningOverview.totalPendingLessons || 0}
                    </div>
                    <div className="small" style={{ color: studentTheme.text }}>
                      <div className="fw-semibold" style={{ color: studentTheme.title }}>{studentLearningOverview.activeCourses || 0} active course{Number(studentLearningOverview.activeCourses || 0) === 1 ? '' : 's'}</div>
                      <div className="mt-1" style={{ color: studentTheme.muted }}>{studentLearningOverview.totalCourses || 0} total enrolled</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-2">
              <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                <div>
                  <div className="fw-semibold" style={{ fontSize: 22, color: studentTheme.title }}>Because You&apos;re Learning {selectedLearningCourse?.course?.courseName?.split(' ')[0] || 'Your Track'}</div>
                </div>
              </div>
              <div className="row g-3">
                {studentRealRecommendedCourses.slice(0, 8).map((course, index) => (
                  <div className="col-12 col-md-6 col-xl-3" key={`my-course-rec-${course._id || index}`}>
                    <div className="position-relative" style={{ borderRadius: 18, overflow: 'hidden', background: '#fff', border: '1px solid #e2e8f0' }}>
                      <button
                        type="button"
                        className="btn btn-sm position-absolute top-0 end-0 m-2"
                        style={{ zIndex: 2, borderRadius: 999, background: 'rgba(255,255,255,0.92)', border: '1px solid #e2e8f0', color: wishlistCourseIdSet.has(String(course._id)) ? '#fb7185' : '#64748b' }}
                        onClick={() => toggleWishlist(course._id)}
                      >
                        <i className={`bi ${wishlistCourseIdSet.has(String(course._id)) ? 'bi-heart-fill' : 'bi-heart'}`} />
                      </button>
                      <CourseArtwork title={course.courseName} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#2563eb'} height={126} compact />
                      <div style={{ padding: 12 }}>
                        <div className="small badge mb-2" style={{ background: 'rgba(59,130,246,0.10)', color: '#2563eb' }}>{course.category || 'Recommended'}</div>
                        <div
                          className="fw-semibold mb-1"
                          style={{
                            color: studentTheme.title,
                            minHeight: 42,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {course.courseName}
                        </div>
                        <div
                          className="small d-flex justify-content-between"
                          style={{
                            color: studentTheme.muted,
                            minHeight: 38,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          <span>{course.shortDescription || course.category || 'Course recommendation'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {!enrolledCourseSliderCards.length && (
              <div className="border rounded p-4 text-center text-muted bg-white mt-3">
                No courses are available for the selected filters right now.
              </div>
            )}
          </StudentSurface>
          )}

          {false && selectedLearningCourse && (
            <StudentSurface style={{ padding: 24 }}>
              <div className="row g-3 mb-4">
                <div className="col-12 col-xl-8">
                  <div className="d-flex flex-wrap align-items-start gap-3 h-100 rounded-4 p-3" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)', border: '1px solid #dbeafe' }}>
                    <div style={{ width: 148, minWidth: 148 }}>
                      <CourseArtwork
                        title={selectedLearningCourse.course?.courseName || 'Course'}
                        imageUrl={selectedLearningCourse.course?.thumbnailUrl || selectedLearningCourse.course?.promoImageUrl}
                        accent={selectedLearningCourse.course?.courseColor || '#2563eb'}
                        height={132}
                        compact
                      />
                    </div>
                    <div className="flex-fill">
                      <div className="small text-muted mb-1">{selectedLearningCourse.course?.category || 'Course'}</div>
                      <div className="fw-semibold" style={{ fontSize: 26, lineHeight: 1.2 }}>{selectedLearningCourse.course?.courseName || 'Course'}</div>
                      <div className="small text-muted mt-2 mb-3">
                        Completed {selectedLearningCourse.learningSummary?.completedLessons || 0} of {selectedLearningCourse.learningSummary?.totalLessons || 0} lessons
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge rounded-pill text-bg-light border">Pending {selectedLearningCourse.learningSummary?.pendingLessons || 0}</span>
                        <span className="badge rounded-pill text-bg-light border">Score {selectedLearningCourse.learningSummary?.averageScore || 0}</span>
                        <span className="badge rounded-pill text-bg-light border">Recent {selectedLearningCourse.learningSummary?.lastAccessedAt ? fmtDate(selectedLearningCourse.learningSummary?.lastAccessedAt) : 'Not opened'}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-xl-4">
                  <div className="rounded-4 p-3 h-100" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
                    <div className="d-flex justify-content-between small mb-2">
                      <span className="text-muted">Progress</span>
                      <span className="fw-semibold">{selectedLearningCourse.learningSummary?.completionPercent || 0}%</span>
                    </div>
                    <ProgressBar value={selectedLearningCourse.learningSummary?.completionPercent || 0} color={selectedLearningCourse.course?.courseColor || '#2563eb'} />
                    <div className="row g-2 mt-3">
                      <div className="col-6">
                        <div className="rounded-4 p-3 h-100" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                          <div className="small text-muted">In progress</div>
                          <div className="fw-semibold" style={{ fontSize: 20 }}>{selectedLearningCourse.learningSummary?.inProgressLessons || 0}</div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="rounded-4 p-3 h-100" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                          <div className="small text-muted">Pending</div>
                          <div className="fw-semibold" style={{ fontSize: 20 }}>{selectedLearningCourse.learningSummary?.pendingLessons || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column gap-3">
                {studentModuleGroups.map((moduleGroup) => {
                  const isModuleExpanded = String(expandedLearningModuleId) === String(moduleGroup.key);
                  const moduleLessonCount = moduleGroup.sections.reduce((sum, section) => sum + Number((section.lessons || []).length), 0);
                  return (
                    <div key={`student-module-${moduleGroup.key}`} className="border bg-white" style={{ borderRadius: 24, padding: 18, borderColor: '#dbeafe', boxShadow: '0 16px 30px rgba(15,23,42,0.05)' }}>
                      <button
                        type="button"
                        className="btn w-100 text-start p-0 border-0 bg-transparent shadow-none"
                        onClick={() => setExpandedLearningModuleId((prev) => (String(prev) === String(moduleGroup.key) ? '' : String(moduleGroup.key)))}
                      >
                        <div className="d-flex justify-content-between align-items-center gap-2">
                          <div>
                            <div className="fw-semibold" style={{ fontSize: 18, color: studentTheme.title }}>{moduleGroup.title}</div>
                            <div className="small text-muted">{moduleLessonCount} lesson{moduleLessonCount === 1 ? '' : 's'} in this module</div>
                          </div>
                          <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 36, height: 36, borderRadius: 12, background: '#eff6ff', color: '#2563eb' }}>
                            <i className={`bi ${isModuleExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                          </div>
                        </div>
                      </button>

                      {isModuleExpanded && (
                        <div className="border-top mt-3 pt-3 d-flex flex-column gap-3" style={{ borderColor: '#dbeafe' }}>
                          {moduleGroup.sections.map((section) => (
                            <div key={`student-topic-${section._id}`}>
                              {moduleGroup.sections.length > 1 && (
                                <div className="fw-semibold mb-2" style={{ fontSize: 14, color: studentTheme.title }}>
                                  {section.title}
                                </div>
                              )}
                              <div className="d-flex flex-column gap-2">
                                {(section.lessons || []).map((lesson) => {
                                  const isExpanded = String(selectedLessonId) === String(lesson._id);
                                  const inlineNextLesson = nextUnlockedLessonById.get(String(lesson._id));
                                  return (
                                    <div
                                      key={`student-lesson-${lesson._id}`}
                                      className="border"
                                      style={{
                                        borderRadius: 18,
                                        padding: 14,
                                        opacity: lesson.access?.isLocked ? 0.75 : 1,
                                        background: isExpanded ? 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)' : '#ffffff',
                                        borderColor: isExpanded ? '#60a5fa' : '#e2e8f0',
                                      }}
                                    >
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (lesson.access?.isLocked) return;
                                          setSelectedLessonId((prev) => (String(prev) === String(lesson._id) ? '' : String(lesson._id)));
                                        }}
                                        className="btn w-100 text-start p-0 border-0 bg-transparent shadow-none"
                                      >
                                        <LessonEpisodeSummary
                                          lesson={lesson}
                                          course={selectedLearningCourse.course}
                                          isActive={isExpanded}
                                          isExpanded={isExpanded}
                                          sequenceNumber={flattenedLearnLessons.findIndex((item) => String(item._id) === String(lesson._id)) + 1}
                                          progressOverride={getLessonWatchPercent(lesson)}
                                        />
                                      </button>
                                      {isExpanded && (
                                        <div className="border-top mt-3 pt-3">
                                          <div className="row g-3 align-items-start">
                                            <div className="col-12 col-xl-8">
                                              {lesson.lessonType === 'uploaded_video' && lesson.uploadedVideoUrl && (
                                                <div style={{ borderRadius: 18, overflow: 'hidden', background: '#020617', boxShadow: '0 16px 36px rgba(15, 23, 42, 0.16)' }}>
                                                  <div className="ratio ratio-16x9">
                                                    <TrackedLessonVideo lesson={lesson} onProgress={handleVideoWatchProgress} className="w-100 h-100 d-block" style={{ objectFit: 'contain' }} />
                                                  </div>
                                                </div>
                                              )}
                                              {lesson.lessonType === 'embedded_video' && lesson.embeddedVideoUrl && (
                                                <div className="ratio ratio-16x9" style={{ borderRadius: 18, overflow: 'hidden', boxShadow: '0 16px 36px rgba(15, 23, 42, 0.12)' }}>
                                                  <iframe title={lesson.title} src={lesson.embeddedVideoUrl} allowFullScreen />
                                                </div>
                                              )}
                                              {lesson.lessonType === 'promo_video' && lesson.uploadedVideoUrl && (
                                                <div style={{ borderRadius: 18, overflow: 'hidden', background: '#020617', boxShadow: '0 16px 36px rgba(15, 23, 42, 0.16)' }}>
                                                  <div className="ratio ratio-16x9">
                                                    <TrackedLessonVideo lesson={lesson} onProgress={handleVideoWatchProgress} className="w-100 h-100 d-block" style={{ objectFit: 'contain' }} />
                                                  </div>
                                                </div>
                                              )}
                                              {lesson.lessonType === 'promo_video' && !lesson.uploadedVideoUrl && lesson.embeddedVideoUrl && (
                                                <div className="ratio ratio-16x9" style={{ borderRadius: 18, overflow: 'hidden', boxShadow: '0 16px 36px rgba(15, 23, 42, 0.12)' }}>
                                                  <iframe title={lesson.title} src={lesson.embeddedVideoUrl} allowFullScreen />
                                                </div>
                                              )}
                                              {lesson.lessonType === 'promo_image' && lesson.imageUrl && (
                                                <img src={lesson.imageUrl} alt={lesson.title} className="img-fluid" style={{ borderRadius: 18, width: '100%', boxShadow: '0 16px 36px rgba(15, 23, 42, 0.12)' }} />
                                              )}
                                              {!['uploaded_video', 'embedded_video', 'promo_video', 'promo_image'].includes(String(lesson.lessonType || '')) && (
                                                <div className="border bg-light d-flex align-items-center justify-content-center text-muted small" style={{ borderRadius: 18, minHeight: 220, borderColor: '#e2e8f0' }}>
                                                  Open the lesson details on the right to continue.
                                                </div>
                                              )}
                                            </div>
                                            <div className="col-12 col-xl-4">
                                              <div className="border h-100" style={{ borderRadius: 20, padding: 16, borderColor: '#dbeafe', background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)' }}>
                                                <div className="small text-muted mb-1">{section.title}</div>
                                                <div className="fw-semibold mb-2" style={{ fontSize: 18, lineHeight: 1.3 }}>{lesson.title}</div>
                                                <div className="small text-muted mb-3">
                                                  {formatLessonTypeLabel(lesson.lessonType)}
                                                  {lesson.durationSeconds ? ` | ${fmtDuration(lesson.durationSeconds)}` : ''}
                                                  {lesson.progress?.lastAccessedAt ? ` | Last opened ${fmtDate(lesson.progress.lastAccessedAt)}` : ''}
                                                </div>
                                                {lesson.summary && (
                                                  <div className="small text-muted mb-3" style={{ lineHeight: 1.7 }}>
                                                    {lesson.summary}
                                                  </div>
                                                )}
                                                {lesson.htmlContent && lesson.lessonType === 'text' && (
                                                  <div className="border p-3 mb-3 bg-white" style={{ borderRadius: 16, borderColor: '#e2e8f0', maxHeight: 260, overflowY: 'auto' }} dangerouslySetInnerHTML={{ __html: lesson.htmlContent }} />
                                                )}
                                                <div className="rounded-4 p-3 mb-3" style={{ background: '#ffffff', border: '1px solid #e2e8f0' }}>
                                                  <div className="small text-muted mb-1">Lesson status</div>
                                                  <div className="fw-semibold" style={{ color: studentTheme.title }}>
                                                    {lesson.progress?.status === 'completed'
                                                      ? 'Completed'
                                                      : lesson.progress?.status === 'in_progress'
                                                        ? 'In Progress'
                                                        : lesson.access?.isLocked
                                                          ? 'Locked'
                                                          : 'Ready to start'}
                                                  </div>
                                                </div>
                                                <div className="d-flex flex-wrap gap-2">
                                                  {['uploaded_video', 'embedded_video', 'promo_video', 'promo_image'].includes(String(lesson.lessonType || '')) && (
                                                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openExpandedLessonPreview(lesson)}>
                                                      Expand View
                                                    </button>
                                                  )}
                                                  {lesson.resourceUrl && (
                                                    <a href={lesson.resourceUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                                                      Open Resource
                                                    </a>
                                                  )}
                                                  {lesson.lessonType === 'quiz_gate' ? (
                                                    <button className="btn btn-primary btn-sm" disabled={saving || !activeQuizQuestions.length} onClick={submitQuizGate}>
                                                      Submit Quiz
                                                    </button>
                                                  ) : (
                                                    lesson.progress?.status !== 'completed' && !lesson.access?.isLocked && (
                                                      <button type="button" className="btn btn-primary btn-sm" disabled={saving || (!canWrite && !isLessonWatchRequirementMet(lesson))} onClick={markLessonComplete}>
                                                        Mark as Completed
                                                      </button>
                                                    )
                                                  )}
                                                  {inlineNextLesson && String(inlineNextLesson._id) !== String(lesson._id) && (
                                                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openLesson(inlineNextLesson._id)}>
                                                      Next Lesson
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {!(section.lessons || []).length && (
                                  <div className="small text-muted border rounded p-3 bg-light">
                                    No visible lessons in this section yet.
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </StudentSurface>
          )}
        </div>
      )}

      {tab === 'learning' && canWrite && (
        <div className="row g-3">
          <div className={`col-12 ${canWrite ? 'col-xl-4' : 'col-xl-4 col-xxl-3'}`}>
            {canWrite ? (
            <CardShell title="My Courses">
              {!canWrite && (
                <div className="row g-2 mb-3">
                  <div className="col-12">
                    <label className="form-label small">Search</label>
                    <input className="form-control" value={learningFilters.search} onChange={(e) => setLearningFilters((prev) => ({ ...prev, search: e.target.value }))} placeholder="Course name, code, category" />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Status</label>
                    <select className="form-select" value={learningFilters.status} onChange={(e) => setLearningFilters((prev) => ({ ...prev, status: e.target.value }))}>
                      <option value="all">All</option>
                      <option value="in_progress">In progress</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="not_started">Not started</option>
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Sort</label>
                    <select className="form-select" value={learningFilters.sort} onChange={(e) => setLearningFilters((prev) => ({ ...prev, sort: e.target.value }))}>
                      <option value="recent">Recent activity</option>
                      <option value="progress">Progress</option>
                      <option value="score">Score</option>
                      <option value="name">Name</option>
                    </select>
                  </div>
                  <div className="col-12 d-flex flex-wrap gap-2">
                    <Pill color="primary">{visibleLearningRows.length} course{visibleLearningRows.length === 1 ? '' : 's'}</Pill>
                    <Pill color="success">{visibleLearningRows.filter((row) => Number(row.learningSummary?.completionPercent || 0) >= 100).length} completed</Pill>
                    <Pill color="warning">{visibleLearningRows.filter((row) => Number(row.learningSummary?.inProgressLessons || 0) > 0).length} active</Pill>
                  </div>
                </div>
              )}
              <select
                className="form-select mb-3"
                value={selectedCourseId}
                onChange={(e) => setSelectedCourseId(e.target.value)}
              >
                <option value="">Select course</option>
                {(canWrite ? learning : visibleLearningRows).map((row) => (
                  <option key={row._id} value={row.course?._id || row.course}>
                    {row.course?.courseName || 'Course'} {row.learningSummary ? `(${row.learningSummary.completionPercent || 0}%)` : ''}
                  </option>
                ))}
              </select>
              {!canWrite && (
                <div className="d-flex flex-column gap-2 mb-3">
                  {visibleLearningRows.slice(0, 5).map((row) => (
                    <button
                      key={`learning-list-${row._id}`}
                      type="button"
                      className={`btn text-start border rounded p-2 ${String(selectedCourseId) === String(row.course?._id || row.course) ? 'border-primary bg-primary-subtle' : 'bg-white'}`}
                      onClick={() => setSelectedCourseId(String(row.course?._id || row.course))}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <div className="fw-semibold">{row.course?.courseName || 'Course'}</div>
                          <div className="small text-muted">{row.course?.category || 'No category'}</div>
                        </div>
                        <Pill color={Number(row.learningSummary?.completionPercent || 0) >= 100 ? 'success' : Number(row.learningSummary?.inProgressLessons || 0) > 0 ? 'warning' : 'secondary'}>
                          {row.learningSummary?.completionPercent || 0}%
                        </Pill>
                      </div>
                    </button>
                  ))}
                  {!visibleLearningRows.length && (
                    <div className="border rounded p-3 text-center text-muted bg-white">
                      No enrolled courses match the selected filters.
                    </div>
                  )}
                </div>
              )}
              {selectedLearningCourse?.learningSummary && (
                <div className="border mb-3" style={{ borderRadius: 20, padding: 16, background: '#f8fafc', borderColor: '#e2e8f0' }}>
                  {(() => {
                    const consistency = selectedLearningCourse.learningSummary.consistency || {};
                    return (
                      <>
                  <div className="d-flex align-items-start gap-3 mb-3">
                    <div style={{ width: 96, minWidth: 96 }}>
                      <CourseArtwork
                        title={selectedLearningCourse.course?.courseName || 'Course progress'}
                        imageUrl={selectedLearningCourse.course?.thumbnailUrl || selectedLearningCourse.course?.promoImageUrl}
                        accent={selectedLearningCourse.course?.courseColor || '#2563eb'}
                        height={96}
                        compact
                      />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                        <div className="fw-semibold">{selectedLearningCourse.course?.courseName || 'Course progress'}</div>
                        <Pill color="primary">{selectedLearningCourse.learningSummary.completionPercent || 0}% complete</Pill>
                      </div>
                      <div className="small text-muted d-flex flex-wrap gap-2 align-items-center">
                        <span>{selectedLearningCourse.course?.category || 'No category'}</span>
                        <span className={`badge bg-${consistencyTone(consistency.consistencyStatus)}`}>
                          {String(consistency.consistencyStatus || 'new').replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <ProgressBar value={selectedLearningCourse.learningSummary.completionPercent || 0} color={selectedLearningCourse.course?.courseColor || '#2563eb'} />
                  </div>
                  <div className="row g-2 small">
                    <div className="col-6 text-muted">Completed: <span className="text-dark fw-semibold">{selectedLearningCourse.learningSummary.completedLessons || 0}</span></div>
                    <div className="col-6 text-muted">Pending: <span className="text-dark fw-semibold">{selectedLearningCourse.learningSummary.pendingLessons || 0}</span></div>
                    <div className="col-6 text-muted">In progress: <span className="text-dark fw-semibold">{selectedLearningCourse.learningSummary.inProgressLessons || 0}</span></div>
                    <div className="col-6 text-muted">Avg score: <span className="text-dark fw-semibold">{selectedLearningCourse.learningSummary.averageScore ?? '-'}</span></div>
                    <div className="col-6 text-muted">Current streak: <span className="text-dark fw-semibold">{consistency.currentStreak || 0} day{Number(consistency.currentStreak || 0) === 1 ? '' : 's'}</span></div>
                    <div className="col-6 text-muted">Active in 7 days: <span className="text-dark fw-semibold">{consistency.activeDaysLast7 || 0} day{Number(consistency.activeDaysLast7 || 0) === 1 ? '' : 's'}</span></div>
                    <div className="col-6 text-muted">Watch time: <span className="text-dark fw-semibold">{fmtDuration(consistency.watchedSecondsTotal || 0)}</span></div>
                    <div className="col-6 text-muted">Inactive: <span className="text-dark fw-semibold">{consistency.daysSinceLastActivity ?? 0} day{Number(consistency.daysSinceLastActivity ?? 0) === 1 ? '' : 's'}</span></div>
                    <div className="col-12 text-muted">Last activity: <span className="text-dark fw-semibold">{fmtDate(selectedLearningCourse.learningSummary.lastAccessedAt)}</span></div>
                  </div>
                      </>
                    );
                  })()}
                </div>
              )}
              {(learnState.outline || []).map((section) => (
                <div key={section._id} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                    <div className="fw-semibold">{section.title}</div>
                    <div className="small text-muted">{(section.lessons || []).length} lesson{(section.lessons || []).length === 1 ? '' : 's'}</div>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {(section.lessons || []).map((lesson) => (
                      <button
                        type="button"
                        key={lesson._id}
                        onClick={() => {
                          if (!canWrite && lesson.access?.isLocked) return;
                          setSelectedLessonId(String(lesson._id));
                        }}
                        className={`btn text-start border ${String(selectedLessonId) === String(lesson._id) ? 'border-primary bg-primary-subtle' : 'bg-white'} ${!canWrite && lesson.access?.isLocked ? 'disabled opacity-75' : ''}`}
                        style={{ borderRadius: 16, padding: 14 }}
                      >
                        <LessonEpisodeSummary
                          lesson={lesson}
                          course={selectedLearningCourse?.course || learnState.course}
                          isActive={String(selectedLessonId) === String(lesson._id)}
                          sequenceNumber={flattenedLearnLessons.findIndex((item) => String(item._id) === String(lesson._id)) + 1}
                          progressOverride={getLessonWatchPercent(lesson)}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </CardShell>
            ) : (
            <StudentSurface style={{ padding: 20, position: 'sticky', top: 88 }}>
              <StudentSectionTitle
                eyebrow="Curriculum"
                title="My courses"
                subtitle="Switch courses, track progress, and open lessons from one sidebar."
              />
              <div className="row g-2 mb-3">
                <div className="col-12">
                  <label className="form-label small fw-semibold">Search</label>
                  <input className="form-control" value={learningFilters.search} onChange={(e) => setLearningFilters((prev) => ({ ...prev, search: e.target.value }))} placeholder="Course name, code, category" />
                </div>
                <div className="col-6">
                  <label className="form-label small fw-semibold">Status</label>
                  <select className="form-select" value={learningFilters.status} onChange={(e) => setLearningFilters((prev) => ({ ...prev, status: e.target.value }))}>
                    <option value="all">All</option>
                    <option value="in_progress">In progress</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="not_started">Not started</option>
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label small fw-semibold">Sort</label>
                  <select className="form-select" value={learningFilters.sort} onChange={(e) => setLearningFilters((prev) => ({ ...prev, sort: e.target.value }))}>
                    <option value="recent">Recent activity</option>
                    <option value="progress">Progress</option>
                    <option value="score">Score</option>
                    <option value="name">Name</option>
                  </select>
                </div>
                <div className="col-12 d-flex flex-wrap gap-2">
                  <Pill color="primary">{visibleLearningRows.length} course{visibleLearningRows.length === 1 ? '' : 's'}</Pill>
                  <Pill color="success">{visibleLearningRows.filter((row) => Number(row.learningSummary?.completionPercent || 0) >= 100).length} completed</Pill>
                  <Pill color="warning">{visibleLearningRows.filter((row) => Number(row.learningSummary?.inProgressLessons || 0) > 0).length} active</Pill>
                </div>
              </div>
              <select
                className="form-select mb-3"
                value={selectedCourseId}
                onChange={(e) => setSelectedCourseId(e.target.value)}
              >
                <option value="">Select course</option>
                {visibleLearningRows.map((row) => (
                  <option key={row._id} value={row.course?._id || row.course}>
                    {row.course?.courseName || 'Course'} {row.learningSummary ? `(${row.learningSummary.completionPercent || 0}%)` : ''}
                  </option>
                ))}
              </select>
              <div className="d-flex flex-column gap-2 mb-3" style={{ maxHeight: 280, overflowY: 'auto', paddingRight: 4 }}>
                {visibleLearningRows.map((row) => (
                  <button
                    key={`learning-list-${row._id}`}
                    type="button"
                    className={`btn text-start border ${String(selectedCourseId) === String(row.course?._id || row.course) ? 'border-primary bg-primary-subtle' : 'bg-white'}`}
                    style={{ borderRadius: 16, padding: 14 }}
                    onClick={() => setSelectedCourseId(String(row.course?._id || row.course))}
                  >
                    <div className="d-flex align-items-start gap-3">
                      <div style={{ width: 64, minWidth: 64 }}>
                        <CourseArtwork
                          title={row.course?.courseName || 'Course'}
                          imageUrl={row.course?.thumbnailUrl || row.course?.promoImageUrl}
                          accent={row.course?.courseColor || '#2563eb'}
                          height={64}
                          compact
                        />
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <div className="fw-semibold">{row.course?.courseName || 'Course'}</div>
                            <div className="small text-muted">{row.course?.category || 'No category'}</div>
                          </div>
                          <Pill color={Number(row.learningSummary?.completionPercent || 0) >= 100 ? 'success' : Number(row.learningSummary?.inProgressLessons || 0) > 0 ? 'warning' : 'secondary'}>
                            {row.learningSummary?.completionPercent || 0}%
                          </Pill>
                        </div>
                        <div className="small text-muted mt-2">
                          {row.learningSummary?.pendingLessons || 0} pending | Score {row.learningSummary?.averageScore ?? '-'}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
                {!visibleLearningRows.length && (
                  <div className="border rounded p-3 text-center text-muted bg-white">
                    No enrolled courses match the selected filters.
                  </div>
                )}
              </div>
              {selectedLearningCourse?.learningSummary && (
                <div className="border" style={{ borderRadius: 20, padding: 16, background: '#f8fafc', borderColor: '#e2e8f0' }}>
                  {(() => {
                    const consistency = selectedLearningCourse.learningSummary.consistency || {};
                    return (
                      <>
                  <div className="d-flex align-items-start gap-3 mb-3">
                    <div style={{ width: 88, minWidth: 88 }}>
                      <CourseArtwork
                        title={selectedLearningCourse.course?.courseName || 'Course progress'}
                        imageUrl={selectedLearningCourse.course?.thumbnailUrl || selectedLearningCourse.course?.promoImageUrl}
                        accent={selectedLearningCourse.course?.courseColor || '#2563eb'}
                        height={88}
                        compact
                      />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                        <div className="fw-semibold">{selectedLearningCourse.course?.courseName || 'Course progress'}</div>
                        <Pill color="primary">{selectedLearningCourse.learningSummary.completionPercent || 0}%</Pill>
                      </div>
                      <div className="small text-muted d-flex flex-wrap gap-2 align-items-center">
                        <span>{selectedLearningCourse.course?.category || 'No category'}</span>
                        <span className={`badge bg-${consistencyTone(consistency.consistencyStatus)}`}>
                          {String(consistency.consistencyStatus || 'new').replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <ProgressBar value={selectedLearningCourse.learningSummary.completionPercent || 0} color={selectedLearningCourse.course?.courseColor || '#2563eb'} />
                  </div>
                  <div className="row g-2 small">
                    <div className="col-6 text-muted">Completed: <span className="text-dark fw-semibold">{selectedLearningCourse.learningSummary.completedLessons || 0}</span></div>
                    <div className="col-6 text-muted">Pending: <span className="text-dark fw-semibold">{selectedLearningCourse.learningSummary.pendingLessons || 0}</span></div>
                    <div className="col-6 text-muted">Streak: <span className="text-dark fw-semibold">{consistency.currentStreak || 0} day{Number(consistency.currentStreak || 0) === 1 ? '' : 's'}</span></div>
                    <div className="col-6 text-muted">7-day activity: <span className="text-dark fw-semibold">{consistency.activeDaysLast7 || 0}</span></div>
                    <div className="col-12 text-muted">Last activity: <span className="text-dark fw-semibold">{fmtDate(selectedLearningCourse.learningSummary.lastAccessedAt)}</span></div>
                  </div>
                      </>
                    );
                  })()}
                </div>
              )}
              <div className="mt-4">
                <div className="fw-semibold mb-3">Lesson outline</div>
                <div className="d-flex flex-column gap-3" style={{ maxHeight: 520, overflowY: 'auto', paddingRight: 4 }}>
                  {(learnState.outline || []).map((section) => (
                    <div key={section._id}>
                      <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                        <div className="fw-semibold">{section.title}</div>
                        <div className="small text-muted">{(section.lessons || []).length} lesson{(section.lessons || []).length === 1 ? '' : 's'}</div>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {(section.lessons || []).map((lesson) => {
                          const isExpanded = String(selectedLessonId) === String(lesson._id);
                          const inlineNextLesson = nextUnlockedLessonById.get(String(lesson._id));
                          return (
                            <div
                              key={lesson._id}
                              className={`border ${isExpanded ? 'border-primary bg-primary-subtle' : 'bg-white'}`}
                              style={{ borderRadius: 16, padding: 14, opacity: lesson.access?.isLocked ? 0.75 : 1 }}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  if (lesson.access?.isLocked) return;
                                  setSelectedLessonId((prev) => (String(prev) === String(lesson._id) ? '' : String(lesson._id)));
                                }}
                                className="btn w-100 text-start p-0 border-0 bg-transparent shadow-none"
                              >
                                <LessonEpisodeSummary
                                  lesson={lesson}
                                  course={selectedLearningCourse?.course || learnState.course}
                                  isActive={isExpanded}
                                  isExpanded={isExpanded}
                                  sequenceNumber={flattenedLearnLessons.findIndex((item) => String(item._id) === String(lesson._id)) + 1}
                                  progressOverride={getLessonWatchPercent(lesson)}
                                />
                              </button>
                              {isExpanded && (
                                <div className="border-top mt-3 pt-3">
                                  {lesson.summary && (
                                    <div className="small text-muted mb-3" style={{ lineHeight: 1.6 }}>
                                      {lesson.summary}
                                    </div>
                                  )}
                                  {lesson.lessonType === 'uploaded_video' && lesson.uploadedVideoUrl && (
                                    <div className="mb-3" style={{ borderRadius: 18, overflow: 'hidden', background: '#020617' }}>
                                      <div className="ratio ratio-16x9">
                                        <TrackedLessonVideo lesson={lesson} onProgress={handleVideoWatchProgress} className="w-100 h-100 d-block" style={{ objectFit: 'contain' }} />
                                      </div>
                                    </div>
                                  )}
                                  {lesson.lessonType === 'embedded_video' && lesson.embeddedVideoUrl && (
                                    <div className="ratio ratio-16x9 mb-3" style={{ borderRadius: 18, overflow: 'hidden' }}>
                                      <iframe title={lesson.title} src={lesson.embeddedVideoUrl} allowFullScreen />
                                    </div>
                                  )}
                                  {lesson.lessonType === 'promo_video' && lesson.uploadedVideoUrl && (
                                    <div className="mb-3" style={{ borderRadius: 18, overflow: 'hidden', background: '#020617' }}>
                                      <div className="ratio ratio-16x9">
                                        <TrackedLessonVideo lesson={lesson} onProgress={handleVideoWatchProgress} className="w-100 h-100 d-block" style={{ objectFit: 'contain' }} />
                                      </div>
                                    </div>
                                  )}
                                  {lesson.lessonType === 'promo_video' && !lesson.uploadedVideoUrl && lesson.embeddedVideoUrl && (
                                    <div className="ratio ratio-16x9 mb-3" style={{ borderRadius: 18, overflow: 'hidden' }}>
                                      <iframe title={lesson.title} src={lesson.embeddedVideoUrl} allowFullScreen />
                                    </div>
                                  )}
                                  {lesson.lessonType === 'promo_image' && lesson.imageUrl && (
                                    <img src={lesson.imageUrl} alt={lesson.title} className="img-fluid mb-3" style={{ borderRadius: 18, width: '100%' }} />
                                  )}
                                  {lesson.htmlContent && lesson.lessonType === 'text' && (
                                    <div className="border p-3 mb-3 bg-white" style={{ borderRadius: 16, borderColor: '#e2e8f0' }} dangerouslySetInnerHTML={{ __html: lesson.htmlContent }} />
                                  )}
                                  <div className="d-flex flex-wrap gap-2">
                                    {lesson.resourceUrl && (
                                      <a href={lesson.resourceUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                                        Open Resource
                                      </a>
                                    )}
                                    {lesson.lessonType !== 'quiz_gate' && lesson.progress?.status !== 'completed' && !lesson.access?.isLocked && (
                                      <button type="button" className="btn btn-primary btn-sm" disabled={saving || (!canWrite && !isLessonWatchRequirementMet(lesson))} onClick={markLessonComplete}>
                                        Mark as Completed
                                      </button>
                                    )}
                                    {inlineNextLesson && String(inlineNextLesson._id) !== String(lesson._id) && (
                                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openLesson(inlineNextLesson._id)}>
                                        Next Lesson
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </StudentSurface>
            )}
          </div>
          <div className={`col-12 ${canWrite ? 'col-xl-8' : 'col-xl-8 col-xxl-9'}`}>
            {canWrite ? (
            <CardShell
              title={activeLesson?.title || learnState.course?.courseName || 'Lesson Player'}
              actions={(
                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                  {learnState.summary ? <Pill color="success">{learnState.summary.completedLessons}/{learnState.summary.totalLessons} complete</Pill> : null}
                  {!!activeLesson && <Pill color="secondary">{formatLessonTypeLabel(activeLesson.lessonType)}</Pill>}
                </div>
              )}
            >
              {learnState.summary && (
                <div className="border mb-3" style={{ borderRadius: 22, padding: 18, background: '#f8fafc', borderColor: '#e2e8f0' }}>
                  {(() => {
                    const consistency = learnState.summary.consistency || {};
                    const sectionProgress = Array.isArray(learnState.summary.sectionProgress) ? learnState.summary.sectionProgress : [];
                    return (
                      <>
                  <div className="d-flex flex-wrap justify-content-between gap-2 align-items-center mb-2">
                    <div className="fw-semibold">Learning Progress</div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {nextAvailableLesson && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => openLesson(nextAvailableLesson._id)}
                        >
                          Continue Next
                        </button>
                      )}
                      <div className="small text-muted">
                        Accessible {learnState.summary.accessibleLessons || 0} | Locked {learnState.summary.lockedLessons || 0} | Gates {learnState.summary.gateLessons || 0}
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <ProgressBar value={learnState.summary.completionPercent || 0} color={learnState.course?.courseColor || '#2563eb'} />
                  </div>
                  <div className="row g-2 small">
                    <div className="col-sm-3 text-muted">Completed: <span className="text-dark fw-semibold">{learnState.summary.completedLessons || 0}</span></div>
                    <div className="col-sm-3 text-muted">In progress: <span className="text-dark fw-semibold">{learnState.summary.inProgressLessons || 0}</span></div>
                    <div className="col-sm-3 text-muted">Average score: <span className="text-dark fw-semibold">{learnState.summary.averageScore ?? '-'}</span></div>
                    <div className="col-sm-3 text-muted">Next lesson: <span className="text-dark fw-semibold">{learnState.summary.nextLessonId && String(activeLesson?._id) !== String(learnState.summary.nextLessonId) ? 'Available' : (learnState.summary.nextLessonId ? 'Open now' : '-')}</span></div>
                    <div className="col-sm-3 text-muted">Streak: <span className="text-dark fw-semibold">{consistency.currentStreak || 0} day{Number(consistency.currentStreak || 0) === 1 ? '' : 's'}</span></div>
                    <div className="col-sm-3 text-muted">Active in 7 days: <span className="text-dark fw-semibold">{consistency.activeDaysLast7 || 0}</span></div>
                    <div className="col-sm-3 text-muted">Watch time: <span className="text-dark fw-semibold">{fmtDuration(consistency.watchedSecondsTotal || 0)}</span></div>
                    <div className="col-sm-3 text-muted">Status: <span className={`badge bg-${consistencyTone(consistency.consistencyStatus)}`}>{String(consistency.consistencyStatus || 'new').replace(/_/g, ' ')}</span></div>
                  </div>
                  {!!sectionProgress.length && (
                    <div className="mt-3">
                      <div className="small fw-semibold mb-2">Completion by topic</div>
                      <div className="row g-2">
                        {sectionProgress.map((section) => (
                          <div key={section.sectionId || section.title} className="col-12 col-lg-6">
                            <div className="border bg-white" style={{ borderRadius: 16, padding: 12, borderColor: '#e2e8f0' }}>
                              <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
                                <div className="small fw-semibold text-truncate">{section.title}</div>
                                <span className="small text-muted">{section.completionPercent || 0}%</span>
                              </div>
                              <ProgressBar value={section.completionPercent || 0} color={learnState.course?.courseColor || '#2563eb'} />
                              <div className="small text-muted mt-2">
                                {section.completedLessons || 0}/{section.totalLessons || 0} completed
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                      </>
                    );
                  })()}
                </div>
              )}
              {activeLesson ? (
                <>
                  {!canWrite && activeLesson.access?.isLocked && (
                    <div className="alert alert-warning">
                      Complete the prerequisite lesson{(activeLesson.access?.missingRequiredIds?.length || 0) === 1 ? '' : 's'} before opening this content.
                    </div>
                  )}
                  {(!activeLesson.access?.isLocked || canWrite) && (
                    <>
                  <div className="border mb-3 bg-white" style={{ borderRadius: 22, padding: 18, borderColor: '#e2e8f0' }}>
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                      <div>
                        <div className="small text-muted mb-1">{activeLesson.sectionTitle || 'Lesson section'}</div>
                        <div className="fw-semibold">{activeLesson.title}</div>
                        <div className="small text-muted mt-1">
                          {formatLessonTypeLabel(activeLesson.lessonType)}
                          {activeLesson.durationSeconds ? ` | ${fmtDuration(activeLesson.durationSeconds)}` : ''}
                          {activeLesson.progress?.lastAccessedAt ? ` | Last opened ${fmtDate(activeLesson.progress.lastAccessedAt)}` : ''}
                        </div>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {previousLesson && (
                          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openLesson(previousLesson._id)}>
                            Previous
                          </button>
                        )}
                        {nextLesson && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={!canWrite && nextLesson.access?.isLocked}
                            onClick={() => openLesson(nextLesson._id)}
                          >
                            Next
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {['promo_video', 'promo_image', 'quiz_gate'].includes(String(activeLesson.lessonType || '')) && (
                    <div className="alert alert-info">
                      This lesson is a required gate. Students must complete it before later gated lessons unlock.
                    </div>
                  )}
                  {activeLesson.lessonType === 'uploaded_video' && activeLesson.uploadedVideoUrl && (
                    <div className="mb-3" style={{ borderRadius: 22, overflow: 'hidden', background: '#0f172a' }}>
                      <TrackedLessonVideo lesson={activeLesson} onProgress={handleVideoWatchProgress} className="w-100 d-block" />
                    </div>
                  )}
                  {activeLesson.lessonType === 'embedded_video' && activeLesson.embeddedVideoUrl && (
                    <div className="ratio ratio-16x9 mb-3" style={{ borderRadius: 22, overflow: 'hidden' }}>
                      <iframe title={activeLesson.title} src={activeLesson.embeddedVideoUrl} allowFullScreen />
                    </div>
                  )}
                  {activeLesson.lessonType === 'promo_video' && activeLesson.uploadedVideoUrl && (
                    <div className="mb-3" style={{ borderRadius: 22, overflow: 'hidden', background: '#0f172a' }}>
                      <TrackedLessonVideo lesson={activeLesson} onProgress={handleVideoWatchProgress} className="w-100 d-block" />
                    </div>
                  )}
                  {activeLesson.lessonType === 'promo_video' && !activeLesson.uploadedVideoUrl && activeLesson.embeddedVideoUrl && (
                    <div className="ratio ratio-16x9 mb-3" style={{ borderRadius: 22, overflow: 'hidden' }}>
                      <iframe title={activeLesson.title} src={activeLesson.embeddedVideoUrl} allowFullScreen />
                    </div>
                  )}
                  {activeLesson.lessonType === 'promo_image' && activeLesson.imageUrl && (
                    <img src={activeLesson.imageUrl} alt={activeLesson.title} className="img-fluid mb-3" style={{ borderRadius: 22 }} />
                  )}
                  {activeLesson.summary && <p className="text-muted">{activeLesson.summary}</p>}
                  {activeLesson.htmlContent && <div className="border p-3 mb-3" style={{ borderRadius: 20, borderColor: '#e2e8f0' }} dangerouslySetInnerHTML={{ __html: activeLesson.htmlContent }} />}
                  {activeLesson.lessonType === 'linked_quiz' && <LinkedQuizAttempt lesson={activeLesson} notify={notify} onCompleted={async () => { await loadOutline(selectedCourseId); await refreshCore(); }} />}
                  {activeLesson.lessonType === 'quiz_gate' && (
                    <div className="border p-3 mb-3" style={{ borderRadius: 20, background: '#fffaf0', borderColor: '#fde68a' }}>
                      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                        <div className="fw-semibold">Quiz Gate</div>
                        <div className="small text-muted">
                          Pass score: {activeLesson.quizConfig?.passPercent ?? 60}%
                          {activeLesson.progress?.score !== null && activeLesson.progress?.score !== undefined ? ` | Current score: ${activeLesson.progress.score}%` : ''}
                        </div>
                      </div>
                      {!activeQuizQuestions.length ? (
                        <div className="text-muted small">No quiz questions are configured for this lesson yet.</div>
                      ) : (
                        <div className="d-flex flex-column gap-3">
                          {activeQuizQuestions.map((question, questionIndex) => (
                            <div key={`student-quiz-${questionIndex}`} className="border rounded p-3 bg-white">
                              <div className="fw-semibold mb-2">{questionIndex + 1}. {question.question}</div>
                              <div className="d-flex flex-column gap-2">
                                {(question.options || []).map((option, optionIndex) => (
                                  <label key={`student-quiz-option-${questionIndex}-${optionIndex}`} className="form-check border rounded p-2">
                                    <input
                                      className="form-check-input"
                                      type="radio"
                                      name={`student-quiz-option-${questionIndex}`}
                                      checked={Number(quizAnswers[questionIndex]) === optionIndex}
                                      onChange={() => setQuizAnswers((prev) => ({ ...prev, [questionIndex]: optionIndex }))}
                                    />
                                    <span className="form-check-label ms-2">{option}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {activeLesson.resourceUrl && (
                    <div className="mb-3">
                      <a href={activeLesson.resourceUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                        Open Resource
                      </a>
                    </div>
                  )}
                  {!canWrite && (
                    <div className="d-flex flex-wrap gap-2">
                      {activeLesson.lessonType === 'quiz_gate' ? (
                        <button className="btn btn-primary btn-sm" disabled={saving || !activeQuizQuestions.length} onClick={submitQuizGate}>
                          Submit Quiz
                        </button>
                      ) : activeLesson.lessonType === 'linked_quiz' ? null : (
                        <button className="btn btn-primary btn-sm" disabled={saving || (!canWrite && !isLessonWatchRequirementMet(activeLesson))} onClick={markLessonComplete}>
                          Mark as Completed
                        </button>
                      )}
                      {nextAvailableLesson && String(nextAvailableLesson._id) !== String(activeLesson._id) && (
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openLesson(nextAvailableLesson._id)}>
                          Open Next Available Lesson
                        </button>
                      )}
                    </div>
                  )}
                    </>
                  )}
                </>
              ) : (
                <div className="text-muted">Select a course and lesson to begin.</div>
              )}
            </CardShell>
            ) : (
            <StudentSurface style={{ padding: 24 }}>
              <StudentSectionTitle
                eyebrow={learnState.course?.category || 'Learning Player'}
                title={activeLesson?.title || learnState.course?.courseName || 'Lesson Player'}
                subtitle={learnState.course?.courseName ? `Inside ${learnState.course.courseName}` : 'Select a course and lesson to begin.'}
                action={(
                  <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                    {learnState.summary ? <Pill color="success">{learnState.summary.completedLessons}/{learnState.summary.totalLessons} complete</Pill> : null}
                    {!!activeLesson && <Pill color="secondary">{formatLessonTypeLabel(activeLesson.lessonType)}</Pill>}
                  </div>
                )}
              />
              {learnState.summary && (
                <div className="border mb-3" style={{ borderRadius: 22, padding: 18, background: '#f8fafc', borderColor: '#e2e8f0' }}>
                  <div className="d-flex flex-wrap justify-content-between gap-2 align-items-center mb-2">
                    <div className="fw-semibold">Learning Progress</div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {nextAvailableLesson && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => openLesson(nextAvailableLesson._id)}
                        >
                          Continue Next
                        </button>
                      )}
                      <div className="small text-muted">
                        Accessible {learnState.summary.accessibleLessons || 0} | Locked {learnState.summary.lockedLessons || 0} | Gates {learnState.summary.gateLessons || 0}
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <ProgressBar value={learnState.summary.completionPercent || 0} color={learnState.course?.courseColor || '#2563eb'} />
                  </div>
                  <div className="row g-2 small">
                    <div className="col-sm-3 text-muted">Completed: <span className="text-dark fw-semibold">{learnState.summary.completedLessons || 0}</span></div>
                    <div className="col-sm-3 text-muted">In progress: <span className="text-dark fw-semibold">{learnState.summary.inProgressLessons || 0}</span></div>
                    <div className="col-sm-3 text-muted">Average score: <span className="text-dark fw-semibold">{learnState.summary.averageScore ?? '-'}</span></div>
                    <div className="col-sm-3 text-muted">Next lesson: <span className="text-dark fw-semibold">{learnState.summary.nextLessonId && String(activeLesson?._id) !== String(learnState.summary.nextLessonId) ? 'Available' : (learnState.summary.nextLessonId ? 'Open now' : '-')}</span></div>
                  </div>
                </div>
              )}
              {activeLesson ? (
                <>
                  {activeLesson.access?.isLocked && (
                    <div className="alert alert-warning">
                      Complete the prerequisite lesson{(activeLesson.access?.missingRequiredIds?.length || 0) === 1 ? '' : 's'} before opening this content.
                    </div>
                  )}
                  {!activeLesson.access?.isLocked && (
                    <>
                      <div className="border mb-3 bg-white" style={{ borderRadius: 22, padding: 18, borderColor: '#e2e8f0' }}>
                        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                          <div>
                            <div className="small text-muted mb-1">{activeLesson.sectionTitle || 'Lesson section'}</div>
                            <div className="fw-semibold" style={{ fontSize: 22, lineHeight: 1.2 }}>{activeLesson.title}</div>
                            <div className="small text-muted mt-1">
                              {formatLessonTypeLabel(activeLesson.lessonType)}
                              {activeLesson.durationSeconds ? ` | ${fmtDuration(activeLesson.durationSeconds)}` : ''}
                              {activeLesson.progress?.lastAccessedAt ? ` | Last opened ${fmtDate(activeLesson.progress.lastAccessedAt)}` : ''}
                            </div>
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            {previousLesson && (
                              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openLesson(previousLesson._id)}>
                                Previous
                              </button>
                            )}
                            {nextLesson && (
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                disabled={nextLesson.access?.isLocked}
                                onClick={() => openLesson(nextLesson._id)}
                              >
                                Next
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      {['promo_video', 'promo_image', 'quiz_gate'].includes(String(activeLesson.lessonType || '')) && (
                        <div className="alert alert-info">
                          This lesson is a required gate. Students must complete it before later gated lessons unlock.
                        </div>
                      )}
                      {activeLesson.lessonType === 'uploaded_video' && activeLesson.uploadedVideoUrl && (
                        <div className="mb-3" style={{ borderRadius: 24, overflow: 'hidden', background: '#020617', boxShadow: '0 30px 60px rgba(15, 23, 42, 0.22)' }}>
                          <div className="ratio ratio-16x9">
                            <TrackedLessonVideo lesson={activeLesson} onProgress={handleVideoWatchProgress} className="w-100 h-100 d-block" style={{ objectFit: 'contain' }} />
                          </div>
                        </div>
                      )}
                      {activeLesson.lessonType === 'embedded_video' && activeLesson.embeddedVideoUrl && (
                        <div className="ratio ratio-16x9 mb-3" style={{ borderRadius: 24, overflow: 'hidden', boxShadow: '0 30px 60px rgba(15, 23, 42, 0.16)' }}>
                          <iframe title={activeLesson.title} src={activeLesson.embeddedVideoUrl} allowFullScreen />
                        </div>
                      )}
                      {activeLesson.lessonType === 'promo_video' && activeLesson.uploadedVideoUrl && (
                        <div className="mb-3" style={{ borderRadius: 24, overflow: 'hidden', background: '#020617', boxShadow: '0 30px 60px rgba(15, 23, 42, 0.22)' }}>
                          <div className="ratio ratio-16x9">
                            <TrackedLessonVideo lesson={activeLesson} onProgress={handleVideoWatchProgress} className="w-100 h-100 d-block" style={{ objectFit: 'contain' }} />
                          </div>
                        </div>
                      )}
                      {activeLesson.lessonType === 'promo_video' && !activeLesson.uploadedVideoUrl && activeLesson.embeddedVideoUrl && (
                        <div className="ratio ratio-16x9 mb-3" style={{ borderRadius: 24, overflow: 'hidden', boxShadow: '0 30px 60px rgba(15, 23, 42, 0.16)' }}>
                          <iframe title={activeLesson.title} src={activeLesson.embeddedVideoUrl} allowFullScreen />
                        </div>
                      )}
                      {activeLesson.lessonType === 'promo_image' && activeLesson.imageUrl && (
                        <img src={activeLesson.imageUrl} alt={activeLesson.title} className="img-fluid mb-3" style={{ borderRadius: 24, width: '100%' }} />
                      )}
                      {activeLesson.summary && <p className="text-muted fs-6">{activeLesson.summary}</p>}
                      {activeLesson.htmlContent && <div className="border p-4 mb-3 bg-white" style={{ borderRadius: 20, borderColor: '#e2e8f0' }} dangerouslySetInnerHTML={{ __html: activeLesson.htmlContent }} />}
                      {activeLesson.lessonType === 'linked_quiz' && <LinkedQuizAttempt lesson={activeLesson} notify={notify} onCompleted={async () => { await loadOutline(selectedCourseId); await refreshCore(); }} />}
                      {activeLesson.lessonType === 'quiz_gate' && (
                        <div className="border p-3 mb-3" style={{ borderRadius: 20, background: '#fffaf0', borderColor: '#fde68a' }}>
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                            <div className="fw-semibold">Quiz Gate</div>
                            <div className="small text-muted">
                              Pass score: {activeLesson.quizConfig?.passPercent ?? 60}%
                              {activeLesson.progress?.score !== null && activeLesson.progress?.score !== undefined ? ` | Current score: ${activeLesson.progress.score}%` : ''}
                            </div>
                          </div>
                          {!activeQuizQuestions.length ? (
                            <div className="text-muted small">No quiz questions are configured for this lesson yet.</div>
                          ) : (
                            <div className="d-flex flex-column gap-3">
                              {activeQuizQuestions.map((question, questionIndex) => (
                                <div key={`student-quiz-${questionIndex}`} className="border rounded p-3 bg-white">
                                  <div className="fw-semibold mb-2">{questionIndex + 1}. {question.question}</div>
                                  <div className="d-flex flex-column gap-2">
                                    {(question.options || []).map((option, optionIndex) => (
                                      <label key={`student-quiz-option-${questionIndex}-${optionIndex}`} className="form-check border rounded p-2">
                                        <input
                                          className="form-check-input"
                                          type="radio"
                                          name={`student-quiz-option-${questionIndex}`}
                                          checked={Number(quizAnswers[questionIndex]) === optionIndex}
                                          onChange={() => setQuizAnswers((prev) => ({ ...prev, [questionIndex]: optionIndex }))}
                                        />
                                        <span className="form-check-label ms-2">{option}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {activeLesson.resourceUrl && (
                        <div className="mb-3">
                          <a href={activeLesson.resourceUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                            Open Resource
                          </a>
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-2">
                        {activeLesson.lessonType === 'quiz_gate' ? (
                          <button className="btn btn-primary btn-sm" disabled={saving || !activeQuizQuestions.length} onClick={submitQuizGate}>
                            Submit Quiz
                          </button>
                        ) : activeLesson.lessonType === 'linked_quiz' ? null : (
                          <button className="btn btn-primary btn-sm" disabled={saving || (!canWrite && !isLessonWatchRequirementMet(activeLesson))} onClick={markLessonComplete}>
                            Mark as Completed
                          </button>
                        )}
                        {nextAvailableLesson && String(nextAvailableLesson._id) !== String(activeLesson._id) && (
                          <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openLesson(nextAvailableLesson._id)}>
                            Open Next Available Lesson
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="text-muted">Select a course and lesson to begin.</div>
              )}
            </StudentSurface>
            )}
          </div>
        </div>
      )}

      {activeTab === 'subscription-details' && !canWrite && (
        <div className="student-account-page">
          <StudentAccountPageHeader
            icon="bi-credit-card-2-front"
            title="My Subscription"
            subtitle="Your current membership, access period, benefits, and usage in one place."
            onBack={() => openTab('my-subscription')}
          />
          <div className="student-account-feature-card">
            <span className="student-account-feature-icon"><i className="bi bi-stars" /></span>
            <div className="student-account-feature-copy">
              <small>Current membership</small>
              <h2>{studentSubscriptionUsage.planName || 'No active membership'}</h2>
              <p>{studentSubscriptionUsage.plan?.description || 'Your access is based on the active membership assigned to your account.'}</p>
              <div className="student-account-meta">
                <span><i className="bi bi-calendar3" />{studentSubscriptionDetails.billingCycle}</span>
                <span><i className="bi bi-clock-history" />Renews or expires {fmtDate(studentSubscriptionDetails.nextBillingDate)}</span>
                <span><i className="bi bi-patch-check" />{studentSubscriptionUsage.renewalState}</span>
              </div>
            </div>
            <div className="student-account-feature-price"><small>Last paid / plan value</small><strong>{studentSubscriptionDetails.amountLabel}</strong></div>
          </div>
          <div className="student-account-two-column">
            <section className="student-account-panel">
              <div className="student-account-section-title"><span><i className="bi bi-check2-circle" /></span><div><h2>Included access</h2><p>Benefits from your current plan configuration.</p></div></div>
              <div className="student-account-benefit-list">
                {(studentCurrentPlanBenefits.length ? studentCurrentPlanBenefits : ['Access follows your assigned membership rules.']).map((benefit) => (
                  <div key={benefit}><i className="bi bi-check-lg" /><span>{benefit}</span></div>
                ))}
              </div>
            </section>
            <section className="student-account-panel">
              <div className="student-account-section-title"><span><i className="bi bi-bar-chart" /></span><div><h2>Membership usage</h2><p>Based on your current learning records.</p></div></div>
              <div className="student-account-stat-grid">
                {[
                  { label: 'Courses', value: studentSubscriptionUsage.enrolledCount || 0, icon: 'bi-book-half' },
                  { label: 'Learning hours', value: studentSubscriptionUsage.totalHours || 0, icon: 'bi-clock' },
                  { label: 'Certificates', value: studentSubscriptionUsage.certificates || 0, icon: 'bi-award' },
                  { label: 'Resources', value: studentSubscriptionUsage.downloads || 0, icon: 'bi-download' },
                ].map((item) => <div key={item.label}><i className={`bi ${item.icon}`} /><strong>{item.value}</strong><span>{item.label}</span></div>)}
              </div>
            </section>
          </div>
          <div className="student-account-actions">
            <button type="button" className="btn btn-outline-primary" onClick={() => openTab('billing')}><i className="bi bi-receipt me-2" />Billing history</button>
            {studentSubscriptionUsage.plan?._id && Number(studentSubscriptionUsage.plan.salePrice || studentSubscriptionUsage.plan.price || 0) > 0 && <button type="button" className="btn btn-primary" onClick={() => openCheckout(studentSubscriptionUsage.plan)}><i className="bi bi-arrow-repeat me-2" />Renew membership</button>}
          </div>
        </div>
      )}

      {activeTab === 'saved-courses' && !canWrite && (
        <div className="student-account-page">
          <StudentAccountPageHeader
            icon="bi-bookmark-heart"
            title="Saved Courses"
            subtitle="Programs you saved while exploring the course catalog."
            onBack={() => openTab('my-subscription')}
            action={<button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openTab('for-you')}>Explore courses</button>}
          />
          {wishlistedCatalogCourses.length ? (
            <div className="student-saved-course-grid">
              {wishlistedCatalogCourses.map((course) => (
                <article key={course._id} className="student-saved-course-card">
                  <CourseArtwork title={course.courseName} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#4f46e5'} height={132} compact />
                  <div className="student-saved-course-body">
                    <span>{formatCourseCategoryLabel(course.category || inferCourseMode(course))}</span>
                    <h2>{course.courseName || 'Course'}</h2>
                    <p>{course.shortDescription || course.description || 'Open this course to review its learning path and available access.'}</p>
                    <div>
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => openStudentCourseCard(course)}>{getLearningFocusedCourseCta(course)} <i className="bi bi-arrow-right ms-1" /></button>
                      <button type="button" className="student-saved-remove" onClick={() => toggleWishlist(course._id)} aria-label={`Remove ${course.courseName || 'course'} from saved courses`}><i className="bi bi-bookmark-x" /></button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="student-account-empty"><i className="bi bi-bookmark" /><h2>No saved courses yet</h2><p>Use the bookmark or heart action while exploring courses, and they will appear here.</p><button type="button" className="btn btn-primary" onClick={() => openTab('for-you')}>Explore courses</button></div>
          )}
        </div>
      )}

      {activeTab === 'billing' && !canWrite && (
        <div className="student-account-page">
          <StudentAccountPageHeader
            icon="bi-receipt-cutoff"
            title="Billing & Payments"
            subtitle="Review membership orders and the payment providers available at checkout."
            onBack={() => openTab('my-subscription')}
          />
          <div className="student-account-two-column student-billing-layout">
            <section className="student-account-panel">
              <div className="student-account-section-title"><span><i className="bi bi-clock-history" /></span><div><h2>Order history</h2><p>Amounts below come from the membership order records.</p></div></div>
              {studentBillingOrders.length ? <div className="student-billing-list">
                {studentBillingOrders.map((order) => (
                  <div key={order._id}>
                    <span className={`student-order-status is-${String(order.status || 'created').replace('_', '-')}`}><i className="bi bi-circle-fill" />{toTitleWords(order.status || 'created')}</span>
                    <div><strong>{order.planId?.name || order.planSnapshot?.name || 'Membership order'}</strong><small>{fmtDateTime(order.paidAt || order.createdAt)} · {toTitleWords(order.provider || 'payment')}</small></div>
                    <div className="student-billing-amount"><strong>{fmtMoney(order.pricing?.finalAmount || 0)}</strong>{Number(order.pricing?.discountAmount || 0) > 0 && <small>Saved {fmtMoney(order.pricing.discountAmount)}</small>}</div>
                  </div>
                ))}
              </div> : <div className="student-account-inline-empty"><i className="bi bi-receipt" /><span>No membership orders are available yet.</span></div>}
            </section>
            <section className="student-account-panel">
              <div className="student-account-section-title"><span><i className="bi bi-shield-lock" /></span><div><h2>Payment providers</h2><p>A method is selected during each secure checkout.</p></div></div>
              <div className="student-payment-provider-list">
                {(paymentProviders.providers || []).map((provider) => <div key={provider.key} className={!provider.enabled ? 'is-disabled' : ''}><span><i className={`bi ${provider.key === 'razorpay' ? 'bi-lightning-charge-fill' : 'bi-shield-check'}`} /></span><div><strong>{provider.label}</strong><small>{provider.message}</small></div><b>{provider.enabled ? 'Available' : 'Unavailable'}</b></div>)}
              </div>
              <div className="student-account-note"><i className="bi bi-info-circle" />Saved cards or UPI accounts are not stored by this LMS. Payment details are handled by the selected provider.</div>
            </section>
          </div>
        </div>
      )}

      {activeTab === 'downloads' && !canWrite && (
        <div className="student-account-page">
          <StudentAccountPageHeader
            icon="bi-folder2-open"
            title="Learning Resources"
            subtitle="Open downloadable files and reference links attached to your accessible lessons."
            onBack={() => openTab('my-subscription')}
          />
          <div className="student-resource-course-picker" aria-label="Choose a course">
            {learning.map((row) => {
              const course = row.course || {};
              const courseId = String(course._id || row.course || '');
              return <button type="button" key={courseId} className={String(selectedCourseId) === courseId ? 'is-active' : ''} onClick={() => setSelectedCourseId(courseId)}><i className="bi bi-book" /><span>{course.courseName || 'Course'}</span></button>;
            })}
          </div>
          <section className="student-account-panel">
            <div className="student-account-section-title"><span><i className="bi bi-download" /></span><div><h2>{learnState.course?.courseName || selectedLearningCourse?.course?.courseName || 'Course resources'}</h2><p>{studentResourceLessons.length} attached {studentResourceLessons.length === 1 ? 'resource' : 'resources'} available.</p></div></div>
            {studentResourceLessons.length ? <div className="student-resource-list">
              {studentResourceLessons.map((lesson) => <a key={lesson._id} href={lesson.resourceUrl} target="_blank" rel="noreferrer"><span><i className="bi bi-file-earmark-arrow-down" /></span><div><strong>{lesson.title || 'Lesson resource'}</strong><small>{lesson.sectionTitle}</small></div><i className="bi bi-box-arrow-up-right" /></a>)}
            </div> : <div className="student-account-inline-empty"><i className="bi bi-folder2" /><span>No resource files are attached to this course yet. Choose another course above to check its lessons.</span></div>}
          </section>
        </div>
      )}

      {activeTab === 'share-programs' && !canWrite && (
        <div className="student-account-page">
          <StudentAccountPageHeader icon="bi-share" title="Share Programs" subtitle="Help friends discover published programs from the current course catalog." onBack={() => openTab('my-subscription')} />
          <div className="student-share-banner"><span><i className="bi bi-people" /></span><div><h2>Learning is better when it is shared</h2><p>Send a program link using your device share menu or copy it to your clipboard.</p></div></div>
          <div className="student-share-program-grid">
            {(studentRecommendedCourses.length ? studentRecommendedCourses : catalog.slice(0, 6)).map((course) => <article key={course._id}><CourseArtwork title={course.courseName} imageUrl={course.thumbnailUrl || course.promoImageUrl} accent={course.courseColor || '#7c3aed'} height={108} compact /><div><small>{formatCourseCategoryLabel(course.category || inferCourseMode(course))}</small><h2>{course.courseName || 'Learning program'}</h2><button type="button" onClick={() => shareStudentProgram(course)}><i className="bi bi-share me-2" />Share program</button></div></article>)}
          </div>
        </div>
      )}

      {activeTab === 'help-center' && !canWrite && (
        <div className="student-account-page">
          <StudentAccountPageHeader icon="bi-question-circle" title="Help Center" subtitle="Quick answers for membership, learning access, payments, and course resources." onBack={() => openTab('my-subscription')} />
          <div className="student-help-grid">
            {[
              { icon: 'bi-credit-card', title: 'Membership access', text: 'Your active plan, access period, and included learning are listed under My Subscription.' },
              { icon: 'bi-receipt', title: 'Payment support', text: 'Billing & Payments shows the provider, order status, discount, and final amount recorded for each order.' },
              { icon: 'bi-book', title: 'Course access', text: 'My Courses contains enrolled courses and courses unlocked by your current membership.' },
              { icon: 'bi-download', title: 'Lesson resources', text: 'Learning Resources lists files and reference links attached to accessible course lessons.' },
            ].map((item) => <article key={item.title}><span><i className={`bi ${item.icon}`} /></span><div><h2>{item.title}</h2><p>{item.text}</p></div></article>)}
          </div>
          <section className="student-account-panel student-help-contact"><span><i className="bi bi-headset" /></span><div><h2>Still need help?</h2><p>Start a conversation with the support team and include the course or order name involved.</p></div><button type="button" className="btn btn-primary btn-sm" onClick={() => navigate('/chat')}>Open support chat</button></section>
        </div>
      )}

      {activeTab === 'advisor-contact' && !canWrite && (
        <div className="student-account-page student-advisor-page">
          <StudentAccountPageHeader
            icon="bi-headset"
            title="Talk to an Advisor"
            subtitle="Contact the student guidance team for program, membership, career, or payment support."
            onBack={() => openTab('my-subscription')}
          />
          <section className="student-advisor-hero">
            <span><i className="bi bi-chat-heart" /></span>
            <div>
              <small>{studentAdvisorContact.organization}</small>
              <h2>Get guidance before your next step</h2>
              <p>Speak with the team about choosing a program, upgrading your membership, course access, or completing a payment.</p>
            </div>
          </section>
          <div className="student-advisor-contact-grid">
            <article>
              <span><i className="bi bi-envelope" /></span>
              <div><small>Email</small><strong>{studentAdvisorContact.email || 'Not configured'}</strong><p>Send your question and include the course or plan name.</p></div>
              {studentAdvisorContact.email && <a href={`mailto:${studentAdvisorContact.email}?subject=${encodeURIComponent('Student guidance request')}`}>Send email <i className="bi bi-arrow-up-right" /></a>}
            </article>
            <article>
              <span><i className="bi bi-telephone" /></span>
              <div><small>Phone</small><strong>{studentAdvisorContact.phone || 'Not configured'}</strong><p>Call the institution directly for enrollment or payment guidance.</p></div>
              {studentAdvisorContact.phone && <a href={`tel:${String(studentAdvisorContact.phone).replace(/[^+\d]/g, '')}`}>Call now <i className="bi bi-arrow-up-right" /></a>}
            </article>
            {studentAdvisorContact.website && (
              <article>
                <span><i className="bi bi-globe2" /></span>
                <div><small>Website</small><strong>{studentAdvisorContact.website}</strong><p>Visit the institution website for published information.</p></div>
                <a href={/^https?:\/\//i.test(studentAdvisorContact.website) ? studentAdvisorContact.website : `https://${studentAdvisorContact.website}`} target="_blank" rel="noreferrer">Open website <i className="bi bi-arrow-up-right" /></a>
              </article>
            )}
            {studentAdvisorContact.address && (
              <article>
                <span><i className="bi bi-geo-alt" /></span>
                <div><small>Office</small><strong>{studentAdvisorContact.address}</strong><p>Use this address for an in-person enquiry when applicable.</p></div>
              </article>
            )}
          </div>
          <section className="student-advisor-support-strip">
            <div><i className="bi bi-chat-dots" /><span><strong>Need in-app support?</strong><small>Start a support conversation without leaving the LMS.</small></span></div>
            <button type="button" onClick={() => navigate('/chat')}>Open support chat <i className="bi bi-arrow-right" /></button>
          </section>
        </div>
      )}

      {activeTab === 'my-subscription' && !canWrite && studentHasSubscriptionTab && (
        <div className="d-flex flex-column gap-3">
          <StudentSurface
            bordered={false}
            className="student-subscription-page"
            style={{
              padding: 0,
              borderRadius: 0,
              background: 'transparent',
              border: 'none',
              boxShadow: 'none',
            }}
          >
            <div className="d-flex flex-column gap-3">
              <div>
                <div>
                  <div className="fw-semibold" style={{ color: '#111827', fontSize: isStudentMobile ? 21 : 28 }}>Subscription</div>
                  <div style={{ color: '#64748b', fontSize: isStudentMobile ? 11 : 13 }}>Review your current learning access, usage, billing status, and support options.</div>
                </div>
              </div>

              <div
                className="border position-relative"
                style={{
                  borderRadius: 10,
                  padding: isStudentMobile ? '46px 14px 14px' : '22px 22px 20px',
                  background: 'linear-gradient(180deg, #ffffff 0%, #f7f8ff 100%)',
                  borderColor: '#e6e9ff',
                  boxShadow: '0 16px 34px rgba(99,102,241,0.08)',
                }}
              >
                <span className="badge position-absolute" style={{ top: 12, right: 12, borderRadius: 6, padding: '6px 8px', color: '#3730a3', background: '#e0e7ff', border: '1px solid #c7d2fe', fontSize: 10 }}>
                  <i className="bi bi-patch-check-fill me-1" />{studentSubscriptionUsage.planName || 'Free Plan'}
                </span>
                <div className={`d-flex ${isStudentMobile ? 'flex-column' : 'align-items-center justify-content-between'} gap-3`}>
                  <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                    <div
                      className="d-inline-flex align-items-center justify-content-center text-white fw-semibold"
                      style={{
                        width: isStudentMobile ? 52 : 62,
                        height: isStudentMobile ? 52 : 62,
                        borderRadius: 10,
                        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                        fontSize: isStudentMobile ? 20 : 24,
                        boxShadow: '0 12px 24px rgba(79,70,229,0.16)',
                        flexShrink: 0,
                      }}
                    >
                      {studentProfileInitials}
                    </div>
                    <div className="flex-fill" style={{ minWidth: 0 }}>
                      <div className="text-uppercase fw-semibold mb-1" style={{ color: '#6366f1', fontSize: 10 }}>Your learning membership</div>
                      <div className="fw-semibold text-truncate" style={{ color: '#111827', fontSize: isStudentMobile ? 17 : 20, lineHeight: 1.15 }}>
                        {studentProfileName}
                      </div>
                      <div className="mt-1" style={{ color: '#64748b', fontSize: isStudentMobile ? 11 : 12, lineHeight: 1.35 }}>
                        Learn at your pace today and unlock guided career growth whenever you are ready.
                      </div>
                    </div>
                  </div>

                  <button type="button" className={`btn btn-outline-primary flex-shrink-0 ${isStudentMobile ? 'w-100' : ''}`} style={{ borderRadius: 8, padding: '8px 11px', fontSize: 12, fontWeight: 600 }} onClick={() => openTab('for-you')}>
                    <i className="bi bi-compass me-2" />Explore Courses
                  </button>
                </div>
                <div className="mt-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                  {[
                    { label: 'Courses', value: studentSubscriptionUsage.enrolledCount || 0, icon: 'bi-book-half', color: '#4f46e5', bg: '#eef2ff' },
                    { label: 'Certificates', value: studentSubscriptionUsage.certificates || 0, icon: 'bi-award-fill', color: '#d97706', bg: '#fff7ed' },
                    { label: 'Learning Hours', value: studentSubscriptionUsage.totalHours || 0, icon: 'bi-clock-history', color: '#059669', bg: '#ecfdf5' },
                  ].map((item) => (
                    <div key={`subscription-summary-${item.label}`} className="d-flex align-items-center gap-2" style={{ minWidth: 0, padding: isStudentMobile ? '8px 6px' : '9px 10px', borderRadius: 8, background: '#ffffff', border: '1px solid #e5e7eb' }}>
                      <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 7, color: item.color, background: item.bg }}><i className={`bi ${item.icon}`} /></span>
                      <span style={{ minWidth: 0 }}>
                        <span className="d-block fw-semibold" style={{ color: '#111827', fontSize: 14, lineHeight: 1 }}>{item.value}</span>
                        <span className="d-block text-truncate" style={{ color: '#64748b', fontSize: isStudentMobile ? 8 : 10 }}>{item.label}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {studentCurrentMembershipIsFree && (
                <section
                  className={`d-flex ${isStudentMobile ? 'flex-column align-items-stretch' : 'align-items-center'} gap-3 border`}
                  style={{
                    padding: isStudentMobile ? 14 : 18,
                    borderRadius: 10,
                    borderColor: '#c7d2fe',
                    background: 'linear-gradient(135deg, #eef2ff 0%, #ffffff 55%, #ecfdf5 100%)',
                    boxShadow: '0 12px 28px rgba(79,70,229,0.08)',
                  }}
                >
                  <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 46, height: 46, borderRadius: 9, color: '#ffffff', background: 'linear-gradient(135deg, #4f46e5, #2563eb)', boxShadow: '0 9px 18px rgba(79,70,229,0.2)' }}>
                    <i className="bi bi-gift-fill" style={{ fontSize: 19 }} />
                  </span>
                  <div className="flex-fill" style={{ minWidth: 0 }}>
                    <div className="fw-semibold" style={{ color: '#3730a3', fontSize: isStudentMobile ? 14 : 16 }}>Your Free Exclusive Membership</div>
                    <div className="fw-semibold mt-1" style={{ color: '#111827', fontSize: isStudentMobile ? 18 : 21 }}>{studentCurrentMembershipDisplayName}</div>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {(studentCurrentPlanBenefits.length ? studentCurrentPlanBenefits : ['Free course access', 'Recorded lessons', 'Practice quizzes']).slice(0, 3).map((benefit) => (
                        <span key={`free-membership-${benefit}`} className="d-inline-flex align-items-center gap-1" style={{ padding: '4px 7px', borderRadius: 6, color: '#334155', background: '#ffffff', border: '1px solid #e2e8f0', fontSize: isStudentMobile ? 9 : 10 }}>
                          <i className="bi bi-check-circle-fill" style={{ color: '#16a34a' }} />{benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button type="button" className={`btn btn-primary flex-shrink-0 ${isStudentMobile ? 'w-100' : ''}`} style={{ borderRadius: 8, padding: '8px 11px', fontSize: 11, fontWeight: 700 }} onClick={() => openTab('my-courses')}>
                    View Included Courses <i className="bi bi-arrow-right ms-1" />
                  </button>
                </section>
              )}

              <div className="mb-2 d-none" aria-hidden="true">
                <StudentMembershipPlanCollection
                  entries={studentDashboardPlanCards}
                  isMobile={isStudentMobile}
                  activeSubscription={studentActiveSubscription}
                  primaryCategory={studentPrimaryCategory}
                  onCurrent={() => openTab(studentActiveSubscription ? 'my-subscription' : 'my-courses')}
                  onExplore={(plan) => openCheckout(plan)}
                />
              </div>

              <div
                className="border d-none"
                style={{
                  borderRadius: 10,
                  padding: isStudentMobile ? 16 : 22,
                  background: '#ffffff',
                  borderColor: '#d9d6fe',
                  boxShadow: '0 18px 40px rgba(99,102,241,0.08)',
                }}
              >
                <div className="fw-semibold" style={{ color: '#111827', fontSize: isStudentMobile ? 17 : 20, lineHeight: 1.2 }}>
                  Level up with Premium
                </div>
                <div className="mt-1 mb-3" style={{ color: '#6b7280', fontSize: isStudentMobile ? 10 : 12 }}>
                  Unlock unlimited learning and career support.
                </div>
                <div className="row g-3 align-items-stretch">
                  <div className="col-12 col-xl-5">
                    <div className="h-100" style={{ borderRadius: 10, padding: 16, background: '#f8fbff', border: '1px solid #e6eefb' }}>
                      <div className="fw-semibold mb-1" style={{ color: '#4f46e5', fontSize: isStudentMobile ? 10 : 11 }}>
                        Your Current Plan
                      </div>
                      <div className="fw-semibold mb-2" style={{ color: '#111827', fontSize: isStudentMobile ? 15 : 17 }}>
                        {studentSubscriptionUsage.planName || 'Free Plan'}
                      </div>
                      <div className="d-flex flex-column gap-1">
                        {studentCurrentPlanBenefits.length ? studentCurrentPlanBenefits.map((benefit, index) => (
                          <div key={`current-benefit-${index}`} className="d-flex align-items-start gap-2">
                            <i className="bi bi-check-circle-fill" style={{ color: '#22c55e', marginTop: 2, fontSize: 11 }} />
                            <span style={{ color: '#334155', fontSize: isStudentMobile ? 10 : 12, lineHeight: 1.35 }}>{benefit}</span>
                          </div>
                        )) : (
                          <div className="d-flex align-items-start gap-2">
                            <i className="bi bi-check-circle-fill" style={{ color: '#22c55e', marginTop: 2, fontSize: 11 }} />
                            <span style={{ color: '#334155', fontSize: isStudentMobile ? 10 : 12, lineHeight: 1.35 }}>Access to your active learning package</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-xl-2 d-flex align-items-center justify-content-center">
                    <div
                      className="d-inline-flex align-items-center justify-content-center fw-semibold"
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 8,
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        color: '#1f2937',
                        boxShadow: '0 10px 20px rgba(15,23,42,0.08)',
                      }}
                    >
                      VS
                    </div>
                  </div>
                  <div className="col-12 col-xl-5">
                    <div
                      className="h-100 position-relative"
                      style={{
                        borderRadius: 10,
                        padding: 16,
                        background: 'linear-gradient(180deg, #fbf7ff 0%, #f3f0ff 100%)',
                        border: '1px solid #ece9ff',
                      }}
                    >
                      <span
                        className="badge position-absolute"
                        style={{ top: 14, right: 14, borderRadius: 6, background: '#ffe7df', color: '#ea580c', fontSize: 9 }}
                      >
                        Most Popular
                      </span>
                      <div className="fw-semibold mb-2" style={{ color: '#7c3aed', fontSize: isStudentMobile ? 10 : 11, paddingRight: 84 }}>
                        {studentSubscriptionDetails.recommendedPlan ? `Unlock with ${studentSubscriptionDetails.recommendedPlan.name}` : 'Unlock with Pro Plan'}
                      </div>
                      <div className="d-flex flex-column gap-1 mt-3">
                        {studentRecommendedPlanBenefits.length ? studentRecommendedPlanBenefits.map((benefit, index) => (
                          <div key={`recommended-benefit-${index}`} className="d-flex align-items-start gap-2">
                            <i className="bi bi-check-circle-fill" style={{ color: '#4f46e5', marginTop: 2, fontSize: 11 }} />
                            <span style={{ color: '#312e81', fontSize: isStudentMobile ? 10 : 12, lineHeight: 1.35 }}>{benefit}</span>
                          </div>
                        )) : (
                          <>
                            <div className="d-flex align-items-start gap-2">
                              <i className="bi bi-check-circle-fill" style={{ color: '#4f46e5', marginTop: 2, fontSize: 11 }} />
                              <span style={{ color: '#312e81', fontSize: isStudentMobile ? 10 : 12, lineHeight: 1.35 }}>Everything in your current plan</span>
                            </div>
                            <div className="d-flex align-items-start gap-2">
                              <i className="bi bi-check-circle-fill" style={{ color: '#4f46e5', marginTop: 2, fontSize: 11 }} />
                              <span style={{ color: '#312e81', fontSize: isStudentMobile ? 10 : 12, lineHeight: 1.35 }}>Mentor support, certificates and advanced practice</span>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="mt-2" style={{ color: '#6d28d9', fontSize: isStudentMobile ? 9 : 11 }}>
                        <i className="bi bi-gift me-2" />
                        {studentFeaturedCoupon ? `${studentFeaturedCoupon.code} available for this plan` : 'Premium plans unlock more guided learning'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12 col-xl-8">
                  <div className="border overflow-hidden" style={{ borderRadius: 10, background: '#ffffff', borderColor: '#edf2f7', boxShadow: '0 10px 24px rgba(15,23,42,0.05)' }}>
                    {studentProfileOverviewCards.map((item, index) => (
                      <button
                        key={`profile-overview-${item.label}`}
                        type="button"
                        onClick={item.action}
                        className="w-100 d-flex align-items-center justify-content-between gap-3 border-0 bg-transparent text-start"
                        style={{
                          padding: isStudentMobile ? '12px 13px' : '14px 16px',
                          borderBottom: index === studentProfileOverviewCards.length - 1 ? 'none' : '1px solid #eef2f7',
                        }}
                      >
                        <div className="d-flex align-items-start gap-3">
                          <div
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{ width: 36, height: 36, borderRadius: 8, background: item.iconBg, color: item.iconColor, fontSize: 14 }}
                          >
                            <i className={`bi ${item.icon}`} />
                          </div>
                          <div>
                            <div className="fw-semibold" style={{ color: '#111827', fontSize: isStudentMobile ? 11 : 13 }}>{item.label}</div>
                            <div style={{ color: '#6b7280', fontSize: isStudentMobile ? 9 : 11 }}>{item.hint}</div>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <div className="fw-semibold text-end" style={{ color: '#4f46e5', maxWidth: isStudentMobile ? 110 : 180, fontSize: isStudentMobile ? 9 : 11 }}>
                            {item.value}
                          </div>
                          <i className="bi bi-chevron-right" style={{ color: '#94a3b8' }} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="col-12 col-xl-4">
                  <div className="d-flex flex-column gap-3">
                    <div className="border" style={{ borderRadius: 10, padding: 16, background: '#ffffff', borderColor: '#edf2f7', boxShadow: '0 10px 24px rgba(15,23,42,0.05)' }}>
                      <div className="fw-semibold mb-3" style={{ color: '#111827', fontSize: isStudentMobile ? 15 : 17 }}>
                        Usage Snapshot
                      </div>
                      <div className="d-flex flex-column gap-3">
                        {[
                          { label: 'Hours Learned', current: studentSubscriptionUsage.totalHours || 0, total: studentSubscriptionDetails.hoursTarget, color: '#7c3aed' },
                          { label: 'Courses Enrolled', current: studentSubscriptionUsage.enrolledCount || 0, total: studentSubscriptionDetails.coursesTarget, color: '#2563eb' },
                          { label: 'Downloads', current: studentSubscriptionUsage.downloads || 0, total: studentSubscriptionDetails.downloadsTarget, color: '#22c55e' },
                        ].map((item) => {
                          const value = item.total > 0 ? Math.min(100, Math.round((item.current / item.total) * 100)) : 0;
                          return (
                            <div key={`snapshot-${item.label}`}>
                              <div className="d-flex justify-content-between mb-2" style={{ fontSize: isStudentMobile ? 10 : 12 }}>
                                <span style={{ color: '#334155' }}>{item.label}</span>
                                <span style={{ color: '#64748b' }}>{item.current} / {item.total}</span>
                              </div>
                              <ProgressBar value={value} color={item.color} />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border" style={{ borderRadius: 10, padding: 16, background: '#ffffff', borderColor: '#edf2f7', boxShadow: '0 10px 24px rgba(15,23,42,0.05)' }}>
                      <div className="fw-semibold mb-3" style={{ color: '#111827', fontSize: isStudentMobile ? 15 : 17 }}>
                        Subscription Details
                      </div>
                      <div className="d-flex flex-column gap-3">
                        {[
                          { label: 'Plan', value: studentSubscriptionUsage.plan?.name || 'No active membership' },
                          { label: 'Billing Cycle', value: studentSubscriptionDetails.billingCycle },
                          { label: 'Amount', value: studentSubscriptionDetails.amountLabel },
                          { label: 'Payment Method', value: studentSubscriptionDetails.paymentMethod },
                          { label: 'Renewal Date', value: fmtDate(studentSubscriptionDetails.nextBillingDate) },
                        ].map((item) => (
                          <div key={`subscription-detail-${item.label}`} className="d-flex justify-content-between gap-3">
                            <span style={{ color: '#6b7280', fontSize: isStudentMobile ? 10 : 12 }}>{item.label}</span>
                            <span className="fw-semibold text-end" style={{ color: '#111827', fontSize: isStudentMobile ? 10 : 12 }}>{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border overflow-hidden" style={{ borderRadius: 10, background: '#ffffff', borderColor: '#edf2f7', boxShadow: '0 10px 24px rgba(15,23,42,0.05)' }}>
                {studentProfileSupportCards.map((item, index) => (
                  <button
                    key={`profile-support-${item.label}`}
                    type="button"
                    onClick={item.action}
                    className="w-100 d-flex align-items-center justify-content-between gap-3 border-0 bg-transparent text-start"
                    style={{
                      padding: isStudentMobile ? '12px 13px' : '14px 16px',
                      borderBottom: index === studentProfileSupportCards.length - 1 ? 'none' : '1px solid #eef2f7',
                    }}
                  >
                    <div className="d-flex align-items-start gap-3">
                      <div
                        className="d-inline-flex align-items-center justify-content-center"
                        style={{ width: 36, height: 36, borderRadius: 8, background: item.iconBg, color: item.iconColor, fontSize: 14 }}
                      >
                        <i className={`bi ${item.icon}`} />
                      </div>
                      <div>
                        <div className="fw-semibold" style={{ color: '#111827', fontSize: isStudentMobile ? 11 : 13 }}>{item.label}</div>
                        <div style={{ color: '#6b7280', fontSize: isStudentMobile ? 9 : 11 }}>{item.hint}</div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      {item.value ? <div className="fw-semibold" style={{ color: '#7c3aed', fontSize: isStudentMobile ? 9 : 11 }}>{item.value}</div> : null}
                      <i className="bi bi-chevron-right" style={{ color: '#94a3b8' }} />
                    </div>
                  </button>
                ))}
              </div>

              <div className="row g-3">
                <div className="col-12 col-xl-6">
                  <button
                    type="button"
                    className="btn w-100 text-white"
                    style={{
                      borderRadius: 10,
                      padding: isStudentMobile ? '13px 14px' : '15px 18px',
                      background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)',
                      boxShadow: '0 18px 34px rgba(79,70,229,0.18)',
                    }}
                    onClick={() => openTab('for-you')}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <div className="d-flex align-items-center gap-3 text-start">
                        <i className="bi bi-compass-fill" style={{ fontSize: 18 }} />
                        <div>
                          <div className="fw-semibold" style={{ fontSize: isStudentMobile ? 11 : 13 }}>Explore Learning</div>
                          <div style={{ opacity: 0.88, fontSize: isStudentMobile ? 9 : 11 }}>
                            Discover free courses and career-focused programs
                          </div>
                        </div>
                      </div>
                      <i className="bi bi-chevron-right" />
                    </div>
                  </button>
                </div>
                <div className="col-12 col-xl-6">
                  <button
                    type="button"
                    className="btn w-100"
                    style={{
                      borderRadius: 10,
                      padding: isStudentMobile ? '13px 14px' : '15px 18px',
                      background: '#ffffff',
                      border: '1px solid #c7d2fe',
                      color: '#4f46e5',
                      boxShadow: '0 12px 28px rgba(99,102,241,0.08)',
                    }}
                    onClick={() => openTab('advisor-contact')}
                  >
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <div className="d-flex align-items-center gap-3 text-start">
                        <i className="bi bi-headset" style={{ fontSize: 18 }} />
                        <div>
                          <div className="fw-semibold" style={{ fontSize: isStudentMobile ? 11 : 13 }}>Talk to Advisor</div>
                          <div style={{ color: '#6b7280', fontSize: isStudentMobile ? 9 : 11 }}>
                            Get personalized career guidance
                          </div>
                        </div>
                      </div>
                      <i className="bi bi-chevron-right" />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </StudentSurface>
        </div>
      )}

      {activeTab === 'checkout' && !canWrite && (
        <div className="student-checkout-page">
          <div className="student-checkout-header">
            <button type="button" className="student-checkout-back" onClick={() => { resetCheckout(); openTab('my-subscription'); }}>
              <i className="bi bi-arrow-left" />
              <span>Subscription</span>
            </button>
            <div>
              <span className="student-checkout-eyebrow">Secure checkout</span>
              <h1>Complete your membership</h1>
              <p>Review your access, confirm the total, and choose a payment method.</p>
            </div>
            <div className="student-checkout-secure-badge">
              <i className="bi bi-shield-lock-fill" />
              <span>Secure payment</span>
            </div>
          </div>

          {!checkoutState.plan || !checkoutPresentation ? (
            <div className="student-checkout-empty">
              <i className="bi bi-credit-card-2-front" />
              <h2>No membership selected</h2>
              <p>Choose a paid membership from Subscription to begin checkout.</p>
              <button type="button" className="btn btn-primary" onClick={() => openTab('my-subscription')}>View memberships</button>
            </div>
          ) : (
            <div className="student-checkout-layout">
              <main className="student-checkout-main">
                <section className="student-checkout-plan">
                  <div className="student-checkout-plan-icon"><i className="bi bi-stars" /></div>
                  <div className="student-checkout-plan-copy">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                      <span className="student-checkout-section-kicker">Selected membership</span>
                      {checkoutState.plan.isDefaultBasic && <Pill color="success">Basic</Pill>}
                    </div>
                    <h2>{checkoutState.plan.name}</h2>
                    <p>{checkoutState.plan.description || 'Membership access configured for your learning journey.'}</p>
                    <div className="student-checkout-plan-meta">
                      <span><i className="bi bi-calendar3" />{checkoutState.plan.durationDays} days</span>
                      <span><i className="bi bi-clock-history" />{formatPlanWindowLabel(checkoutState.plan.validFrom, checkoutState.plan.validTo)}</span>
                      <span><i className="bi bi-tag" />{checkoutState.plan.code}</span>
                    </div>
                  </div>
                  <div className="student-checkout-plan-price">
                    {checkoutPresentation.planDiscount > 0 && <del>{fmtMoney(checkoutPresentation.listPrice)}</del>}
                    <strong>{fmtMoney(checkoutPresentation.planPrice)}</strong>
                    <small>for this membership</small>
                  </div>
                </section>

                <section className="student-checkout-section">
                  <div className="student-checkout-section-heading">
                    <span><i className="bi bi-check2-circle" /></span>
                    <div><h2>What you get</h2><p>Benefits are based on this membership's configured access.</p></div>
                  </div>
                  {checkoutPresentation.benefits.length ? (
                    <div className="student-checkout-benefits">
                      {checkoutPresentation.benefits.map((benefit) => (
                        <div key={benefit}><i className="bi bi-check-lg" /><span>{benefit}</span></div>
                      ))}
                    </div>
                  ) : (
                    <div className="student-checkout-muted">Access will follow the rules configured for this membership.</div>
                  )}
                </section>

                <section className="student-checkout-section">
                  <div className="student-checkout-section-heading">
                    <span><i className="bi bi-wallet2" /></span>
                    <div><h2>Payment method</h2><p>Select an available provider to complete your purchase.</p></div>
                  </div>
                  {checkoutState.stage === 'review' ? (
                    <div className="student-checkout-providers">
                      {(paymentProviders.providers || []).map((provider) => (
                        <button
                          type="button"
                          key={provider.key}
                          className={checkoutState.provider === provider.key ? 'is-selected' : ''}
                          disabled={!provider.enabled}
                          onClick={() => setCheckoutState((prev) => ({ ...prev, provider: provider.key, order: null }))}
                        >
                          <span className="student-checkout-provider-icon"><i className={`bi ${provider.key === 'razorpay' ? 'bi-lightning-charge-fill' : 'bi-shield-check'}`} /></span>
                          <span className="student-checkout-provider-copy">
                            <strong>{provider.label}</strong>
                            <small>{provider.message}</small>
                          </span>
                          <span className="student-checkout-provider-status">{provider.enabled ? (checkoutState.provider === provider.key ? 'Selected' : toTitleWords(provider.mode)) : 'Unavailable'}</span>
                          <i className={`bi ${checkoutState.provider === provider.key ? 'bi-check-circle-fill' : 'bi-circle'}`} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="student-checkout-confirmed-provider">
                      <span><i className={`bi ${checkoutState.provider === 'razorpay' ? 'bi-lightning-charge-fill' : 'bi-shield-check'}`} /></span>
                      <div><small>Selected provider</small><strong>{(paymentProviders.providers || []).find((provider) => provider.key === checkoutState.provider)?.label || toTitleWords(checkoutState.provider)}</strong></div>
                      <button type="button" disabled={saving} onClick={() => setCheckoutState((prev) => ({ ...prev, stage: 'review', order: null }))}>Change</button>
                    </div>
                  )}
                </section>

                <section className="student-checkout-section student-checkout-coupon-section">
                  <div className="student-checkout-section-heading">
                    <span><i className="bi bi-ticket-perforated" /></span>
                    <div><h2>Offer or coupon</h2><p>Plan offers are applied automatically. Add a coupon if you have one.</p></div>
                  </div>
                  {checkoutState.stage === 'review' ? (
                    <div className="student-checkout-coupon">
                      <input
                        className="form-control"
                        value={checkoutState.couponCode}
                        onChange={(e) => setCheckoutState((prev) => ({ ...prev, couponCode: e.target.value.toUpperCase(), order: null }))}
                        placeholder="Enter coupon code"
                        aria-label="Coupon code"
                      />
                      <span>Validated securely when you continue</span>
                    </div>
                  ) : (
                    <div className="student-checkout-applied-offer">
                      <i className="bi bi-check-circle-fill" />
                      <span>{checkoutState.order?.paymentMeta?.couponCode ? `Coupon ${checkoutState.order.paymentMeta.couponCode} applied` : 'Best available plan price applied'}</span>
                      <button type="button" disabled={saving} onClick={() => setCheckoutState((prev) => ({ ...prev, stage: 'review', order: null }))}>Edit</button>
                    </div>
                  )}
                </section>

                <section className="student-checkout-assurance">
                  <div><i className="bi bi-shield-check" /><span><strong>Protected checkout</strong><small>Payment details are handled by the selected provider.</small></span></div>
                  <div><i className="bi bi-lightning-charge" /><span><strong>Quick activation</strong><small>Membership activates after successful confirmation.</small></span></div>
                  <div><i className="bi bi-receipt" /><span><strong>Clear pricing</strong><small>The backend validates coupons and the final total.</small></span></div>
                </section>

                <section className="student-checkout-policies">
                  <details>
                    <summary>Terms and conditions <i className="bi bi-chevron-down" /></summary>
                    <p>By completing payment, you agree to the membership duration, access limits, and course rules shown for this plan.</p>
                  </details>
                  <details>
                    <summary>Cancellation and refund policy <i className="bi bi-chevron-down" /></summary>
                    <p>Cancellation and refund eligibility follows the institution's published policy. Contact support before payment if you need clarification.</p>
                  </details>
                </section>
              </main>

              <aside className="student-checkout-summary">
                <div className="student-checkout-summary-heading">
                  <div><span>Order summary</span><small>{checkoutState.plan.name}</small></div>
                  <i className="bi bi-receipt-cutoff" />
                </div>
                <div className="student-checkout-price-lines">
                  <div><span>Original price</span><strong>{fmtMoney(checkoutPresentation.listPrice)}</strong></div>
                  {checkoutPresentation.planDiscount > 0 && <div className="is-saving"><span>Plan offer</span><strong>- {fmtMoney(checkoutPresentation.planDiscount)}</strong></div>}
                  {checkoutState.stage === 'payment' && <div className="is-saving"><span>Coupon discount</span><strong>- {fmtMoney(checkoutPresentation.couponDiscount)}</strong></div>}
                </div>
                {(checkoutPresentation.planDiscount + checkoutPresentation.couponDiscount) > 0 && (
                  <div className="student-checkout-offer-note"><i className="bi bi-stars" />You save {fmtMoney(checkoutPresentation.planDiscount + checkoutPresentation.couponDiscount)} on this order</div>
                )}
                <div className="student-checkout-total">
                  <span>Total payable</span>
                  <strong>{fmtMoney(checkoutPresentation.finalAmount)}</strong>
                  <small>Taxes included where applicable</small>
                </div>

                {checkoutState.stage === 'review' ? (
                  <button
                    type="button"
                    className="student-checkout-primary-action"
                    disabled={saving || !(paymentProviders.providers || []).some((provider) => provider.key === checkoutState.provider && provider.enabled)}
                    onClick={() => startCheckout(checkoutState.plan._id)}
                  >
                    <span>{saving ? 'Preparing order...' : `Continue to pay ${fmtMoney(checkoutPresentation.finalAmount)}`}</span>
                    <i className="bi bi-arrow-right" />
                  </button>
                ) : checkoutState.provider === 'razorpay' ? (
                  <button type="button" className="student-checkout-primary-action" disabled={saving} onClick={launchRazorpayCheckout}>
                    <span>{saving ? 'Opening secure payment...' : `Pay ${fmtMoney(checkoutPresentation.finalAmount)} securely`}</span>
                    <i className="bi bi-shield-lock-fill" />
                  </button>
                ) : (
                  <button type="button" className="student-checkout-primary-action" disabled={saving} onClick={completeCheckout}>
                    <span>{saving ? 'Processing...' : 'Confirm test payment'}</span>
                    <i className="bi bi-check2-circle" />
                  </button>
                )}

                <div className="student-checkout-payment-note"><i className="bi bi-lock-fill" />Your payment is processed through the selected secure provider.</div>
                <div className="student-checkout-help">
                  <span><i className="bi bi-headset" /></span>
                  <div><strong>Need payment help?</strong><small>Get support before completing checkout.</small></div>
                  <button type="button" onClick={() => openTab('for-you')}>Get help</button>
                </div>
              </aside>
            </div>
          )}
        </div>
      )}

      {tab === 'plans' && (
        <div className="row g-3">
          <div className={canWrite ? 'col-12' : 'col-12 col-xl-4'}>
            {canWrite ? (
              <AdminSurface style={{ padding: 24 }}>
                <AdminSectionTitle
                  eyebrow="Available Memberships"
                  title="Available Memberships"
                  subtitle="Show the memberships in a cleaner accordion grid and expand only the one you want to work on."
                  action={<button className="btn btn-primary btn-sm" onClick={openNewPlanEditor}><i className="bi bi-plus-lg me-1" />Add Membership</button>}
                />
                <div className="row g-3">
                  {plans.map((plan) => (
                    <div
                      key={plan._id}
                      className="col-12"
                    >
                    <div className="border h-100 position-relative" style={{ borderRadius: 26, borderColor: 'rgba(148, 163, 184, 0.24)', background: 'linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(239,246,255,0.88) 52%, rgba(236,253,245,0.84) 100%)', boxShadow: '0 24px 60px rgba(15, 23, 42, 0.10)', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', inset: '0 auto 0 0', width: 6, background: 'linear-gradient(180deg, #2563eb 0%, #06b6d4 52%, #22c55e 100%)' }} />
                      <button
                        type="button"
                        className="w-100 text-start border-0 bg-transparent"
                        style={{ padding: 20, paddingLeft: 24, background: expandedPlanIds.includes(String(plan._id)) ? 'linear-gradient(180deg, rgba(219,234,254,0.96) 0%, rgba(255,255,255,0.78) 100%)' : 'linear-gradient(180deg, rgba(255,255,255,0.64) 0%, rgba(255,255,255,0.36) 100%)' }}
                        onClick={() => togglePlanAccordion(plan._id)}
                      >
                        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                          <div className="d-flex align-items-center gap-3 flex-wrap">
                            <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 48, height: 48, borderRadius: 16, background: 'linear-gradient(135deg, rgba(37,99,235,0.14) 0%, rgba(34,197,94,0.14) 100%)', color: '#2563eb', boxShadow: 'inset 0 0 0 1px rgba(191,219,254,0.9)' }}>
                              <i className="bi bi-stars" />
                            </div>
                            <div>
                              <div className="fw-semibold" style={{ fontSize: 18, color: '#0f172a', lineHeight: 1.2 }}>{plan.name}</div>
                              <div className="small mt-1" style={{ color: '#64748b', letterSpacing: '.02em' }}>{plan.code}</div>
                            </div>
                            {plan.isDefaultBasic && <Pill color="success">Basic</Pill>}
                            <Pill color={plan.isActive ? 'success' : 'secondary'}>{plan.isActive ? 'Active' : 'Inactive'}</Pill>
                          </div>
                          <div className="d-flex align-items-center gap-3 flex-wrap">
                            <div className="text-end">
                              <div className="fw-semibold" style={{ fontSize: 21, lineHeight: 1, color: '#0f172a' }}>{fmtMoney(plan.salePrice > 0 ? plan.salePrice : plan.price)}</div>
                              {Number(plan.salePrice || 0) > 0 && Number(plan.price || 0) > Number(plan.salePrice || 0) ? (
                                <div className="small" style={{ textDecoration: 'line-through', color: '#94a3b8' }}>{fmtMoney(plan.price)}</div>
                              ) : null}
                            </div>
                            <div className="d-inline-flex align-items-center justify-content-center" style={{ width: 38, height: 38, borderRadius: 14, background: 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(219,234,254,0.9) 100%)', boxShadow: '0 10px 22px rgba(37, 99, 235, 0.10), inset 0 0 0 1px rgba(191,219,254,0.95)', color: '#2563eb' }}>
                              <i className={`bi ${expandedPlanIds.includes(String(plan._id)) ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                            </div>
                          </div>
                        </div>
                      </button>
                      {expandedPlanIds.includes(String(plan._id)) && (
                        <div className="px-3 pb-3 pt-2" style={{ borderTop: '1px solid rgba(191,219,254,0.65)', background: 'linear-gradient(180deg, rgba(255,255,255,0.52) 0%, rgba(248,250,252,0.86) 100%)' }}>
                          <div className="row g-3">
                            {[
                              { label: 'Description', value: plan.description || 'No description added.' },
                              { label: 'Subscription Duration', value: `${plan.durationDays || 0} day(s)` },
                              { label: 'Membership Start Date', value: fmtDate(plan.validFrom) },
                              { label: 'Plan End Date', value: fmtDate(plan.validTo) },
                              { label: 'Expiry Date', value: fmtDate(plan.expiryDate) },
                            ].map((item) => (
                              <div key={`${plan._id}-${item.label}`} className="col-12 col-md-6 col-xl-3">
                                <div className="border h-100" style={{ borderRadius: 18, padding: 14, borderColor: 'rgba(191,219,254,0.72)', background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(239,246,255,0.86) 100%)', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04)' }}>
                                  <div className="small mb-1" style={{ color: '#64748b' }}>{item.label}</div>
                                  <div className="fw-semibold" style={{ color: '#334155', lineHeight: 1.35 }}>{item.value}</div>
                                </div>
                              </div>
                            ))}
                            <div className="col-12 col-md-6 col-xl-3">
                              <div className="border h-100" style={{ borderRadius: 18, padding: 14, borderColor: 'rgba(191,219,254,0.72)', background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(239,246,255,0.86) 100%)', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.04)' }}>
                                <div className="small mb-1" style={{ color: '#64748b' }}>Included Access</div>
                                <div className="fw-semibold" style={{ color: '#334155' }}>{(plan.rules || []).length} access rule(s)</div>
                              </div>
                            </div>
                          </div>
                          {!!plan.rules?.length && (
                            <div className="border mt-3" style={{ borderRadius: 22, padding: 16, borderColor: 'rgba(191,219,254,0.72)', background: 'linear-gradient(180deg, rgba(248,250,252,0.78) 0%, rgba(255,255,255,0.98) 100%)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.55)' }}>
                              <div className="fw-semibold mb-3" style={{ color: '#334155' }}>Included Access</div>
                              <div className="row g-3">
                                {(plan.rules || []).map((rule, index) => (
                                  <div key={`${plan._id}-rule-${index}`} className="col-12 col-md-6 col-xl-3">
                                    <div className="border h-100" style={{ borderRadius: 18, padding: 14, borderColor: 'rgba(191,219,254,0.72)', background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(240,249,255,0.86) 100%)' }}>
                                      <div className="fw-semibold mb-1" style={{ color: '#334155' }}>{(rule.categories || [])[0] || `Access ${index + 1}`}</div>
                                      <div className="small mb-2" style={{ color: '#64748b' }}>{(rule.courseIds || []).length ? `${rule.courseIds.length} selected course(s)` : 'Full category access'}</div>
                                      <div className="small mb-1" style={{ color: '#64748b' }}>Limit: <span style={{ color: '#334155', fontWeight: 600 }}>{Number(rule.selectionLimit || 0) > 0 ? rule.selectionLimit : 'Open'}</span></div>
                                      <div className="small" style={{ color: '#64748b' }}>Discount: <span style={{ color: '#334155', fontWeight: 600 }}>{rule.discountType && rule.discountType !== 'none' ? `${rule.discountType === 'percent' ? `${rule.discountValue || 0}%` : fmtMoney(rule.discountValue || 0)}` : 'None'}</span></div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="d-flex gap-2 flex-wrap mt-3">
                            <button className="btn btn-outline-primary btn-sm" onClick={() => openEditPlanEditor(plan)}>Edit Membership</button>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => deletePlan(plan)}>Delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                    </div>
                  ))}
                </div>
              </AdminSurface>
            ) : (
              <StudentSurface style={{ padding: 24 }}>
                <StudentSectionTitle
                  eyebrow="Memberships"
                  title="Choose your learning package"
                  subtitle="Pick the package that matches the categories and course access you want to unlock."
                />
                <div className="row g-3">
                  {studentVisiblePlans.map((plan) => (
                    <div className="col-12 col-md-6" key={plan._id}>
                      <div className="border h-100 d-flex flex-column" style={{ borderRadius: 22, padding: 22, borderColor: '#e2e8f0', background: plan.isDefaultBasic ? 'linear-gradient(180deg, #ecfeff 0%, #ffffff 100%)' : '#ffffff' }}>
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                          <div>
                            <div className="fw-semibold" style={{ fontSize: 20 }}>{plan.name}</div>
                            <div className="small text-muted">{plan.code}</div>
                          </div>
                          {plan.isDefaultBasic && <Pill color="success">Basic</Pill>}
                        </div>
                        <div className="text-muted mb-3" style={{ minHeight: 48 }}>{plan.description || 'No description added.'}</div>
                        <div className="d-flex align-items-end gap-2 mb-3">
                          <div className="fw-semibold" style={{ fontSize: 28, lineHeight: 1 }}>{fmtMoney(plan.salePrice > 0 ? plan.salePrice : plan.price)}</div>
                          {Number(plan.salePrice || 0) > 0 && Number(plan.price || 0) > Number(plan.salePrice || 0) ? (
                            <div className="small text-muted mb-1" style={{ textDecoration: 'line-through' }}>{fmtMoney(plan.price)}</div>
                          ) : null}
                        </div>
                        <div className="small text-muted mb-2">Valid for {plan.durationDays} days</div>
                        <div className="small text-muted mb-2">Package window: {formatPlanWindowLabel(plan.validFrom, plan.validTo)}</div>
                        <div className="small text-muted mb-3">Expiry date: {plan.expiryDate ? fmtDate(plan.expiryDate) : 'Open'}</div>
                        <div className="row g-2 small mb-4">
                          <div className="col-12">Live quota: <span className="fw-semibold text-dark">{plan.quotas?.liveCourseLimit || 'Open'}</span></div>
                          <div className="col-12">On-demand quota: <span className="fw-semibold text-dark">{plan.quotas?.unlimitedOnDemand ? 'Unlimited' : plan.quotas?.onDemandCourseLimit || 'Open'}</span></div>
                          <div className="col-12">Any-course limit: <span className="fw-semibold text-dark">{plan.quotas?.anyCourseLimit || 'Open'}</span></div>
                        </div>
                        <div className="mt-auto d-flex gap-2 flex-wrap">
                          <button className="btn btn-primary" disabled={saving} onClick={() => openCheckout(plan)}>
                            {plan.isDefaultBasic ? 'View Membership' : 'Buy Membership'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!studentVisiblePlans.length && (
                    <div className="col-12">
                      <div className="border bg-white text-muted" style={{ borderRadius: 22, padding: 22, borderColor: '#e2e8f0' }}>
                        {'No memberships are available for this learning profile.'}
                      </div>
                    </div>
                  )}
                </div>
              </StudentSurface>
            )}
          </div>
          {!canWrite && <div className="col-12 col-xl-8">
            <CardShell title={canWrite ? 'Plan Editor' : 'Access Overview'}>
              {canWrite ? (
                <>
                  <div className="d-flex flex-column gap-3">
                    <div className="border shadow-sm" style={{ borderRadius: 18, borderColor: '#dbe4f0', background: 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)', padding: 20 }}>
                      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                        <div>
                          <div className="text-uppercase small fw-semibold mb-2" style={{ letterSpacing: '.04em', color: '#1d4ed8' }}>Membership Package</div>
                          <div className="fw-semibold" style={{ fontSize: 24, lineHeight: 1.15 }}>{planForm.name || 'New Membership'}</div>
                          <div className="small text-muted mt-1">Code: {String(planForm.code || generatePlanCode(planForm.name)).trim().toUpperCase()}</div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                          <span className={`badge ${planForm.isActive ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`} style={{ padding: '8px 12px', borderRadius: 999 }}> {planForm.isActive ? 'Active' : 'Inactive'} </span>
                          {planForm.isDefaultBasic && (
                            <span className="badge bg-primary-subtle text-primary" style={{ padding: '8px 12px', borderRadius: 999 }}>Default Basic</span>
                          )}
                        </div>
                      </div>
                      <div className="row g-2 mt-3">
                        <div className="col-6 col-md-3">
                          <div className="border bg-white h-100 text-center" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12 }}>
                            <div className="small text-muted">Effective Price</div>
                            <div className="fw-semibold">{fmtMoney(planEditorSummary.effectivePrice)}</div>
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="border bg-white h-100 text-center" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12 }}>
                            <div className="small text-muted">Categories</div>
                            <div className="fw-semibold">{planEditorSummary.categoryCount}</div>
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="border bg-white h-100 text-center" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12 }}>
                            <div className="small text-muted">Included Items</div>
                            <div className="fw-semibold">{planEditorSummary.ruleCount}</div>
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="border bg-white h-100 text-center" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12 }}>
                            <div className="small text-muted">Selected Courses</div>
                            <div className="fw-semibold">{planEditorSummary.scopedCourseCount}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border shadow-sm bg-white" style={{ borderRadius: 18, borderColor: '#e2e8f0', padding: 20 }}>
                      <div className="fw-semibold mb-1">Package Details</div>
                      <div className="small text-muted mb-3">Create the membership first. Then decide what course categories and courses it unlocks.</div>
                      <div className="row g-3">
                        <div className="col-md-7">
                          <label className="form-label small fw-semibold">Membership Name</label>
                          <input className="form-control" value={planForm.name} onChange={(e) => setPlanForm((prev) => ({ ...prev, name: e.target.value }))} />
                        </div>
                        <div className="col-md-5">
                          <label className="form-label small fw-semibold">Package Code</label>
                          <input className="form-control bg-light" value={String(planForm.code || generatePlanCode(planForm.name)).trim().toUpperCase()} readOnly />
                          <div className="form-text">Generated automatically.</div>
                        </div>
                        <div className="col-12">
                          <label className="form-label small fw-semibold">Description</label>
                          <textarea className="form-control" rows={3} value={planForm.description} onChange={(e) => setPlanForm((prev) => ({ ...prev, description: e.target.value }))} />
                        </div>
                      </div>
                    </div>

                    <div className="border shadow-sm bg-white" style={{ borderRadius: 18, borderColor: '#e2e8f0', padding: 20 }}>
                      <div className="fw-semibold mb-1">Pricing and Validity</div>
                      <div className="small text-muted mb-3">Membership start date opens the plan, plan end date closes new access, and expiry date is the hard final stop. Subscription duration is counted from the user's activation date.</div>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">Price</label>
                          <input type="number" className="form-control" value={planForm.price} onChange={(e) => setPlanForm((prev) => ({ ...prev, price: e.target.value }))} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">Sale Price</label>
                          <input type="number" className="form-control" value={planForm.salePrice} onChange={(e) => setPlanForm((prev) => ({ ...prev, salePrice: e.target.value }))} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">Subscription Duration</label>
                          <input type="number" className="form-control" value={planForm.durationDays} onChange={(e) => setPlanForm((prev) => ({ ...prev, durationDays: e.target.value }))} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">Membership Start Date</label>
                          <input type="date" className="form-control" value={planForm.validFrom} onChange={(e) => setPlanForm((prev) => ({ ...prev, validFrom: e.target.value }))} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">Plan End Date</label>
                          <input type="date" className="form-control bg-light" min={planForm.validFrom || ''} value={planForm.validTo} readOnly />
                          <div className="form-text">Calculated automatically from start date and duration.</div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">Expiry Date</label>
                          <input type="date" className="form-control" min={planForm.validFrom || ''} value={planForm.expiryDate} onChange={(e) => setPlanForm((prev) => ({ ...prev, expiryDate: e.target.value }))} />
                        </div>
                        <div className="col-12">
                          <div className="border bg-light" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12 }}>
                            <div className="small text-muted">Package Window: <span className="text-dark fw-semibold">{planEditorSummary.planWindowLabel}</span></div>
                            <div className="small text-muted mt-1">Hard Expiry Date: <span className="text-dark fw-semibold">{planEditorSummary.expiryDateLabel}</span></div>
                            <div className="small text-muted mt-1">Access from user plan start date: <span className="text-dark fw-semibold">{planForm.durationDays || 0} day(s)</span></div>
                            {planEditorSummary.discountAmount > 0 && (
                              <div className="small text-success mt-2">Sale pricing reduces the base amount by {fmtMoney(planEditorSummary.discountAmount)}.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border shadow-sm bg-white" style={{ borderRadius: 18, borderColor: '#e2e8f0', padding: 20 }}>
                      <div className="fw-semibold mb-1">Status</div>
                      <div className="small text-muted mb-3">Use these switches to control package visibility and your default free/basic plan.</div>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <div className="border h-100" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 14 }}>
                            <div className="form-check">
                              <input className="form-check-input" type="checkbox" checked={planForm.isActive} onChange={(e) => setPlanForm((prev) => ({ ...prev, isActive: e.target.checked }))} id="plan-active" />
                              <label className="form-check-label fw-semibold" htmlFor="plan-active">Active Membership</label>
                            </div>
                            <div className="small text-muted mt-2">Only active packages should appear for new LMS purchases.</div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="border h-100" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 14 }}>
                            <div className="form-check">
                              <input className="form-check-input" type="checkbox" checked={planForm.isDefaultBasic} onChange={(e) => setPlanForm((prev) => ({ ...prev, isDefaultBasic: e.target.checked }))} id="plan-basic" />
                              <label className="form-check-label fw-semibold" htmlFor="plan-basic">Default Basic Plan</label>
                            </div>
                            <div className="small text-muted mt-2">This becomes the automatic starter package for new users.</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border shadow-sm bg-white" style={{ borderRadius: 18, borderColor: '#e2e8f0', padding: 20 }}>
                      <div className="fw-semibold mb-1">Quota Settings</div>
                      <div className="small text-muted mb-3">Keep these empty if the package should not have limits. Use them only when you want to cap course usage.</div>
                      <div className="row g-3">
                        <div className="col-md-3">
                          <label className="form-label small fw-semibold">Any Course</label>
                          <input type="number" className="form-control" value={planForm.quotas.anyCourseLimit} onChange={(e) => setPlanForm((prev) => ({ ...prev, quotas: { ...prev.quotas, anyCourseLimit: e.target.value } }))} />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small fw-semibold">Live Courses</label>
                          <input type="number" className="form-control" value={planForm.quotas.liveCourseLimit} onChange={(e) => setPlanForm((prev) => ({ ...prev, quotas: { ...prev.quotas, liveCourseLimit: e.target.value } }))} />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small fw-semibold">On-demand Courses</label>
                          <input type="number" className="form-control" value={planForm.quotas.onDemandCourseLimit} onChange={(e) => setPlanForm((prev) => ({ ...prev, quotas: { ...prev.quotas, onDemandCourseLimit: e.target.value } }))} />
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                          <div className="form-check">
                            <input className="form-check-input" type="checkbox" checked={planForm.quotas.unlimitedOnDemand} onChange={(e) => setPlanForm((prev) => ({ ...prev, quotas: { ...prev.quotas, unlimitedOnDemand: e.target.checked } }))} id="plan-unlimited" />
                            <label className="form-check-label fw-semibold" htmlFor="plan-unlimited">Unlimited On-demand</label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="border shadow-sm bg-white" style={{ borderRadius: 18, borderColor: '#e2e8f0', padding: 20 }}>
                      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
                        <div>
                          <div className="fw-semibold">Included Access</div>
                          <div className="small text-muted">Choose which categories this membership unlocks. Inside each category you can allow all courses or only selected courses.</div>
                        </div>
                        <button type="button" className="btn btn-primary btn-sm" onClick={addPlanRule}>
                          <i className="bi bi-plus-lg me-1" />Add Course Access
                        </button>
                      </div>
                      <div className="d-flex flex-column gap-3">
                          {(planForm.rules || []).map((rule, index) => {
                            const categoryCourses = membershipCourses.filter((course) => String(course.category || '').trim() === String(rule.category || '').trim());
                            return (
                              <div key={`${index}-${rule.category || 'new'}`} className="border" style={{ borderRadius: 16, borderColor: '#dbe4f0', background: '#f8fafc', padding: 16 }}>
                                <div className="d-flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
                                  <div>
                                    <div className="fw-semibold">Course Access {index + 1}</div>
                                    <div className="small text-muted">{rule.category || 'Select a category to continue'}</div>
                                  </div>
                                  <div className="d-flex flex-wrap gap-2">
                                    <button type="button" className="btn btn-outline-secondary btn-sm" disabled={index === 0} onClick={() => movePlanRule(index, -1)}>
                                      <i className="bi bi-arrow-up" />
                                    </button>
                                    <button type="button" className="btn btn-outline-secondary btn-sm" disabled={index === (planForm.rules || []).length - 1} onClick={() => movePlanRule(index, 1)}>
                                      <i className="bi bi-arrow-down" />
                                    </button>
                                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => duplicatePlanRule(index)}>
                                      Duplicate
                                    </button>
                                    {(planForm.rules || []).length > 1 && (
                                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removePlanRule(index)}>
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className="row g-3">
                                  <div className="col-md-6">
                                    <label className="form-label small fw-semibold">Category</label>
                                    <select
                                      className="form-select"
                                      value={rule.category || ''}
                                      onChange={(e) => setPlanRule(index, { category: e.target.value, courseIds: [], allowAllCourses: true })}
                                    >
                                      <option value="">Select category</option>
                                      {courseCategories.map((category) => <option key={category} value={category}>{category}</option>)}
                                    </select>
                                  </div>
                                  <div className="col-md-6">
                                    <label className="form-label small fw-semibold">Access Type</label>
                                    <div className="d-flex gap-2 flex-wrap">
                                      <button
                                        type="button"
                                        className={`btn btn-sm ${rule.allowAllCourses !== false ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setPlanRule(index, { allowAllCourses: true, courseIds: [] })}
                                      >
                                        Full Category
                                      </button>
                                      <button
                                        type="button"
                                        className={`btn btn-sm ${rule.allowAllCourses === false ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setPlanRule(index, { allowAllCourses: false })}
                                        disabled={!rule.category}
                                      >
                                        Selected Courses
                                      </button>
                                    </div>
                                    <div className="form-text">{rule.allowAllCourses !== false ? 'Every course in this category will be unlocked.' : 'Only checked courses will be unlocked.'}</div>
                                  </div>
                                  <div className="col-12">
                                    <label className="form-label small fw-semibold">Courses</label>
                                    {rule.category && rule.allowAllCourses === false && !!categoryCourses.length && (
                                      <div className="d-flex gap-2 mb-2">
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary btn-sm"
                                          onClick={() => setPlanRule(index, { courseIds: categoryCourses.map((course) => String(course._id)) })}
                                        >
                                          Select All
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-outline-secondary btn-sm"
                                          onClick={() => setPlanRule(index, { courseIds: [] })}
                                        >
                                          Clear
                                        </button>
                                      </div>
                                    )}
                                    <div className="border bg-white" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12, minHeight: 84 }}>
                                      {!rule.category && <div className="small text-muted px-1 py-2">Select a category first.</div>}
                                      {rule.category && !categoryCourses.length && <div className="small text-muted px-1 py-2">No courses found in this category.</div>}
                                      {rule.category && rule.allowAllCourses !== false && (
                                        <div className="small text-muted px-1 py-2">
                                          All {categoryCourses.length || 0} courses in <span className="text-dark fw-semibold">{rule.category}</span> will be available in this membership.
                                        </div>
                                      )}
                                      {rule.category && rule.allowAllCourses === false && (
                                        <div className="row g-2">
                                          {categoryCourses.map((course) => {
                                        const checked = (rule.courseIds || []).includes(String(course._id));
                                        return (
                                          <div key={course._id} className="col-12 col-md-6">
                                            <label className="border d-flex align-items-start gap-2 h-100" htmlFor={`rule-${index}-course-${course._id}`} style={{ borderRadius: 12, borderColor: checked ? '#93c5fd' : '#e2e8f0', background: checked ? '#eff6ff' : '#ffffff', padding: 12, cursor: 'pointer' }}>
                                              <input
                                                className="form-check-input mt-1"
                                                type="checkbox"
                                                id={`rule-${index}-course-${course._id}`}
                                                checked={checked}
                                                onChange={(e) => {
                                                  const current = new Set((rule.courseIds || []).map(String));
                                                  if (e.target.checked) current.add(String(course._id));
                                                  else current.delete(String(course._id));
                                                  setPlanRule(index, { courseIds: Array.from(current) });
                                                }}
                                              />
                                              <span className="small text-dark fw-semibold">{course.courseName}</span>
                                            </label>
                                          </div>
                                        );
                                      })}
                                        </div>
                                      )}
                                    </div>
                                    <div className="form-text">
                                      {rule.allowAllCourses !== false ? 'Switch to Selected Courses only if this membership should unlock specific courses from the category.' : 'Choose the courses you want this membership to unlock.'}
                                    </div>
                                    <div className="small text-muted mt-2">
                                      {rule.allowAllCourses !== false
                                        ? `All ${categoryCourses.length || 0} courses in this category will be enabled.`
                                        : `${(rule.courseIds || []).length} course${(rule.courseIds || []).length === 1 ? '' : 's'} selected.`}
                                    </div>
                                  </div>
                                  <div className="col-12">
                                    <div className="border bg-white small" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12 }}>
                                      <div className="fw-semibold mb-1">Access Summary</div>
                                      <div className="text-muted">
                                        Category: <span className="text-dark">{rule.category || 'Not selected'}</span>
                                      </div>
                                      <div className="text-muted">
                                        Access: <span className="text-dark">{rule.allowAllCourses !== false ? 'Full category access' : `${(rule.courseIds || []).length} selected course(s)`}</span>
                                      </div>
                                      <div className="text-muted">
                                        Membership start, end, and expiry are controlled at package level above.
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                  </div>
                  <div className="d-flex gap-2 mt-3">
                    <button className="btn btn-primary btn-sm" disabled={saving} onClick={savePlan}>{editingPlanId ? 'Update Plan' : 'Create Plan'}</button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={resetPlanForm}>Reset</button>
                  </div>
                </>
              ) : (
                <div className="d-flex flex-column gap-3">
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="border rounded p-2 text-center h-100">
                        <div className="small text-muted">Accessible</div>
                        <div className="fw-semibold">{studentAccessOverview.accessibleCourses.length}</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="border rounded p-2 text-center h-100">
                        <div className="small text-muted">Locked</div>
                        <div className="fw-semibold">{studentAccessOverview.lockedCourses.length}</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="border rounded p-2 text-center h-100">
                        <div className="small text-muted">Renew Soon</div>
                        <div className="fw-semibold text-warning">{studentAccessOverview.expiringPlans.length}</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="border rounded p-2 text-center h-100">
                        <div className="small text-muted">Quota Tracked</div>
                        <div className="fw-semibold text-info">{studentAccessOverview.quotaCourses.length}</div>
                      </div>
                    </div>
                  </div>

                  <div className="border rounded p-3">
                    <div className="fw-semibold mb-2">Current Packages</div>
                    {subscriptions.length ? (
                      <div className="d-flex flex-column gap-2">
                        {subscriptions.slice(0, 3).map((subscription) => (
                          <div key={subscription._id} className="border rounded p-2 bg-light">
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <div>
                                <div className="fw-semibold">{subscription.planId?.name || 'Plan'}</div>
                                <div className="small text-muted">{subscription.expiresAt ? `Expiry ${fmtDate(subscription.expiresAt)}` : 'Open expiry'}</div>
                                <div className="small text-muted mt-1">
                                  Live: {subscription.quotaUsage?.live_course?.isUnlimited ? 'Open' : `${subscription.quotaUsage?.live_course?.used || 0}/${subscription.quotaUsage?.live_course?.limit || 0}`} | On-demand: {subscription.quotaUsage?.on_demand_course?.isUnlimited ? 'Unlimited' : `${subscription.quotaUsage?.on_demand_course?.used || 0}/${subscription.quotaUsage?.on_demand_course?.limit || 0}`}
                                </div>
                              </div>
                              <Pill color={subscription.lifecycle?.isExpired ? 'danger' : subscription.lifecycle?.isExpiringSoon ? 'warning' : 'success'}>
                                {subscription.lifecycle?.isExpired ? 'Expired' : subscription.lifecycle?.isExpiringSoon ? 'Renew soon' : 'Active'}
                              </Pill>
                            </div>
                            {!!subscription.quotaUsage && (
                              <div className="small text-muted mt-2">
                                Remaining: live {subscription.quotaUsage.live_course?.isUnlimited ? 'Open' : (subscription.quotaUsage.live_course?.remaining || 0)}, on-demand {subscription.quotaUsage.on_demand_course?.isUnlimited ? 'Unlimited' : (subscription.quotaUsage.on_demand_course?.remaining || 0)}, any {subscription.quotaUsage.any_course?.isUnlimited ? 'Open' : (subscription.quotaUsage.any_course?.remaining || 0)}
                              </div>
                            )}
                            {subscription.lifecycle?.canRenew && subscription.planId && (
                              <button className="btn btn-outline-primary btn-sm mt-2" onClick={() => openCheckout(subscription.planId)}>
                                Renew Package
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="small text-muted">No package is active yet.</div>
                    )}
                  </div>

                  {!!studentAccessOverview.quotaCourses.length && (
                    <div className="border rounded p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="fw-semibold">Quota-Based Access</div>
                        <Pill color="info">{studentAccessOverview.quotaCourses.length} tracked</Pill>
                      </div>
                      <div className="row g-2 small mb-3">
                        <div className="col-4 text-muted">Healthy: <span className="text-dark fw-semibold">{studentAccessOverview.quotaBuckets.available.length}</span></div>
                        <div className="col-4 text-muted">Low: <span className="text-dark fw-semibold">{studentAccessOverview.quotaBuckets.low.length}</span></div>
                        <div className="col-4 text-muted">Used up: <span className="text-dark fw-semibold">{studentAccessOverview.quotaBuckets.exhausted.length}</span></div>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {studentAccessOverview.quotaCourses.slice(0, 5).map((course) => (
                          <div key={`access-quota-${course._id}`} className="border rounded p-2 bg-light">
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <div>
                                <div className="fw-semibold">{course.courseName}</div>
                                <div className="small text-muted">{formatCourseCategoryLabel(course.category)}</div>
                              </div>
                              <Pill color={Number(course.access?.remainingQuota || 0) <= 0 ? 'danger' : Number(course.access?.remainingQuota || 0) <= 2 ? 'warning' : 'success'}>
                                {Number(course.access?.remainingQuota || 0) <= 0 ? 'Used up' : `${course.access?.remainingQuota} left`}
                              </Pill>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="border rounded p-3">
                    <div className="fw-semibold mb-2">Locked Course Reasons</div>
                    {studentAccessOverview.lockedCourses.length ? (
                      <div className="d-flex flex-column gap-2">
                        {studentAccessOverview.lockedCourses.slice(0, 4).map((course) => (
                          <div key={course._id} className="border rounded p-2 bg-light">
                            <div className="fw-semibold">{course.courseName}</div>
                            <div className="small text-muted">{course.category || 'No category'}</div>
                            <div className="small text-danger mt-1">{course.access?.reason || 'This course requires a package.'}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="small text-muted">All visible courses are accessible with your current packages.</div>
                    )}
                  </div>

                  <div className="border rounded p-3">
                    <div className="fw-semibold mb-2">Coupon Ready</div>
                    <label className="form-label">Coupon code</label>
                    <input className="form-control" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="Optional coupon before checkout" />
                    <div className="small text-muted mt-2">
                      Select a package from the left, then continue to checkout. Coupon validation happens against the selected package.
                    </div>
                  </div>
                </div>
              )}
            </CardShell>
          </div>}
        </div>
      )}

      {tab === 'orders' && (
        <div className="row g-3">
          {canWrite ? (
            <>
              <div className="col-12">
                <AdminSurface style={{ padding: 24 }}>
                  <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-3">
                    <div className="fw-semibold">Subscriptions</div>
                    <button className="btn btn-outline-primary btn-sm" onClick={refreshCore}>Refresh</button>
                  </div>
                  {orderQueueMode !== 'all' && (
                    <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                      <Pill color={orderQueueMode === 'failed' ? 'danger' : 'warning'}>
                        Queue mode: {orderQueueMode === 'failed' ? 'Failed Orders' : 'Pending Orders'}
                      </Pill>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setOrderQueueMode('all');
                          setOrderFilters((prev) => ({ ...prev, status: '' }));
                        }}
                      >
                        Clear Queue Mode
                      </button>
                    </div>
                  )}
                  <div className="row g-3">
                    <div className="col-12 col-md-3">
                      <label className="form-label small">Status</label>
                      <select className="form-select" value={orderFilters.status} onChange={(e) => {
                        setOrderQueueMode('all');
                        setOrderFilters((prev) => ({ ...prev, status: e.target.value }));
                      }}>
                        <option value="">All</option>
                        <option value="created">Created</option>
                        <option value="payment_pending">Payment pending</option>
                        <option value="paid">Paid</option>
                        <option value="failed">Failed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="col-12 col-md-2">
                      <label className="form-label small">Provider</label>
                      <select className="form-select" value={orderFilters.provider} onChange={(e) => setOrderFilters((prev) => ({ ...prev, provider: e.target.value }))}>
                        <option value="">All</option>
                        <option value="test">Test</option>
                        <option value="razorpay">Razorpay</option>
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label small">Search</label>
                      <input className="form-control" value={orderFilters.search} onChange={(e) => setOrderFilters((prev) => ({ ...prev, search: e.target.value }))} placeholder="Name, email, plan, coupon" />
                    </div>
                    <div className="col-12 col-md-2">
                      <label className="form-label small">From</label>
                      <input type="date" className="form-control" value={orderFilters.dateFrom} onChange={(e) => setOrderFilters((prev) => ({ ...prev, dateFrom: e.target.value }))} />
                    </div>
                    <div className="col-12 col-md-2">
                      <label className="form-label small">To</label>
                      <input type="date" className="form-control" value={orderFilters.dateTo} onChange={(e) => setOrderFilters((prev) => ({ ...prev, dateTo: e.target.value }))} />
                    </div>
                  </div>
                </AdminSurface>
              </div>
              <div className="col-12">
                <AdminSurface style={{ padding: 24 }}>
                  <AdminSectionTitle
                    title="Subscriptions"
                    subtitle="Payment status, membership activation, renewal information, and provider references."
                  />
                  <div className="row g-3 mb-3">
                    <div className="col-6 col-xl-3"><AdminMetricTile label="Subscriptions" value={filteredAdminOrders.length} icon="bi-receipt" accent="#2563eb" /></div>
                    <div className="col-6 col-xl-3"><AdminMetricTile label="Paid" value={filteredAdminOrders.filter((row) => row.status === 'paid').length} icon="bi-bag-check" accent="#16a34a" /></div>
                    <div className="col-6 col-xl-3"><AdminMetricTile label="Pending" value={filteredAdminOrders.filter((row) => ['created', 'payment_pending'].includes(row.status)).length} icon="bi-hourglass-split" accent="#f59e0b" /></div>
                    <div className="col-6 col-xl-3"><AdminMetricTile label="Revenue" value={fmtMoney(filteredAdminOrders.filter((row) => row.status === 'paid').reduce((sum, row) => sum + Number(row.payment?.amount || 0), 0))} icon="bi-currency-rupee" accent="#7c3aed" /></div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Plan</th>
                          <th>Order</th>
                          <th>Provider</th>
                          <th>Amount</th>
                          <th>Coupon</th>
                          <th>Status</th>
                          <th>Renewal Info</th>
                          <th>Paid Date</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAdminOrders.map((order) => {
                          const renewal = renewalByOrderId.get(String(order._id)) || null;
                          return (
                          <tr key={order._id}>
                            <td>
                              <div className="fw-semibold">{order.customer?.name || '-'}</div>
                              <div className="small text-muted">{order.customer?.email || '-'}</div>
                            </td>
                            <td>
                              <div className="fw-semibold">{order.planId?.name || '-'}</div>
                              <div className="small text-muted">{order.planId?.code || '-'}</div>
                              <div className="small text-muted">Enrollments: {order.enrollmentLinks?.count || 0}</div>
                            </td>
                            <td>
                              <div className="small">{order.providerOrderId || '-'}</div>
                              <div className="small text-muted">{order.providerPaymentId || '-'}</div>
                            </td>
                            <td>
                              <div className="text-capitalize">{order.provider || '-'}</div>
                              <div className="small text-muted text-capitalize">{String(order.payment?.providerStatus || '-').replaceAll('_', ' ')}</div>
                            </td>
                            <td>
                              <div className="fw-semibold">{fmtMoney(order.payment?.amount || 0)}</div>
                              <div className="small text-muted">Disc {fmtMoney(order.payment?.discountAmount || 0)}</div>
                            </td>
                            <td>{order.payment?.couponCode || '-'}</td>
                            <td><Pill color={orderStatusColor(order.status)}>{String(order.status || 'created').replaceAll('_', ' ')}</Pill></td>
                            <td>
                              {renewal ? (
                                <div className="small">
                                  <div className="fw-semibold text-capitalize">{renewal.status || 'active'}</div>
                                  <div className="text-muted">Expiry: {fmtDate(renewal.expiresAt)}</div>
                                  <div className="text-muted">
                                    {renewal.lifecycle?.daysRemaining == null
                                      ? 'Open'
                                      : renewal.lifecycle?.daysRemaining < 0
                                        ? `${Math.abs(renewal.lifecycle.daysRemaining)} day(s) overdue`
                                        : `${renewal.lifecycle.daysRemaining} day(s) left`}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-muted small">Not activated</span>
                              )}
                            </td>
                            <td>{fmtDateTime(order.timeline?.paidAt)}</td>
                            <td>
                              <div className="d-flex flex-wrap gap-2">
                                <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/enrollments')}>
                                  View
                                </button>
                                {renewal && (
                                  <button className="btn btn-outline-primary btn-sm" onClick={() => openRenewalAction(renewal, 'extend')}>
                                    Edit
                                  </button>
                                )}
                                {order.provider === 'razorpay' && ['created', 'payment_pending'].includes(order.status) ? (
                                  <button className="btn btn-outline-primary btn-sm" disabled={saving} onClick={() => resyncMembershipOrder(order._id)}>
                                    {saving ? 'Checking...' : 'Resync'}
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        )})}
                        {!filteredAdminOrders.length && (
                          <tr><td colSpan="10" className="text-center text-muted py-4">No orders found for the selected filters.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </AdminSurface>
              </div>
            </>
          ) : (
            <>
              <div className="col-12 col-xl-4">
                <StudentSurface style={{ padding: 24 }}>
                  <StudentSectionTitle
                    eyebrow="Orders"
                    title="Purchase summary"
                    subtitle="Track payments, activation, and packages that still need a retry."
                  />
                  <div className="row g-3">
                    <div className="col-6"><StudentMetricTile label="Total Orders" value={orders.length} icon="bi-receipt" tone="primary" /></div>
                    <div className="col-6"><StudentMetricTile label="Paid" value={stats.paidOrders} icon="bi-bag-check" tone="success" /></div>
                    <div className="col-6"><StudentMetricTile label="Pending" value={orders.filter((row) => row.status === 'payment_pending' || row.status === 'created').length} icon="bi-hourglass-split" tone="warning" /></div>
                    <div className="col-6"><StudentMetricTile label="Failed" value={orders.filter((row) => row.status === 'failed').length} icon="bi-x-octagon" tone="danger" /></div>
                    <div className="col-6"><StudentMetricTile label="Activated" value={orders.filter((row) => !!row.activation).length} icon="bi-patch-check" tone="info" /></div>
                    <div className="col-6"><StudentMetricTile label="Retry Needed" value={orders.filter((row) => !row.activation && row.status !== 'paid').length} icon="bi-arrow-repeat" tone="warning" /></div>
                  </div>
                </StudentSurface>
              </div>
              <div className="col-12 col-xl-8">
                <StudentSurface style={{ padding: 24 }}>
                  <StudentSectionTitle
                    eyebrow="History"
                    title="Membership order history"
                    subtitle="Every purchase keeps coupon, payment, and activation details together."
                    action={<button className="btn btn-outline-primary btn-sm" onClick={refreshCore}>Refresh</button>}
                  />
                  {orders.length ? (
                    <div className="d-flex flex-column gap-3">
                      {orders.map((order) => (
                        <div key={order._id} className="border" style={{ borderRadius: 20, padding: 20, background: '#fff', borderColor: '#e2e8f0' }}>
                          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                            <div>
                              <div className="fw-semibold" style={{ fontSize: 18 }}>{order.planId?.name || 'Membership Plan'}</div>
                              <div className="small text-muted">{order.planId?.code || '-'} | {fmtMoney(order.payment?.amount || 0)} | {order.planId?.durationDays || 0} days</div>
                              <div className="small text-muted">Package window: {formatPlanWindowLabel(order.planId?.validFrom, order.planId?.validTo)}</div>
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                              <Pill color={orderStatusColor(order.status)}>{String(order.status || 'created').replaceAll('_', ' ')}</Pill>
                              <Pill color="secondary">{String(order.provider || 'test').toUpperCase()}</Pill>
                              {order.activation && <Pill color="success">Activated</Pill>}
                            </div>
                          </div>
                          <div className="row g-3 small">
                            <div className="col-12 col-md-4">
                              <div className="text-muted">Created</div>
                              <div>{fmtDateTime(order.timeline?.createdAt)}</div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="text-muted">Paid</div>
                              <div>{fmtDateTime(order.timeline?.paidAt)}</div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="text-muted">Failed</div>
                              <div>{fmtDateTime(order.timeline?.failedAt)}</div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="text-muted">Coupon</div>
                              <div>{order.payment?.couponCode || '-'}</div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="text-muted">Base Amount</div>
                              <div>{fmtMoney(order.payment?.baseAmount || 0)}</div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="text-muted">Discount</div>
                              <div>{fmtMoney(order.payment?.discountAmount || 0)}</div>
                            </div>
                            <div className="col-12 col-md-4">
                              <div className="text-muted">Gateway Status</div>
                              <div className="text-capitalize">{String(order.payment?.providerStatus || '-').replaceAll('_', ' ')}</div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="text-muted">Order Reference</div>
                              <div className="text-break">{order.payment?.orderReference || '-'}</div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="text-muted">Payment Reference</div>
                              <div className="text-break">{order.payment?.paymentReference || '-'}</div>
                            </div>
                          </div>
                          {order.activation && (
                            <div className="border mt-3" style={{ borderRadius: 16, padding: 16, background: '#f8fafc', borderColor: '#e2e8f0' }}>
                              <div className="fw-semibold mb-2">Activated Subscription</div>
                              <div className="row g-2 small">
                                <div className="col-12 col-md-4 text-muted">Status: <span className="text-dark fw-semibold text-capitalize">{order.activation.status || 'active'}</span></div>
                                <div className="col-12 col-md-4 text-muted">Start: <span className="text-dark fw-semibold">{fmtDate(order.activation.startsAt)}</span></div>
                                <div className="col-12 col-md-4 text-muted">Expiry: <span className="text-dark fw-semibold">{fmtDate(order.activation.expiresAt)}</span></div>
                              </div>
                            </div>
                          )}
                          {!!order.enrollmentLinks?.count && (
                            <div className="border mt-3" style={{ borderRadius: 16, padding: 16, background: '#fff7ed', borderColor: '#fed7aa' }}>
                              <div className="fw-semibold mb-2">Enrollment Records</div>
                              <div className="d-flex flex-column gap-2 small">
                                {(order.enrollmentLinks.items || []).map((item) => (
                                  <div key={`${order._id}-${item.enrollmentId}`} className="d-flex flex-wrap justify-content-between gap-2">
                                    <div>
                                      <div className="fw-semibold text-dark">{item.courseName || '-'}</div>
                                      <div className="text-muted">{item.enrollmentNumber || '-'}{item.batchNumber ? ` | ${item.batchNumber}` : ''}</div>
                                    </div>
                                    <Pill color="secondary">{item.status || 'active'}</Pill>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {order.status !== 'paid' && order.planId && (
                            <div className="mt-3">
                      <button className="btn btn-outline-primary btn-sm" onClick={() => openCheckout(order.planId)}>
                                Retry Purchase
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted">No membership orders yet.</div>
                  )}
                </StudentSurface>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'renewals' && canWrite && (
        <div className="row g-3">
          <div className="col-12">
            <AdminSurface style={{ padding: 24 }}>
              <AdminSectionTitle
                eyebrow="Renewals"
                title="Renewal filters"
                subtitle="Work the renewal queue by lifecycle, timing window, and learner search."
                action={<button className="btn btn-outline-primary btn-sm" onClick={refreshCore}>Refresh</button>}
              />
              {renewalQueueMode !== 'all' && (
                <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                  <Pill color={renewalQueueMode === 'expired' ? 'danger' : 'warning'}>
                    Queue mode: {renewalQueueMode === 'expired' ? 'Expired Renewals' : 'Urgent Renewals'}
                  </Pill>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setRenewalQueueMode('all');
                      setRenewalFilters({ status: '', search: '', days: 30 });
                    }}
                  >
                    Clear Queue Mode
                  </button>
                </div>
              )}
              <div className="row g-3">
                <div className="col-12 col-md-3">
                  <label className="form-label small">Status</label>
                  <select className="form-select" value={renewalFilters.status} onChange={(e) => {
                    setRenewalQueueMode('all');
                    setRenewalFilters((prev) => ({ ...prev, status: e.target.value }));
                  }}>
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label small">Renewal Window</label>
                  <select className="form-select" value={renewalFilters.days} onChange={(e) => {
                    setRenewalQueueMode('all');
                    setRenewalFilters((prev) => ({ ...prev, days: Number(e.target.value || 30) }));
                  }}>
                    <option value={7}>Next 7 days</option>
                    <option value={15}>Next 15 days</option>
                    <option value={30}>Next 30 days</option>
                    <option value={60}>Next 60 days</option>
                  </select>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small">Search</label>
                  <input className="form-control" value={renewalFilters.search} onChange={(e) => setRenewalFilters((prev) => ({ ...prev, search: e.target.value }))} placeholder="Student name, email, plan" />
                </div>
              </div>
            </AdminSurface>
          </div>
          <div className="col-12">
            <AdminSurface style={{ padding: 24 }}>
              <AdminSectionTitle
                eyebrow="Queue"
                title="Renewal queue"
                subtitle="Prioritize expiring and expired subscriptions without leaving the LMS module."
              />
              <div className="row g-3 mb-3">
                <div className="col-6 col-xl-3"><AdminMetricTile label="Queue" value={filteredRenewals.length} icon="bi-arrow-repeat" accent="#2563eb" /></div>
                <div className="col-6 col-xl-3"><AdminMetricTile label="Expiring Soon" value={filteredRenewals.filter((row) => row.lifecycle?.isExpiringSoon).length} icon="bi-alarm" accent="#f59e0b" /></div>
                <div className="col-6 col-xl-3"><AdminMetricTile label="Expired" value={filteredRenewals.filter((row) => row.lifecycle?.isExpired).length} icon="bi-x-circle" accent="#dc2626" /></div>
                <div className="col-6 col-xl-3"><AdminMetricTile label="Renewable" value={filteredRenewals.filter((row) => row.lifecycle?.canRenew).length} icon="bi-patch-check" accent="#16a34a" /></div>
              </div>
              <div className="border mb-3" style={{ borderRadius: 18, padding: 16, background: '#f8fafc', borderColor: '#e2e8f0' }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                  <div className="fw-semibold">Priority Window</div>
                  <div className="d-flex flex-wrap gap-2">
                    <button className="btn btn-outline-danger btn-sm" onClick={() => openRenewalsQueue('expired')}>Expired Only</button>
                    <button className="btn btn-outline-warning btn-sm" onClick={() => openRenewalsQueue('urgent')}>Next 7 Days</button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => openRenewalsQueue('all')}>Reset</button>
                  </div>
                </div>
                <div className="row g-2 small">
                  <div className="col-4 text-muted">Expired now: <span className="text-dark fw-semibold">{renewalQueueSummary.expired}</span></div>
                  <div className="col-4 text-muted">0-3 days: <span className="text-dark fw-semibold">{renewalQueueSummary.todayTo3}</span></div>
                  <div className="col-4 text-muted">4-7 days: <span className="text-dark fw-semibold">{renewalQueueSummary.week}</span></div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Start</th>
                      <th>Expiry</th>
                      <th>Days Left</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRenewals.map((row) => (
                      <tr key={row._id}>
                        <td>
                          <div className="fw-semibold">{row.customer?.name || '-'}</div>
                          <div className="small text-muted">{row.customer?.email || '-'}</div>
                        </td>
                        <td>
                          <div className="fw-semibold">{row.planId?.name || '-'}</div>
                          <div className="small text-muted">{row.planId?.code || '-'}</div>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <Pill color={row.lifecycle?.isExpired ? 'danger' : row.lifecycle?.isExpiringSoon ? 'warning' : 'secondary'}>
                              {row.lifecycle?.isExpired ? 'Expired' : row.lifecycle?.isExpiringSoon ? 'Expiring soon' : (row.status || 'active')}
                            </Pill>
                            <span className="small">
                              <span className={`badge ${row.lifecycle?.isExpired ? 'bg-danger-subtle text-danger border border-danger-subtle' : Number(row.lifecycle?.daysRemaining ?? 9999) <= 3 ? 'bg-warning-subtle text-warning border border-warning-subtle' : 'bg-light text-dark border'}`}>
                                {row.lifecycle?.isExpired ? 'Immediate' : Number(row.lifecycle?.daysRemaining ?? 9999) <= 3 ? 'High priority' : 'Planned'}
                              </span>
                            </span>
                            <span className="small text-muted text-capitalize">{row.status || 'active'}</span>
                          </div>
                        </td>
                        <td>{fmtDate(row.startsAt)}</td>
                        <td>{fmtDate(row.expiresAt)}</td>
                        <td>
                          {row.lifecycle?.daysRemaining === null
                            ? '-'
                            : row.lifecycle?.daysRemaining < 0
                              ? `${Math.abs(row.lifecycle.daysRemaining)} day(s) ago`
                              : `${row.lifecycle.daysRemaining} day(s)`}
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            {row.planId && (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => {
                                  setTab('plans');
                                }}
                              >
                                View Plan
                              </button>
                            )}
                            <button className="btn btn-outline-success btn-sm" onClick={() => openRenewalAction(row, 'extend')}>
                              Extend
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => openRenewalAction(row, 'note')}>
                              Note
                            </button>
                            {row.lifecycle?.isExpired && (
                              <button className="btn btn-outline-warning btn-sm" onClick={() => openRenewalAction(row, 'reactivate')}>
                                Reactivate
                              </button>
                            )}
                            {row.status !== 'cancelled' && (
                              <button className="btn btn-outline-danger btn-sm" onClick={() => openRenewalAction(row, 'cancel')}>
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!filteredRenewals.length && (
                      <tr><td colSpan="7" className="text-center text-muted py-4">No subscriptions in the selected renewal window.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </AdminSurface>
          </div>
        </div>
      )}

      {tab === 'coupons' && canWrite && (
        <div className="row g-3">
          <div className="col-12">
            <AdminSurface style={{ padding: 24 }}>
              <AdminSectionTitle
                eyebrow="Coupons"
                title="Discount codes"
                subtitle="Keep the list compact and open the editor only when you need to create or update a coupon."
                action={<button className="btn btn-primary btn-sm" onClick={openNewCouponEditor}><i className="bi bi-plus-lg me-1" />Add Coupon</button>}
              />
              <div className="row g-3">
                {coupons.map((coupon) => (
                  <div className="col-12 col-md-6 col-xl-4" key={coupon._id}>
                    <div className="border h-100" style={{ borderRadius: 22, padding: 20, borderColor: '#dbe4f0', background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)', boxShadow: '0 14px 32px rgba(15, 23, 42, 0.05)' }}>
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div>
                          <div className="fw-semibold" style={{ fontSize: 18 }}>{coupon.code}</div>
                          <div className="small text-muted mt-1">{coupon.title || 'No title added.'}</div>
                        </div>
                        <Pill color={coupon.approvalStatus === 'approved' ? 'success' : coupon.approvalStatus === 'rejected' ? 'danger' : 'warning'}>
                          {coupon.approvalStatus || 'pending'}
                        </Pill>
                      </div>
                      <div className="small text-muted mb-2">Type: <span className="text-dark fw-semibold">{coupon.discountType === 'percent' ? 'Percentage' : 'Fixed amount'}</span></div>
                      <div className="small text-muted mb-2">Value: <span className="text-dark fw-semibold">{coupon.discountType === 'percent' ? `${coupon.discountValue || 0}%` : fmtMoney(coupon.discountValue || 0)}</span></div>
                      <div className="small text-muted mb-2">Packages: <span className="text-dark fw-semibold">{coupon.packageIds?.length || 0}</span></div>
                      <div className="small text-muted mb-3">Window: <span className="text-dark fw-semibold">{formatPlanWindowLabel(coupon.validFrom, coupon.validTo)}</span></div>
                      <div className="small text-muted mb-3">Status: <span className="text-dark fw-semibold">{coupon.isActive ? 'Usable' : 'Blocked until approval'}</span></div>
                      <div className="d-flex gap-2 flex-wrap">
                        <button className="btn btn-outline-primary btn-sm" onClick={() => openEditCouponEditor(coupon)}>Edit Coupon</button>
                        {coupon.approvalStatus !== 'approved' && <button className="btn btn-outline-success btn-sm" onClick={() => approveCoupon(coupon, 'approve')}>Approve</button>}
                        {coupon.approvalStatus !== 'rejected' && <button className="btn btn-outline-danger btn-sm" onClick={() => approveCoupon(coupon, 'reject')}>Reject</button>}
                      </div>
                    </div>
                  </div>
                ))}
                {!coupons.length && (
                  <div className="col-12">
                    <div className="text-muted">No coupons created yet.</div>
                  </div>
                )}
              </div>
            </AdminSurface>
          </div>
        </div>
      )}

      {tab === 'promotions' && canWrite && (
        <div className="row g-3">
          <div className="col-12">
            <AdminSurface style={{ padding: 24 }}>
              <AdminSectionTitle
                eyebrow="Promotions"
                title="Access promotions"
                subtitle="Review category and course-level access offers from cards, then open the editor only when you need to change one."
                action={<button className="btn btn-primary btn-sm" onClick={openNewPromotionEditor}><i className="bi bi-plus-lg me-1" />Add Promotion</button>}
              />
              <div className="row g-3">
                {promotions.map((promotion) => (
                  <div className="col-12 col-md-6 col-xl-4" key={promotion._id}>
                    <div className="border h-100" style={{ borderRadius: 22, padding: 20, borderColor: '#dbe4f0', background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)', boxShadow: '0 14px 32px rgba(15, 23, 42, 0.05)' }}>
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div>
                          <div className="fw-semibold" style={{ fontSize: 18 }}>{promotion.name}</div>
                          <div className="small text-muted mt-1">{promotion.description || 'No description added.'}</div>
                        </div>
                        <Pill color={promotion.isActive ? 'success' : 'secondary'}>
                          {promotion.isActive ? 'Active' : 'Inactive'}
                        </Pill>
                      </div>
                      <div className="small text-muted mb-2">Period: <span className="text-dark fw-semibold">{formatPlanWindowLabel(promotion.startsAt, promotion.endsAt)}</span></div>
                      <div className="small text-muted mb-2">Categories: <span className="text-dark fw-semibold">{(promotion.categories || []).length}</span></div>
                      <div className="small text-muted mb-2">Courses: <span className="text-dark fw-semibold">{promotion.courseIds?.length || 0}</span></div>
                      <div className="small text-muted mb-3">Audience: <span className="text-dark fw-semibold">{(promotion.audienceRoleKeys || []).length ? promotion.audienceRoleKeys.join(', ') : 'All'}</span></div>
                      <div className="d-flex gap-2">
                        <button className="btn btn-outline-primary btn-sm" onClick={() => openEditPromotionEditor(promotion)}>Edit Promotion</button>
                      </div>
                    </div>
                  </div>
                ))}
                {!promotions.length && (
                  <div className="col-12">
                    <div className="text-muted">No access promotions created yet.</div>
                  </div>
                )}
              </div>
            </AdminSurface>
          </div>
        </div>
      )}

      {tab === 'studio' && canWrite && (
        <div className="row g-3">
          <div className="col-12 col-xl-4">
            <CardShell title="Course + Section">
              <div className="mb-3">
                <label className="form-label">Course</label>
                <select className="form-select" value={selectedCourseId} onChange={(e) => setSelectedCourseId(e.target.value)}>
                  <option value="">Select course</option>
                  {studioCourses.map((course) => <option key={course._id} value={course._id}>{course.courseName}</option>)}
                </select>
              </div>
              <div className="border rounded p-3 mb-3">
                <div className="fw-semibold mb-2">Create Section</div>
                <input className="form-control mb-2" placeholder="Section title" value={sectionForm.title} onChange={(e) => setSectionForm((prev) => ({ ...prev, title: e.target.value }))} />
                <textarea className="form-control mb-2" rows={2} placeholder="Section description" value={sectionForm.description} onChange={(e) => setSectionForm((prev) => ({ ...prev, description: e.target.value }))} />
                <div className="form-check mb-2">
                  <input className="form-check-input" type="checkbox" checked={sectionForm.isPublished} onChange={(e) => setSectionForm((prev) => ({ ...prev, isPublished: e.target.checked }))} id="section-published" />
                  <label className="form-check-label" htmlFor="section-published">Published</label>
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-primary btn-sm" disabled={saving} onClick={saveSection}>
                    {editingSectionId ? 'Update Section' : 'Add Section'}
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={resetSectionForm}>
                    {editingSectionId ? 'Cancel Edit' : 'Reset'}
                  </button>
                </div>
              </div>
              <div>
                {(learnState.outline || []).map((section) => (
                  <div key={section._id} className="border rounded p-2 mb-2">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <div className="fw-semibold">{section.title}</div>
                        <div className="small text-muted">{section.description || 'No description'}</div>
                        <div className="small text-muted mt-1">
                          {(section.lessons || []).length} lesson{(section.lessons || []).length === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => moveSection(section._id, 'up')}>
                          <i className="bi bi-arrow-up" />
                        </button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => moveSection(section._id, 'down')}>
                          <i className="bi bi-arrow-down" />
                        </button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => editSection(section)}>Edit</button>
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => deleteSection(section)}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardShell>
          </div>
          <div className="col-12 col-xl-4">
            <CardShell title="Create Lesson">
              {(() => {
                const prerequisiteOptions = (learnState.outline || []).flatMap((section) =>
                  (section.lessons || []).map((lesson) => ({
                    ...lesson,
                    sectionTitle: section.title,
                  }))
                );
                return (
              <div className="row g-2">
                <div className="col-12">
                  <label className="form-label small">Section</label>
                  <select className="form-select" value={lessonForm.sectionId} onChange={(e) => setLessonForm((prev) => ({ ...prev, sectionId: e.target.value }))}>
                    <option value="">Select section</option>
                    {(learnState.outline || []).map((section) => <option key={section._id} value={section._id}>{section.title}</option>)}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label small">Lesson title</label>
                  <input className="form-control" value={lessonForm.title} onChange={(e) => setLessonForm((prev) => ({ ...prev, title: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label className="form-label small">Summary</label>
                  <textarea className="form-control" rows={2} value={lessonForm.summary} onChange={(e) => setLessonForm((prev) => ({ ...prev, summary: e.target.value }))} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Lesson type</label>
                  <select className="form-select" value={lessonForm.lessonType} onChange={(e) => setLessonForm((prev) => ({ ...prev, lessonType: e.target.value }))}>
                    <option value="uploaded_video">Uploaded video</option>
                    <option value="embedded_video">Embedded video</option>
                    <option value="text">Text</option>
                    <option value="resource">Resource</option>
                    <option value="promo_video">Promo video</option>
                    <option value="promo_image">Promo image</option>
                    <option value="quiz_gate">Quiz gate</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Duration seconds</label>
                  <input type="number" className="form-control" value={lessonForm.durationSeconds} onChange={(e) => setLessonForm((prev) => ({ ...prev, durationSeconds: e.target.value }))} />
                </div>
                {['uploaded_video', 'embedded_video', 'promo_video'].includes(String(lessonForm.lessonType || '')) && (
                  <div className="col-12">
                    <label className="form-label small">Thumbnail image</label>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      disabled={saving || lessonUploadState.thumbnail}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const url = await uploadLessonAsset('thumbnail', file);
                          setLessonForm((prev) => ({ ...prev, thumbnailUrl: url || prev.thumbnailUrl }));
                        } catch (err) {
                          notify(err.response?.data?.message || err.message || 'Could not upload thumbnail.', 'danger');
                        } finally {
                          e.target.value = '';
                        }
                      }}
                    />
                    <div className="form-text">{lessonUploadState.thumbnail ? 'Uploading thumbnail...' : 'Upload a thumbnail image. Recommended 1280x720 or 1920x1080, up to 5 MB.'}</div>
                    {!!lessonForm.thumbnailUrl && <div className="small text-success mt-1">Thumbnail uploaded.</div>}
                  </div>
                )}
                {['uploaded_video', 'promo_video'].includes(String(lessonForm.lessonType || '')) && (
                  <div className="col-12">
                    <label className="form-label small">{lessonForm.lessonType === 'promo_video' ? 'Promo video file' : 'Video file'}</label>
                    <input
                      type="file"
                      accept="video/*"
                      className="form-control"
                      disabled={saving || lessonUploadState.video}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const url = await uploadLessonAsset('video', file);
                          setLessonForm((prev) => ({ ...prev, uploadedVideoUrl: url || prev.uploadedVideoUrl }));
                        } catch (err) {
                          notify(err.response?.data?.message || err.message || 'Could not upload video.', 'danger');
                        } finally {
                          e.target.value = '';
                        }
                      }}
                    />
                    <div className="form-text">{lessonUploadState.video ? 'Uploading video...' : 'Upload MP4, WEBM, MOV, AVI, MKV, or MPEG. Maximum size 300 MB.'}</div>
                    {!!lessonForm.uploadedVideoUrl && <div className="small text-success mt-1">Video uploaded.</div>}
                  </div>
                )}
                {['embedded_video', 'promo_video'].includes(String(lessonForm.lessonType || '')) && (
                  <div className="col-12">
                    <label className="form-label small">{lessonForm.lessonType === 'promo_video' ? 'Promo video URL (optional)' : 'Embedded video URL'}</label>
                    <input className="form-control" value={lessonForm.embeddedVideoUrl} onChange={(e) => setLessonForm((prev) => ({ ...prev, embeddedVideoUrl: e.target.value }))} />
                  </div>
                )}
                {lessonForm.lessonType === 'promo_image' && (
                  <div className="col-12">
                    <label className="form-label small">Promo image</label>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control"
                      disabled={saving || lessonUploadState.image}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const url = await uploadLessonAsset('image', file);
                          setLessonForm((prev) => ({ ...prev, imageUrl: url || prev.imageUrl }));
                        } catch (err) {
                          notify(err.response?.data?.message || err.message || 'Could not upload image.', 'danger');
                        } finally {
                          e.target.value = '';
                        }
                      }}
                    />
                    <div className="form-text">{lessonUploadState.image ? 'Uploading image...' : 'Upload a promo image. Recommended 1280x720 or 1920x1080, up to 5 MB.'}</div>
                    {!!lessonForm.imageUrl && <div className="small text-success mt-1">Image uploaded.</div>}
                  </div>
                )}
                {lessonForm.lessonType === 'resource' && (
                  <div className="col-12">
                    <label className="form-label small">Resource URL</label>
                    <input className="form-control" value={lessonForm.resourceUrl} onChange={(e) => setLessonForm((prev) => ({ ...prev, resourceUrl: e.target.value }))} />
                  </div>
                )}
                {lessonForm.lessonType === 'text' && (
                  <div className="col-12">
                    <label className="form-label small">HTML content</label>
                    <textarea className="form-control" rows={4} value={lessonForm.htmlContent} onChange={(e) => setLessonForm((prev) => ({ ...prev, htmlContent: e.target.value }))} />
                  </div>
                )}
                {lessonForm.lessonType === 'quiz_gate' && (
                  <>
                    <div className="col-12">
                      <div className="border rounded p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="fw-semibold">Quiz Questions</div>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => setLessonForm((prev) => ({
                              ...prev,
                              quizConfig: {
                                ...(prev.quizConfig || {}),
                                questions: [...(Array.isArray(prev.quizConfig?.questions) ? prev.quizConfig.questions : []), defaultQuizQuestion()],
                              },
                            }))}
                          >
                            Add Question
                          </button>
                        </div>
                        {(lessonForm.quizConfig?.questions || []).map((question, questionIndex) => (
                          <div key={`quiz-question-${questionIndex}`} className="border rounded p-2 mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="small fw-semibold">Question {questionIndex + 1}</div>
                              {(lessonForm.quizConfig?.questions || []).length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => setLessonForm((prev) => ({
                                    ...prev,
                                    quizConfig: {
                                      ...(prev.quizConfig || {}),
                                      questions: (prev.quizConfig?.questions || []).filter((_, index) => index !== questionIndex),
                                    },
                                  }))}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            <label className="form-label small">Question</label>
                            <input
                              className="form-control mb-2"
                              value={question.question}
                              onChange={(e) => setLessonForm((prev) => ({
                                ...prev,
                                quizConfig: {
                                  ...(prev.quizConfig || {}),
                                  questions: (prev.quizConfig?.questions || []).map((item, index) => (
                                    index === questionIndex ? { ...item, question: e.target.value } : item
                                  )),
                                },
                              }))}
                            />
                            {(question.options || []).map((option, optionIndex) => (
                              <div key={`quiz-option-${questionIndex}-${optionIndex}`} className="input-group mb-2">
                                <span className="input-group-text">
                                  <input
                                    className="form-check-input mt-0"
                                    type="radio"
                                    name={`correct-option-${questionIndex}`}
                                    checked={Number(question.correctOptionIndex) === optionIndex}
                                    onChange={() => setLessonForm((prev) => ({
                                      ...prev,
                                      quizConfig: {
                                        ...(prev.quizConfig || {}),
                                        questions: (prev.quizConfig?.questions || []).map((item, index) => (
                                          index === questionIndex ? { ...item, correctOptionIndex: optionIndex } : item
                                        )),
                                      },
                                    }))}
                                  />
                                </span>
                                <input
                                  className="form-control"
                                  value={option}
                                  onChange={(e) => setLessonForm((prev) => ({
                                    ...prev,
                                    quizConfig: {
                                      ...(prev.quizConfig || {}),
                                      questions: (prev.quizConfig?.questions || []).map((item, index) => (
                                        index === questionIndex
                                          ? { ...item, options: (item.options || []).map((opt, optIndex) => (optIndex === optionIndex ? e.target.value : opt)) }
                                          : item
                                      )),
                                    },
                                  }))}
                                />
                              </div>
                            ))}
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setLessonForm((prev) => ({
                                  ...prev,
                                  quizConfig: {
                                    ...(prev.quizConfig || {}),
                                    questions: (prev.quizConfig?.questions || []).map((item, index) => (
                                      index === questionIndex ? { ...item, options: [...(item.options || []), ''] } : item
                                    )),
                                  },
                                }))}
                              >
                                Add Option
                              </button>
                              {(question.options || []).length > 2 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => setLessonForm((prev) => ({
                                    ...prev,
                                    quizConfig: {
                                      ...(prev.quizConfig || {}),
                                      questions: (prev.quizConfig?.questions || []).map((item, index) => (
                                        index === questionIndex
                                          ? {
                                            ...item,
                                            options: (item.options || []).slice(0, -1),
                                            correctOptionIndex: Math.min(Number(item.correctOptionIndex || 0), (item.options || []).length - 2),
                                          }
                                          : item
                                      )),
                                    },
                                  }))}
                                >
                                  Remove Last Option
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                <div className="col-12">
                  <label className="form-label small">Prerequisite lessons</label>
                  <div className="border rounded p-2 bg-white" style={{ maxHeight: 220, overflowY: 'auto' }}>
                    {!prerequisiteOptions.length && <div className="small text-muted px-1 py-2">Create earlier lessons first to use prerequisites.</div>}
                    {prerequisiteOptions.map((lesson) => {
                      const checked = (lessonForm.requiresLessonIds || []).includes(String(lesson._id));
                      return (
                        <div key={lesson._id} className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`prereq-${lesson._id}`}
                            checked={checked}
                            onChange={(e) => {
                              const current = new Set((lessonForm.requiresLessonIds || []).map(String));
                              if (e.target.checked) current.add(String(lesson._id));
                              else current.delete(String(lesson._id));
                              setLessonForm((prev) => ({ ...prev, requiresLessonIds: Array.from(current) }));
                            }}
                          />
                          <label className="form-check-label" htmlFor={`prereq-${lesson._id}`}>
                            {lesson.title}
                            <span className="text-muted"> - {lesson.sectionTitle}</span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <div className="form-text">
                    Selected: {(lessonForm.requiresLessonIds || []).length} prerequisite lesson{(lessonForm.requiresLessonIds || []).length === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" checked={lessonForm.isPublished} onChange={(e) => setLessonForm((prev) => ({ ...prev, isPublished: e.target.checked }))} id="lesson-published" />
                    <label className="form-check-label" htmlFor="lesson-published">Published</label>
                  </div>
                </div>
              </div>
                );
              })()}
              <div className="d-flex gap-2 mt-3">
                <button className="btn btn-primary btn-sm" disabled={saving} onClick={saveLesson}>
                  {editingLessonId ? 'Update Lesson' : 'Add Lesson'}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={resetLessonForm}>
                  {editingLessonId ? 'Cancel Edit' : 'Reset'}
                </button>
              </div>
            </CardShell>
          </div>
          <div className="col-12 col-xl-4">
            <CardShell title="Current Outline">
              <div className="row g-2 mb-3">
                <div className="col-12">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Search lesson, section, type"
                    value={studioLessonFilters.search}
                    onChange={(e) => setStudioLessonFilters((prev) => ({ ...prev, search: e.target.value }))}
                  />
                </div>
                <div className="col-12">
                  <select
                    className="form-select form-select-sm"
                    value={studioLessonFilters.release}
                    onChange={(e) => setStudioLessonFilters((prev) => ({ ...prev, release: e.target.value }))}
                  >
                    <option value="all">All release states</option>
                    <option value="live">Live</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                <div className="col-12 d-flex flex-wrap gap-2">
                  <Pill color="success">{studioLessonRows.filter((lesson) => lesson.releaseStatus === 'live').length} live</Pill>
                  <Pill color="info">{studioLessonRows.filter((lesson) => lesson.releaseStatus === 'scheduled').length} scheduled</Pill>
                  <Pill color="secondary">{studioLessonRows.filter((lesson) => lesson.releaseStatus === 'draft').length} draft</Pill>
                </div>
              </div>
              {(learnState.outline || []).map((section) => {
                const sectionLessons = studioLessonRows.filter((lesson) => String(lesson.sectionId) === String(section._id));
                if (!sectionLessons.length) return null;
                return (
                <div key={section._id} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                    <div className="fw-semibold">{section.title}</div>
                    <div className="small text-muted">{sectionLessons.length} lesson{sectionLessons.length === 1 ? '' : 's'}</div>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {sectionLessons.map((lesson) => (
                      <div key={lesson._id} className={`border rounded p-2 ${String(selectedLessonId) === String(lesson._id) ? 'border-primary bg-primary-subtle' : 'bg-white'}`}>
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div className="flex-grow-1">
                            <button
                              type="button"
                              className="btn btn-link p-0 text-start text-decoration-none fw-semibold"
                              onClick={() => setSelectedLessonId(String(lesson._id))}
                            >
                              {lesson.title}
                            </button>
                            <div className="small text-muted mt-1">
                              {formatLessonTypeLabel(lesson.lessonType)}
                              {lesson.durationSeconds ? ` | ${fmtDuration(lesson.durationSeconds)}` : ''}
                              {Array.isArray(lesson.requiresLessonIds) && lesson.requiresLessonIds.length ? ` | prereq ${lesson.requiresLessonIds.length}` : ''}
                              {lesson.releaseAt ? ` | release ${fmtDateTime(lesson.releaseAt)}` : ''}
                            </div>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {['promo_video', 'promo_image', 'quiz_gate'].includes(String(lesson.lessonType || '')) && <span className="badge bg-warning-subtle text-warning border border-warning-subtle">Gate</span>}
                              {lesson.releaseStatus === 'scheduled' && <span className="badge bg-info-subtle text-info border border-info-subtle">Scheduled</span>}
                              {lesson.releaseStatus === 'draft' && <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle">Draft</span>}
                              {lesson.releaseStatus === 'live' && <span className="badge bg-success-subtle text-success border border-success-subtle">Live</span>}
                            </div>
                          </div>
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex gap-2">
                              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => moveLesson(lesson, 'up')}>
                                <i className="bi bi-arrow-up" />
                              </button>
                              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => moveLesson(lesson, 'down')}>
                                <i className="bi bi-arrow-down" />
                              </button>
                            </div>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => editLesson(lesson)}>Edit</button>
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => toggleLessonPublish(lesson)}>
                              {lesson.isPublished ? 'Unpublish' : 'Publish'}
                            </button>
                            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => deleteLesson(lesson)}>Delete</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                );
              })}
              {!studioLessonRows.length && (
                <div className="border rounded p-3 text-center text-muted bg-white">
                  No lessons match the current studio filters.
                </div>
              )}
            </CardShell>
          </div>
        </div>
      )}

      {false && !canWrite && showCourseWorkspaceModal && selectedLearningCourse && (
        <OverlayModal
          show={showCourseWorkspaceModal}
          title={selectedLearningCourse.course?.courseName || 'Course Workspace'}
          onClose={closeCourseWorkspace}
        >
          <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
              <div>
                <div className="small text-muted mb-1">{selectedLearningCourse.course?.category || 'Course'}</div>
                <div className="fw-semibold" style={{ fontSize: 24, lineHeight: 1.2 }}>{selectedLearningCourse.course?.courseName || 'Course'}</div>
                <div className="small text-muted mt-2">
                  Completed {selectedLearningCourse.learningSummary?.completedLessons || 0} of {selectedLearningCourse.learningSummary?.totalLessons || 0} lessons
                </div>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <span className="badge rounded-pill text-bg-light border">Progress {selectedLearningCourse.learningSummary?.completionPercent || 0}%</span>
                <span className="badge rounded-pill text-bg-light border">Pending {selectedLearningCourse.learningSummary?.pendingLessons || 0}</span>
                <span className="badge rounded-pill text-bg-light border">Score {selectedLearningCourse.learningSummary?.averageScore || 0}</span>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
              <button
                type="button"
                className={`btn ${courseWorkspaceTab === 'content' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                onClick={() => setCourseWorkspaceTab('content')}
              >
                Course Content
              </button>
              {showSelectedCourseAssessments && (
                <button
                  type="button"
                  className={`btn ${courseWorkspaceTab === 'assessments' ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                  onClick={() => setCourseWorkspaceTab('assessments')}
                >
                  Assessments
                </button>
              )}
            </div>

            {courseWorkspaceTab === 'content' && (
              <div className="row g-3">
                <div className="col-12 col-xl-5">
                  <div className="border rounded-4 p-3 bg-white" style={{ borderColor: '#e2e8f0' }}>
                    <div className="fw-semibold mb-3">Modules</div>
                    <div className="d-flex flex-column gap-3">
                      {!studentModuleGroups.length && <div className="small text-muted">No modules or lessons available yet.</div>}
                      {studentModuleGroups.map((moduleGroup) => {
                        const isModuleExpanded = String(expandedLearningModuleId) === String(moduleGroup.key);
                        const moduleLessonCount = moduleGroup.sections.reduce((sum, section) => sum + Number((section.lessons || []).length), 0);
                        const moduleTopicCount = moduleGroup.sections.length;
                        return (
                          <div key={`workspace-modal-module-${moduleGroup.key}`} className="border rounded-4 p-3" style={{ borderColor: '#dbeafe' }}>
                            <button
                              type="button"
                              className="btn w-100 text-start p-0 border-0 bg-transparent shadow-none"
                              onClick={() => setExpandedLearningModuleId((prev) => (String(prev) === String(moduleGroup.key) ? '' : String(moduleGroup.key)))}
                            >
                              <div className="d-flex justify-content-between align-items-center gap-2">
                                <div>
                                  <div className="fw-semibold">{moduleGroup.title}</div>
                                  <div className="small text-muted">
                                    {moduleTopicCount} topic{moduleTopicCount === 1 ? '' : 's'} | {moduleLessonCount} content item{moduleLessonCount === 1 ? '' : 's'}
                                  </div>
                                </div>
                                <i className={`bi ${isModuleExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} />
                              </div>
                            </button>
                            {isModuleExpanded && (
                              <div className="border-top mt-3 pt-3 d-flex flex-column gap-2">
                                {moduleGroup.sections.map((section) => (
                                  <div key={`workspace-modal-section-${section._id}`}>
                                    <button
                                      type="button"
                                      className="btn w-100 text-start p-0 border-0 bg-transparent shadow-none mb-2"
                                      onClick={() => {
                                        const firstLesson = (section.lessons || []).find((lesson) => !lesson.access?.isLocked) || (section.lessons || [])[0];
                                        if (firstLesson?._id) openLesson(firstLesson._id);
                                      }}
                                    >
                                      <div className="d-flex justify-content-between align-items-center gap-2">
                                        <div className="small fw-semibold text-muted">{section.title}</div>
                                        <div className="small text-muted">{(section.lessons || []).length} item{(section.lessons || []).length === 1 ? '' : 's'}</div>
                                      </div>
                                    </button>
                                    {!(section.lessons || []).length && (
                                      <div className="small text-muted border rounded-3 p-2 bg-light">
                                        No approved content items are added under this topic yet.
                                      </div>
                                    )}
                                    <div className="d-flex flex-column gap-2">
                                      {(section.lessons || []).map((lesson) => (
                                        <button
                                          key={`workspace-modal-lesson-${lesson._id}`}
                                          type="button"
                                          className="btn text-start border bg-white"
                                          disabled={lesson.access?.isLocked}
                                          style={{
                                            borderRadius: 16,
                                            borderColor: String(selectedLessonId) === String(lesson._id) ? '#60a5fa' : '#e2e8f0',
                                            background: String(selectedLessonId) === String(lesson._id) ? 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)' : '#ffffff',
                                            opacity: lesson.access?.isLocked ? 0.7 : 1,
                                          }}
                                          onClick={() => openLesson(lesson._id)}
                                        >
                                          <div className="d-flex justify-content-between align-items-start gap-2">
                                            <div>
                                              <div className="fw-semibold">{lesson.title}</div>
                                              <div className="small text-muted">
                                                {formatLessonTypeLabel(lesson.lessonType)}
                                                {lesson.durationSeconds ? ` | ${fmtDuration(lesson.durationSeconds)}` : ''}
                                              </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                              {lesson.progress?.status === 'completed' && <i className="bi bi-check-circle-fill text-success" />}
                                              {lesson.access?.isLocked && <i className="bi bi-lock-fill text-muted" />}
                                            </div>
                                          </div>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="col-12 col-xl-7">
                  <div className="border rounded-4 p-3 bg-white h-100" style={{ borderColor: '#e2e8f0' }}>
                    {activeLesson ? (
                      <>
                        <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                          <div>
                            <div className="small text-muted mb-1">{activeLesson.sectionTitle || 'Lesson section'}</div>
                            <div className="fw-semibold" style={{ fontSize: 22, lineHeight: 1.2 }}>{activeLesson.title}</div>
                            <div className="small text-muted mt-1">
                              {formatLessonTypeLabel(activeLesson.lessonType)}
                              {activeLesson.durationSeconds ? ` | ${fmtDuration(activeLesson.durationSeconds)}` : ''}
                              {activeLesson.progress?.lastAccessedAt ? ` | Last opened ${fmtDate(activeLesson.progress.lastAccessedAt)}` : ''}
                            </div>
                          </div>
                          <div className="d-flex gap-2 flex-wrap">
                            {previousLesson && (
                              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openLesson(previousLesson._id)}>
                                Previous
                              </button>
                            )}
                            {nextLesson && (
                              <button type="button" className="btn btn-outline-secondary btn-sm" disabled={nextLesson.access?.isLocked} onClick={() => openLesson(nextLesson._id)}>
                                Next
                              </button>
                            )}
                          </div>
                        </div>

                        {activeLesson.access?.isLocked && (
                          <div className="alert alert-warning">
                            Complete the prerequisite lesson{(activeLesson.access?.missingRequiredIds?.length || 0) === 1 ? '' : 's'} before opening this content.
                          </div>
                        )}

                        {!activeLesson.access?.isLocked && (
                          <>
                            {activeLesson.lessonType === 'uploaded_video' && activeLesson.uploadedVideoUrl && (
                              <div className="mb-3" style={{ borderRadius: 20, overflow: 'hidden', background: '#020617' }}>
                                <div className="ratio ratio-16x9">
                                  <TrackedLessonVideo lesson={activeLesson} onProgress={handleVideoWatchProgress} className="w-100 h-100 d-block" style={{ objectFit: 'contain' }} />
                                </div>
                              </div>
                            )}
                            {activeLesson.lessonType === 'embedded_video' && activeLesson.embeddedVideoUrl && (
                              <div className="ratio ratio-16x9 mb-3" style={{ borderRadius: 20, overflow: 'hidden' }}>
                                <iframe title={activeLesson.title} src={activeLesson.embeddedVideoUrl} allowFullScreen />
                              </div>
                            )}
                            {activeLesson.lessonType === 'promo_video' && activeLesson.uploadedVideoUrl && (
                              <div className="mb-3" style={{ borderRadius: 20, overflow: 'hidden', background: '#020617' }}>
                                <div className="ratio ratio-16x9">
                                  <TrackedLessonVideo lesson={activeLesson} onProgress={handleVideoWatchProgress} className="w-100 h-100 d-block" style={{ objectFit: 'contain' }} />
                                </div>
                              </div>
                            )}
                            {activeLesson.lessonType === 'promo_video' && !activeLesson.uploadedVideoUrl && activeLesson.embeddedVideoUrl && (
                              <div className="ratio ratio-16x9 mb-3" style={{ borderRadius: 20, overflow: 'hidden' }}>
                                <iframe title={activeLesson.title} src={activeLesson.embeddedVideoUrl} allowFullScreen />
                              </div>
                            )}
                            {activeLesson.lessonType === 'promo_image' && activeLesson.imageUrl && (
                              <img src={activeLesson.imageUrl} alt={activeLesson.title} className="img-fluid mb-3" style={{ width: '100%', borderRadius: 20 }} />
                            )}
                            {activeLesson.summary && <p className="text-muted">{activeLesson.summary}</p>}
                            {activeLesson.htmlContent && (
                              <div className="border p-3 mb-3 bg-white" style={{ borderRadius: 18, borderColor: '#e2e8f0' }} dangerouslySetInnerHTML={{ __html: activeLesson.htmlContent }} />
                            )}
                            {activeLesson.lessonType === 'linked_quiz' && <LinkedQuizAttempt lesson={activeLesson} notify={notify} onCompleted={async () => { await loadOutline(selectedCourseId); await refreshCore(); }} />}
                            {activeLesson.lessonType === 'quiz_gate' && (
                              <div className="border p-3 mb-3" style={{ borderRadius: 18, background: '#fffaf0', borderColor: '#fde68a' }}>
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                  <div className="fw-semibold">Quiz Gate</div>
                                  <div className="small text-muted">
                                    Pass score: {activeLesson.quizConfig?.passPercent ?? 60}%
                                    {activeLesson.progress?.score !== null && activeLesson.progress?.score !== undefined ? ` | Current score: ${activeLesson.progress.score}%` : ''}
                                  </div>
                                </div>
                                {!activeQuizQuestions.length ? (
                                  <div className="text-muted small">No quiz questions are configured for this lesson yet.</div>
                                ) : (
                                  <div className="d-flex flex-column gap-3">
                                    {activeQuizQuestions.map((question, questionIndex) => (
                                      <div key={`student-modal-quiz-${questionIndex}`} className="border rounded p-3 bg-white">
                                        <div className="fw-semibold mb-2">{questionIndex + 1}. {question.question}</div>
                                        <div className="d-flex flex-column gap-2">
                                          {(question.options || []).map((option, optionIndex) => (
                                            <label key={`student-modal-quiz-option-${questionIndex}-${optionIndex}`} className="form-check border rounded p-2">
                                              <input
                                                className="form-check-input"
                                                type="radio"
                                                name={`student-quiz-option-${questionIndex}`}
                                                checked={Number(quizAnswers[questionIndex]) === optionIndex}
                                                onChange={() => setQuizAnswers((prev) => ({ ...prev, [questionIndex]: optionIndex }))}
                                              />
                                              <span className="form-check-label ms-2">{option}</span>
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="d-flex flex-wrap gap-2">
                              {activeLesson.resourceUrl && (
                                <a href={activeLesson.resourceUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                                  Open Resource
                                </a>
                              )}
                              {['uploaded_video', 'embedded_video', 'promo_video', 'promo_image'].includes(String(activeLesson.lessonType || '')) && (
                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openExpandedLessonPreview(activeLesson)}>
                                  Expand View
                                </button>
                              )}
                              {activeLesson.lessonType === 'quiz_gate' ? (
                                <button className="btn btn-primary btn-sm" disabled={saving || !activeQuizQuestions.length} onClick={submitQuizGate}>
                                  Submit Quiz
                                </button>
                              ) : activeLesson.lessonType === 'linked_quiz' ? null : (
                                <button className="btn btn-primary btn-sm" disabled={saving || activeLesson.progress?.status === 'completed' || (!canWrite && !isLessonWatchRequirementMet(activeLesson))} onClick={markLessonComplete}>
                                  {activeLesson.progress?.status === 'completed' ? 'Completed' : 'Mark as Completed'}
                                </button>
                              )}
                              {nextAvailableLesson && String(nextAvailableLesson._id) !== String(activeLesson._id) && (
                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openLesson(nextAvailableLesson._id)}>
                                  Open Next Available Lesson
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="text-muted">Select a lesson to begin.</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {courseWorkspaceTab === 'assessments' && showSelectedCourseAssessments && (
              <div className="d-flex flex-column gap-3">
                <div className="row g-3">
                  <div className="col-6 col-xl-3"><StudentMetricTile label="Total" value={studentCourseAssessments.length} hint="Course assessments" tone="primary" icon="bi-clipboard-check" /></div>
                  <div className="col-6 col-xl-3"><StudentMetricTile label="Active" value={studentCourseAssessments.filter((item) => String(item.status || '').toLowerCase() === 'active').length} hint="Open now" tone="success" icon="bi-play-circle" /></div>
                  <div className="col-6 col-xl-3"><StudentMetricTile label="Submitted" value={studentCourseAssessments.filter((item) => item.mySubmission?.submittedAt).length} hint="Attempted" tone="info" icon="bi-check2-square" /></div>
                  <div className="col-6 col-xl-3"><StudentMetricTile label="Pending" value={studentCourseAssessments.filter((item) => !item.mySubmission?.submittedAt).length} hint="Yet to take" tone="warning" icon="bi-hourglass-split" /></div>
                </div>
                {studentCourseAssessmentsLoading ? (
                  <div className="border rounded-4 p-4 bg-white text-muted" style={{ borderColor: '#dbeafe' }}>Loading assessments...</div>
                ) : studentCourseAssessments.length ? (
                  <div className="row g-3">
                    {studentCourseAssessments.map((assessment) => (
                      <div key={`modal-course-assessment-${assessment._id}`} className="col-12 col-xl-6">
                        <div className="border bg-white h-100" style={{ borderRadius: 22, padding: 18, borderColor: '#dbeafe' }}>
                          <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                            <div>
                              <div className="fw-semibold">{assessment.title || 'Assessment'}</div>
                              <div className="small text-muted">{(assessment.type || 'quiz').replace(/_/g, ' ')}{assessment.duration ? ` | ${assessment.duration} min` : ''}</div>
                            </div>
                            <Pill color={assessment.mySubmission?.submittedAt ? 'success' : String(assessment.status || '').toLowerCase() === 'closed' ? 'secondary' : 'warning'}>
                              {assessment.mySubmission?.submittedAt ? 'Submitted' : (assessment.status || 'Pending')}
                            </Pill>
                          </div>
                          <div className="row g-2 small mb-3">
                            <div className="col-6 text-muted">Marks: <span className="text-dark fw-semibold">{assessment.totalMarks || 0}</span></div>
                            <div className="col-6 text-muted">Pass: <span className="text-dark fw-semibold">{assessment.passingMarks || 0}</span></div>
                            <div className="col-6 text-muted">Due: <span className="text-dark fw-semibold">{assessment.endDate || assessment.dueDate ? fmtDate(assessment.endDate || assessment.dueDate) : '-'}</span></div>
                            <div className="col-6 text-muted">Batch: <span className="text-dark fw-semibold">{assessment.batch?.batchNumber || '-'}</span></div>
                          </div>
                          <div className="rounded-4 p-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            {assessment.mySubmission?.submittedAt ? (
                              <div className="row g-2 small">
                                <div className="col-6 text-muted">Status: <span className="text-dark fw-semibold">{assessment.mySubmission.status || '-'}</span></div>
                                <div className="col-6 text-muted">Score: <span className="text-dark fw-semibold">{assessment.mySubmission.percentage ?? 0}%</span></div>
                                <div className="col-12 text-muted">Submitted: <span className="text-dark fw-semibold">{assessment.mySubmission.submittedAt ? fmtDate(assessment.mySubmission.submittedAt) : '-'}</span></div>
                              </div>
                            ) : assessment.mySubmission ? (
                              <div className="small text-muted">
                                Your attempt has been started but not submitted yet.
                              </div>
                            ) : (
                              <div className="small text-muted">This assessment is available for this course when published and assigned.</div>
                            )}
                          </div>
                          <div className="d-flex flex-wrap gap-2 mt-3">
                            {assessment.mySubmission?.submittedAt ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => openStudentAssessmentDialog(assessment, 'review')}
                              >
                                <i className="bi bi-eye me-1" />
                                View score and answers
                              </button>
                            ) : assessment.mySubmission ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => openStudentAssessmentDialog(assessment, 'take')}
                              >
                                <i className="bi bi-arrow-repeat me-1" />
                                Resume assessment
                              </button>
                            ) : String(assessment.status || '').toLowerCase() === 'active' ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => openStudentAssessmentDialog(assessment, 'take')}
                              >
                                <i className="bi bi-play-circle me-1" />
                                Attend assessment
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border rounded-4 p-4 bg-white text-muted" style={{ borderColor: '#dbeafe' }}>No assessments are linked to this course yet.</div>
                )}
              </div>
            )}
          </div>
        </OverlayModal>
      )}

      <StudentAssessmentDialog
        show={studentAssessmentDialog.show}
        mode={studentAssessmentDialog.mode}
        assessment={studentAssessmentDialog.assessment}
        onClose={closeStudentAssessmentDialog}
        onSubmitted={(assessmentId, detail) => {
          syncStudentAssessmentSubmission(assessmentId, detail);
          refreshCore();
        }}
        notify={notify}
      />

      {!canWrite && expandedLessonPreview && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
          style={{ background: 'rgba(2, 6, 23, 0.82)', zIndex: 1080 }}
          onClick={closeExpandedLessonPreview}
        >
          <div
            className="bg-white w-100"
            style={{
              maxWidth: 1120,
              borderRadius: 24,
              overflow: 'hidden',
              boxShadow: '0 32px 80px rgba(15, 23, 42, 0.32)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-start gap-3 p-3 border-bottom">
              <div>
                <div className="small text-muted mb-1">{formatLessonTypeLabel(expandedLessonPreview.lessonType)}</div>
                <div className="fw-semibold" style={{ fontSize: 20, lineHeight: 1.3 }}>
                  {expandedLessonPreview.title}
                </div>
              </div>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={closeExpandedLessonPreview}>
                Close
              </button>
            </div>
            <div className="p-3">
              {expandedLessonPreview.lessonType === 'uploaded_video' && expandedLessonPreview.uploadedVideoUrl && (
                <div style={{ borderRadius: 20, overflow: 'hidden', background: '#020617' }}>
                  <div className="ratio ratio-16x9">
                    <video
                      controls
                      autoPlay
                      className="w-100 h-100 d-block"
                      style={{ objectFit: 'contain' }}
                      src={expandedLessonPreview.uploadedVideoUrl}
                    />
                  </div>
                </div>
              )}
              {expandedLessonPreview.lessonType === 'embedded_video' && expandedLessonPreview.embeddedVideoUrl && (
                <div className="ratio ratio-16x9" style={{ borderRadius: 20, overflow: 'hidden' }}>
                  <iframe title={expandedLessonPreview.title} src={expandedLessonPreview.embeddedVideoUrl} allowFullScreen />
                </div>
              )}
              {expandedLessonPreview.lessonType === 'promo_video' && expandedLessonPreview.uploadedVideoUrl && (
                <div style={{ borderRadius: 20, overflow: 'hidden', background: '#020617' }}>
                  <div className="ratio ratio-16x9">
                    <video
                      controls
                      autoPlay
                      className="w-100 h-100 d-block"
                      style={{ objectFit: 'contain' }}
                      src={expandedLessonPreview.uploadedVideoUrl}
                    />
                  </div>
                </div>
              )}
              {expandedLessonPreview.lessonType === 'promo_video' && !expandedLessonPreview.uploadedVideoUrl && expandedLessonPreview.embeddedVideoUrl && (
                <div className="ratio ratio-16x9" style={{ borderRadius: 20, overflow: 'hidden' }}>
                  <iframe title={expandedLessonPreview.title} src={expandedLessonPreview.embeddedVideoUrl} allowFullScreen />
                </div>
              )}
              {expandedLessonPreview.lessonType === 'promo_image' && expandedLessonPreview.imageUrl && (
                <img
                  src={expandedLessonPreview.imageUrl}
                  alt={expandedLessonPreview.title}
                  className="img-fluid"
                  style={{ width: '100%', borderRadius: 20 }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <OverlayModal
        show={showPlanEditor}
        title={editingPlanId ? 'Edit Membership' : 'Create Membership'}
        onClose={resetPlanForm}
        footer={(
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={resetPlanForm}>Close</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={savePlan}>
              {saving ? 'Saving...' : (editingPlanId ? 'Update Membership' : 'Create Membership')}
            </button>
          </>
        )}
      >
        <div className="d-flex flex-column gap-3">
          <div className="border shadow-sm" style={{ borderRadius: 18, borderColor: '#dbe4f0', background: 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)', padding: 20 }}>
            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
              <div>
                <div className="text-uppercase small fw-semibold mb-2" style={{ letterSpacing: '.04em', color: '#1d4ed8' }}>Membership Package</div>
                <div className="fw-semibold" style={{ fontSize: 24, lineHeight: 1.15 }}>{planForm.name || 'New Membership'}</div>
                <div className="small text-muted mt-1">Code: {String(planForm.code || generatePlanCode(planForm.name)).trim().toUpperCase()}</div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <span className={`badge ${planForm.isActive ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'}`} style={{ padding: '8px 12px', borderRadius: 999 }}>{planForm.isActive ? 'Active' : 'Inactive'}</span>
                {planForm.isDefaultBasic && (
                  <span className="badge bg-primary-subtle text-primary" style={{ padding: '8px 12px', borderRadius: 999 }}>Default Basic</span>
                )}
              </div>
            </div>
            <div className="row g-2 mt-3">
              <div className="col-6 col-md-3">
                <div className="border bg-white h-100 text-center" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12 }}>
                  <div className="small text-muted">Effective Price</div>
                  <div className="fw-semibold">{fmtMoney(planEditorSummary.effectivePrice)}</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="border bg-white h-100 text-center" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12 }}>
                  <div className="small text-muted">Categories</div>
                  <div className="fw-semibold">{planEditorSummary.categoryCount}</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="border bg-white h-100 text-center" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12 }}>
                  <div className="small text-muted">Included Items</div>
                  <div className="fw-semibold">{planEditorSummary.ruleCount}</div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="border bg-white h-100 text-center" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12 }}>
                  <div className="small text-muted">Selected Courses</div>
                  <div className="fw-semibold">{planEditorSummary.scopedCourseCount}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="border shadow-sm bg-white" style={{ borderRadius: 18, borderColor: '#e2e8f0', padding: 20 }}>
            <div className="fw-semibold mb-1">Package Details</div>
            <div className="small text-muted mb-3">Create the membership first. Then decide what course categories and courses it unlocks.</div>
            <div className="row g-3">
              <div className="col-md-7">
                <label className="form-label small fw-semibold">Membership Name</label>
                <input className="form-control" value={planForm.name} onChange={(e) => setPlanForm((prev) => ({ ...prev, name: e.target.value }))} />
              </div>
              <div className="col-md-5">
                <label className="form-label small fw-semibold">Membership Code</label>
                <input className="form-control bg-light" value={String(planForm.code || generatePlanCode(planForm.name)).trim().toUpperCase()} readOnly />
                <div className="form-text">Generated automatically.</div>
              </div>
              <div className="col-12">
                <label className="form-label small fw-semibold">Description</label>
                <textarea className="form-control" rows={3} value={planForm.description} onChange={(e) => setPlanForm((prev) => ({ ...prev, description: e.target.value }))} />
              </div>
            </div>
          </div>

          <div className="border shadow-sm bg-white" style={{ borderRadius: 18, borderColor: '#e2e8f0', padding: 20 }}>
            <div className="fw-semibold mb-1">Pricing and Validity</div>
            <div className="small text-muted mb-3">Membership start date opens the plan, plan end date closes new access, and expiry date is the hard final stop. Subscription duration is counted from the user's activation date.</div>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Price</label>
                <input type="number" className="form-control" value={planForm.price} onChange={(e) => setPlanForm((prev) => ({ ...prev, price: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Sale Price</label>
                <input type="number" className="form-control" value={planForm.salePrice} onChange={(e) => setPlanForm((prev) => ({ ...prev, salePrice: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">Subscription Duration</label>
                <input type="number" className="form-control" value={planForm.durationDays} onChange={(e) => setPlanForm((prev) => ({ ...prev, durationDays: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Membership Start Date</label>
                <input type="date" className="form-control" value={planForm.validFrom} onChange={(e) => setPlanForm((prev) => ({ ...prev, validFrom: e.target.value }))} />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Plan End Date</label>
                <input type="date" className="form-control bg-light" min={planForm.validFrom || ''} value={planForm.validTo} readOnly />
                <div className="form-text">Calculated automatically from start date and duration.</div>
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Expiry Date</label>
                <input type="date" className="form-control" min={planForm.validFrom || ''} value={planForm.expiryDate} onChange={(e) => setPlanForm((prev) => ({ ...prev, expiryDate: e.target.value }))} />
              </div>
              <div className="col-12">
                <div className="border bg-light" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12 }}>
                <div className="small text-muted">Membership Window: <span className="text-dark fw-semibold">{planEditorSummary.planWindowLabel}</span></div>
                  <div className="small text-muted mt-1">Hard Expiry Date: <span className="text-dark fw-semibold">{planEditorSummary.expiryDateLabel}</span></div>
                  <div className="small text-muted mt-1">Access from user plan start date: <span className="text-dark fw-semibold">{planForm.durationDays || 0} day(s)</span></div>
                  {planEditorSummary.discountAmount > 0 && (
                    <div className="small text-success mt-2">Sale pricing reduces the base amount by {fmtMoney(planEditorSummary.discountAmount)}.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-xl-6">
              <div className="border shadow-sm bg-white h-100" style={{ borderRadius: 18, borderColor: '#e2e8f0', padding: 20 }}>
                <div className="fw-semibold mb-1">Status</div>
                <div className="small text-muted mb-3">Use these switches to control package visibility and your default free/basic plan.</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="border h-100" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 14 }}>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked={planForm.isActive} onChange={(e) => setPlanForm((prev) => ({ ...prev, isActive: e.target.checked }))} id="plan-active-modal" />
                        <label className="form-check-label fw-semibold" htmlFor="plan-active-modal">Active Membership</label>
                      </div>
                      <div className="small text-muted mt-2">Only active packages should appear for new LMS purchases.</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="border h-100" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 14 }}>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" checked={planForm.isDefaultBasic} onChange={(e) => setPlanForm((prev) => ({ ...prev, isDefaultBasic: e.target.checked }))} id="plan-basic-modal" />
                        <label className="form-check-label fw-semibold" htmlFor="plan-basic-modal">Default Basic Plan</label>
                      </div>
                      <div className="small text-muted mt-2">This becomes the automatic starter package for new users.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-xl-6">
              <div className="border shadow-sm bg-white h-100" style={{ borderRadius: 18, borderColor: '#e2e8f0', padding: 20 }}>
                <div className="fw-semibold mb-1">Access Configuration</div>
                <div className="small text-muted mb-3">Limits and discounts are now controlled inside each Included Access row below. This keeps each category or course group self-contained.</div>
                <div className="border bg-light" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 14 }}>
                  <div className="small text-muted">Set course selection limits and optional discounts per Included Access rule.</div>
                </div>
              </div>
            </div>
          </div>

          <div className="border shadow-sm bg-white" style={{ borderRadius: 18, borderColor: '#e2e8f0', padding: 20 }}>
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
              <div>
                <div className="fw-semibold">Included Access</div>
                <div className="small text-muted">Choose which categories this membership unlocks. Inside each category you can allow all courses or only selected courses.</div>
              </div>
              <button type="button" className="btn btn-primary btn-sm" onClick={addPlanRule}>
                <i className="bi bi-plus-lg me-1" />Add Course Access
              </button>
            </div>
            <div className="d-flex flex-column gap-3">
              {(planForm.rules || []).map((rule, index) => {
                const categoryCourses = membershipCourses.filter((course) => String(course.category || '').trim() === String(rule.category || '').trim());
                return (
                  <div key={`${index}-${rule.category || 'new'}-modal`} className="border" style={{ borderRadius: 16, borderColor: '#dbe4f0', background: '#f8fafc', padding: 16 }}>
                    <div className="d-flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
                      <div>
                        <div className="fw-semibold">Course Access {index + 1}</div>
                        <div className="small text-muted">{rule.category || 'Select a category to continue'}</div>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={index === 0} onClick={() => movePlanRule(index, -1)}><i className="bi bi-arrow-up" /></button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" disabled={index === (planForm.rules || []).length - 1} onClick={() => movePlanRule(index, 1)}><i className="bi bi-arrow-down" /></button>
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => duplicatePlanRule(index)}>Duplicate</button>
                        {(planForm.rules || []).length > 1 && <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removePlanRule(index)}>Remove</button>}
                      </div>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Category</label>
                        <select className="form-select" value={rule.category || ''} onChange={(e) => setPlanRule(index, { category: e.target.value, courseIds: [], allowAllCourses: true })}>
                          <option value="">Select category</option>
                          {[...new Set([...(rule.category ? [rule.category] : []), ...courseCategories])].map((category) => <option key={category} value={category}>{category}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Access Type</label>
                        <div className="d-flex gap-2 flex-wrap">
                          <button type="button" className={`btn btn-sm ${rule.allowAllCourses !== false ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPlanRule(index, { allowAllCourses: true, courseIds: [] })}>Full Category</button>
                          <button type="button" className={`btn btn-sm ${rule.allowAllCourses === false ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setPlanRule(index, { allowAllCourses: false })} disabled={!rule.category}>Selected Courses</button>
                        </div>
                        <div className="form-text">{rule.allowAllCourses !== false ? 'Every course in this category will be unlocked.' : 'Only checked courses will be unlocked.'}</div>
                      </div>
                      <div className="col-12">
                        <label className="form-label small fw-semibold">Courses</label>
                        {rule.category && rule.allowAllCourses === false && !!categoryCourses.length && (
                          <div className="d-flex gap-2 mb-2">
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setPlanRule(index, { courseIds: categoryCourses.map((course) => String(course._id)) })}>Select All</button>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setPlanRule(index, { courseIds: [] })}>Clear</button>
                          </div>
                        )}
                        <div className="border bg-white" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 12, minHeight: 84 }}>
                          {!rule.category && <div className="small text-muted px-1 py-2">Select a category first.</div>}
                          {rule.category && !categoryCourses.length && <div className="small text-muted px-1 py-2">No courses found in this category.</div>}
                          {rule.category && rule.allowAllCourses !== false && (
                            <div className="small text-muted px-1 py-2">
                              All {categoryCourses.length || 0} courses in <span className="text-dark fw-semibold">{rule.category}</span> will be available in this membership.
                            </div>
                          )}
                          {rule.category && rule.allowAllCourses === false && (
                            <div className="row g-2">
                              {categoryCourses.map((course) => {
                                const checked = (rule.courseIds || []).includes(String(course._id));
                                return (
                                  <div key={course._id} className="col-12 col-md-6">
                                    <label className="border d-flex align-items-start gap-2 h-100" htmlFor={`rule-modal-${index}-course-${course._id}`} style={{ borderRadius: 12, borderColor: checked ? '#93c5fd' : '#e2e8f0', background: checked ? '#eff6ff' : '#ffffff', padding: 12, cursor: 'pointer' }}>
                                      <input
                                        className="form-check-input mt-1"
                                        type="checkbox"
                                        id={`rule-modal-${index}-course-${course._id}`}
                                        checked={checked}
                                        onChange={(e) => {
                                          const current = new Set((rule.courseIds || []).map(String));
                                          if (e.target.checked) current.add(String(course._id));
                                          else current.delete(String(course._id));
                                          setPlanRule(index, { courseIds: Array.from(current) });
                                        }}
                                      />
                                      <span className="small text-dark fw-semibold">{course.courseName}</span>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="form-text">
                          {rule.allowAllCourses !== false ? 'Switch to Selected Courses only if this membership should unlock specific courses from the category.' : 'Choose the courses you want this membership to unlock.'}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Access Limit</label>
                        <input
                          type="number"
                          min="0"
                          className="form-control"
                          value={rule.selectionLimit}
                          onChange={(e) => setPlanRule(index, { selectionLimit: e.target.value })}
                        />
                        <div className="form-text">Keep this empty or 0 for open access inside this rule.</div>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold">Discount</label>
                        <div className="d-flex gap-2">
                          <select className="form-select" value={rule.discountType || 'none'} onChange={(e) => setPlanRule(index, { discountType: e.target.value, discountValue: e.target.value === 'none' ? '' : rule.discountValue })}>
                            <option value="none">No Discount</option>
                            <option value="percent">Percent</option>
                            <option value="fixed">Fixed</option>
                          </select>
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            value={rule.discountValue}
                            onChange={(e) => setPlanRule(index, { discountValue: e.target.value })}
                            disabled={(rule.discountType || 'none') === 'none'}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </OverlayModal>

      <OverlayModal
        show={showCouponEditor}
        title={editingCouponId ? 'Edit Coupon' : 'Create Coupon'}
        onClose={resetCouponForm}
        footer={(
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={resetCouponForm}>Close</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={saveCoupon}>
              {saving ? 'Saving...' : (editingCouponId ? 'Update Coupon' : 'Create Coupon')}
            </button>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Coupon Code</label>
            <input className="form-control" value={couponForm.code} onChange={(e) => setCouponForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))} />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Coupon Title</label>
            <input className="form-control" value={couponForm.title} onChange={(e) => setCouponForm((prev) => ({ ...prev, title: e.target.value }))} />
          </div>
          <div className="col-12">
            <label className="form-label small fw-semibold">Description</label>
            <textarea className="form-control" rows={2} value={couponForm.description} onChange={(e) => setCouponForm((prev) => ({ ...prev, description: e.target.value }))} />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Discount Type</label>
            <select className="form-select" value={couponForm.discountType} onChange={(e) => setCouponForm((prev) => ({ ...prev, discountType: e.target.value }))}>
              <option value="percent">Percentage Discount</option>
              <option value="fixed">Fixed Amount Discount</option>
            </select>
            <div className="form-text">Percentage discount reduces by a percent. Fixed amount discount reduces by a flat rupee amount.</div>
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Discount Value</label>
            <input type="number" className="form-control" value={couponForm.discountValue} onChange={(e) => setCouponForm((prev) => ({ ...prev, discountValue: e.target.value }))} />
          </div>
          <div className="col-12">
            <label className="form-label small fw-semibold">Applicable Memberships</label>
            <div className="border rounded p-3" style={{ maxHeight: 220, overflowY: 'auto', background: '#f8fafc' }}>
              {plans.map((plan) => {
                const checked = (couponForm.packageIds || []).map(String).includes(String(plan._id));
                return (
                  <div className="form-check mb-2" key={`coupon-membership-${plan._id}`}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`coupon-membership-${plan._id}`}
                      checked={checked}
                      onChange={(e) => {
                        const current = new Set((couponForm.packageIds || []).map(String));
                        if (e.target.checked) current.add(String(plan._id));
                        else current.delete(String(plan._id));
                        setCouponForm((prev) => ({ ...prev, packageIds: Array.from(current) }));
                      }}
                    />
                    <label className="form-check-label" htmlFor={`coupon-membership-${plan._id}`}>{plan.name}</label>
                  </div>
                );
              })}
            </div>
            <div className="form-text">Select one or more memberships where this coupon can be used.</div>
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-semibold">Maximum Uses</label>
            <input type="number" className="form-control" value={couponForm.maxUses} onChange={(e) => setCouponForm((prev) => ({ ...prev, maxUses: e.target.value }))} />
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-semibold">Max Uses Per User</label>
            <input type="number" className="form-control" value={couponForm.maxUsesPerUser} onChange={(e) => setCouponForm((prev) => ({ ...prev, maxUsesPerUser: e.target.value }))} />
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-semibold d-block">Status</label>
            <div className="form-check mt-2">
              <input className="form-check-input" type="checkbox" checked={couponForm.isActive} onChange={(e) => setCouponForm((prev) => ({ ...prev, isActive: e.target.checked }))} id="coupon-active-modal" />
              <label className="form-check-label" htmlFor="coupon-active-modal">Active after admin approval</label>
            </div>
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Valid From</label>
            <input type="date" className="form-control" value={couponForm.validFrom} onChange={(e) => setCouponForm((prev) => ({ ...prev, validFrom: e.target.value }))} />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Valid To</label>
            <input type="date" className="form-control" value={couponForm.validTo} onChange={(e) => setCouponForm((prev) => ({ ...prev, validTo: e.target.value }))} />
          </div>
        </div>
      </OverlayModal>

      <OverlayModal
        show={showPromotionEditor}
        title={editingPromotionId ? 'Edit Promotion' : 'Create Promotion'}
        onClose={resetPromotionForm}
        footer={(
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={resetPromotionForm}>Close</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={savePromotion}>
              {saving ? 'Saving...' : (editingPromotionId ? 'Update Promotion' : 'Create Promotion')}
            </button>
          </>
        )}
      >
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Promotion Name</label>
            <input className="form-control" value={promotionForm.name} onChange={(e) => setPromotionForm((prev) => ({ ...prev, name: e.target.value }))} />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold d-block">Status</label>
            <div className="form-check mt-2">
              <input className="form-check-input" type="checkbox" checked={promotionForm.isActive} onChange={(e) => setPromotionForm((prev) => ({ ...prev, isActive: e.target.checked }))} id="promotion-active-modal" />
              <label className="form-check-label" htmlFor="promotion-active-modal">Active</label>
            </div>
          </div>
          <div className="col-12">
            <label className="form-label small fw-semibold">Description</label>
            <textarea className="form-control" rows={2} value={promotionForm.description} onChange={(e) => setPromotionForm((prev) => ({ ...prev, description: e.target.value }))} />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Promotion Start Date</label>
            <input type="date" className="form-control" value={promotionForm.startsAt} onChange={(e) => setPromotionForm((prev) => ({ ...prev, startsAt: e.target.value }))} />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Promotion End Date</label>
            <input type="date" className="form-control" min={promotionForm.startsAt || ''} value={promotionForm.endsAt} onChange={(e) => setPromotionForm((prev) => ({ ...prev, endsAt: e.target.value }))} />
          </div>
          <div className="col-12">
            <label className="form-label small fw-semibold">Categories</label>
            <div className="border bg-white" style={{ borderRadius: 12, borderColor: '#e2e8f0', padding: 12, maxHeight: 180, overflowY: 'auto' }}>
              {!courseCategories.length && <div className="small text-muted">No categories found.</div>}
              {courseCategories.map((category) => {
                const checked = (promotionForm.categories || []).includes(category);
                return (
                  <div key={category} className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`promotion-category-modal-${category}`}
                      checked={checked}
                      onChange={(e) => {
                        const current = new Set((promotionForm.categories || []).map(String));
                        if (e.target.checked) current.add(category);
                        else current.delete(category);
                        const nextCategories = Array.from(current);
                        setPromotionForm((prev) => ({
                          ...prev,
                          categories: nextCategories,
                          courseIds: (prev.courseIds || []).filter((courseId) => membershipCourses.some((course) => String(course._id) === String(courseId) && nextCategories.includes(String(course.category || '').trim()))),
                        }));
                      }}
                    />
                    <label className="form-check-label" htmlFor={`promotion-category-modal-${category}`}>{category}</label>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="col-12">
            <label className="form-label small fw-semibold">Courses</label>
            <div className="border bg-white" style={{ borderRadius: 12, borderColor: '#e2e8f0', padding: 12, maxHeight: 220, overflowY: 'auto' }}>
              {!promotionScopedCourses.length && <div className="small text-muted">No courses available for the selected categories.</div>}
              {promotionScopedCourses.map((course) => {
                const checked = (promotionForm.courseIds || []).includes(String(course._id));
                return (
                  <div key={course._id} className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`promotion-course-modal-${course._id}`}
                      checked={checked}
                      onChange={(e) => {
                        const current = new Set((promotionForm.courseIds || []).map(String));
                        if (e.target.checked) current.add(String(course._id));
                        else current.delete(String(course._id));
                        setPromotionForm((prev) => ({ ...prev, courseIds: Array.from(current) }));
                      }}
                    />
                    <label className="form-check-label" htmlFor={`promotion-course-modal-${course._id}`}>
                      {course.courseName} <span className="text-muted">({course.category || 'No category'})</span>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Delivery Modes</label>
            <select multiple className="form-select" value={promotionForm.deliveryModes} onChange={(e) => setPromotionForm((prev) => ({ ...prev, deliveryModes: Array.from(e.target.selectedOptions).map((item) => item.value) }))}>
              <option value="live">Live</option>
              <option value="on_demand">On Demand</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Audience Roles</label>
            <select multiple className="form-select" value={promotionForm.audienceRoleKeys} onChange={(e) => setPromotionForm((prev) => ({ ...prev, audienceRoleKeys: Array.from(e.target.selectedOptions).map((item) => item.value) }))}>
              <option value="student">Student</option>
              <option value="faculty">Faculty</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {promotionForm.deliveryModes.includes('live') && (
            <div className="col-12">
              <div className="border bg-light" style={{ borderRadius: 14, borderColor: '#e2e8f0', padding: 16 }}>
                <div className="fw-semibold mb-3">Live Class Details</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Live Class Date</label>
                    <input type="date" className="form-control" value={promotionForm.liveSession.sessionDate} onChange={(e) => setPromotionForm((prev) => ({ ...prev, liveSession: { ...prev.liveSession, sessionDate: e.target.value } }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Live Class Time</label>
                    <input type="time" className="form-control" value={promotionForm.liveSession.sessionTime} onChange={(e) => setPromotionForm((prev) => ({ ...prev, liveSession: { ...prev.liveSession, sessionTime: e.target.value } }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold">Google Meet Link</label>
                    <input className="form-control" value={promotionForm.liveSession.meetingUrl} onChange={(e) => setPromotionForm((prev) => ({ ...prev, liveSession: { ...prev.liveSession, meetingUrl: e.target.value } }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold">Live Class Note</label>
                    <textarea className="form-control" rows={2} value={promotionForm.liveSession.meetingNote} onChange={(e) => setPromotionForm((prev) => ({ ...prev, liveSession: { ...prev.liveSession, meetingNote: e.target.value } }))} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </OverlayModal>

      <OverlayModal
        show={leadCaptureState.show}
        title={leadCaptureState.title || 'Submit enquiry'}
        onClose={closeLeadCapture}
        dialogClassName="student-enquiry-dialog"
        contentClassName="student-enquiry-modal"
        bodyClassName="student-enquiry-modal-body"
        footer={(
          <div className="student-enquiry-actions">
            <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={closeLeadCapture}>Cancel</button>
            <button type="button" className="btn btn-primary student-enquiry-submit" disabled={saving} onClick={submitLeadCapture}>
              {leadCaptureSubmitLabel}
              {!saving && <i className="bi bi-arrow-right" />}
            </button>
          </div>
        )}
      >
        <div className="d-flex flex-column gap-3 student-enquiry-form">
          <div className="student-enquiry-intro">
            <span><i className="bi bi-headset" /></span>
            <div>
              <strong>Talk to our program team</strong>
              <p>Share your details and our team will contact you with curriculum, schedule, eligibility, and enrollment guidance.</p>
            </div>
          </div>
          {leadCaptureState.subtitle ? (
            <div className="student-enquiry-selected-program">
              <div className="small text-muted mb-1">
                {isLeadProgram ? 'Selected program' : isLeadLive ? 'Selected live class' : 'Selected course'}
              </div>
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-mortarboard-fill" />
                <div className="fw-semibold" style={{ color: '#0f172a' }}>{leadCaptureState.subtitle}</div>
              </div>
            </div>
          ) : null}
          <div className="row g-3 student-enquiry-fields">
            <div className="col-md-6">
              <label className="form-label small fw-semibold">{isLeadProgram ? 'Student Name' : 'Name'}</label>
              <div className="student-enquiry-input"><i className="bi bi-person" /><input className="form-control" required minLength={2} autoComplete="name" value={leadCaptureForm.name} onChange={(e) => setLeadCaptureForm((prev) => ({ ...prev, name: e.target.value }))} /></div>
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-semibold">Mobile Number</label>
              <div className="student-enquiry-input"><i className="bi bi-telephone" /><input className="form-control" required type="tel" inputMode="tel" autoComplete="tel" value={leadCaptureForm.phone} onChange={(e) => setLeadCaptureForm((prev) => ({ ...prev, phone: e.target.value }))} /></div>
            </div>
            {!isLeadProgram && (
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Email Address</label>
                <div className="student-enquiry-input"><i className="bi bi-envelope" /><input className="form-control" required type="email" inputMode="email" autoComplete="email" value={leadCaptureForm.email} onChange={(e) => setLeadCaptureForm((prev) => ({ ...prev, email: e.target.value }))} /></div>
              </div>
            )}
            <div className="col-md-6">
              <label className="form-label small fw-semibold">{isLeadProgram ? 'College / Institution' : 'College'}</label>
              <div className="student-enquiry-input"><i className="bi bi-building" /><input className="form-control" required={!isLeadProgram} minLength={2} value={leadCaptureForm.collegeName} onChange={(e) => setLeadCaptureForm((prev) => ({ ...prev, collegeName: e.target.value }))} /></div>
            </div>
            {isLeadProgram ? (
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Members Count</label>
                <input type="number" min="1" className="form-control" value={leadCaptureForm.membersCount} onChange={(e) => setLeadCaptureForm((prev) => ({ ...prev, membersCount: Math.max(1, Number(e.target.value || 1)) }))} />
              </div>
            ) : (
              <>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">{isLeadLive ? 'Preferred Area / Branch' : 'Area'}</label>
                  <input className="form-control" required minLength={2} value={leadCaptureForm.area} onChange={(e) => setLeadCaptureForm((prev) => ({ ...prev, area: e.target.value }))} />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">{isLeadLive ? 'Address / Preferred Location' : 'Address'}</label>
                  <input className="form-control" required minLength={5} value={leadCaptureForm.address} onChange={(e) => setLeadCaptureForm((prev) => ({ ...prev, address: e.target.value }))} />
                </div>
              </>
            )}
            <div className="col-12">
              <label className="form-label small fw-semibold">{isLeadProgram ? 'Requirement / Message' : isLeadLive ? 'Timing Preference / Message' : 'Message'}</label>
              <textarea
                className="form-control"
                rows={3}
                value={leadCaptureForm.notes}
                onChange={(e) => setLeadCaptureForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder={
                  isLeadProgram
                    ? 'Add your requirement, project expectation, team notes, or any message for the team.'
                    : isLeadLive
                      ? 'Add your preferred class timing, meeting preference, or any message for the team.'
                      : 'Add any requirement or message for the team.'
                }
              />
            </div>
          </div>
        </div>
      </OverlayModal>

      <OverlayModal
        show={renewalActionState.show}
        title="Subscription Action"
        onClose={closeRenewalAction}
        footer={(
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={closeRenewalAction}>Close</button>
            <button type="button" className="btn btn-primary" disabled={saving} onClick={submitRenewalAction}>
              {saving ? 'Saving...' : 'Save Action'}
            </button>
          </>
        )}
      >
        <div className="d-flex flex-column gap-3">
          <div>
            <div className="fw-semibold">{renewalActionState.subscription?.customer?.name || '-'}</div>
            <div className="small text-muted">{renewalActionState.subscription?.customer?.email || '-'}</div>
            <div className="small text-muted mt-1">{renewalActionState.subscription?.planId?.name || 'Membership plan'}</div>
          </div>
          <div>
            <label className="form-label small">Action</label>
            <select className="form-select" value={renewalActionState.action} onChange={(e) => setRenewalActionState((prev) => ({ ...prev, action: e.target.value }))}>
              <option value="extend">Extend expiry</option>
              <option value="note">Update note</option>
              <option value="reactivate">Reactivate</option>
              <option value="cancel">Cancel</option>
            </select>
          </div>
          {['extend', 'reactivate'].includes(renewalActionState.action) && (
            <div>
              <label className="form-label small">Expiry date</label>
              <input type="date" className="form-control" value={renewalActionState.expiresAt} onChange={(e) => setRenewalActionState((prev) => ({ ...prev, expiresAt: e.target.value }))} />
            </div>
          )}
          <div>
            <label className="form-label small">Note</label>
            <textarea className="form-control" rows={3} value={renewalActionState.note} onChange={(e) => setRenewalActionState((prev) => ({ ...prev, note: e.target.value }))} placeholder="Optional internal/admin note" />
          </div>
        </div>
      </OverlayModal>

      <OverlayModal
        show={!!programPreviewCourse}
        title={programPreviewCourse?.courseName || 'Program details'}
        onClose={() => setProgramPreviewCourse(null)}
        dialogClassName="modal-xl student-program-dialog"
        contentClassName="student-program-modal"
        bodyClassName="student-program-modal-body"
        footer={(
          <div className="student-program-footer-actions">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setProgramPreviewCourse(null)}>Close</button>
            <button type="button" className="btn btn-primary student-program-join-action" disabled={saving} onClick={joinPreviewProgram}>
              <span>{saving ? 'Preparing...' : getLearningFocusedCourseCta(programPreviewCourse)}</span>
              <i className="bi bi-arrow-right" />
            </button>
          </div>
        )}
      >
        {programPreviewCourse && programPreviewDetails && (
          <article className="student-program-detail">
            <section className="student-program-hero">
              <div className="student-program-hero-copy">
                <span className="student-program-category"><i className="bi bi-star-fill" />{programPreviewCourse.category || 'Career program'}</span>
                <h2>{programPreviewCourse.courseName}</h2>
                <p>{programPreviewCourse.shortDescription || programPreviewCourse.description || 'Course information is available in the curriculum below.'}</p>
                <div className="student-program-hero-meta">
                  <span><i className="bi bi-clock" />{programPreviewDetails.durationLabel}</span>
                  {!!programPreviewDetails.durationHours && <span><i className="bi bi-hourglass-split" />{programPreviewDetails.durationHours} learning hours</span>}
                  <span><i className="bi bi-display" />{programPreviewDetails.modeLabel}</span>
                  {!!programPreviewCourse.enrollmentCount && <span><i className="bi bi-people-fill" />{Number(programPreviewCourse.enrollmentCount).toLocaleString('en-IN')} enrolled</span>}
                </div>
              </div>
              <div className="student-program-artwork">
                <CourseArtwork title={programPreviewCourse.courseName} imageUrl={programPreviewCourse.thumbnailUrl || programPreviewCourse.promoImageUrl} accent={programPreviewCourse.courseColor || '#60a5fa'} height={250} compact />
              </div>
            </section>

            <section className="student-program-purchase-bar">
              <div>
                <div className="student-program-price-label">{programPreviewDetails.priceSource}</div>
                <div className="student-program-price-row">
                  <strong>{fmtMoney(programPreviewDetails.displayPrice)}</strong>
                  {programPreviewDetails.originalPrice > programPreviewDetails.displayPrice && <del>{fmtMoney(programPreviewDetails.originalPrice)}</del>}
                  {!!programPreviewCourse.discountPercent && <span>{programPreviewCourse.discountPercent}% off</span>}
                </div>
              </div>
              {programPreviewPlan && (
                <div className="student-program-access-note">
                  <i className="bi bi-shield-check" />
                  <span>Access through <strong>{programPreviewPlan.name || programPreviewPlan.title}</strong> for {programPreviewPlan.durationDays || 0} days</span>
                </div>
              )}
            </section>

            <section className="student-program-section">
              <div className="student-program-section-heading"><span>Program value</span><small>Benefits configured for this learning path</small></div>
              <div className="student-program-value-grid">
                {programPreviewDetails.valueHighlights.map((item) => (
                  <div className={`student-program-value-item tone-${item.tone}`} key={item.title}>
                    <span><i className={`bi ${item.icon}`} /></span>
                    <div><strong>{item.title}</strong><p>{item.text}</p></div>
                  </div>
                ))}
              </div>
            </section>

            <div className="student-program-two-column">
              <section className="student-program-section">
                <div className="student-program-section-heading"><span>What you'll learn</span><small>{programPreviewDetails.lessonCount ? `${programPreviewDetails.lessonCount} lessons` : 'Published curriculum'}</small></div>
                <div className="student-program-check-list">
                  {(programPreviewDetails.skills.length
                    ? programPreviewDetails.skills
                    : programPreviewDetails.syllabus.flatMap((module) => module.topics || []).slice(0, 8)
                  ).map((item) => <div key={item}><i className="bi bi-check-circle-fill" /><span>{item}</span></div>)}
                  {!programPreviewDetails.skills.length && !programPreviewDetails.syllabus.some((module) => module.topics?.length) && <p className="student-program-empty">Learning outcomes have not been added to this course yet.</p>}
                </div>
              </section>

              <section className="student-program-section">
                <div className="student-program-section-heading"><span>Who can join</span><small>Entry requirements</small></div>
                {programPreviewDetails.prerequisites.length ? (
                  <div className="student-program-check-list neutral">
                    {programPreviewDetails.prerequisites.map((item) => <div key={item}><i className="bi bi-person-check-fill" /><span>{item}</span></div>)}
                  </div>
                ) : <p className="student-program-empty">No prerequisites have been specified for this program.</p>}
                <div className="student-program-format-strip">
                  <span><i className="bi bi-calendar3" />{programPreviewDetails.durationLabel}</span>
                  <span><i className="bi bi-display" />{programPreviewDetails.modeLabel}</span>
                </div>
              </section>
            </div>

            <section className="student-program-section">
              <div className="student-program-section-heading"><span>Curriculum preview</span><small>{programPreviewDetails.syllabus.length} modules</small></div>
              {programPreviewDetails.syllabus.length ? (
                <div className="student-program-curriculum">
                  {programPreviewDetails.syllabus.slice(0, 6).map((module, index) => (
                    <div key={`${module.module || 'module'}-${index}`}>
                      <span>{index + 1}</span>
                      <div><strong>{module.module || `Module ${index + 1}`}</strong><small>{module.topics?.length || 0} lessons{module.hours ? ` | ${module.hours} hours` : ''}</small></div>
                    </div>
                  ))}
                </div>
              ) : <p className="student-program-empty">Curriculum details have not been published for this course yet.</p>}
            </section>

            {!!programPreviewCourse.discountPercent && (
              <section className="student-program-offer">
                <span><i className="bi bi-tag-fill" /></span>
                <div><strong>Current course offer</strong><p>Save {programPreviewCourse.discountPercent}% on the listed course fee while this configured discount remains active.</p></div>
              </section>
            )}
          </article>
        )}
      </OverlayModal>

      <AlertToast
        show={toast.show}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, show: false }))}
      />

      <OverlayModal
        show={batchPicker.show}
        title={`Select Batch${batchPicker.course?.courseName ? ` - ${batchPicker.course.courseName}` : ''}`}
        onClose={() => {
          if (saving) return;
          setBatchPicker({ show: false, course: null, batches: [] });
          setSelectedBatchId('');
        }}
        footer={(
          <>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={saving}
              onClick={() => {
                setBatchPicker({ show: false, course: null, batches: [] });
                setSelectedBatchId('');
              }}
            >
              Cancel
            </button>
            <button type="button" className="btn btn-primary btn-sm" disabled={saving || !selectedBatchId} onClick={confirmBatchEnrollment}>
              {saving ? 'Enrolling...' : 'Confirm Enrollment'}
            </button>
          </>
        )}
      >
        <div className="d-flex flex-column gap-3">
          {(batchPicker.batches || []).map((batch) => {
            const seats = Number(batch.maxStudents || 0) > 0
              ? `${Number(batch.currentStudents || 0)}/${Number(batch.maxStudents || 0)}`
              : `${Number(batch.currentStudents || 0)} enrolled`;
            return (
              <label key={batch._id} className={`border rounded p-3 ${String(selectedBatchId) === String(batch._id) ? 'border-primary bg-primary-subtle' : 'bg-white'}`} style={{ cursor: 'pointer' }}>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="membership-batch"
                    checked={String(selectedBatchId) === String(batch._id)}
                    onChange={() => setSelectedBatchId(String(batch._id))}
                  />
                  <span className="form-check-label ms-2">
                    <span className="fw-semibold d-block">{batch.batchNumber || 'Unnamed Batch'}</span>
                    <span className="small text-muted d-block">
                      {batch.classroom || 'No classroom'} | {batch.status || 'active'} | Seats: {seats}
                    </span>
                    <span className="small text-muted d-block">
                      Start: {batch.startDate ? fmtDate(batch.startDate) : '-'}
                    </span>
                  </span>
                </div>
              </label>
            );
          })}
          {!batchPicker.batches?.length && <div className="text-muted">No available batches found.</div>}
        </div>
      </OverlayModal>
    </div>
  );
}
